"use client";

import { WORKBAR_WIDTH } from "../lib/workbar-width";
import { captureDurableOwner, durableRetries, isDurableSubmissionPending, pendingDurableTab, recoveryAccount, removeDurableRetry, registerDurableRefineOwner, retireDurableOwner, startDurableRecovery } from "../lib/ecom-durable-submission";
import { useState, useEffect, useRef, useCallback, useMemo, useSyncExternalStore, type CSSProperties, type KeyboardEvent as ReactKeyboardEvent, type ReactNode, type SetStateAction } from "react";
import { useSearchParams } from "next/navigation";
import dynamic from "next/dynamic";
import {
  DndContext,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  rectSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Heart, ImageIcon, Layers, Lightbulb, Palette, RotateCcw, Save, Sparkles, X } from "lucide-react";
import { useHistoryFavorite } from "../components/ecom-image/useHistoryFavorite";
import HistoryPanel from "../components/ecom-image/AssetPanel";
import PreviewArea from "../components/ecom-image/PreviewArea";
import ImageLayerRecognitionModal from "../components/ecom-image/ImageLayerRecognitionModal";
import ImageStitchPanel from "../components/ecom-image/ImageStitchPanel";
import DetailPageReplicaWorkspace from "../components/ecom-image/DetailPageReplicaWorkspace";
import DetailPageReplicaResultPanel from "../components/ecom-image/DetailPageReplicaResultPanel";
import DynamicComponentPanel, { checkRequiredFieldsFilled, hasBatchSelectorInConfig, hasPendingBlobUrls, stripBlobUrls, type UploadImageHoverPreview } from "../components/ecom-image/DynamicComponentPanel";
import EcomUploadEmptyState from "../components/ecom-image/EcomUploadEmptyState";
import ReferenceSettingModal, { ReferenceConfirmResult } from "../components/ecom-image/ReferenceSettingModal";
import InspirationUploadModal from "../components/ecom-image/InspirationUploadModal";
import RetryConfirmModal from "../components/ecom-image/RetryConfirmModal";
import InspirationCommunityModal from "../components/ecom-image/InspirationCommunityModal";
import { ecomImageApi, patchTaskOutputImage, getFunctionalResult as requestFunctionalResult, executeFunctional, executeFunctionalBatch, getResultGroup as requestResultGroup, WorkflowUIConfig, ComponentConfig, ModelAsset, BackgroundAsset, PoseAsset, PoseCategory, MergedPoseItem, TASK_STATUS, WorkflowCostItem, NextStepTarget, ExpandConfigItem, MyCostsResult, type ModuleNameConfig } from "../lib/ecom-image-api";
import { mapLegacyEcomFailureCode, type EcomApiFailure } from "../lib/ecom-api-error";
import { getErrorMessage } from "../lib/error-codes";
import { normalizeReversePromptToPlainText } from "../lib/ecom-reverse-prompt-plain-text";
import { resolveInspireWorkflowBindings, type InspireTemplateTargetConfig } from "../lib/ecom-inspire-workflow-bindings";
import InspireResultPanel, { InspireBatchResult, InspireBatchStatus } from "../components/ecom-image/InspireResultPanel";
import {
  isAutoRatioPlaceholder,
  normalizeRatioSelectorSelection,
  resolveAutoRatiosInPlace,
  resolveRatioSelectorOptions,
  type AutoRatioResolveResult,
} from "../lib/ecom-auto-ratio";
import { useBatchSSE, type BatchChildUpdate } from "../lib/useBatchSSE";
import { hasAllEcomBatchResults, mergeEcomBatchResults, type EcomBatchResults } from "../lib/ecom-batch-results";
import { ECOM_PREFILL_SOURCE_IMAGES_KEY } from "../lib/ecom-prefill-utils";
import { collectImageReversePromptDraftUrls, stripEcomDraftOnlyParams } from "../lib/ecom-draft-meta";
import {
  getPromptTextFromParams,
  isClonePromptReferenceWorkflow,
  resolveFlowXImagePromptReference,
  shouldUsePromptReferenceForCurrentPrompt,
} from "../lib/ecom-clone-prompt-reference";
import { createClientUUID } from "../lib/client-uuid";
import { IMAGE_ACCEPT, isImageFile, MAX_REVERSE_PROMPT_BYTES } from "../lib/file-utils";
import SortablePoseGrid from "../components/ecom-image/SortablePoseGrid";
import { downloadFile, downloadFiles } from "../lib/download";
import { preloadGeneratedImages } from "../lib/image-utils";
import { useShellState } from "@flowx/module-runtime/client";
import { notifyPointsChanged } from "../lib/points-notify";
import type { EcomActiveHistoryTaskPayload, EcomHistoryRegeneratePayload, EcomHistorySelection } from "../lib/ecom-history-selection";
import { loadEcomOpenedTabs, saveEcomOpenedTabs } from "../lib/ecom-opened-tabs";
import { filterLegacyAgentWorkspaceTabs } from "../lib/ecom-agent-tab-visibility";
import { getEcomWorkflowName, isEcomAgentEntryDismissed, isEcomAgentSelection, groupEcomWorkflowCatalog, setEcomAgentEntryDismissed } from "../lib/ecom-workflow-navigation";
import type { ImageLayerTask } from "../lib/image-layer-api";
import { AgentEntryPlaceholder } from "../components/agent/AgentEntryPlaceholder";
import { extractPromptFromParams, extractPromptValue, resolveTaskOutputImages } from "../lib/ecom-image-history-utils";
import { getEcomRefinePollOutcome } from "../lib/ecom-refine-poll-decision";
import { decideRetryEntryAction, normalizeEcomRetrySource, shouldOpenRetryModalForDecision, shouldUseRestoreParamsForRetry } from "../lib/ecom-retry-decision";
import {
  bindActualRetryTaskId,
  buildRetryPreviewState,
  captureRetrySourceIdentity,
  isRetryProjectionIdentityCurrent,
  isSameActiveRetryIdentity,
  isRetrySourceIdentityCurrent,
  shouldFinalizeRetryPreview,
  type ActiveRetryIdentity,
  type RetryWorkspaceContext,
} from "../lib/ecom-retry-preview-state";
import { resolveSingleRetryTaskId } from "../lib/ecom-retry-selection";
import {
  COZE_TEMPLATE_REFERENCE_DRAFT_ID,
  buildCozeTemplateSetGenerateRequest,
  getCozeTemplateSetValidationMessage,
  isFallbackCozeTemplateSetId,
  normalizeCozeTemplateSetValue,
  resolveCozeTemplateSetGenerateGate,
  validateCozeTemplateSetValue,
} from "../lib/ecom-coze-template-set";
import { resolveEcomBillingBatchCount } from "../lib/ecom-billing-batch-count";
import { calculateEcomFixedBillingEstimate } from "../lib/ecom-fixed-billing-estimate";
import {
  clearCozeReversePromptHistoryScope,
  createCozeReversePromptHistoryScope,
} from "../lib/ecom-coze-reverse-prompt-history";
import {
  activateReversePromptScope,
  cancelReversePromptScope,
  claimReversePromptOperation,
  isReversePromptOperationCurrent,
  updateReversePromptOperation,
  type ReversePromptOperationClaim,
} from "../lib/ecom-reverse-prompt-lifecycle";
import { createReverseNavigateSourceWorkspaceFields } from "../lib/ecom-reverse-navigate-source";
import { clearDetailReplicaReverseHistoryScope } from "../lib/ecom-detail-page-replica";
import { EcomUiProvider } from "../ui";
import { EcomButton } from "../ui";
import { EcomSwitch } from "../ui";
import { EcomTabs } from "../ui";
import { AgentWorkspaceTabTitle } from "../components/agent/AgentWorkspaceTabTitle";
import { useEcomFeedback } from "../ui";
import { useEcomPollingFeedback } from "../ui/feedback/polling-feedback";
import {
  createEcomTabId,
  captureEcomGenerationInputs,
  restoreEcomGenerationInputs,
  createEmptyEcomWorkspaceTab,
  createFreshEcomWorkspaceTabCopy,
  createUniqueEcomTabId,
  applyEcomRefinePreviews,
  findEcomWorkspaceTabByTaskIds,
  hasAnyPerBatchOverride,
  loadEcomTabHistory,
  normalizeEcomMainTab,
  normalizeEcomRefineSourceImages,
  normalizePerBatchRatios,
  reconcileEcomRefinePreviewsForPersistence,
  parseEcomTabId,
  removeClosedDetachedWorkspaceTab,
  resolveActiveRefinesForTab,
  saveEcomTabHistory,
  type EcomGenerationStatus,
  type EcomInspireBatchStatus,
  type EcomInspireTabResult,
  type EcomRefinePreview,
  type EcomRefineTrackInfo,
  type EcomWorkspaceTab,
  type EcomWorkspaceTabHistory,
  type PerBatchRatio,
} from "../lib/ecom-tab-history";

const ImageRefineEditor = dynamic(
  () => import("../components/ecom-image/ImageRefineEditor"),
  { ssr: false },
);

type InspirationUploadPayload = Readonly<{
  session: number;
  imageUrl: string;
  prompt: string;
}>;

import { loadEcomDraftState, saveEcomDraftState } from "../lib/ecom-draft-state";
import { buildEcomStorageScope, getEcomWindowId, migrateLegacyEcomStorageScope } from "../lib/ecom-window-scope";
import { isEcomGenerationRequestOwnerCurrent } from "../lib/ecom-generation-request-owner";
import { resolveEcomPreviewDetailImageUrl } from "../lib/ecom-result-image-source";
import { startEcomPollingLoop, type EcomPollingLoopController } from "../lib/ecom-polling";
import PerBatchRatioPanel from "../components/ecom-image/PerBatchRatioPanel";
import {
  DETAIL_REPLICA_CROP_SIZES_KEY,
  DETAIL_REPLICA_BLANK_FLAGS_KEY,
  DETAIL_REPLICA_MAX_SCREENS,
  DETAIL_REPLICA_RATIOS_KEY,
  DETAIL_REPLICA_REFERENCE_IMAGES_KEY,
  DETAIL_REPLICA_RATIO_CONFIG_SIGNATURE_KEY,
  type DetailReplicaDeletedScreen,
  isDetailPageReplicaWorkflow,
  normalizeDetailReplicaImages,
  removeDetailReplicaScreen,
  resolveDetailReplicaBatchCount,
  resolveDetailReplicaComponentKeys,
  resolveDetailReplicaGate,
  resolveDetailReplicaRatioOptions,
  resolveDetailReplicaState,
  stripDeprecatedDetailReplicaParams,
  restoreDetailReplicaScreen,
} from "../lib/ecom-detail-page-replica";
import { useDetailReplicaActiveIndex } from "../lib/use-detail-replica-active-index";
import { resolveDetailReplicaUploadURL } from "../lib/ecom-detail-replica-upload-url";
import { useEcomAgentHostController, useEcomAgentHostStore, createEcomCozeHostTabId, EcomAgentHostConnection, EcomAgentHostLifecycle, EcomAgentHostProvider, EcomAgentHostSurface, EcomAgentParameterSurface, type EcomHostTab } from "../components/agent/EcomAgentHostAdapter";

type GenerationStatus = "empty" | "generating" | "completed" | "failed";
type MainTab = string;
type WorkspaceView = "generate" | "history";
type PoseCategoryOption = Pick<PoseCategory, "id" | "name" | "description" | "sort_order"> & {
  count?: number;
};

const HISTORY_TAB_LABEL = "历史记录";
const WORKFLOW_UI_CONFIG_MAX_ATTEMPTS = 3;
const WORKFLOW_UI_CONFIG_RETRY_DELAYS_MS = [500, 1000, 2000];
const GENERATE_BUTTON_COOLDOWN_MS = 500;

function createRefineTrackerKey(ownerTabId: string, sourceTaskId: string, imageIndex: number): string {
  return `${ownerTabId}::${sourceTaskId}::${imageIndex}`;
}
const POSE_ALL_CATEGORY_ID = 0;
const DEFAULT_MODULE_KEY = "flow_creative";
const ECOM_IMAGE_MODULE_KEY = "ecom_image";
const INSPIRE_REVERSE_MODULE_KEY = "inspire_reverse";
const LEGACY_MODULE_DISPLAY_NAMES: Record<string, string> = {
  ecom_image: "AI智能商拍",
  inspire_reverse: "反推",
  flow_creative: "FLOW智创",
};
const DEFAULT_MODULE_CONFIGS: ModuleNameConfig[] = [
  { module_key: ECOM_IMAGE_MODULE_KEY, display_name: "AI智能商拍", sort_order: 0, is_enabled: true, icon_key: "ecom_image", icon_svg: "" },
  { module_key: INSPIRE_REVERSE_MODULE_KEY, display_name: "反推", sort_order: 1, is_enabled: true, icon_key: "inspire_reverse", icon_svg: "" },
  { module_key: DEFAULT_MODULE_KEY, display_name: "FLOW智创", sort_order: 2, is_enabled: true, icon_key: "flow_creative", icon_svg: "" },
];
const DEFAULT_FEATURE_NAMES: Record<string, string> = {
  model_gen: "模特文/图生图",
  migration: "一键迁移",
  model_suite: "模特套图生产",
};

const waitForWorkflowUIRetry = (delayMs: number): Promise<void> =>
  new Promise((resolve) => {
    setTimeout(resolve, delayMs);
  });

const historyDrawerToggleBtnClass =
  "flex cursor-pointer flex-col items-center gap-2.5 rounded-[4px] mr-3 border border-[#091B24] " +
  "bg-[#030D10] backdrop-blur-md px-2 py-3.5 text-[12px] text-white/75 " +
  "shadow-[0_4px_20px_rgba(0,0,0,0.45)] transition-colors hover:bg-[#1A1D24] hover:text-white";

function HistoryDrawerToggleIcon({ collapsed }: { collapsed: boolean }) {
  return (
    <svg
      className={`h-4 w-4 shrink-0 text-[#27ADBD] ${collapsed ? "" : "scale-x-[-1]"}`}
      viewBox="0 0 16 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <path
        d="M7 14L3 14C2.44772 14 2 13.5523 2 13L2 3C2 2.44772 2.44772 2 3 2L7 2"
        stroke="currentColor"
        strokeLinecap="round"
      />
      <path
        d="M10 4L6 8L10 12M6 8L15 8"
        stroke="currentColor"
        strokeLinecap="round"
      />
    </svg>
  );
}

type PreviewDetails = {
  url: string;
  taskId: string;
  favoriteTaskId: string;
  favoriteImageIndex: number;
  featureKey: string;
  featureName: string;
  prompt: string;
  createdAt: string;
  completedAt: string;
  pointsCost: number;
  imageCount: number;
  imageIndex: number;
};

type PreviewContext = {
  generatedImage: string | null;
  generatedImages: string[];
  currentTaskIds: string[];
  batchChildTaskIds: string[];
  viewingTaskId: string;
  historyFeatureKey: string | null;
  activeMainTab: MainTab;
  activeSubOption: string;
  currentPrompt: string;
  previewOutputCount: number;
  featureKey: string;
  featureName: string;
  refinePreviews: EcomRefinePreview[];
};

type EcomForegroundGenerationWorkspaceFields = Pick<
  EcomWorkspaceTab,
  | "status"
  | "progress"
  | "generatedImage"
  | "generatedImages"
  | "resultModelImage"
  | "resultProductImage"
  | "errorMessage"
  | "currentTaskIds"
  | "viewingTaskId"
  | "generationStartedAt"
  | "currentPrompt"
  | "failedIndexes"
  | "batchChildTaskIds"
  | "previewOutputCount"
  | "taskProgresses"
  | "batchProcessMode"
  | "batchParamValues"
  | "batchParentTaskId"
  | "batchTaskStatus"
  | "manualStitchMode"
  | "historyFeatureKey"
  | "refImageUrl"
  | "perBatchRatios"
  | "inspireResults"
  | "multiTemplateLabels"
  | "multiTemplateWorkflows"
  | "refinePreviews"
>;

function createEmptyForegroundGenerationWorkspaceFields(): EcomForegroundGenerationWorkspaceFields {
  return {
    status: "empty",
    progress: 0,
    generatedImage: null,
    generatedImages: [],
    resultModelImage: "",
    resultProductImage: "",
    errorMessage: "",
    currentTaskIds: [],
    viewingTaskId: "",
    generationStartedAt: null,
    currentPrompt: "",
    failedIndexes: [],
    batchChildTaskIds: [],
    previewOutputCount: 1,
    taskProgresses: [],
    batchProcessMode: false,
    batchParamValues: {},
    batchParentTaskId: null,
    batchTaskStatus: null,
    manualStitchMode: false,
    historyFeatureKey: null,
    refImageUrl: "",
    perBatchRatios: [],
    inspireResults: [],
    multiTemplateLabels: {},
    multiTemplateWorkflows: {},
    refinePreviews: [],
  };
}

function getUniqueOpenedOptions(options: string[]): string[] {
  return Array.from(new Set(options.filter((option) => option.trim().length > 0)));
}

function getRenderableOpenedOptions(options: string[]): string[] {
  return getUniqueOpenedOptions(options).filter(
    (option) => option === HISTORY_TAB_LABEL || parseEcomTabId(option) !== null,
  );
}

function areSameStringList(a: string[], b: string[]): boolean {
  return a.length === b.length && a.every((item, index) => item === b[index]);
}

function isWorkspaceTabId(tabId: string): boolean {
  return tabId !== HISTORY_TAB_LABEL && tabId.includes("::");
}

function getInitialOpenedTabsFromHistory(
  historyTabIds: string[],
  savedOpenedTabs: string[],
  includeHistoryTab: boolean,
): string[] {
  const historyTabSet = new Set(historyTabIds);
  const orderedHistoryTabIds = [
    ...savedOpenedTabs.filter((tabId) => historyTabSet.has(tabId)),
    ...historyTabIds.filter((tabId) => !savedOpenedTabs.includes(tabId)),
  ];

  if (!includeHistoryTab) {
    return getUniqueOpenedOptions(orderedHistoryTabIds);
  }

  if (!savedOpenedTabs.includes(HISTORY_TAB_LABEL)) {
    return getUniqueOpenedOptions([...orderedHistoryTabIds, HISTORY_TAB_LABEL]);
  }

  const orderedWithHistory = savedOpenedTabs.flatMap((tabId) => {
    if (tabId === HISTORY_TAB_LABEL) return [tabId];
    if (historyTabSet.has(tabId)) return [tabId];
    return [];
  });
  const orderedWithHistorySet = new Set(orderedWithHistory);
  return getUniqueOpenedOptions([
    ...orderedWithHistory,
    ...historyTabIds.filter((tabId) => !orderedWithHistorySet.has(tabId)),
  ]);
}

function getOrderedHistoryOpenTabIds(openedTabs: string[], history: EcomWorkspaceTabHistory): string[] {
  const orderedWorkspaceTabIds = openedTabs.filter(
    (tabId) => isWorkspaceTabId(tabId) && !!history.tabs[tabId],
  );
  const orderedWorkspaceTabSet = new Set(orderedWorkspaceTabIds);
  return [
    ...orderedWorkspaceTabIds,
    ...history.openTabIds.filter(
      (tabId) => isWorkspaceTabId(tabId) && !!history.tabs[tabId] && !orderedWorkspaceTabSet.has(tabId),
    ),
  ];
}

function isModuleEnabledValue(value: unknown): boolean {
  if (value === true || value === 1 || value === "1") return true;
  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    return normalized === "true" || normalized === "enabled" || normalized === "on";
  }
  return false;
}

function WorkspaceTabCloseIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 1024 1024" fill="currentColor" className="block shrink-0" aria-hidden="true" focusable="false">
      <path d="M512 466.944l233.472-233.472a31.744 31.744 0 0 1 45.056 45.056L557.056 512l233.472 233.472a31.744 31.744 0 0 1-45.056 45.056L512 557.056l-233.472 233.472a31.744 31.744 0 0 1-45.056-45.056L466.944 512 233.472 278.528a31.744 31.744 0 0 1 45.056-45.056z" />
    </svg>
  );
}

type SortableWorkspaceTabProps = {
  tabId: string;
  title: string;
  className: string;
  children: ReactNode;
  onContextMenu: (event: React.MouseEvent<HTMLDivElement>) => void;
  isActive: boolean;
  onActivate: () => void;
};

function SortableWorkspaceTab({
  tabId,
  title,
  className,
  children,
  onContextMenu,
  isActive,
  onActivate,
}: SortableWorkspaceTabProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: tabId });
  const [mirrorSweep, setMirrorSweep] = useState(0);

  const isImageDrag = (dataTransfer: DataTransfer) => {
    const types = Array.from(dataTransfer.types);
    if (types.includes("application/x-flowx-ecom-image") || types.includes("text/uri-list")) return true;
    if (!types.includes("Files")) return false;
    // Drag data is protected until drop. Some browsers expose only the Files type.
    const files = Array.from(dataTransfer.items).filter(item => item.kind === "file");
    return files.length === 0 || files.some(item => !item.type || item.type.startsWith("image/"));
  };
  const handleImageDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    if (isDragging || !isImageDrag(event.dataTransfer)) return;
    event.preventDefault();
    if (!isActive) onActivate();
  };

  const style: CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.45 : 1,
    zIndex: isDragging ? 60 : "auto",
    touchAction: "none",
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      onContextMenu={onContextMenu}
      onDragEnter={handleImageDragOver}
      onDragOver={handleImageDragOver}
      onDrop={(event) => {
        // Tabs only reveal a destination; dropping here must not open the image URL.
        if (isImageDrag(event.dataTransfer)) event.preventDefault();
      }}
      onClickCapture={(event) => {
        if (!isDragging && event.target instanceof Element && event.target.closest('[data-ecom-workspace-tab-select]')) {
          setMirrorSweep((value) => value + 1);
        }
      }}
      className={`${className} ${isDragging ? "cursor-grabbing" : "cursor-grab"}`}
      title={title}
      {...attributes}
      {...listeners}
    >
      {children}
      {mirrorSweep > 0 && (
        <span
          key={mirrorSweep}
          aria-hidden="true"
          className="ecom-workspace-tab-mirror"
          onAnimationEnd={() => setMirrorSweep(0)}
        />
      )}
    </div>
  );
}

function formatPromptFromParams(jsonStr: string): string {
  if (!jsonStr) return "";
  try {
    const params = JSON.parse(jsonStr);
    return typeof params === "object" && params !== null && !Array.isArray(params)
      ? extractPromptFromParams(params as Record<string, unknown>)
      : "";
  } catch {
    return "";
  }
}

function formatPreviewFullTime(dateStr: string): string {
  if (!dateStr) return "—";
  try {
    return new Date(dateStr).toLocaleString("zh-CN", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
    });
  } catch {
    return dateStr;
  }
}

function parseApiTimestampMs(value?: string): number | null {
  if (!value) return null;
  const parsed = Date.parse(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function formatPreviewFileSize(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return "";
  if (bytes > 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  return `${Math.max(1, Math.round(bytes / 1024))} KB`;
}

function DouziAmount({
  amount,
  className = "",
  iconClassName = "",
}: {
  amount: number;
  className?: string;
  iconClassName?: string;
}) {
  return (
    <span className={`inline-flex items-center gap-0.5 text-[#00FAE5] ${className}`}>
      <span className={`h-3.5 w-3.5 shrink-0 bg-[url('/douzi.svg')] bg-contain bg-center bg-no-repeat ${iconClassName}`} aria-hidden />
      <span>{amount}</span>
    </span>
  );
}

function toErrorCode(raw: string): string {
  const code = mapLegacyEcomFailureCode(raw);
  return `${code} ${getErrorMessage(Number(code.slice(1)))}`;
}

// 判断错误是否为「豌豆余额不足」(E2006)。
// 优先用后端返回的 errorCode 判断，避免依赖 raw 文案；保留 raw 兜底以兼容历史路径。
function isInsufficientBalanceError(err: unknown): boolean {
  const ec = (err as Record<string, unknown> | null | undefined)?.errorCode;
  if (ec === 2006) return true;
  const raw = err instanceof Error ? err.message : "";
  return raw.includes("豌豆余额不足") || raw.includes("余额不足");
}

const STORAGE_CDN_HOSTS = new Set(
  [
    "filesoss.flowxai.cn",
    process.env.NEXT_PUBLIC_STORAGE_CDN_DOMAIN,
  ]
    .filter(Boolean)
    .map((host) => String(host).replace(/^https?:\/\//, "").split("/")[0].toLowerCase())
);

function isPublicStorageUrl(value: string): boolean {
  if (value.startsWith("/api/v1/public/storage/file/") || value.includes("/api/v1/public/storage/file/")) {
    return true;
  }
  try {
    const parsed = new URL(value);
    return STORAGE_CDN_HOSTS.has(parsed.hostname.toLowerCase());
  } catch {
    return false;
  }
}

function looksLikeImageURL(value: string): boolean {
  if (!value || typeof value !== "string") return false;
  if (value.startsWith("http://") || value.startsWith("https://")) return true;
  if (value.startsWith("/api/v1/public/storage/file/")) return true;
  if (value.startsWith("data:image/")) return true;
  return false;
}

function normalizeImageURLFromParam(value: unknown): string {
  if (typeof value === "string") {
    const trimmed = value.trim();
    return looksLikeImageURL(trimmed) ? trimmed : "";
  }
  if (!value || typeof value !== "object" || Array.isArray(value)) return "";
  const record = value as Record<string, unknown>;
  for (const key of ["file_url", "public_url", "url", "image_url", "thumbnail_url", "coze_value"]) {
    const candidate = record[key];
    if (typeof candidate === "string") {
      const normalized = normalizeImageURLFromParam(candidate);
      if (normalized) return normalized;
    }
  }
  return "";
}

function collectImageURLsFromParams(value: unknown): string[] {
  const result: string[] = [];
  const visit = (item: unknown) => {
    if (Array.isArray(item)) {
      item.forEach(visit);
      return;
    }
    const normalized = normalizeImageURLFromParam(item);
    if (normalized) {
      result.push(normalized);
      return;
    }
    if (item && typeof item === "object") {
      Object.values(item as Record<string, unknown>).forEach(visit);
    }
  };
  visit(value);
  return Array.from(new Set(result));
}

function markParamUrlOnly(params: Record<string, unknown>, key: string) {
  const existing = Array.isArray(params.__url_only_params) ? params.__url_only_params as string[] : [];
  if (!existing.includes(key)) {
    params.__url_only_params = [...existing, key];
  }
}

function normalizeLogoPresetSubmission(
  params: Record<string, unknown>,
  uiConfig: WorkflowUIConfig | null,
): Record<string, unknown> {
  const logoKeys: string[] = ["logo_presets", "logo_preset"];
  uiConfig?.components
    ?.filter((comp) => comp.component_type === "logo_preset" && comp.coze_param_key)
    .forEach((comp) => {
      if (!logoKeys.includes(comp.coze_param_key)) {
        logoKeys.push(comp.coze_param_key);
      }
    });

  let payload: unknown = params.logo_presets;
  for (const key of logoKeys) {
    if (key === "logo_presets") continue;
    if (params[key] !== undefined && params[key] !== null) {
      payload = params[key];
      break;
    }
  }

  for (const key of logoKeys) {
    if (key !== "logo_presets") {
      delete params[key];
    }
  }

  if (
    payload &&
    typeof payload === "object" &&
    Array.isArray((payload as { items?: unknown }).items) &&
    (payload as { items: unknown[] }).items.length > 0
  ) {
    params.logo_presets = { enabled: true, items: (payload as { items: unknown[] }).items };
  } else {
    delete params.logo_presets;
  }

  return params;
}

const OUTPUT_CROP_SIZE_PARAM = "__output_crop_size";
const OUTPUT_CROP_RATIO_PARAM = "__output_crop_ratio";

function normalizeDimensionValue(value: string): string {
  return value.trim().toLowerCase().replace(/\s+/g, "").replace("*", "x");
}

function findBoundRatioForSize(
  ratioResolutions: unknown,
  size: string,
): { ratio: string; cropSize: string } | null {
  if (!ratioResolutions || typeof ratioResolutions !== "object" || Array.isArray(ratioResolutions)) {
    return null;
  }
  const normalizedSize = normalizeDimensionValue(size);
  if (!normalizedSize) return null;

  for (const [ratio, resolutions] of Object.entries(ratioResolutions as Record<string, unknown>)) {
    if (!Array.isArray(resolutions)) continue;
    const matched = resolutions
      .map(String)
      .find((resolution) => normalizeDimensionValue(resolution) === normalizedSize);
    if (matched) {
      return { ratio, cropSize: size };
    }
  }
  return null;
}

function isRatioValue(value: string): boolean {
  return /^\d+:\d+$/.test(value);
}

function applyOutputCropRatioParam(params: Record<string, unknown>, ratio: string | null | undefined) {
  if (typeof params[OUTPUT_CROP_SIZE_PARAM] === "string" && params[OUTPUT_CROP_SIZE_PARAM]) {
    delete params[OUTPUT_CROP_RATIO_PARAM];
    return;
  }
  if (ratio && !isAutoRatioPlaceholder(ratio) && isRatioValue(ratio)) {
    params[OUTPUT_CROP_RATIO_PARAM] = ratio;
  } else {
    delete params[OUTPUT_CROP_RATIO_PARAM];
  }
}

function readComponentConfigString(comp: ComponentConfig | null | undefined, key: string): string {
  const value = comp?.config?.[key];
  return typeof value === "string" ? value.trim() : "";
}

function isPerBatchRatioTargetComponent(comp: ComponentConfig | null | undefined): comp is ComponentConfig {
  return !!comp && comp.is_visible && comp.component_type === "batch_multi_image";
}

type PerBatchRatioBindingConfig = {
  targetParamKey: string;
  ratioComp: ComponentConfig;
};

function resolvePerBatchRatioBoundParamKey(
  uiConfig: WorkflowUIConfig | null,
  ratioComp: ComponentConfig | null,
): string | null {
  if (!uiConfig || !ratioComp) return null;
  const components = uiConfig.components || [];
  const configuredKey = readComponentConfigString(ratioComp, "batch_ratio_bind_param_key");
  if (configuredKey) {
    const directTarget = components.find((comp) => comp.coze_param_key === configuredKey);
    if (isPerBatchRatioTargetComponent(directTarget)) {
      return directTarget.coze_param_key;
    }
  }

  const ratioKey = ratioComp.coze_param_key;
  const reverseTarget = components.find(
    (comp) =>
      isPerBatchRatioTargetComponent(comp) &&
      readComponentConfigString(comp, "batch_ratio_source_param_key") === ratioKey,
  );
  return reverseTarget?.coze_param_key || null;
}

function resolvePerBatchRatioBindingConfig(uiConfig: WorkflowUIConfig | null): PerBatchRatioBindingConfig | null {
  if (!uiConfig) return null;
  const components = uiConfig.components || [];
  const ratioComps = components.filter((comp) => comp.component_type === "ratio_selector");

  for (const ratioComp of ratioComps) {
    const targetParamKey = resolvePerBatchRatioBoundParamKey(uiConfig, ratioComp);
    if (targetParamKey) {
      return { targetParamKey, ratioComp };
    }
  }

  for (const targetComp of components) {
    if (!isPerBatchRatioTargetComponent(targetComp)) continue;
    const sourceKey = readComponentConfigString(targetComp, "batch_ratio_source_param_key");
    if (!sourceKey) continue;
    const ratioComp = ratioComps.find((comp) => comp.coze_param_key === sourceKey);
    if (ratioComp) {
      return { targetParamKey: targetComp.coze_param_key, ratioComp };
    }
  }

  return null;
}

/**
 * 在调用 applyRatioSelectorParams 之前，先尝试根据锚点上传图片智能识别比例。
 * 命中时会原地修改 params。智能比例是提交细节，不向用户弹出提示。
 */
async function applyAutoRatioParams(
  params: Record<string, unknown>,
  uiConfig: WorkflowUIConfig | null,
): Promise<AutoRatioResolveResult[]> {
  if (!uiConfig?.components) return [];
  try {
    return await resolveAutoRatiosInPlace(params, uiConfig.components);
  } catch (error) {
    console.warn("auto-ratio resolve failed, fallback to default", error);
    return [];
  }
}

/**
 * 详情页复刻的“智能”比例按当前屏的参考图解析。后台 auto 候选通常用于
 * 普通工作流；在这里它不能将已知的竖图锁成 1:1。详情页比例菜单本身（显式
 * options 或默认比例集）才是本工作流可选的实际比例集合。
 */
async function resolveDetailReplicaAutoRatioParams(
  params: Record<string, unknown>,
  uiConfig: WorkflowUIConfig | null,
  referenceKey: string,
): Promise<AutoRatioResolveResult[]> {
  if (!uiConfig?.components || !referenceKey) return [];

  const candidates = resolveDetailReplicaRatioOptions(uiConfig)
    .map((option) => option.value)
    .filter((ratio) => isRatioValue(ratio));
  if (candidates.length === 0) return [];

  const components = uiConfig.components.map((component) => (
    component.component_type === "ratio_selector"
      ? {
          ...component,
          config: {
            ...component.config,
            auto_anchor_param_key: referenceKey,
            auto_supported_ratios: candidates,
          },
        }
      : component
  ));
  return resolveAutoRatiosInPlace(params, components);
}

function applyRatioSelectorParams(params: Record<string, unknown>, uiConfig: WorkflowUIConfig | null) {
  if (!uiConfig?.components) return;

  for (const comp of uiConfig.components) {
    if (comp.component_type !== "ratio_selector") continue;

    if (!(comp.coze_param_key in params)) {
      const defaultVal = comp.config?.default_value as string | undefined;
      if (defaultVal) {
        params[comp.coze_param_key] = defaultVal;
      }
    }

    const value = params[comp.coze_param_key];
    if (typeof value !== "string") {
      continue;
    }

    if (isRatioValue(value)) {
      const normalized = normalizeRatioSelectorSelection(
        resolveRatioSelectorOptions(comp.config),
        value,
        params[OUTPUT_CROP_SIZE_PARAM],
      );
      if (!normalized.ratio) {
        params[comp.coze_param_key] = "auto";
        delete params[OUTPUT_CROP_SIZE_PARAM];
        delete params[OUTPUT_CROP_RATIO_PARAM];
        continue;
      }
    }

    if (isAutoRatioPlaceholder(value) || isRatioValue(value)) {
      if (isAutoRatioPlaceholder(value)) {
        params[comp.coze_param_key] = "auto";
      }
      applyOutputCropRatioParam(params, value);
      continue;
    }

    const binding = findBoundRatioForSize(comp.config?.ratio_resolutions, value);
    if (!binding) continue;

    params[comp.coze_param_key] = binding.ratio;
    if (typeof params[OUTPUT_CROP_SIZE_PARAM] !== "string" || !params[OUTPUT_CROP_SIZE_PARAM]) {
      params[OUTPUT_CROP_SIZE_PARAM] = binding.cropSize;
    }
    delete params[OUTPUT_CROP_RATIO_PARAM];
  }
}

function shouldAppendCozeTemplateSetFallbackComponent(
  workflowKey: string,
  workflowConfig?: {
    feature_key?: string;
    name?: string;
    description?: string;
    workflow_name?: string;
    callable_key?: string;
  } | null,
): boolean {
  const haystack = [
    workflowKey,
    workflowConfig?.feature_key,
    workflowConfig?.name,
    workflowConfig?.description,
    workflowConfig?.workflow_name,
    workflowConfig?.callable_key,
  ].filter(Boolean).join(" ").toLowerCase();

  return (
    haystack.includes("coze_template_set") ||
    haystack.includes("coze_template") ||
    haystack.includes("模板套装") ||
    haystack.includes("模板库")
  );
}

function withCozeTemplateSetFallbackComponent(
  config: WorkflowUIConfig,
  workflowConfig?: {
    feature_key?: string;
    name?: string;
    description?: string;
    workflow_name?: string;
    callable_key?: string;
  } | null,
): WorkflowUIConfig {
  if (config.components.some((comp) => comp.component_type === "coze_template_set")) {
    return config;
  }
  if (!shouldAppendCozeTemplateSetFallbackComponent(config.workflow_key, workflowConfig)) {
    return config;
  }

  const maxDisplayOrder = config.components.reduce((max, comp) => Math.max(max, comp.display_order || 0), 0);
  return {
    ...config,
    components: [
      ...config.components,
      {
        id: -9001,
        component_type: "coze_template_set",
        label: "模板套装",
        coze_param_key: "__coze_template_set",
        display_order: maxDisplayOrder + 1,
        is_visible: true,
        is_required: false,
        columns: 1,
        config: {
          allow_user_slots: true,
          max_user_slots: 12,
          template_image_param: "ref_image",
          upload_images_param: "product_image",
          prompt_param: "prompt",
        },
      },
    ],
  };
}

type TemplateTargetConfig = InspireTemplateTargetConfig & {
  label: string;
};

function getTemplateTargetWorkflowKeys(template: TemplateTargetConfig): string[] {
  const values = Array.isArray(template.default_target_workflows)
    ? [...template.default_target_workflows]
    : [];
  if (values.length === 0 && template.default_target_workflow) {
    values.push(template.default_target_workflow);
  }
  return Array.from(new Set(values.map(value => String(value).trim()).filter(Boolean)));
}

function BatchTabsSelector({
  options,
  value,
  unit,
  onChange,
}: {
  options: number[];
  value: number;
  unit: string;
  onChange: (value: number) => void;
}) {
  const items = Array.from(new Set(options)).map((count) => ({
    key: String(count),
    label: `${count}${unit}`,
  }));

  return (
    <EcomTabs
      fullWidth
      wrap
      size="medium"
      activeKey={String(value)}
      items={items}
      onChange={(key) => onChange(Number(key))}
    />
  );
}

function ModuleNavIcon({ iconKey, iconSvg }: { iconKey?: string; iconSvg?: string }) {
  // 优先使用自定义 SVG
  if (iconSvg && iconSvg.trim()) {
    return (
      <div
        className="flex h-5 w-5 items-center justify-center"
        dangerouslySetInnerHTML={{ __html: iconSvg }}
      />
    );
  }

  const key = (iconKey || "sparkles").trim();
  if (key === "ecom_image") {
    return (
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M11.6667 3.5C13.0475 3.5 14.1667 4.6125 14.1667 5.9845L14.1665 8.51575L18.6822 5.3105C19.2337 4.919 20 5.31075 20 5.98425V14.2657C20 14.9392 19.2337 15.331 18.6825 14.9395L14.1663 11.734V14.2657C14.1663 15.6378 13.0472 16.75 11.6663 16.75H2.5C1.11925 16.75 0 15.6375 0 14.2655V5.9845C0 4.612 1.11925 3.5 2.5 3.5H11.6667ZM11.6667 5.15625H2.5C2.04 5.15625 1.66675 5.527 1.66675 5.9845V14.2655C1.66675 14.723 2.03975 15.0938 2.5 15.0938H11.6667C12.1267 15.0938 12.5 14.723 12.5 14.2655V5.9845C12.5 5.527 12.127 5.15625 11.6667 5.15625ZM18.3333 7.59375L14.767 10.125L18.3333 12.6565V7.5935V7.59375Z" fill="currentColor" />
      </svg>
    );
  }
  if (key === "inspire_reverse") {
    return (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M17.3327 3.9502C18.8327 3.9502 20.0492 5.1667 20.0492 6.6667V12.0002L19.9992 12.0502H18.6657L18.6157 12.0002V6.6667C18.6156 6.32646 18.4804 6.0002 18.2398 5.75962C17.9992 5.51904 17.673 5.38383 17.3327 5.3837H6.66572C6.32549 5.38383 5.99923 5.51904 5.75865 5.75962C5.51807 6.0002 5.38285 6.32646 5.38272 6.6667V17.3337C5.38285 17.6739 5.51807 18.0002 5.75865 18.2408C5.99923 18.4813 6.32549 18.6166 6.66572 18.6167H11.9992L12.0492 18.6667V20.0002L11.9992 20.0502H6.66572C5.16572 20.0502 3.94922 18.8337 3.94922 17.3337V6.6667C3.94922 5.1667 5.16572 3.9502 6.66572 3.9502H17.3327Z" fill="currentColor" />
        <path d="M13.3238 17.1682C13.2446 17.0821 13.1997 16.9701 13.1973 16.8532V16.6002C13.1997 16.4833 13.2446 16.3713 13.3238 16.2852L16.5503 13.0707C16.5789 13.0408 16.6133 13.017 16.6513 13.0007C16.6894 12.9845 16.7304 12.9761 16.7718 12.9761C16.8132 12.9761 16.8541 12.9845 16.8922 13.0007C16.9303 13.017 16.9647 13.0408 16.9933 13.0707L17.4363 13.5122C17.4662 13.5406 17.49 13.5748 17.5063 13.6127C17.5226 13.6506 17.531 13.6915 17.531 13.7327C17.531 13.774 17.5226 13.8148 17.5063 13.8527C17.49 13.8906 17.4662 13.9248 17.4363 13.9532L15.2848 16.0962H20.8293C21.0193 16.0962 21.1453 16.2222 21.1453 16.4112V17.0412C21.1453 17.2307 21.0188 17.3562 20.8293 17.3562H15.2848L17.4363 19.5002C17.6263 19.6262 17.6263 19.8782 17.4993 19.9412L17.0563 20.3822C17.0277 20.4121 16.9933 20.4358 16.9553 20.452C16.9173 20.4683 16.8764 20.4766 16.835 20.4766C16.7937 20.4766 16.7528 20.4683 16.7147 20.452C16.6767 20.4358 16.6424 20.4121 16.6138 20.3822L13.3238 17.1682Z" fill="currentColor" />
        <path d="M16.1372 8.50586V9.60586C16.1372 9.67216 16.1109 9.73575 16.064 9.78264C16.0171 9.82952 15.9535 9.85586 15.8872 9.85586H7.88722C7.82091 9.85586 7.75733 9.82952 7.71044 9.78264C7.66356 9.73575 7.63722 9.67216 7.63722 9.60586V8.50586C7.63722 8.43956 7.66356 8.37597 7.71044 8.32908C7.75733 8.2822 7.82091 8.25586 7.88722 8.25586H15.8872C15.9535 8.25586 16.0171 8.2822 16.064 8.32908C16.1109 8.37597 16.1372 8.43956 16.1372 8.50586ZM13.5567 11.5059V12.6059C13.5567 12.6722 13.5304 12.7358 13.4835 12.7826C13.4366 12.8295 13.373 12.8559 13.3067 12.8559H7.88672C7.82041 12.8559 7.75683 12.8295 7.70994 12.7826C7.66306 12.7358 7.63672 12.6722 7.63672 12.6059V11.5059C7.63672 11.4396 7.66306 11.376 7.70994 11.3291C7.75683 11.2822 7.82041 11.2559 7.88672 11.2559H13.3067C13.373 11.2559 13.4366 11.2822 13.4835 11.3291C13.5304 11.376 13.5567 11.4396 13.5567 11.5059Z" fill="currentColor" />
      </svg>
    );
  }
  if (key === "flow_creative") {
    return (
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M14.516 6.96119C14.5433 7.03492 14.5926 7.0985 14.6571 7.14341C14.7216 7.18832 14.7984 7.21239 14.877 7.21239C14.9556 7.21239 15.0324 7.18832 15.0969 7.14341C15.1614 7.0985 15.2107 7.03492 15.238 6.96119L15.711 5.68819C15.841 5.33919 16.118 5.06419 16.469 4.93519L17.749 4.46419C17.8228 4.43764 17.8866 4.38896 17.9317 4.3248C17.9768 4.26064 18.001 4.18412 18.001 4.10569C18.001 4.02727 17.9768 3.95075 17.9317 3.88659C17.8866 3.82243 17.8228 3.77375 17.749 3.74719L16.469 3.27719C16.2959 3.21329 16.1387 3.11288 16.0079 2.98277C15.8771 2.85266 15.7758 2.69592 15.711 2.52319L15.238 1.25119C15.2107 1.17747 15.1614 1.11388 15.0969 1.06898C15.0324 1.02407 14.9556 1 14.877 1C14.7984 1 14.7216 1.02407 14.6571 1.06898C14.5926 1.11388 14.5433 1.17747 14.516 1.25119L14.042 2.52419C13.9776 2.69695 13.8765 2.85367 13.7457 2.98351C13.6148 3.11335 13.4573 3.21321 13.284 3.27619L12.004 3.74619C11.9303 3.77299 11.8666 3.82182 11.8217 3.88604C11.7767 3.95027 11.7525 4.02678 11.7525 4.10519C11.7525 4.18361 11.7767 4.26012 11.8217 4.32434C11.8666 4.38857 11.9303 4.4374 12.004 4.46419L13.284 4.93519C13.457 4.99899 13.6142 5.09925 13.745 5.22918C13.8758 5.3591 13.9771 5.51565 14.042 5.68819L14.516 6.96119ZM17.951 15.3512C17.9489 16.0488 17.6698 16.717 17.1751 17.2089C16.6804 17.7008 16.0106 17.9761 15.313 17.9742L4.639 18.0012C4.29349 18.0022 3.95116 17.9352 3.63155 17.804C3.31194 17.6727 3.02131 17.4798 2.77626 17.2363C2.5312 16.9927 2.33652 16.7032 2.20333 16.3844C2.07014 16.0656 2.00105 15.7237 2 15.3782V5.07319C2.00092 4.72765 2.06992 4.38567 2.20306 4.0668C2.33621 3.74794 2.53088 3.45844 2.77596 3.21484C3.02104 2.97125 3.31172 2.77834 3.63139 2.64713C3.95106 2.51593 4.29345 2.44901 4.639 2.45019L10.483 2.42319C10.899 2.42319 11.237 2.75819 11.237 3.17319C11.2357 3.37221 11.1556 3.56261 11.0143 3.70277C10.873 3.84293 10.682 3.92146 10.483 3.92119L4.639 3.94819C4.014 3.94819 3.508 4.45219 3.508 5.07319V13.9292L6.921 10.8362C7.636 10.1882 9.04 10.1762 9.738 10.8412L13.888 14.7992L15.596 16.4382C16.083 16.3132 16.443 15.8742 16.444 15.3512V9.82819C16.444 9.62835 16.5234 9.4367 16.6647 9.29539C16.806 9.15408 16.9977 9.07469 17.1975 9.07469C17.3973 9.07469 17.589 9.15408 17.7303 9.29539C17.8716 9.4367 17.951 9.62835 17.951 9.82819V15.3512ZM11.704 9.98719C12.561 9.98719 13.255 9.29719 13.255 8.44519C13.255 7.59319 12.561 6.90219 11.704 6.90219C10.847 6.90219 10.153 7.59319 10.153 8.44519C10.153 9.29619 10.847 9.98719 11.703 9.98719H11.704ZM3.577 15.7672C3.737 16.1962 4.152 16.5022 4.639 16.5022L13.599 16.4762L12.908 15.8112L8.76 11.8542C8.68197 11.7802 8.57889 11.7384 8.47137 11.7371C8.36385 11.7358 8.2598 11.7751 8.18 11.8472L3.577 15.7672Z" fill="currentColor" />
      </svg>
    );
  }
  const fallbackProps = { size: 20, strokeWidth: 1.8 };
  if (key === "image") return <ImageIcon {...fallbackProps} />;
  if (key === "layers") return <Layers {...fallbackProps} />;
  if (key === "palette") return <Palette {...fallbackProps} />;
  if (key === "lightbulb") return <Lightbulb {...fallbackProps} />;
  return <Sparkles {...fallbackProps} />;
}

export function EcomImagePage() {
  return (
    <EcomUiProvider>
      <EcomAgentHostProvider>
        <EcomImageContent />
      </EcomAgentHostProvider>
    </EcomUiProvider>
  );
}

const ECOM_PAGE_SESSION_KEY = "flowx:main-site:ecom-image:page-session:v1";
const ECOM_LEFT_NAV_COLLAPSED_KEY = "flowx:main-site:ecom-image:left-nav-collapsed:v1";

function getEcomPageSessionKey(scope?: string) {
  return scope ? `${ECOM_PAGE_SESSION_KEY}:${scope}` : ECOM_PAGE_SESSION_KEY;
}

function ensureEcomPageSession(scope?: string) {
  if (typeof window === "undefined") return;
  try {
    const key = getEcomPageSessionKey(scope);
    if (sessionStorage.getItem(key)) return;
    sessionStorage.setItem(key, String(Date.now()));
  } catch {
    // Storage can fail in private mode; keep the in-memory page usable.
  }
}

function loadLeftNavCollapsed(): boolean {
  if (typeof window === "undefined") return true;
  try {
    const raw = localStorage.getItem(ECOM_LEFT_NAV_COLLAPSED_KEY);
    return raw === null ? true : raw === "true";
  } catch {
    return true;
  }
}

function saveLeftNavCollapsed(collapsed: boolean) {
  if (typeof window === "undefined") return;
  try {
    localStorage.setItem(ECOM_LEFT_NAV_COLLAPSED_KEY, String(collapsed));
  } catch {
    // Ignore storage failures; the current render still reflects the user's choice.
  }
}

function clampProgress(value: number | undefined): number {
  if (typeof value !== "number" || Number.isNaN(value)) return 0;
  return Math.max(0, Math.min(100, value));
}

function hasRunnableGenerationSnapshot(tab: EcomWorkspaceTab | null | undefined): boolean {
  if (!tab || tab.status !== "generating") return false;
  if ((tab.currentTaskIds || []).some(Boolean)) return true;
  if ((tab.batchChildTaskIds || []).some(Boolean)) return true;
  if (tab.batchParentTaskId) return true;
  return (tab.inspireResults || []).some((result) =>
    (result.status === "pending" || result.status === "processing") &&
    !!result.taskId &&
    !result.taskId.startsWith("pending-")
  );
}

function normalizeStaleGeneratingTab(tab: EcomWorkspaceTab | null | undefined, submitting = false): EcomWorkspaceTab | null {
  if (!tab) return null;
  if (submitting) return tab;
  if (tab.status !== "generating" || hasRunnableGenerationSnapshot(tab)) return tab;

  const settledInspireResults = (tab.inspireResults || []).filter((result) =>
    result.status === "completed" || result.status === "failed" || result.status === "cancelled"
  );
  const hasOutput = !!tab.generatedImage || (tab.generatedImages || []).length > 0 || settledInspireResults.some((result) => result.status === "completed");
  const hasFailure = !!tab.errorMessage || settledInspireResults.some((result) => result.status === "failed" || result.status === "cancelled");
  const nextStatus: GenerationStatus = hasOutput ? "completed" : hasFailure ? "failed" : "empty";

  return {
    ...tab,
    status: nextStatus,
    progress: nextStatus === "empty" ? 0 : 100,
    generationStartedAt: null,
    currentTaskIds: [],
    batchChildTaskIds: [],
    batchParentTaskId: null,
    batchTaskStatus: null,
    taskProgresses: [],
    inspireResults: settledInspireResults,
  };
}

function toRetryWorkspaceContext(tab: EcomWorkspaceTab): RetryWorkspaceContext {
  return {
    tabId: tab.tabId,
    viewingTaskId: tab.viewingTaskId || "",
    currentTaskIds: tab.currentTaskIds || [],
    batchChildTaskIds: tab.batchChildTaskIds || [],
    generatedImage: tab.generatedImage || null,
    generatedImages: tab.generatedImages || [],
  };
}

function EcomImageContent() {
  const agentController = useEcomAgentHostController();
  const agentStore = useEcomAgentHostStore();
  const agentConnection = useSyncExternalStore(agentController.subscribe, agentController.getSnapshot, agentController.getSnapshot);
  const agentWorkspace = useSyncExternalStore(agentStore.subscribe, agentStore.getSnapshot, agentStore.getSnapshot);
  const agentActive = !!(agentWorkspace.activeTabInstanceId && agentWorkspace.instances[agentWorkspace.activeTabInstanceId]?.viewOpen);
  const agentSelectionRef = useRef(false);
  const handledAgentSelectionsRef = useRef(new Set<string>());
  const [agentLaunchFailed, setAgentLaunchFailed] = useState(false);
  const feedback = useEcomFeedback();
  const { run: runEcomPollingRequest } = useEcomPollingFeedback();
  const getFunctionalResult = useCallback(
    (taskId: string) => runEcomPollingRequest(
      `ecom-image:functional-result:${taskId}`,
      () => requestFunctionalResult(taskId),
    ),
    [runEcomPollingRequest],
  );
  const getResultGroup = useCallback(
    (groupId: string) => runEcomPollingRequest(
      `ecom-image:functional-result-group:${groupId}`,
      () => requestResultGroup(groupId),
    ),
    [runEcomPollingRequest],
  );
  const getTaskStatus = useCallback(
    (taskId: string) => runEcomPollingRequest(
      `ecom-image:task-status:${taskId}`,
      () => ecomImageApi.getTaskStatus(taskId),
    ),
    [runEcomPollingRequest],
  );
  const getBatchTaskStatus = useCallback(
    (parentId: string) => runEcomPollingRequest(
      `ecom-image:batch-task-status:${parentId}`,
      () => ecomImageApi.getBatchTaskStatus(parentId),
    ),
    [runEcomPollingRequest],
  );
  const { user } = useShellState();
  const ecomWindowIdRef = useRef<string | undefined>(undefined);
  if (!ecomWindowIdRef.current) {
    ecomWindowIdRef.current = getEcomWindowId();
  }
  const legacyEcomStorageScope = user?.uuid || (user?.id ? String(user.id) : undefined);
  const ecomStorageScope = buildEcomStorageScope(
    legacyEcomStorageScope,
    user?.current_team_id,
    ecomWindowIdRef.current,
  );
  migrateLegacyEcomStorageScope(legacyEcomStorageScope, ecomStorageScope);
  const ecomReversePromptHistoryBaseScope = user?.id
    ? `u${user.id}:t${user.current_team_id || 0}:w${ecomWindowIdRef.current}`
    : undefined;
  const pageSessionInitializedRef = useRef(false);
  if (!pageSessionInitializedRef.current) {
    ensureEcomPageSession(ecomStorageScope);
    pageSessionInitializedRef.current = true;
  }
  const draft = useRef(loadEcomDraftState(ecomStorageScope)).current;
  const initialTabHistoryRef = useRef<EcomWorkspaceTabHistory | null>(loadEcomTabHistory(ecomStorageScope));
  const initialActiveTab = normalizeStaleGeneratingTab(initialTabHistoryRef.current?.activeTabId
    ? initialTabHistoryRef.current.tabs[initialTabHistoryRef.current.activeTabId]
    : null, pendingDurableTab(ecomStorageScope, initialTabHistoryRef.current?.activeTabId || ""));
  const initialViewingTaskId =
    initialActiveTab?.viewingTaskId ||
    initialActiveTab?.batchParentTaskId ||
    initialActiveTab?.currentTaskIds?.[0] ||
    "";
  const initialGenerationStartedAt =
    initialActiveTab?.generationStartedAt ??
    (initialActiveTab?.status === "generating"
      ? initialActiveTab.createdAt || initialActiveTab.updatedAt || Date.now()
      : null);
  const initialExpandedMainTab = normalizeEcomMainTab(initialActiveTab?.mainTab || draft.activeMainTab || DEFAULT_MODULE_KEY);
  const [activeMainTab, setActiveMainTab] = useState<MainTab>(initialExpandedMainTab);
  const [expandedMainTabs, setExpandedMainTabs] = useState<MainTab[]>([initialExpandedMainTab]);
  const [activeSubOption, setActiveSubOption] = useState<string>(initialActiveTab?.subOption || draft.activeSubOption || "");
  const ensureMainTabExpanded = useCallback((tab: MainTab) => {
    setExpandedMainTabs((prev) => prev.includes(tab) ? prev : [...prev, tab]);
  }, []);
  // Each (mainTab, subOption) pair may have multiple open tabs (e.g. when re-submitting while one is still generating).
  // Instance 1 is the base tab; instance N>=2 produces tabIds like "AI智能商拍::模特文/图生图#2".
  const [activeTabInstance, setActiveTabInstance] = useState<number>(() => {
    const activeId = initialTabHistoryRef.current?.activeTabId;
    if (!activeId) return 1;
    const parsed = parseEcomTabId(activeId);
    return parsed?.instance ?? 1;
  });
  const [activeWorkspaceView, setActiveWorkspaceView] = useState<WorkspaceView>(initialTabHistoryRef.current?.activeWorkspaceView || "generate");
  const [openedSubOptions, setOpenedSubOptions] = useState<string[]>(() => {
    const savedOpenedTabs = loadEcomOpenedTabs(undefined, ecomStorageScope);
    const historyTabIds = (initialTabHistoryRef.current?.openTabIds || []).filter(
      (tabId) => !!initialTabHistoryRef.current?.tabs[tabId],
    );
    if (historyTabIds.length > 0) {
      return getRenderableOpenedOptions(
        getInitialOpenedTabsFromHistory(
          historyTabIds,
          savedOpenedTabs,
          initialTabHistoryRef.current?.activeWorkspaceView === "history",
        ),
      );
    }
    return getRenderableOpenedOptions(savedOpenedTabs);
  });
  const openedTabSensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 6 },
    }),
  );
  // Per-tab status snapshot used to render the spinner on tab titles. The active tab is
  // kept in sync via an effect; other tabs only update when we persist their state.
  const [tabStatusMap, setTabStatusMap] = useState<Record<string, EcomGenerationStatus>>(() => {
    const map: Record<string, EcomGenerationStatus> = {};
    const historyTabs = initialTabHistoryRef.current?.tabs || {};
    for (const [id, tab] of Object.entries(historyTabs)) {
      const normalizedTab = normalizeStaleGeneratingTab(tab);
      if (normalizedTab && typeof normalizedTab.status === "string") {
        map[id] = normalizedTab.status as EcomGenerationStatus;
      }
    }
    return map;
  });
  const [tabContextMenu, setTabContextMenu] = useState<{ tabId: string; x: number; y: number } | null>(null);
  const [isLeftNavCollapsed, setIsLeftNavCollapsed] = useState(loadLeftNavCollapsed);
  const leftNavShellRef = useRef<HTMLDivElement>(null);
  const generateScrollRef = useRef<HTMLDivElement>(null);
  const [generateScrollHasBottomOverflow, setGenerateScrollHasBottomOverflow] = useState(false);
  const syncGenerateScrollFade = useCallback(() => {
    const scroll = generateScrollRef.current;
    if (!scroll) {
      setGenerateScrollHasBottomOverflow(false);
      return;
    }
    setGenerateScrollHasBottomOverflow(
      scroll.scrollHeight > scroll.clientHeight + 1 &&
      scroll.scrollTop + scroll.clientHeight < scroll.scrollHeight - 1,
    );
  }, []);
  const isRestoringRef = useRef(!!initialActiveTab || !!(draft.cozeParams && Object.keys(draft.cozeParams).length > 0));
  const isApplyingTabHistoryRef = useRef(false);
  const persistTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const tabSwitchEpochRef = useRef(0);
  const [status, setStatus] = useState<GenerationStatus>(initialActiveTab?.status || "empty");
  const [generationStartedAt, setGenerationStartedAt] = useState<number | null>(initialGenerationStartedAt);
  const [progress, setProgress] = useState(initialActiveTab?.progress || 0);
  const [generatedImage, setGeneratedImage] = useState<string | null>(initialActiveTab?.generatedImage || null);
  const [resultModelImage, setResultModelImage] = useState<string>(initialActiveTab?.resultModelImage || "");
  const [resultProductImage, setResultProductImage] = useState<string>(initialActiveTab?.resultProductImage || "");
  const [generatedImages, setGeneratedImages] = useState<string[]>(initialActiveTab?.generatedImages || []);
  const [refinePreviews, setRefinePreviews] = useState<EcomRefinePreview[]>(initialActiveTab?.refinePreviews || []);
  const [historyRefreshTrigger, setHistoryRefreshTrigger] = useState(0);
  const [isHistoryDrawerOpen, setIsHistoryDrawerOpen] = useState(false);
  const [shouldRenderHistoryDrawer, setShouldRenderHistoryDrawer] = useState(() => activeWorkspaceView === "generate");
  const [historyFilterKey, setHistoryFilterKey] = useState<string>("all");
  const [historyImageLayer, setHistoryImageLayer] = useState<{ task: ImageLayerTask; imageUrl: string } | null>(null);
  const [errorMessage, setErrorMessage] = useState(initialActiveTab?.errorMessage || "");
  const [currentTaskIds, setCurrentTaskIds] = useState<string[]>(initialActiveTab?.currentTaskIds || []);
  const [currentPrompt, setCurrentPrompt] = useState<string>(initialActiveTab?.currentPrompt || "");
  const [regeneratingIndexes, setRegeneratingIndexes] = useState<number[]>([]);
  const [failedIndexes, setFailedIndexes] = useState<number[]>(initialActiveTab?.failedIndexes || []);
  /** 批处理子任务 uuid（与 generatedImages 下标对齐），供单张 retryTask */
  const [batchChildTaskIds, setBatchChildTaskIds] = useState<string[]>(initialActiveTab?.batchChildTaskIds || []);
  const [previewOutputCount, setPreviewOutputCount] = useState(initialActiveTab?.previewOutputCount || 1);
  const [manualStitchMode, setManualStitchMode] = useState(initialActiveTab?.manualStitchMode || false);
  // Keyed only by the backend's actual retry task ID. Owner/source/slot/image
  // context lives in the value and is revalidated before every UI write.
  const activeRetryTrackerRef = useRef<Map<string, ActiveRetryIdentity>>(new Map());
  const [retryTrackerTrigger, setRetryTrackerTrigger] = useState(0);
  // Persistent refine tracker. Each entry belongs to a workspace tab and only
  // projects a disposable preview; it never patches the source task.
  type ActiveRefineTracker = {
    refineTaskId: string;
    targetTaskId: string;
    batchParentId: string;
    batchIndex: number;
    ownerTabId: string;
    operationId: string;
    originalUrl: string;
    startedAt: number;
  };
  const activeRefineTrackerRef = useRef<Map<string, ActiveRefineTracker>>((() => {
    const trackers = new Map<string, ActiveRefineTracker>();
    for (const [ownerTabId, tab] of Object.entries(initialTabHistoryRef.current?.tabs || {})) {
      for (const preview of tab.refinePreviews || []) {
        if (preview.status === "completed" || !preview.refineTaskId || preview.refineTaskId.startsWith("__")) continue;
        trackers.set(createRefineTrackerKey(ownerTabId, preview.sourceTaskId, preview.imageIndex), {
          refineTaskId: preview.refineTaskId,
          targetTaskId: preview.sourceTaskId,
          batchParentId: tab.viewingTaskId || tab.batchParentTaskId || "",
          batchIndex: preview.imageIndex,
          ownerTabId,
          operationId: `restored:${preview.refineTaskId}`,
          originalUrl: preview.originalUrl,
          startedAt: preview.startedAt,
        });
      }
    }
    return trackers;
  })());
  const [refineTrackerTrigger, setRefineTrackerTrigger] = useState(0);
  const [refineLifecycleEpoch, setRefineLifecycleEpoch] = useState(0);
  // Background generation tracking — decoupled from display state so polling survives history switches
  const [generationActive, setGenerationActive] = useState(false);
  const [generationTrigger, setGenerationTrigger] = useState(0);
  const generationEpochRef = useRef(0);
  // Monotonic owner token for async submit responses. This is intentionally
  // separate from generationEpochRef, which also changes when foreground
  // polling starts after task IDs arrive.
  const generationRequestEpochRef = useRef(0);
  const generationPageMountedRef = useRef(true);
  // Submission ownership survives tab switches until the server returns task IDs.
  const pendingGenerationSubmissionsRef = useRef(new Map<string, number>());
  const generatingTaskIdsRef = useRef<string[]>([]);
  const generatingViewingTaskIdRef = useRef("");
  const generatingBatchChildIdsRef = useRef<string[]>([]);
  const generatingFeatureKeyRef = useRef<string | null>(null);
  // Accumulate completed images/progress regardless of current view, so results are never lost
  const generatingImagesRef = useRef<string[]>([]);
  const generatingProgressRef = useRef<number[]>([]);
  const generatingFailedRef = useRef<number[]>([]);
  const generatingModelImgRef = useRef("");
  const generatingProductImgRef = useRef("");
  const batchChildTaskIdsRef = useRef<string[]>([]);
  batchChildTaskIdsRef.current = batchChildTaskIds;
  const viewingTaskIdRef = useRef<string>("");
  const simulatedProgressRef = useRef(0);
  const progressTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const [taskProgresses, setTaskProgresses] = useState<number[]>(initialActiveTab?.taskProgresses || []);
  const clearTaskIdsTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // tabId -> cancel function. Tracks background pollers that keep updating
  // localStorage for tabs that were forked away while still generating.
  const detachedTabMonitorsRef = useRef<Map<string, () => void>>(new Map());
  // In-memory tombstones prevent a delayed render/pagehide callback from writing a
  // snapshot back after the user explicitly closed that workspace tab.
  const closedTabIdsRef = useRef<Set<string>>(new Set());

  const [moduleDisplayNames, setModuleDisplayNames] = useState<Record<string, string>>(LEGACY_MODULE_DISPLAY_NAMES);
  const [moduleConfigs, setModuleConfigs] = useState<ModuleNameConfig[]>(DEFAULT_MODULE_CONFIGS);
  const [moduleConfigsLoaded, setModuleConfigsLoaded] = useState(false);
  const getTabLabel = (tab: MainTab) => moduleDisplayNames[tab] || LEGACY_MODULE_DISPLAY_NAMES[tab] || tab;
  const getTabIconKey = (tab: MainTab) => moduleConfigs.find((item) => item.module_key === tab)?.icon_key || tab;
  const getTabIconSvg = (tab: MainTab) => moduleConfigs.find((item) => item.module_key === tab)?.icon_svg || '';

  // ====== 动态组件系统状态 ======
  const [workflowUIConfig, setWorkflowUIConfig] = useState<WorkflowUIConfig | null>(null);
  const [workflowUIConfigLoading, setWorkflowUIConfigLoading] = useState(false);
  const [workflowUIConfigError, setWorkflowUIConfigError] = useState(false);
  const [workflowConfigReloadKey, setWorkflowConfigReloadKey] = useState(0);
  // Coze template workflows render their empty-state onboarding in a portal.
  // Keep only the narrow module navigation visible while that state is active.
  const [isTemplateOnboarding, setIsTemplateOnboarding] = useState(false);
  // 详情页复刻三组件共享的选中索引与定位请求，集中管理避免各组件各自上报导致的更新死循环。
  const {
    activeIndex: detailReplicaActiveIndex,
    focusRequest: detailReplicaFocusRequest,
    setActiveIndex: setDetailReplicaActiveIndex,
    focusIndex: focusDetailReplicaIndex,
  } = useDetailReplicaActiveIndex();
  const [cozeParams, setCozeParams] = useState<Record<string, unknown>>(() => (
    stripDeprecatedDetailReplicaParams(initialActiveTab?.cozeParams || draft.cozeParams || {})
  ));
  const [cozeBatchCount, setCozeBatchCount] = useState(initialActiveTab?.cozeBatchCount || draft.cozeBatchCount || 1);
  // 每批次独立比例覆盖（null 表示"跟随全局"）。长度与 cozeBatchCount 同步。
  const [perBatchRatios, setPerBatchRatios] = useState<PerBatchRatio[]>(() =>
    normalizePerBatchRatios(initialActiveTab?.perBatchRatios || draft.perBatchRatios, initialActiveTab?.cozeBatchCount || draft.cozeBatchCount || 1),
  );
  const perBatchRatiosRef = useRef(perBatchRatios);
  perBatchRatiosRef.current = perBatchRatios;
  const [, setImageUploading] = useState(false);
  const [promptGenerating, setPromptGenerating] = useState(false);
  const promptGeneratingRef = useRef(false);
  const handlePromptGeneratingChange = useCallback((generating: boolean) => {
    promptGeneratingRef.current = generating;
    setPromptGenerating(generating);
  }, []);
  const cozeParamsRef = useRef(cozeParams);
  cozeParamsRef.current = cozeParams;

  // Bind observers to the DOM lifetime: Agent tabs unmount this scroll area
  // without changing the ordinary workflow's selected view or onboarding state.
  const attachGenerateScroll = useCallback((scroll: HTMLDivElement | null) => {
    generateScrollRef.current = scroll;
    if (!scroll || isTemplateOnboarding) {
      setGenerateScrollHasBottomOverflow(false);
      return undefined;
    }

    const frame = window.requestAnimationFrame(syncGenerateScrollFade);
    const resizeObserver = typeof ResizeObserver !== "undefined"
      ? new ResizeObserver(syncGenerateScrollFade)
      : null;
    const observedScrollChildren = new Set<Element>();
    resizeObserver?.observe(scroll);
    const observeScrollChildren = () => {
      if (!resizeObserver) return;
      const currentChildren = new Set(scroll.children);
      // Workflow changes replace these subtrees while the scroller stays mounted.
      // Release removed targets so the observer cannot retain their DOM/Fiber trees.
      for (const child of observedScrollChildren) {
        if (currentChildren.has(child)) continue;
        resizeObserver.unobserve(child);
        observedScrollChildren.delete(child);
      }
      for (const child of currentChildren) {
        if (observedScrollChildren.has(child)) continue;
        resizeObserver.observe(child);
        observedScrollChildren.add(child);
      }
    };
    observeScrollChildren();

    const mutationObserver = typeof MutationObserver !== "undefined"
      ? new MutationObserver(() => {
          observeScrollChildren();
          syncGenerateScrollFade();
        })
      : null;
    mutationObserver?.observe(scroll, {
      childList: true,
      subtree: true,
      characterData: true,
    });

    return () => {
      window.cancelAnimationFrame(frame);
      resizeObserver?.disconnect();
      observedScrollChildren.clear();
      mutationObserver?.disconnect();
      generateScrollRef.current = null;
    };
  }, [isTemplateOnboarding, syncGenerateScrollFade]);

  // ====== 批处理模式 ======
  const [batchProcessMode, setBatchProcessMode] = useState(initialActiveTab?.batchProcessMode || false);
  const [batchParamValues, setBatchParamValues] = useState<Record<string, string[]>>(initialActiveTab?.batchParamValues || {});
  const [batchConcurrency, setBatchConcurrency] = useState(initialActiveTab?.batchConcurrency || 5);
  const [batchParentTaskId, setBatchParentTaskId] = useState<string | null>(initialActiveTab?.batchParentTaskId || null);
  const [batchTaskStatus, setBatchTaskStatus] = useState<import("../lib/ecom-image-api").BatchTaskStatus | null>(initialActiveTab?.batchTaskStatus || null);
  const [batchUploading, setBatchUploading] = useState<Record<string, { total: number; done: number }>>({});
  const [batchDragOver, setBatchDragOver] = useState<string | null>(null);

  // ====== 灵感反推（llm_vision_reverse 执行模式）状态 ======
  // 反推任务的轮询/结果由独立逻辑维护，不复用图片任务的 generatedImages/PreviewArea。
  const [inspireResults, setInspireResults] = useState<InspireBatchResult[]>(initialActiveTab?.inspireResults || []);
  const inspireResultsRef = useRef<InspireBatchResult[]>(initialActiveTab?.inspireResults || []);
  const [multiTemplateLabels, setMultiTemplateLabels] = useState<Record<number, string>>(initialActiveTab?.multiTemplateLabels || {});
  const multiTemplateLabelsRef = useRef<Record<number, string>>(initialActiveTab?.multiTemplateLabels || {});
  const [multiTemplateWorkflows, setMultiTemplateWorkflows] = useState<Record<number, string[]>>(initialActiveTab?.multiTemplateWorkflows || {});
  const multiTemplateWorkflowsRef = useRef<Record<number, string[]>>(initialActiveTab?.multiTemplateWorkflows || {});
  const inspireTaskIdsRef = useRef<string[]>([]);
  const inspirePollTimerRef = useRef<EcomPollingLoopController | null>(null);

  const stopInspirePolling = useCallback(() => {
    if (inspirePollTimerRef.current) {
      inspirePollTimerRef.current.stop();
      inspirePollTimerRef.current = null;
    }
    inspireTaskIdsRef.current = [];
  }, []);

  const applyInspireResults = useCallback((updater: SetStateAction<InspireBatchResult[]>) => {
    const next = typeof updater === "function"
      ? (updater as (value: InspireBatchResult[]) => InspireBatchResult[])(inspireResultsRef.current)
      : updater;
    inspireResultsRef.current = next;
    setInspireResults(next);
  }, []);

  const applyMultiTemplateLabels = useCallback((labels: Record<number, string>) => {
    multiTemplateLabelsRef.current = labels;
    setMultiTemplateLabels(labels);
  }, []);

  const applyMultiTemplateWorkflows = useCallback((workflows: Record<number, string[]>) => {
    multiTemplateWorkflowsRef.current = workflows;
    setMultiTemplateWorkflows(workflows);
  }, []);

  useEffect(() => {
    return () => stopInspirePolling();
  }, [stopInspirePolling]);


  // ====== 姿势预设状态 ======
  const [showPoseModal, setShowPoseModal] = useState(false);
  const [poseCategories, setPoseCategories] = useState<PoseCategoryOption[]>([]);
  const [poseAssets, setPoseAssets] = useState<PoseAsset[]>([]);
  const [mergedPoses, setMergedPoses] = useState<MergedPoseItem[]>([]);
  const [activePoseCategoryId, setActivePoseCategoryId] = useState(POSE_ALL_CATEGORY_ID);
  const [poseSaving, setPoseSaving] = useState(false);

  const poseAssetById = useMemo(() => {
    const map = new Map<number, PoseAsset>();
    poseAssets.forEach((asset) => {
      map.set(asset.id, asset);
    });
    return map;
  }, [poseAssets]);

  const getPoseCategoryId = useCallback((pose: MergedPoseItem) => {
    const sourceAssetId = pose.source_asset_id ?? pose.id;
    return poseAssetById.get(sourceAssetId)?.category_id ?? null;
  }, [poseAssetById]);

  const poseCategoryCounts = useMemo(() => {
    const counts = new Map<number, number>();
    mergedPoses.forEach((pose) => {
      const categoryId = getPoseCategoryId(pose);
      if (categoryId === null) return;
      counts.set(categoryId, (counts.get(categoryId) || 0) + 1);
    });
    return counts;
  }, [getPoseCategoryId, mergedPoses]);

  const poseCategoryOptions = useMemo(() => {
    const sortedCategories = [...poseCategories]
      .filter((category) => category.id !== POSE_ALL_CATEGORY_ID)
      .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0) || a.id - b.id);

    const seenCategoryIds = new Set<number>();
    const options: PoseCategoryOption[] = sortedCategories.map((category) => {
      seenCategoryIds.add(category.id);
      return {
        ...category,
        count: poseCategoryCounts.get(category.id) || 0,
      };
    });

    poseAssets.forEach((asset) => {
      if (!asset.category_id || seenCategoryIds.has(asset.category_id)) return;
      seenCategoryIds.add(asset.category_id);
      options.push({
        id: asset.category_id,
        name: `分类 ${asset.category_id}`,
        sort_order: asset.category_id,
        count: poseCategoryCounts.get(asset.category_id) || 0,
      });
    });

    return [
      {
        id: POSE_ALL_CATEGORY_ID,
        name: "全部",
        sort_order: 0,
        count: mergedPoses.length,
      },
      ...options,
    ];
  }, [mergedPoses.length, poseAssets, poseCategories, poseCategoryCounts]);

  const filteredMergedPoses = useMemo(() => {
    if (activePoseCategoryId === POSE_ALL_CATEGORY_ID) return mergedPoses;
    return mergedPoses.filter((pose) => getPoseCategoryId(pose) === activePoseCategoryId);
  }, [activePoseCategoryId, getPoseCategoryId, mergedPoses]);

  const poseDisplayIndexById = useMemo(() => {
    const map = new Map<number, number>();
    mergedPoses.forEach((pose, index) => {
      map.set(pose.id, index);
    });
    return map;
  }, [mergedPoses]);

  useEffect(() => {
    if (
      activePoseCategoryId !== POSE_ALL_CATEGORY_ID &&
      !poseCategoryOptions.some((category) => category.id === activePoseCategoryId)
    ) {
      setActivePoseCategoryId(POSE_ALL_CATEGORY_ID);
    }
  }, [activePoseCategoryId, poseCategoryOptions]);

  const handlePoseReorderNew = useCallback((newPoses: MergedPoseItem[]) => {
    setMergedPoses((prev) => {
      if (activePoseCategoryId === POSE_ALL_CATEGORY_ID) return newPoses;

      const reorderedIds = new Set(newPoses.map((pose) => pose.id));
      const reorderedPoses = [...newPoses];
      return prev.map((pose) => (
        reorderedIds.has(pose.id) ? reorderedPoses.shift() || pose : pose
      ));
    });
  }, [activePoseCategoryId]);

  // ====== 豌豆余额不足弹窗 ======
  const [showBalanceModal, setShowBalanceModal] = useState(false);

  // ====== 重试确认弹窗 ======
  const [retryModalOpen, setRetryModalOpen] = useState(false);
  const [retryModalTaskIds, setRetryModalTaskIds] = useState<string[]>([]);
  const [retryModalSingleIndex, setRetryModalSingleIndex] = useState<number | null>(null);

  // ====== 发布到灵感社区 ======
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [uploadPayload, setUploadPayload] = useState<InspirationUploadPayload | null>(null);
  const uploadSessionRef = useRef(0);
  const uploadClosingSessionRef = useRef<number | null>(null);

  const openInspirationUpload = (imageUrl: string) => {
    const session = ++uploadSessionRef.current;
    uploadClosingSessionRef.current = null;
    setUploadPayload({
      session,
      imageUrl,
      prompt: formatPromptFromParams(currentPrompt),
    });
    setUploadModalOpen(true);
  };

  const closeInspirationUpload = () => {
    uploadClosingSessionRef.current = uploadSessionRef.current;
    setUploadModalOpen(false);
  };

  const finishInspirationUploadClose = () => {
    const closingSession = uploadClosingSessionRef.current;
    uploadClosingSessionRef.current = null;
    if (closingSession === null || closingSession !== uploadSessionRef.current) return;
    setUploadPayload((current) => current?.session === closingSession ? null : current);
  };

  // ====== 图片预览 ======
  const [previewImageUrl, setPreviewImageUrl] = useState<string | null>(null);
  const [previewScale, setPreviewScale] = useState(1);
  const [previewPos, setPreviewPos] = useState({ x: 0, y: 0 });
  const [previewDragging, setPreviewDragging] = useState(false);
  const [previewDragStart, setPreviewDragStart] = useState({ x: 0, y: 0 });
  const [previewDetails, setPreviewDetails] = useState<PreviewDetails | null>(null);
  const previewFavoriteTaskUUID = /^[0-9a-f]{8}(?:-[0-9a-f]{4}){3}-[0-9a-f]{12}$/i.test(previewDetails?.favoriteTaskId || "")
    ? previewDetails!.favoriteTaskId : "";
  const { isFavorited: previewIsFavorited, saving: previewFavoriteSaving, checking: previewFavoriteChecking, failed: previewFavoriteFailed, toggle: togglePreviewFavorite } = useHistoryFavorite({
    taskUUID: previewFavoriteTaskUUID,
    mediaURL: previewImageUrl || "",
    imageIndex: previewDetails?.favoriteImageIndex || 0,
    createPost: (meta) => ({
      title: `${activeMainTab} - ${activeSubOption}`,
      description: "来自电商生图",
      media_url: previewImageUrl || "",
      media_type: "image",
      category: "其他",
      tags: [],
      prompt: previewDetails?.prompt || currentPrompt || undefined,
      source_type: "generated",
      source_task_uuid: previewFavoriteTaskUUID,
      source_image_index: previewDetails?.favoriteImageIndex || 0,
      visibility: "private",
      width: meta.width,
      height: meta.height,
    }),
  });
  const [previewFallbackFavorite, setPreviewFallbackFavorite] = useState<{ url: string; uuid: string; favorited: boolean } | null>(null);
  const previewFavoriteActive = previewFavoriteTaskUUID ? previewIsFavorited : previewFallbackFavorite?.url === previewImageUrl && previewFallbackFavorite.favorited;
  const [previewImageDimensions, setPreviewImageDimensions] = useState<{ w: number; h: number } | null>(null);
  const [previewImageFileSize, setPreviewImageFileSize] = useState<string>("");
  const [uploadImageHoverPreview, setUploadImageHoverPreview] = useState<UploadImageHoverPreview | null>(null);
  const previewContainerRef = useRef<HTMLDivElement>(null);
  // 图片详情弹层会话计数：每次 openPreview 递增，用于丢弃旧弹层的慢详情响应
  const previewSessionRef = useRef(0);
  const previewContextRef = useRef<PreviewContext>({
    generatedImage: null,
    generatedImages: [],
    currentTaskIds: [],
    batchChildTaskIds: [],
    viewingTaskId: "",
    historyFeatureKey: null,
    activeMainTab: ECOM_IMAGE_MODULE_KEY,
    activeSubOption: "模特文/图生图",
    currentPrompt: "",
    previewOutputCount: 1,
    featureKey: "model_gen",
    featureName: "模特文/图生图",
    refinePreviews: [],
  });

  const openPreview = useCallback((url: string, requestedIndex?: number) => {
    const ctx = previewContextRef.current;
    const sourceImages = ctx.generatedImages.length > 0
      ? ctx.generatedImages
      : ctx.generatedImage
        ? [ctx.generatedImage]
        : [];
    const projectedImages = applyEcomRefinePreviews(
      ctx.generatedImage,
      ctx.generatedImages,
      ctx.refinePreviews,
    );
    const visibleImages = projectedImages.generatedImages.length > 0
      ? projectedImages.generatedImages
      : projectedImages.generatedImage
        ? [projectedImages.generatedImage]
        : [];
    const matchedVisibleIndex = visibleImages.findIndex((image) => image === url);
    const sourceUrlIndex = sourceImages.findIndex((image) => image === url);
    const imageIndex = Number.isInteger(requestedIndex) && (requestedIndex ?? -1) >= 0
      ? requestedIndex as number
      : matchedVisibleIndex >= 0
        ? matchedVisibleIndex
        : sourceUrlIndex >= 0
          ? sourceUrlIndex
          : 0;
    const temporaryProjection = ctx.refinePreviews.find((preview) => (
      preview.status === "completed"
      && preview.imageIndex === imageIndex
      && Boolean(preview.outputUrl)
    ));
    const visibleUrl = temporaryProjection?.outputUrl || visibleImages[imageIndex] || url;
    const slotTaskId = ctx.batchChildTaskIds[imageIndex] || ctx.currentTaskIds[imageIndex];
    const taskId =
      temporaryProjection?.sourceTaskId ||
      slotTaskId ||
      (visibleImages.length <= 1 ? ctx.viewingTaskId : "") ||
      ctx.viewingTaskId ||
      "";
    // Gallery position is not the output index of a single-image child task.
    // Repeated IDs represent several outputs of the same task in slot order.
    const taskImageIndex = slotTaskId
      ? Array.from({ length: imageIndex }, (_, index) => ctx.batchChildTaskIds[index] || ctx.currentTaskIds[index])
        .filter((id) => id === slotTaskId).length
      : imageIndex;
    const promptText = formatPromptFromParams(ctx.currentPrompt) || ctx.currentPrompt;

    setPreviewImageUrl(visibleUrl);
    setPreviewScale(1);
    setPreviewPos({ x: 0, y: 0 });
    const previewSession = ++previewSessionRef.current;
    setPreviewDetails({
      url: visibleUrl,
      taskId,
      favoriteTaskId: temporaryProjection?.refineTaskId || taskId,
      favoriteImageIndex: temporaryProjection ? 0 : taskImageIndex,
      featureKey: ctx.historyFeatureKey || ctx.featureKey,
      featureName: ctx.featureName,
      prompt: promptText,
      createdAt: "",
      completedAt: "",
      pointsCost: 0,
      imageCount: visibleImages.length || ctx.previewOutputCount || 1,
      imageIndex,
    });
    targetScaleRef.current = 1;
    if (scaleAnimRef.current) {
      cancelAnimationFrame(scaleAnimRef.current);
      scaleAnimRef.current = null;
    }

    if (taskId) {
      ecomImageApi.getTask(taskId).then((res) => {
        // 弹层会话保护：用户已切换到其它图片/关闭弹层时，旧详情响应必须整体丢弃，
        // 防止先前图片的慢响应覆盖后来打开的图片
        if (previewSessionRef.current !== previewSession) return;
        const data = res.data;
        const task = data?.task;
        const params = (data?.params || task?.params || null) as Record<string, unknown> | null;
        const detailPrompt =
          extractPromptFromParams(params) ||
          (typeof data?.prompt === "string" ? extractPromptValue(data.prompt) : "") ||
          (typeof task?.prompt === "string" ? extractPromptValue(task.prompt) : "");
        const outputCount = Array.isArray(data?.output_images) && data.output_images.length > 0
          ? data.output_images.length
          : undefined;
        // 普通结果仍用源任务 output_images 自愈缩略图；AI 重绘临时投影只借用
        // 源任务的详情元数据，绝不能被源任务原图覆盖。
        const canonicalUrl = resolveEcomPreviewDetailImageUrl(
          visibleUrl,
          data?.output_images,
          imageIndex,
          Boolean(temporaryProjection),
        );
        if (canonicalUrl !== visibleUrl) setPreviewImageUrl(canonicalUrl);

        setPreviewDetails((prev) => {
          if (
            !prev ||
            prev.url !== visibleUrl ||
            prev.taskId !== taskId ||
            prev.imageIndex !== imageIndex
          ) return prev;
          return {
            ...prev,
            url: canonicalUrl || prev.url,
            featureKey: data?.feature_key || task?.feature_key || prev.featureKey,
            featureName: data?.feature_key || task?.feature_key
              ? previewContextRef.current.featureName
              : prev.featureName,
            prompt: detailPrompt || prev.prompt,
            createdAt: task?.created_at || prev.createdAt,
            completedAt: task?.completed_at || prev.completedAt,
            pointsCost: typeof task?.points_cost === "number" ? task.points_cost : prev.pointsCost,
            imageCount: outputCount || prev.imageCount,
          };
        });
      }).catch(() => {
        // Keep the locally inferred preview details if task detail lookup fails.
      });
    }
  }, []);

  const targetScaleRef = useRef(1);
  const scaleAnimRef = useRef<number | null>(null);

  const handlePreviewWheel = useCallback((e: React.WheelEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const clampedDelta = Math.max(-30, Math.min(30, e.deltaY));
    const factor = 1 - clampedDelta * 0.001;
    targetScaleRef.current = Math.min(Math.max(targetScaleRef.current * factor, 0.1), 10);

    if (scaleAnimRef.current) return;

    const animate = () => {
      setPreviewScale(prev => {
        const diff = targetScaleRef.current - prev;
        if (Math.abs(diff) < 0.001) {
          scaleAnimRef.current = null;
          return targetScaleRef.current;
        }
        scaleAnimRef.current = requestAnimationFrame(animate);
        return prev + diff * 0.25;
      });
    };
    scaleAnimRef.current = requestAnimationFrame(animate);
  }, []);

  const handlePreviewMouseDown = useCallback((e: React.MouseEvent) => {
    if (e.button !== 0) return;
    e.preventDefault();
    setPreviewDragging(true);
    setPreviewDragStart({ x: e.clientX - previewPos.x, y: e.clientY - previewPos.y });
  }, [previewPos]);

  const handlePreviewMouseMove = useCallback((e: React.MouseEvent) => {
    if (!previewDragging) return;
    setPreviewPos({ x: e.clientX - previewDragStart.x, y: e.clientY - previewDragStart.y });
  }, [previewDragging, previewDragStart]);

  const handlePreviewMouseUp = useCallback(() => setPreviewDragging(false), []);

  const resetPreviewView = useCallback(() => {
    setPreviewScale(1);
    setPreviewPos({ x: 0, y: 0 });
  }, []);

  useEffect(() => {
    if (!previewImageUrl) {
      setPreviewImageDimensions(null);
      setPreviewImageFileSize("");
      setPreviewDetails(null);
      return;
    }

    let cancelled = false;
    setPreviewImageDimensions(null);
    setPreviewImageFileSize("");

    const img = new Image();
    img.onload = () => {
      if (!cancelled) {
        setPreviewImageDimensions({ w: img.naturalWidth, h: img.naturalHeight });
      }
    };
    img.src = previewImageUrl;

    fetch(previewImageUrl, { method: "HEAD" })
      .then((res) => {
        if (cancelled) return;
        const len = res.headers.get("content-length");
        const bytes = len ? Number.parseInt(len, 10) : 0;
        const size = formatPreviewFileSize(bytes);
        if (size) setPreviewImageFileSize(size);
      })
      .catch(() => {
        fetch(previewImageUrl)
          .then((res) => res.blob())
          .then((blob) => {
            if (!cancelled) setPreviewImageFileSize(formatPreviewFileSize(blob.size));
          })
          .catch(() => { });
      });

    return () => {
      cancelled = true;
    };
  }, [previewImageUrl]);

  // ====== 推荐下一步 ======
  const [nextStepTargets, setNextStepTargets] = useState<NextStepTarget[]>([]);
  const [prefillData, setPrefillData] = useState<Record<string, unknown> | null>(null);
  const [inspirationPrefill, setInspirationPrefill] = useState<{ workflowKey: string; prompt: string; paramKey: string; scope: string | undefined } | null>(null);

  // ====== 历史记录回显时的工作流 feature_key（用于匹配 stitch 模式） ======
  const [historyFeatureKey, setHistoryFeatureKey] = useState<string | null>(initialActiveTab?.historyFeatureKey || null);
  const [viewingTaskId, setViewingTaskId] = useState<string>(initialViewingTaskId);
  viewingTaskIdRef.current = viewingTaskId;

  // ====== 单图编辑(refine)活跃状态 ======
  const [isRefining, setIsRefining] = useState(false);
  const [historyRefineEditor, setHistoryRefineEditor] = useState<{
    ownerTabId: string;
    imageUrl: string;
    taskId: string;
    imageIndex: number;
    productImage?: string;
  } | null>(null);

  // ====== 生成过程中查看历史记录覆盖层 ======
  const [historyViewOverlay, setHistoryViewOverlay] = useState<{
    images: string[];
    modelImage: string;
    productImage: string;
    featureKey: string | null;
    nextStepTargets: NextStepTarget[];
  } | null>(null);

  // Legacy: auto-clear overlay if set (no longer used for blocking)
  useEffect(() => {
    if (historyViewOverlay) setHistoryViewOverlay(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [historyViewOverlay]);

  // ====== 参考图设置状态 ======
  const [showRefSettingModal, setShowRefSettingModal] = useState(false);
  const [referenceResult, setReferenceResult] = useState<ReferenceConfirmResult | null>(null);
  const [refImageUrl, setRefImageUrl] = useState(initialActiveTab?.refImageUrl || draft.refImageUrl || "");


  const handleOpenRefSetting = () => {
    setShowRefSettingModal(true);
  };

  const handleRefSettingConfirm = (result: ReferenceConfirmResult | null) => {
    setReferenceResult(result);
  };

  const activeTabId = useMemo(
    () => createEcomTabId(activeMainTab, activeSubOption, activeTabInstance),
    [activeMainTab, activeSubOption, activeTabInstance],
  );
  const activeAgentHostTabId = activeWorkspaceView === "generate"
    ? createEcomCozeHostTabId(activeTabId)
    : null;
  const activeTabIdRef = useRef(activeTabId);
  activeTabIdRef.current = activeTabId;
  const getReversePromptOwnerScope = useCallback(
    (tabId: string) => ecomReversePromptHistoryBaseScope
      ? createCozeReversePromptHistoryScope(ecomReversePromptHistoryBaseScope, tabId)
      : `${ecomStorageScope}:reverse:${tabId}`,
    [ecomReversePromptHistoryBaseScope, ecomStorageScope],
  );
  const activateReversePromptTab = useCallback((tabId: string) => {
    activateReversePromptScope(getReversePromptOwnerScope(tabId));
  }, [getReversePromptOwnerScope]);
  const reversePromptClaimsRef = useRef<Map<string, ReversePromptOperationClaim>>(new Map());
  const activeRetryWorkspaceContextRef = useRef<RetryWorkspaceContext>({
    tabId: activeTabId,
    viewingTaskId,
    currentTaskIds,
    batchChildTaskIds,
    generatedImage,
    generatedImages,
  });
  activeRetryWorkspaceContextRef.current = {
    tabId: activeTabId,
    viewingTaskId,
    currentTaskIds,
    batchChildTaskIds,
    generatedImage,
    generatedImages,
  };
  // Async generation responses must only commit to the tab/epoch that started
  // them. A slow request can finish after the user switched or closed a tab;
  // without this ownership check its result would overwrite the new tab.
  const beginGenerationRequest = useCallback(() => {
    generationRequestEpochRef.current += 1;
    return {
      requestTabId: activeTabIdRef.current,
      requestEpoch: generationRequestEpochRef.current,
    };
  }, []);
  const invalidateGenerationRequests = useCallback(() => {
    generationRequestEpochRef.current += 1;
  }, []);
  const isGenerationRequestCurrent = useCallback((requestTabId: string, requestEpoch: number) => (
    generationPageMountedRef.current && isEcomGenerationRequestOwnerCurrent(
      { tabId: requestTabId, epoch: requestEpoch },
      activeTabIdRef.current,
      generationRequestEpochRef.current,
      closedTabIdsRef.current,
    )
  ), []);

  const activeTabTitle = useMemo(
    () => (activeTabInstance > 1 ? `${activeSubOption} #${activeTabInstance}` : activeSubOption),
    [activeSubOption, activeTabInstance],
  );

  const handleCozeParamChange = useCallback(
    (cozeParamKey: string, value: unknown, ownerTabId?: string) => {
      const targetTabId = ownerTabId ?? activeTabIdRef.current;
      // 异步上传等延迟回调可能在用户切换标签后才触发。
      // 始终把变更写入它所归属的 tab 历史，避免污染当前 tab 状态。
      try {
        const currentHistory = loadEcomTabHistory(ecomStorageScope);
        if (currentHistory) {
          const existingTab = currentHistory.tabs[targetTabId];
          if (existingTab) {
            const updatedTab: EcomWorkspaceTab = {
              ...existingTab,
              cozeParams: { ...(existingTab.cozeParams || {}), [cozeParamKey]: value },
              updatedAt: Date.now(),
            };
            saveEcomTabHistory({
              ...currentHistory,
              tabs: { ...currentHistory.tabs, [targetTabId]: updatedTab },
              updatedAt: Date.now(),
            }, ecomStorageScope);
          }
        }
      } catch {
        // 持久化失败不影响内存态。
      }
      if (targetTabId === activeTabIdRef.current) {
        setCozeParams((prev) => ({ ...prev, [cozeParamKey]: value }));
      }
    },
    [ecomStorageScope],
  );

  // 把 onParamChange 与发起调用时的 tabId 绑定，确保异步上传完成后写回正确的 tab。
  const ownedCozeParamChange = useCallback(
    (cozeParamKey: string, value: unknown) => {
      handleCozeParamChange(cozeParamKey, value, activeTabId);
    },
    [handleCozeParamChange, activeTabId],
  );

  const buildCurrentWorkspaceTab = useCallback((tabId: string = activeTabId): EcomWorkspaceTab => {
    const existing = loadEcomTabHistory(ecomStorageScope)?.tabs[tabId];
    const now = Date.now();
    return {
      tabId,
      recoveryGeneration: existing?.recoveryGeneration,
      generationInputs: existing?.generationInputs,
      mainTab: activeMainTab,
      subOption: activeSubOption,
      title: tabId === activeTabId ? activeTabTitle : (existing?.title || activeSubOption),
      createdAt: existing?.createdAt || now,
      updatedAt: now,
      cozeParams: stripDeprecatedDetailReplicaParams(stripBlobUrls(cozeParams)),
      cozeBatchCount,
      status,
      progress,
      generatedImage,
      resultModelImage,
      resultProductImage,
      generatedImages,
      errorMessage,
      currentTaskIds,
      viewingTaskId,
      generationStartedAt,
      currentPrompt,
      failedIndexes,
      batchChildTaskIds,
      previewOutputCount,
      taskProgresses,
      batchProcessMode,
      batchParamValues,
      batchConcurrency,
      batchParentTaskId,
      batchTaskStatus,
      manualStitchMode,
      historyFeatureKey,
      refImageUrl,
      perBatchRatios,
      inspireResults: inspireResultsRef.current as EcomInspireTabResult[],
      multiTemplateLabels: multiTemplateLabelsRef.current,
      multiTemplateWorkflows: multiTemplateWorkflowsRef.current,
      refinePreviews: reconcileEcomRefinePreviewsForPersistence(
        existing?.refinePreviews,
        refinePreviews,
      ),
    };
  }, [
    activeMainTab,
    activeSubOption,
    activeTabId,
    activeTabTitle,
    batchChildTaskIds,
    batchConcurrency,
    batchParamValues,
    batchParentTaskId,
    batchProcessMode,
    batchTaskStatus,
    cozeBatchCount,
    cozeParams,
    currentPrompt,
    currentTaskIds,
    ecomStorageScope,
    errorMessage,
    failedIndexes,
    generationStartedAt,
    generatedImage,
    generatedImages,
    historyFeatureKey,
    manualStitchMode,
    perBatchRatios,
    previewOutputCount,
    progress,
    refImageUrl,
    refinePreviews,
    resultModelImage,
    resultProductImage,
    status,
    taskProgresses,
    viewingTaskId,
  ]);

  const cancelScheduledWorkspacePersistence = useCallback(() => {
    tabSwitchEpochRef.current += 1;
    if (persistTimerRef.current) {
      clearTimeout(persistTimerRef.current);
      persistTimerRef.current = null;
    }
  }, []);

  const persistCurrentWorkspaceTab = useCallback((
    overrides?: Partial<EcomWorkspaceTabHistory>,
    workspaceOverrides?: Partial<EcomWorkspaceTab>,
  ) => {
    if (agentSelectionRef.current) return;
    const tabId = activeTabId;
    if (closedTabIdsRef.current.has(tabId)) return;
    const currentHistory = loadEcomTabHistory(ecomStorageScope);
    const historyTabs = currentHistory?.tabs || {};
    const historyOpenTabIds = currentHistory?.openTabIds || [];
    const shouldKeepCurrentTabOpen = historyOpenTabIds.includes(tabId) || openedSubOptions.includes(tabId);
    if (!shouldKeepCurrentTabOpen) return;
    const openTabIds = Array.from(new Set([
      ...historyOpenTabIds,
      tabId,
    ])).filter((id) => id !== HISTORY_TAB_LABEL && id.includes("::"));

    saveEcomTabHistory({
      version: 1,
      activeTabId: shouldKeepCurrentTabOpen ? tabId : currentHistory?.activeTabId || null,
      activeWorkspaceView,
      openTabIds,
      tabs: {
        ...historyTabs,
        [tabId]: {
          ...buildCurrentWorkspaceTab(tabId),
          updatedAt: Date.now(),
          ...workspaceOverrides,
        },
      },
      updatedAt: Date.now(),
      ...overrides,
    }, ecomStorageScope);
  }, [activeTabId, activeWorkspaceView, buildCurrentWorkspaceTab, ecomStorageScope, openedSubOptions]);

  const commitReversePromptOwnerPatch = useCallback((
    operation: ReversePromptOperationClaim | null,
    ownerTabId: string,
    patch: Partial<EcomWorkspaceTab>,
  ): boolean => {
    if (
      !isReversePromptOperationCurrent(operation) ||
      operation.ownerScope !== getReversePromptOwnerScope(ownerTabId) ||
      !ownerTabId ||
      ownerTabId === HISTORY_TAB_LABEL ||
      closedTabIdsRef.current.has(ownerTabId)
    ) {
      return false;
    }
    const currentHistory = loadEcomTabHistory(ecomStorageScope);
    const existing = currentHistory?.tabs[ownerTabId];
    if (!existing) return false;
    const nextInspireResults = (patch.inspireResults || existing.inspireResults || []) as EcomInspireTabResult[];
    const shouldCompleteInspire =
      patch.status === "completed" &&
      nextInspireResults.length > 0 &&
      nextInspireResults.every((result) =>
        result.status !== "pending" && result.status !== "processing"
      );
    const nextTab: EcomWorkspaceTab = {
      ...existing,
      ...patch,
      recoveryGeneration: patch.generationStartedAt && patch.generationStartedAt !== existing.generationStartedAt ? createClientUUID() : existing.recoveryGeneration,
      status: shouldCompleteInspire ? "completed" : patch.status ?? existing.status,
      inspireResults: nextInspireResults,
      multiTemplateLabels: patch.multiTemplateLabels || existing.multiTemplateLabels || {},
      multiTemplateWorkflows: patch.multiTemplateWorkflows || existing.multiTemplateWorkflows || {},
      updatedAt: Date.now(),
    };
    saveEcomTabHistory({
      version: 1,
      activeTabId: currentHistory?.activeTabId || ownerTabId,
      activeWorkspaceView: currentHistory?.activeWorkspaceView || activeWorkspaceView,
      openTabIds: Array.from(new Set([...(currentHistory?.openTabIds || []), ownerTabId])),
      tabs: {
        ...(currentHistory?.tabs || {}),
        [ownerTabId]: nextTab,
      },
      updatedAt: Date.now(),
    }, ecomStorageScope);
    if (patch.status) {
      setTabStatusMap((prev) => prev[ownerTabId] === nextTab.status
        ? prev
        : { ...prev, [ownerTabId]: nextTab.status });
    }
    return true;
  }, [activeWorkspaceView, ecomStorageScope, getReversePromptOwnerScope]);

  const readReversePromptOwnerResults = useCallback((
    ownerTabId: string,
    fallback: InspireBatchResult[] = [],
  ): InspireBatchResult[] => {
    const persistedResults = loadEcomTabHistory(ecomStorageScope)?.tabs[ownerTabId]?.inspireResults;
    return Array.isArray(persistedResults)
      ? persistedResults as InspireBatchResult[]
      : fallback;
  }, [ecomStorageScope]);

  const isReversePromptOwnerVisible = useCallback((
    operation: ReversePromptOperationClaim | null,
    ownerTabId: string,
  ) => (
    isReversePromptOperationCurrent(operation) &&
    activeTabIdRef.current === ownerTabId &&
    !closedTabIdsRef.current.has(ownerTabId)
  ), []);

  const applyWorkspaceTab = useCallback((incomingTab: EcomWorkspaceTab, replaceGeneration = false) => {
    invalidateGenerationRequests();
    // A retry can replace one slot while an old batch poll is still awaiting
    // siblings. Revoke its accumulated results synchronously, before React's
    // effect cleanup; the strict retry path intentionally skips ordinary polling.
    generationEpochRef.current += 1;
    generatingTaskIdsRef.current = [];
    setGenerationActive(false);
    if (replaceGeneration) {
      incomingTab = { ...incomingTab, recoveryGeneration: retireDurableOwner(ecomStorageScope, incomingTab.tabId) };
      // Restoring history replaces the task context even when the tab ID is reused.
      // Ordinary tab switches keep pending submissions owned by their original tab.
      pendingGenerationSubmissionsRef.current.delete(incomingTab.tabId);
      detachedTabMonitorsRef.current.get(incomingTab.tabId)?.();
      detachedTabMonitorsRef.current.delete(incomingTab.tabId);
    }
    const tab = normalizeStaleGeneratingTab(incomingTab, pendingGenerationSubmissionsRef.current.has(incomingTab.tabId) || pendingDurableTab(ecomStorageScope, incomingTab.tabId)) || incomingTab;
    isApplyingTabHistoryRef.current = true;
    isRestoringRef.current = true;
    const parsed = parseEcomTabId(tab.tabId);
    setActiveMainTab(parsed?.mainTab ?? tab.mainTab);
    ensureMainTabExpanded(parsed?.mainTab ?? tab.mainTab);
    setActiveSubOption(parsed?.subOption ?? tab.subOption);
    setActiveTabInstance(parsed?.instance ?? 1);
    setCozeParams(stripDeprecatedDetailReplicaParams(tab.cozeParams || {}));
    setCozeBatchCount(tab.cozeBatchCount || 1);
    setPerBatchRatios(normalizePerBatchRatios(tab.perBatchRatios, tab.cozeBatchCount || 1));
    setStatus(tab.status || "empty");
    setProgress(tab.progress || 0);
    setGeneratedImage(tab.generatedImage || null);
    setResultModelImage(tab.resultModelImage || "");
    setResultProductImage(tab.resultProductImage || "");
    setGeneratedImages(tab.generatedImages || []);
    setRefinePreviews(tab.refinePreviews || []);
    setErrorMessage(tab.errorMessage || "");
    setCurrentTaskIds(tab.currentTaskIds || []);
    const nextViewingTaskId = tab.viewingTaskId || tab.batchParentTaskId || tab.currentTaskIds?.[0] || "";
    setViewingTaskId(nextViewingTaskId);
    viewingTaskIdRef.current = nextViewingTaskId;
    setGenerationStartedAt(
      tab.generationStartedAt ??
      (tab.status === "generating" ? tab.createdAt || tab.updatedAt || Date.now() : null),
    );
    setCurrentPrompt(tab.currentPrompt || "");
    setFailedIndexes(tab.failedIndexes || []);
    setBatchChildTaskIds(tab.batchChildTaskIds || []);
    setPreviewOutputCount(tab.previewOutputCount || 1);
    setTaskProgresses(tab.taskProgresses || []);
    setBatchProcessMode(tab.batchProcessMode || false);
    setBatchParamValues(tab.batchParamValues || {});
    setBatchConcurrency(tab.batchConcurrency || 5);
    setBatchParentTaskId(tab.batchParentTaskId || null);
    setBatchTaskStatus(tab.batchTaskStatus || null);
    setManualStitchMode(tab.manualStitchMode || false);
    setHistoryFeatureKey(tab.historyFeatureKey || null);
    setRefImageUrl(tab.refImageUrl || "");
    applyInspireResults(tab.inspireResults || []);
    applyMultiTemplateLabels(tab.multiTemplateLabels || {});
    applyMultiTemplateWorkflows(tab.multiTemplateWorkflows || {});
    inspireTaskIdsRef.current = (tab.inspireResults || [])
      .map((result) => result.taskId)
      .filter((taskId) => taskId && !taskId.startsWith("pending-"));
    setNextStepTargets([]);
    setRegeneratingIndexes([]);
    setHistoryViewOverlay(null);
    setTimeout(() => {
      isApplyingTabHistoryRef.current = false;
    }, 50);
  }, [applyInspireResults, applyMultiTemplateLabels, applyMultiTemplateWorkflows, ecomStorageScope, ensureMainTabExpanded, invalidateGenerationRequests]);

  // ====== 姿势预设处理 ======
  const fetchPoseAssets = async () => {
    try {
      const [categoryRes, sysRes, mergedRes] = await Promise.all([
        ecomImageApi.getPoseCategories().catch((err) => {
          console.warn('加载姿势分类失败，使用资产分类:', err?.message || err);
          return { data: [] };
        }),
        ecomImageApi.getPoseAssets(),
        ecomImageApi.getMergedPosePresets().catch((err) => {
          console.warn('加载用户姿势预设失败，使用系统默认:', err?.message || err);
          return { data: null };
        }),
      ]);
      const categoryItems = Array.isArray(categoryRes.data)
        ? categoryRes.data
        : [];
      const sysItems = Array.isArray(sysRes.data) ? sysRes.data : [];
      const sortedCategoryItems = [...categoryItems].sort(
        (a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0) || a.id - b.id
      );
      const sortedSysItems = [...sysItems].sort((a: PoseAsset, b: PoseAsset) => a.sort_order - b.sort_order);
      setPoseCategories(sortedCategoryItems);
      setPoseAssets(sortedSysItems);

      if (mergedRes.data && Array.isArray(mergedRes.data) && mergedRes.data.length > 0) {
        setMergedPoses(mergedRes.data);
      } else {
        setMergedPoses(sortedSysItems.map(a => ({
          id: a.id, source_asset_id: a.id, name: a.name,
          image_url: a.image_url, thumbnail_url: a.thumbnail_url,
          prompt: '', is_enabled: true, sort_order: a.sort_order,
          is_custom: false, override_id: 0,
        })));
      }
    } catch (err) {
      console.error('Failed to fetch pose assets:', err);
    }
  };

  const handlePoseImageReplace = async (file: File, poseId: number) => {
    try {
      const uploadRes = await ecomImageApi.uploadMedia(file);
      if (!uploadRes.data?.file_url) {
        feedback.error({ code: "E5003", fallback: "upload" });
        return;
      }
      const newImageUrl = uploadRes.data.file_url;
      const thumbnailUrl = uploadRes.data.thumbnail_url || newImageUrl;

      setMergedPoses(prev => prev.map(p =>
        p.id === poseId
          ? { ...p, image_url: newImageUrl, thumbnail_url: thumbnailUrl, is_custom: true }
          : p
      ));
    } catch (err) {
      console.error('Failed to replace pose image:', err);
      feedback.error({ code: "E5003", fallback: "upload" });
    }
  };

  const handleSavePoseOverrides = async () => {
    if (mergedPoses.length === 0) return;

    setPoseSaving(true);
    try {
      const saveData = mergedPoses.map((p, idx) => ({
        source_asset_id: p.source_asset_id,
        name: p.name,
        image_url: p.image_url,
        thumbnail_url: p.thumbnail_url || '',
        prompt: p.prompt || '',
        is_enabled: true,
        sort_order: idx,
      }));

      await ecomImageApi.batchSaveUserPoseOverrides(saveData);
      await fetchPoseAssets();
    } catch (err) {
      console.error('Failed to save pose overrides:', err);
      feedback.error({ code: "E8102", fallback: "save" });
    } finally {
      setPoseSaving(false);
    }
  };

  const handleResetPose = async (poseId: number) => {
    const pose = mergedPoses.find(p => p.id === poseId);
    if (!pose?.source_asset_id) return;

    try {
      await ecomImageApi.resetUserPoseOverride(pose.source_asset_id);
      const sys = poseAssets.find(a => a.id === pose.source_asset_id);
      if (sys) {
        setMergedPoses(prev => prev.map(p =>
          p.id === poseId
            ? { ...p, image_url: sys.image_url, thumbnail_url: sys.thumbnail_url, name: sys.name, is_custom: false, override_id: 0 }
            : p
        ));
      }
    } catch (err) {
      console.error('Failed to reset pose:', err);
    }
  };

  const handleResetAllPoses = async () => {
    setPoseSaving(true);
    try {
      await ecomImageApi.resetAllUserPoseOverrides();
      await fetchPoseAssets();
    } catch (err) {
      console.error('Failed to reset all poses:', err);
    } finally {
      setPoseSaving(false);
    }
  };


  // URL 参数：是否默认打开灵感社区（用于从编辑页返回）
  const searchParams = useSearchParams();
  const defaultShowCommunity = searchParams.get("openInspiration") === "1";
  const [showInspirationCommunity, setShowInspirationCommunity] = useState(false);
  useEffect(() => {
    if (defaultShowCommunity) setShowInspirationCommunity(true);
  }, [defaultShowCommunity]);
  const urlWorkflow = searchParams.get("workflow");
  const urlPrefillPrompt = searchParams.get("prefill_prompt");
  const urlPrefillPromptKey = searchParams.get("prefill_prompt_key")?.trim() || "prompt";

  // 用于生成入口冷却控制 - 0.5秒内只能请求一次
  const lastRequestTimeRef = useRef<number>(0);
  const [generateButtonCoolingDown, setGenerateButtonCoolingDown] = useState(false);
  const generateButtonCooldownRef = useRef(false);
  const generateButtonCooldownTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const startGenerateButtonCooldown = useCallback(() => {
    generateButtonCooldownRef.current = true;
    setGenerateButtonCoolingDown(true);
    if (generateButtonCooldownTimerRef.current) {
      clearTimeout(generateButtonCooldownTimerRef.current);
    }
    generateButtonCooldownTimerRef.current = setTimeout(() => {
      generateButtonCooldownRef.current = false;
      setGenerateButtonCoolingDown(false);
      generateButtonCooldownTimerRef.current = null;
    }, GENERATE_BUTTON_COOLDOWN_MS);
  }, []);

  useEffect(() => {
    return () => {
      if (generateButtonCooldownTimerRef.current) {
        clearTimeout(generateButtonCooldownTimerRef.current);
      }
    };
  }, []);

  // 工作流配置（从后端动态获取，控制 AI智能商拍 子选项的显示/隐藏/名称）
  type EcomWorkflowConfig = {
    feature_key: string;
    name: string;
    is_enabled: boolean;
    support_pose?: boolean;
    support_stitch?: boolean;
    sort_order: number;
    batch_options?: string;
    batch_style?: string;
    batch_unit?: string;
    module?: string;
    execution_mode?: string;
    batch_process_enabled?: boolean;
    batch_process_params?: string;
    batch_process_concurrency?: number;
    callable_key?: string;
    default_inspire_template_id?: number | null;
  };
  const [workflowConfigsByModule, setWorkflowConfigsByModule] = useState<Record<string, EcomWorkflowConfig[]>>({});
  const currentModuleWorkflowConfigs = workflowConfigsByModule[activeMainTab] || [];
  const allWorkflowConfigs = useMemo(() => {
    const seen = new Set<string>();
    const out: EcomWorkflowConfig[] = [];
    moduleConfigs.forEach((moduleConfig) => {
      (workflowConfigsByModule[moduleConfig.module_key] || []).forEach((wf) => {
        if (seen.has(wf.feature_key)) return;
        seen.add(wf.feature_key);
        out.push(wf);
      });
    });
    Object.values(workflowConfigsByModule).forEach((items) => {
      items.forEach((wf) => {
        if (seen.has(wf.feature_key)) return;
        seen.add(wf.feature_key);
        out.push(wf);
      });
    });
    return out;
  }, [moduleConfigs, workflowConfigsByModule]);
  const renderableOpenedSubOptions = useMemo(
    () => filterLegacyAgentWorkspaceTabs(getRenderableOpenedOptions(openedSubOptions), allWorkflowConfigs),
    [openedSubOptions, allWorkflowConfigs],
  );
  const agentHostTabs = useMemo<readonly EcomHostTab[]>(() => (
    renderableOpenedSubOptions
      .filter((tabId) => tabId !== HISTORY_TAB_LABEL)
      .map((tabId) => ({ kind: "coze", tabInstanceId: createEcomCozeHostTabId(tabId) }))
  ), [renderableOpenedSubOptions]);
  const [configsLoaded, setConfigsLoaded] = useState(false);
  const selectedWorkflow = currentModuleWorkflowConfigs.find(workflow => workflow.name === activeSubOption)
    ?? allWorkflowConfigs.find(workflow => workflow.name === activeSubOption);
  const agentSelected = isEcomAgentSelection(activeSubOption, selectedWorkflow, configsLoaded);
  agentSelectionRef.current = agentSelected;
  const agentWorkspaceMode = agentActive || (activeWorkspaceView === 'generate' && agentSelected);
  const agentSelectionKey = `${ecomStorageScope}:${selectedWorkflow?.feature_key ?? activeSubOption}`;
  // ====== 提示词扩写配置 ======
  const [expandConfigs, setExpandConfigs] = useState<ExpandConfigItem[]>([]);

  // ====== 计费信息 ======
  const [billingCosts, setBillingCosts] = useState<WorkflowCostItem[]>([]);
  const [myCosts, setMyCosts] = useState<MyCostsResult | null>(null);

  // 根据显示名称找到 feature_key（支持管理员重命名后仍能正确映射）
  const getFeatureKey = (name: string): string => {
    const currentModuleMatch = currentModuleWorkflowConfigs.find((wf) => wf.name === name);
    if (currentModuleMatch) return currentModuleMatch.feature_key;
    const found = allWorkflowConfigs.find((wf) => wf.name === name);
    if (found) return found.feature_key;
    const reverseMap: Record<string, string> = {
      "模特文/图生图": "model_gen",
      "一键迁移": "migration",
      "模特套图生产": "model_suite",
    };
    return reverseMap[name] || name;
  };

  const getFeatureDisplayName = (key: string): string => {
    const found = allWorkflowConfigs.find((wf) => wf.feature_key === key);
    if (found?.name) return found.name;
    const fallbackMap: Record<string, string> = {
      model_gen: "模特文/图生图",
      migration: "一键迁移",
      model_suite: "模特套图生产",
    };
    return fallbackMap[key] || key || "—";
  };

  const isCurrentPreviewTask = (taskId?: string, featureKey?: string) => {
    const trimmedTaskId = (taskId || "").trim();
    const trimmedFeatureKey = (featureKey || "").trim();
    if (trimmedTaskId) {
      const currentChildIds = batchChildTaskIdsRef.current;
      const currentIds = generatingTaskIdsRef.current.length > 0 ? generatingTaskIdsRef.current : currentTaskIds;
      if (
        viewingTaskIdRef.current === trimmedTaskId ||
        currentChildIds.includes(trimmedTaskId) ||
        currentIds.includes(trimmedTaskId)
      ) {
        return true;
      }
      return false;
    }
    if (trimmedFeatureKey) {
      const currentFeatureKey = historyFeatureKey || getFeatureKey(activeSubOption);
      return currentFeatureKey === trimmedFeatureKey;
    }
    return false;
  };

  const getWorkflowTabForFeatureKey = useCallback((featureKey?: string): { mainTab: MainTab; subOption: string } | null => {
    if (!featureKey) return null;
    for (const moduleConfig of moduleConfigs) {
      const match = (workflowConfigsByModule[moduleConfig.module_key] || []).find((wf) => wf.feature_key === featureKey && wf.is_enabled);
      if (match) return { mainTab: moduleConfig.module_key, subOption: match.name };
    }
    return null;
  }, [moduleConfigs, workflowConfigsByModule]);

  const findEnabledWorkflowByFeatureKey = useCallback((featureKey?: string): EcomWorkflowConfig | null => {
    if (!featureKey) return null;
    return allWorkflowConfigs.find((wf) => wf.feature_key === featureKey && wf.is_enabled) || null;
  }, [allWorkflowConfigs]);

  const currentFeatureKey = getFeatureKey(activeSubOption);
  const flowXPromptReferenceText = useMemo(() => {
    if (!isClonePromptReferenceWorkflow(activeSubOption, currentFeatureKey)) {
      return "";
    }
    const currentHistory = loadEcomTabHistory(ecomStorageScope);
    const currentTab = buildCurrentWorkspaceTab(activeTabId);
    return resolveFlowXImagePromptReference({
      activeTabId,
      tabs: {
        ...(currentHistory?.tabs || {}),
        [activeTabId]: currentTab,
      },
    });
  }, [
    activeSubOption,
    activeTabId,
    buildCurrentWorkspaceTab,
    currentFeatureKey,
    ecomStorageScope,
  ]);

  useEffect(() => {
    if (!flowXPromptReferenceText) return;
    if (!shouldUsePromptReferenceForCurrentPrompt({
      workflowName: activeSubOption,
      workflowKey: currentFeatureKey,
      currentPrompt: cozeParamsRef.current.prompt,
    })) {
      return;
    }
    handleCozeParamChange("prompt", flowXPromptReferenceText, activeTabId);
  }, [
    activeSubOption,
    activeTabId,
    currentFeatureKey,
    flowXPromptReferenceText,
    handleCozeParamChange,
  ]);

  const getModuleOptions = useCallback((moduleKey: MainTab): { feature_key: string; name: string }[] => (
    (workflowConfigsByModule[moduleKey] || [])
      .filter((wf) => wf.is_enabled)
      .sort((a, b) => a.sort_order - b.sort_order)
      .map((wf) => ({ feature_key: wf.feature_key, name: wf.name }))
  ), [workflowConfigsByModule]);

  const enabledWorkflowOptions = useMemo(() => Array.from(
    new Map(
      allWorkflowConfigs
        .filter((wf) => wf.is_enabled)
        .map((wf) => [wf.feature_key, { feature_key: wf.feature_key, name: wf.name }] as const),
    ).values(),
  ), [allWorkflowConfigs]);

  const currentPreviewFeatureKey = getFeatureKey(activeSubOption);
  previewContextRef.current = {
    generatedImage,
    generatedImages,
    currentTaskIds,
    batchChildTaskIds,
    viewingTaskId,
    historyFeatureKey,
    activeMainTab,
    activeSubOption,
    currentPrompt,
    previewOutputCount,
    featureKey: currentPreviewFeatureKey,
    featureName: getFeatureDisplayName(currentPreviewFeatureKey),
    refinePreviews,
  };

  // 获取当前工作流的批次配置
  const getCurrentBatchConfig = (): { options: number[] } => {
    const currentKey = getFeatureKey(activeSubOption);
    const wf = allWorkflowConfigs.find(w => w.feature_key === currentKey);
    let options = [1, 2, 3, 4];
    if (wf?.batch_options) {
      try {
        const parsed = JSON.parse(wf.batch_options);
        if (Array.isArray(parsed) && parsed.length > 0) options = parsed;
      } catch { /* use default */ }
    }
    return { options };
  };

  // 获取当前工作流的批次单位（后台可配置为"屏"等，默认"批"）
  const getCurrentBatchUnit = (): string => {
    const currentKey = getFeatureKey(activeSubOption);
    const wf = allWorkflowConfigs.find(w => w.feature_key === currentKey);
    const unit = wf?.batch_unit;
    if (!unit || unit === "batch") return "批";
    if (unit === "screen") return "屏";
    return unit;
  };

  // 获取当前工作流的 callable_key（用于功能性工作流 / 灵感反推执行）
  const getCurrentCallableKey = useCallback((): string => {
    const currentKey = getFeatureKey(activeSubOption);
    const wf = allWorkflowConfigs.find(w => w.feature_key === currentKey);
    return (wf?.callable_key as string | undefined) || "";
  }, [activeSubOption, allWorkflowConfigs, getFeatureKey]);

  // 当前工作流是否为「灵感反推」执行模式
  const isCurrentInspireWorkflow = (): boolean => {
    const currentKey = getFeatureKey(activeSubOption);
    const wf = allWorkflowConfigs.find(w => w.feature_key === currentKey);
    return (wf?.execution_mode === "llm_vision_reverse");
  };

  // 获取当前工作流的批处理配置
  const getCurrentBatchProcessConfig = useCallback((): { enabled: boolean; params: string[]; concurrency: number } => {
    const currentKey = getFeatureKey(activeSubOption);
    const wf = allWorkflowConfigs.find(w => w.feature_key === currentKey);
    if (!wf) return { enabled: false, params: [], concurrency: 5 };
    const enabled = wf.batch_process_enabled ?? false;
    let params: string[] = [];
    try {
      const raw = wf.batch_process_params;
      if (typeof raw === 'string') params = JSON.parse(raw);
      else if (Array.isArray(raw)) params = raw;
    } catch { /* ignore */ }
    const concurrency = wf.batch_process_concurrency || 5;
    return { enabled, params, concurrency };
  }, [activeSubOption, allWorkflowConfigs, getFeatureKey]);

  const IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif', 'image/bmp', 'image/tiff', 'image/svg+xml'];
  const IMAGE_EXTS = /\.(jpe?g|png|webp|gif|bmp|tiff?|svg)$/i;

  const collectImageFiles = async (dataTransfer: DataTransfer): Promise<File[]> => {
    const files: File[] = [];
    const entries = Array.from(dataTransfer.items)
      .map(item => item.webkitGetAsEntry?.())
      .filter(Boolean) as FileSystemEntry[];

    const readEntry = (entry: FileSystemEntry): Promise<File[]> => {
      return new Promise(resolve => {
        if (entry.isFile) {
          (entry as FileSystemFileEntry).file(f => {
            if (IMAGE_TYPES.includes(f.type) || IMAGE_EXTS.test(f.name)) resolve([f]);
            else resolve([]);
          }, () => resolve([]));
        } else if (entry.isDirectory) {
          const reader = (entry as FileSystemDirectoryEntry).createReader();
          const results: File[] = [];
          const readBatch = () => {
            reader.readEntries(async entries => {
              if (entries.length === 0) { resolve(results); return; }
              for (const e of entries) {
                const sub = await readEntry(e);
                results.push(...sub);
              }
              readBatch();
            }, () => resolve(results));
          };
          readBatch();
        } else {
          resolve([]);
        }
      });
    };

    if (entries.length > 0) {
      for (const entry of entries) {
        files.push(...await readEntry(entry));
      }
    } else {
      for (const f of Array.from(dataTransfer.files)) {
        if (IMAGE_TYPES.includes(f.type) || IMAGE_EXTS.test(f.name)) files.push(f);
      }
    }
    return files;
  };

  const handleBatchUploadFiles = async (paramKey: string, files: File[]) => {
    const imageFiles = files.filter(f => IMAGE_TYPES.includes(f.type) || IMAGE_EXTS.test(f.name));
    if (imageFiles.length === 0) return;
    setBatchUploading(prev => ({ ...prev, [paramKey]: { total: imageFiles.length, done: 0 } }));
    const batchUpload = { feature: getFeatureKey(activeSubOption), param: paramKey };
    for (const file of imageFiles) {
      try {
        const res = await ecomImageApi.uploadProductImage(file, { batchUpload });
        if (res.data?.file_url) {
          setBatchParamValues(prev => ({
            ...prev,
            [paramKey]: [...(prev[paramKey] || []), res.data!.file_url],
          }));
        }
      } catch {
        console.error('Upload failed for', file.name);
      }
      setBatchUploading(prev => ({
        ...prev,
        [paramKey]: { ...prev[paramKey], done: (prev[paramKey]?.done || 0) + 1 },
      }));
    }
    setBatchUploading(prev => { const next = { ...prev }; delete next[paramKey]; return next; });
  };

  // 获取当前工作流的预估消耗豌豆数（含折扣信息）
  const getCurrentEstimatedCost = (): {
    cost: number;
    originalCost: number;
    hasDiscount: boolean;
    mode: string;
    ruleLabel?: string;
  } | null => {
    const currentKey = getFeatureKey(activeSubOption);

    // 多选模板组件：使用模板自身的 cost 计算
    const tplComp = workflowUIConfig?.components?.find(c => c.component_type === "template_multi_select");
    if (tplComp) {
      const templates = (tplComp.config?.templates as Array<{ id: number; cost: number }>) || [];
      const selectedIds = (cozeParams[tplComp.coze_param_key] as number[]) || [];
      const effectiveSelectedIds = selectedIds.length > 0
        ? selectedIds
        : (templates[0]?.id != null ? [templates[0].id] : []);
      const totalCost = templates
        .filter(t => effectiveSelectedIds.includes(t.id))
        .reduce((sum, t) => sum + (t.cost || 1), 0) * cozeBatchCount;
      if (totalCost > 0) {
        const discountRate = myCosts?.tier?.discount_rate;
        const actualCost = typeof discountRate === "number"
          ? Math.ceil(totalCost * discountRate)
          : totalCost;
        return {
          cost: actualCost,
          originalCost: totalCost,
          hasDiscount: actualCost < totalCost,
          mode: "fixed",
        };
      }
    }

    const effectiveBillingBatchCount = resolveEcomBillingBatchCount({
      components: workflowUIConfig?.components,
      params: cozeParams,
      defaultBatchCount: cozeBatchCount,
    });

    const billing = billingCosts.find(b => b.workflow_key === currentKey);
    if (!billing || !billing.enabled) return null;

    // Check for per-mode price_rules
    let effectiveMode = billing.billing_mode;
    let effectiveFixedCost = billing.fixed_cost;
    let ruleLabel: string | undefined;

    if (billing.match_field && billing.price_rules?.length) {
      const paramValue = String(cozeParams[billing.match_field] ?? "");
      const matchedRule = billing.price_rules.find(r => r.match_value === paramValue);
      if (matchedRule) {
        effectiveMode = matchedRule.billing_mode;
        effectiveFixedCost = matchedRule.fixed_cost;
        ruleLabel = matchedRule.label;
      }
    }

    if (effectiveMode === "fixed") {
      const myCostItem = myCosts?.costs?.find(c => c.workflow_key === currentKey);
      const estimate = calculateEcomFixedBillingEstimate({
        standardUnitCost: effectiveFixedCost,
        batchCount: effectiveBillingBatchCount,
        userCostItem: myCostItem,
        discountRate: myCosts?.tier?.discount_rate,
      });
      return { ...estimate, mode: "fixed", ruleLabel };
    }
    if (effectiveMode === "time_based") {
      return { cost: 0, originalCost: 0, hasDiscount: false, mode: "time_based", ruleLabel };
    }
    return null;
  };

  // ====== 当前工作流执行模式 ======
  const currentExecutionMode = useMemo(() => {
    const currentKey = getFeatureKey(activeSubOption);
    const wfConfig = allWorkflowConfigs.find(w => w.feature_key === currentKey);
    return wfConfig?.execution_mode || 'coze_direct';
  }, [activeSubOption, allWorkflowConfigs]);

  // ====== 画布排版模式（管理端开启 support_stitch 的工作流自动生效） ======
  const stitchMode = useMemo(() => {
    const effectiveKey = historyFeatureKey || getFeatureKey(activeSubOption);
    const wfConfig = allWorkflowConfigs.find(w => w.feature_key === effectiveKey);
    return !!(wfConfig?.support_stitch) && previewOutputCount > 1;
  }, [activeSubOption, allWorkflowConfigs, previewOutputCount, historyFeatureKey]);

  // 推理模式下的状态提示文案
  const inferenceStatusHint = useMemo(() => {
    if (currentExecutionMode !== 'llm_inference' || status !== 'generating') return undefined;
    if (currentTaskIds.length === 0) return 'AI 推理中...';
    if (progress < 10) return 'AI 推理分析中...';
    if (progress < 40) return '正在生成提示词...';
    return '正在并行生图...';
  }, [currentExecutionMode, status, progress, currentTaskIds]);

  // 获取工作流配置（通过 Next.js proxy 走网关，带可选认证按团队过滤可见性）
  useEffect(() => {
    setModuleConfigsLoaded(false);
    setConfigsLoaded(false);
	    setWorkflowConfigsByModule({});
    let cancelled = false;

    const fetchBillingCosts = async () => {
      try {
        const res = await ecomImageApi.getWorkflowCosts("ecom-image");
        if (res.data) {
          setBillingCosts(Array.isArray(res.data) ? res.data : []);
        }
      } catch {
        // billing info is non-critical
      }
    };

    const fetchMyCosts = async () => {
      try {
        const res = await ecomImageApi.getMyCosts();
        if (res.data) {
          setMyCosts(res.data);
        }
      } catch {
        // my-costs is non-critical; falls back to standard pricing
      }
    };

    const fetchModulesAndWorkflows = async () => {
      try {
        // Independent public reads: one catalog already filters team visibility
        // and includes module ownership; avoid one database read per module.
        const [moduleResponse, workflowResponse] = await Promise.all([
          ecomImageApi.getModuleNames(),
          ecomImageApi.getWorkflowConfigs().catch(() => ({ code: -1, data: [] })),
        ]);
        if (cancelled) return;
        const hasModuleResponse = moduleResponse.code === 0 && Array.isArray(moduleResponse.data);
        const modules = hasModuleResponse && moduleResponse.data
          ? moduleResponse.data
            .slice()
            .filter((item) => isModuleEnabledValue(item.is_enabled))
            .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0))
          : [];
        const effectiveModules = hasModuleResponse ? modules : DEFAULT_MODULE_CONFIGS;
        const names: Record<string, string> = { ...LEGACY_MODULE_DISPLAY_NAMES };
        effectiveModules.forEach(item => { names[item.module_key] = item.display_name; });
        setModuleDisplayNames(names);
        setModuleConfigs(effectiveModules);
        setModuleConfigsLoaded(true);

        setWorkflowConfigsByModule(groupEcomWorkflowCatalog(
          effectiveModules, workflowResponse.code === 0 ? (workflowResponse.data || []) : [],
        ));
        setConfigsLoaded(true);
	      } catch (err) {
        if (cancelled) return;
        console.error("获取电商生图板块配置失败:", err);
	        setModuleConfigsLoaded(true);
	        setConfigsLoaded(true);
	      }
    };

    fetchModulesAndWorkflows();
    fetchBillingCosts();
    fetchMyCosts();
    return () => {
      cancelled = true;
    };
  }, [user?.id, user?.current_team_id]);

  useEffect(() => {
    if (!moduleConfigsLoaded) return;
    if (moduleConfigs.length === 0) {
      setExpandedMainTabs([]);
      setActiveSubOption("");
      setActiveTabInstance(1);
      return;
    }
    if (moduleConfigs.some((item) => item.module_key === activeMainTab)) return;
    const fallback = moduleConfigs[0].module_key;
    setActiveMainTab(fallback);
    ensureMainTabExpanded(fallback);
    setActiveTabInstance(1);
  }, [activeMainTab, ensureMainTabExpanded, moduleConfigs, moduleConfigsLoaded]);

  useEffect(() => {
    if (!moduleConfigsLoaded) return;
    const enabledModuleKeys = new Set(moduleConfigs.map((item) => item.module_key));
    const isVisibleTabId = (tabId: string) => {
      if (tabId === HISTORY_TAB_LABEL) return true;
      const parsed = parseEcomTabId(tabId);
      return !!parsed && enabledModuleKeys.has(parsed.mainTab);
    };

    setExpandedMainTabs((prev) => prev.filter((tab) => enabledModuleKeys.has(tab)));
    setOpenedSubOptions((prev) => {
      const next = prev.filter(isVisibleTabId);
      if (areSameStringList(next, prev)) return prev;
      saveEcomOpenedTabs(next, ecomStorageScope);
      return next;
    });

    const currentHistory = loadEcomTabHistory(ecomStorageScope);
    if (!currentHistory) return;
    const nextOpenTabIds = currentHistory.openTabIds.filter(isVisibleTabId);
    const nextActiveTabId = currentHistory.activeTabId && isVisibleTabId(currentHistory.activeTabId)
      ? currentHistory.activeTabId
      : nextOpenTabIds[0] || null;
    if (areSameStringList(nextOpenTabIds, currentHistory.openTabIds) && nextActiveTabId === currentHistory.activeTabId) {
      return;
    }
    saveEcomTabHistory({
      ...currentHistory,
      activeTabId: nextActiveTabId,
      openTabIds: nextOpenTabIds,
      updatedAt: Date.now(),
    }, ecomStorageScope);
  }, [ecomStorageScope, moduleConfigs, moduleConfigsLoaded]);

  // 获取动态组件UI配置（所有开启一级板块下的 Coze 工作流子选项都加载）
  useEffect(() => {
    if (agentSelected || !moduleConfigs.some((item) => item.module_key === activeMainTab)) {
      setWorkflowUIConfig(null);
      setWorkflowUIConfigLoading(false);
      setWorkflowUIConfigError(false);
      return;
    }

    if (!configsLoaded) return;

    const currentKey = getFeatureKey(activeSubOption);
    if (!currentKey) {
      setWorkflowUIConfig(null);
      setWorkflowUIConfigLoading(false);
      setWorkflowUIConfigError(false);
      return;
    }

    setWorkflowUIConfig(null);
    setWorkflowUIConfigLoading(true);
    setWorkflowUIConfigError(false);
    if (isRestoringRef.current) {
      isRestoringRef.current = false;
    } else {
      setCozeParams({});
    }

    let cancelled = false;
    const fetchUIConfig = async () => {
      let loadedConfig: WorkflowUIConfig | null = null;

      for (let attempt = 0; attempt < WORKFLOW_UI_CONFIG_MAX_ATTEMPTS; attempt += 1) {
        try {
          const res = await ecomImageApi.getWorkflowUIConfig(currentKey);
          if (cancelled) return;
          if (res.data && Array.isArray(res.data.components)) {
            loadedConfig = res.data;
            break;
          }
        } catch {
          if (cancelled) return;
        }

        if (attempt < WORKFLOW_UI_CONFIG_MAX_ATTEMPTS - 1) {
          const retryDelay = WORKFLOW_UI_CONFIG_RETRY_DELAYS_MS[attempt] ?? 2000;
          await waitForWorkflowUIRetry(retryDelay);
          if (cancelled) return;
        }
      }

      if (cancelled) return;
      if (loadedConfig) {
        const currentWorkflowConfig = allWorkflowConfigs.find((wf) => wf.feature_key === currentKey);
        setWorkflowUIConfig(withCozeTemplateSetFallbackComponent(loadedConfig, currentWorkflowConfig));
      } else {
        setWorkflowUIConfigError(true);
      }
      setWorkflowUIConfigLoading(false);
    };
    fetchUIConfig();

    // 加载扩写配置
    setExpandConfigs([]);
    const fetchExpandConfig = async () => {
      try {
        const res = await ecomImageApi.getExpandConfig(currentKey);
        if (!cancelled && res.data) {
          setExpandConfigs(res.data);
        }
      } catch {
        // 静默失败，扩写按钮不显示
      }
    };
    fetchExpandConfig();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeMainTab, activeSubOption, agentSelected, allWorkflowConfigs, configsLoaded, moduleConfigs, workflowConfigReloadKey]);

  // ====== 草稿自动保存到 sessionStorage ======
  useEffect(() => {
    saveEcomDraftState({
      activeMainTab,
      activeSubOption,
      cozeParams: stripBlobUrls(cozeParams),
      cozeBatchCount,
      refImageUrl,
      perBatchRatios,
    }, ecomStorageScope);
  }, [activeMainTab, activeSubOption, cozeParams, cozeBatchCount, ecomStorageScope, refImageUrl, perBatchRatios]);

  const persistOpenedTabOrder = useCallback((tabs: string[]) => {
    saveEcomOpenedTabs(tabs, ecomStorageScope);
    const currentHistory = loadEcomTabHistory(ecomStorageScope);
    if (!currentHistory) return;

    const nextOpenTabIds = getOrderedHistoryOpenTabIds(tabs, currentHistory);
    if (areSameStringList(nextOpenTabIds, currentHistory.openTabIds)) return;

    saveEcomTabHistory({
      ...currentHistory,
      openTabIds: nextOpenTabIds,
      updatedAt: Date.now(),
    }, ecomStorageScope);
  }, [ecomStorageScope]);

  useEffect(() => {
    const normalized = filterLegacyAgentWorkspaceTabs(getRenderableOpenedOptions(openedSubOptions), allWorkflowConfigs);
    if (!areSameStringList(normalized, openedSubOptions)) {
      setOpenedSubOptions(normalized);
      return;
    }
    persistOpenedTabOrder(normalized);
  }, [allWorkflowConfigs, openedSubOptions, persistOpenedTabOrder]);

  const handleOpenedTabDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    setOpenedSubOptions((prev) => {
      const activeTabId = String(active.id);
      const overTabId = String(over.id);
      const oldIndex = prev.indexOf(activeTabId);
      const newIndex = prev.indexOf(overTabId);
      if (oldIndex === -1 || newIndex === -1) return prev;

      const next = getUniqueOpenedOptions(arrayMove(prev, oldIndex, newIndex));
      persistOpenedTabOrder(next);
      return next;
    });
  }, [persistOpenedTabOrder]);

  useEffect(() => {
    if (activeWorkspaceView !== "generate" || isApplyingTabHistoryRef.current || activeSubOption.trim().length === 0) return;
    if (persistTimerRef.current) {
      clearTimeout(persistTimerRef.current);
    }
    const scheduledTabId = activeTabId;
    const scheduledSwitchEpoch = tabSwitchEpochRef.current;
    persistTimerRef.current = setTimeout(() => {
      persistTimerRef.current = null;
      if (
        scheduledSwitchEpoch !== tabSwitchEpochRef.current
        || scheduledTabId !== activeTabIdRef.current
      ) {
        return;
      }
      persistCurrentWorkspaceTab();
    }, 250);
    return () => {
      if (persistTimerRef.current) {
        clearTimeout(persistTimerRef.current);
        persistTimerRef.current = null;
      }
    };
  }, [
    activeTabId,
    activeWorkspaceView,
    activeMainTab,
    activeSubOption,
    cozeParams,
    cozeBatchCount,
    status,
    progress,
    generatedImage,
    resultModelImage,
    resultProductImage,
    generatedImages,
    errorMessage,
    currentTaskIds,
    currentPrompt,
    failedIndexes,
    batchChildTaskIds,
    previewOutputCount,
    taskProgresses,
    batchProcessMode,
    batchParamValues,
    batchConcurrency,
    batchParentTaskId,
    batchTaskStatus,
    manualStitchMode,
    historyFeatureKey,
    refImageUrl,
    inspireResults,
    multiTemplateLabels,
    multiTemplateWorkflows,
    refinePreviews,
    perBatchRatios,
    persistCurrentWorkspaceTab,
  ]);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const flushDraftBeforeUnload = () => {
      saveEcomDraftState({
        activeMainTab,
        activeSubOption,
        cozeParams: stripBlobUrls(cozeParams),
        cozeBatchCount,
        refImageUrl,
        perBatchRatios,
      }, ecomStorageScope);

      if (activeWorkspaceView === "generate" && activeSubOption.trim().length > 0) {
        persistCurrentWorkspaceTab();
      }
    };

    window.addEventListener("pagehide", flushDraftBeforeUnload);
    window.addEventListener("beforeunload", flushDraftBeforeUnload);
    return () => {
      window.removeEventListener("pagehide", flushDraftBeforeUnload);
      window.removeEventListener("beforeunload", flushDraftBeforeUnload);
    };
  }, [
    activeMainTab,
    activeSubOption,
    activeWorkspaceView,
    cozeBatchCount,
    cozeParams,
    ecomStorageScope,
    perBatchRatios,
    persistCurrentWorkspaceTab,
    refImageUrl,
  ]);

  useEffect(() => {
    if (!configsLoaded || agentSelected || activeSubOption.trim().length === 0) return;
    const tabId = activeTabId;
    if (closedTabIdsRef.current.has(tabId)) return;
    setOpenedSubOptions(prev => {
      const next = getUniqueOpenedOptions(prev.includes(tabId) ? prev : [...prev, tabId]);
      saveEcomOpenedTabs(next, ecomStorageScope);
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTabId, agentSelected, configsLoaded]);

  // Keep the active tab's status in sync with the per-tab status map so the spinner on the
  // tab title accurately reflects whether a generation is currently running.
  useEffect(() => {
    if (!activeTabId || activeTabId === HISTORY_TAB_LABEL) return;
    setTabStatusMap((prev) => {
      if (prev[activeTabId] === status) return prev;
      return { ...prev, [activeTabId]: status };
    });
  }, [activeTabId, status]);

  // ====== 进度模拟系统 ======
  const startProgressSimulation = useCallback((initialProgress: number) => {
    simulatedProgressRef.current = initialProgress;
    if (progressTimerRef.current) clearInterval(progressTimerRef.current);
    progressTimerRef.current = setInterval(() => {
      const cur = simulatedProgressRef.current;
      let increment: number;
      if (cur < 20) increment = 1.5;
      else if (cur < 40) increment = 1;
      else if (cur < 60) increment = 0.6;
      else if (cur < 75) increment = 0.3;
      else if (cur < 85) increment = 0.15;
      else if (cur < 92) increment = 0.08;
      else increment = 0;
      simulatedProgressRef.current = Math.min(cur + increment, 95);
      setProgress(prev => Math.max(prev, Math.round(simulatedProgressRef.current)));
    }, 500);
  }, []);

  const stopProgressSimulation = useCallback(() => {
    if (progressTimerRef.current) {
      clearInterval(progressTimerRef.current);
      progressTimerRef.current = null;
    }
  }, []);

  const updateRetryOwnerWorkspace = useCallback((
    tracker: ActiveRetryIdentity,
    phase: "pending" | "completed" | "failed",
    outputUrl = "",
  ): boolean => {
    const registeredTracker = activeRetryTrackerRef.current.get(tracker.retryTaskId);
    if (!registeredTracker || !isSameActiveRetryIdentity(registeredTracker, tracker)) return false;
    if (closedTabIdsRef.current.has(tracker.ownerTabId)) return false;
    const history = loadEcomTabHistory(ecomStorageScope);
    const existing = history?.tabs[tracker.ownerTabId];
    if (!history || !existing || !history.openTabIds.includes(tracker.ownerTabId)) return false;

    const storedContext = toRetryWorkspaceContext(existing);
    const matchesStoredContext = phase === "pending"
      ? isRetrySourceIdentityCurrent(tracker, storedContext)
      : isRetryProjectionIdentityCurrent(tracker, storedContext);
    if (!matchesStoredContext) return false;

    if (activeTabIdRef.current === tracker.ownerTabId) {
      const activeContextMatches = phase === "pending"
        ? isRetrySourceIdentityCurrent(tracker, activeRetryWorkspaceContextRef.current)
        : isRetryProjectionIdentityCurrent(tracker, activeRetryWorkspaceContextRef.current);
      if (!activeContextMatches) return false;
    }

    let nextTab: EcomWorkspaceTab;
    if (phase === "pending") {
      const sourceImages = existing.generatedImages.length > 0
        ? existing.generatedImages
        : existing.generatedImage
          ? [existing.generatedImage]
          : [];
      const previewPatch = buildRetryPreviewState({
        index: tracker.slotIndex,
        retryTaskId: tracker.retryTaskId,
        currentTaskIds: existing.currentTaskIds,
        batchChildTaskIds: existing.batchChildTaskIds,
        failedIndexes: existing.failedIndexes,
        existingGeneratedImages: sourceImages,
      });
      nextTab = {
        ...existing,
        ...previewPatch,
        generatedImage: tracker.slotIndex === 0 ? null : existing.generatedImage,
        taskProgresses: Array.from(
          { length: previewPatch.previewOutputCount },
          (_, index) => index === tracker.slotIndex ? 5 : existing.taskProgresses[index] || 100,
        ),
        updatedAt: Date.now(),
      };
    } else {
      const nextImages = existing.generatedImages.length > 0
        ? [...existing.generatedImages]
        : existing.generatedImage
          ? [existing.generatedImage]
          : [];
      while (nextImages.length <= tracker.slotIndex) nextImages.push("");
      if (phase === "completed") nextImages[tracker.slotIndex] = outputUrl;

      const nextFailedIndexes = phase === "failed"
        ? Array.from(new Set([...existing.failedIndexes, tracker.slotIndex])).sort((a, b) => a - b)
        : existing.failedIndexes.filter((index) => index !== tracker.slotIndex);
      const remainingRetryTaskCount = Array.from(activeRetryTrackerRef.current.values())
        .filter((item) => item.retryTaskId !== tracker.retryTaskId && item.ownerTabId === tracker.ownerTabId)
        .length;
      const isResolved = shouldFinalizeRetryPreview({
        images: nextImages,
        outputCount: existing.previewOutputCount,
        failedIndexes: nextFailedIndexes,
        remainingRetryTaskCount,
      });
      const hasCompletedImage = nextImages.some((image) => image.trim().length > 0);
      const nextStatus: GenerationStatus = isResolved
        ? hasCompletedImage ? "completed" : "failed"
        : "generating";
      nextTab = {
        ...existing,
        status: nextStatus,
        progress: isResolved ? 100 : existing.progress,
        generationStartedAt: isResolved ? null : existing.generationStartedAt,
        generatedImage: tracker.slotIndex === 0
          ? nextImages[0] || null
          : existing.generatedImage,
        generatedImages: nextImages,
        failedIndexes: nextFailedIndexes,
        taskProgresses: Array.from(
          { length: Math.max(existing.previewOutputCount, nextImages.length, 1) },
          (_, index) => nextImages[index] || nextFailedIndexes.includes(index)
            ? 100
            : existing.taskProgresses[index] || 0,
        ),
        errorMessage: phase === "failed" && !hasCompletedImage
          ? "操作失败，请稍后重试"
          : "",
        updatedAt: Date.now(),
      };
    }

    saveEcomTabHistory({
      ...history,
      tabs: { ...history.tabs, [tracker.ownerTabId]: nextTab },
      updatedAt: Date.now(),
    }, ecomStorageScope);
    setTabStatusMap((previous) => previous[tracker.ownerTabId] === nextTab.status
      ? previous
      : { ...previous, [tracker.ownerTabId]: nextTab.status });

    if (activeTabIdRef.current === tracker.ownerTabId) {
      if (phase === "pending") setActiveWorkspaceView("generate");
      applyWorkspaceTab(nextTab);
      if (phase === "pending") {
        startProgressSimulation(5);
      } else if (nextTab.status !== "generating") {
        stopProgressSimulation();
      }
    }
    return true;
  }, [applyWorkspaceTab, ecomStorageScope, startProgressSimulation, stopProgressSimulation]);

  useEffect(() => {
    return () => stopProgressSimulation();
  }, [stopProgressSimulation]);

  // Sync generation refs when generation starts (status becomes "generating" with task IDs)
  useEffect(() => {
    if (!batchParentTaskId && status === "generating" && currentTaskIds.length > 0) {
      const hasStrictRetryOwner = Array.from(activeRetryTrackerRef.current.values()).some((tracker) => (
        tracker.ownerTabId === activeTabIdRef.current
        && isRetryProjectionIdentityCurrent(tracker, activeRetryWorkspaceContextRef.current)
      ));
      if (hasStrictRetryOwner) return;
      const currentViewingTaskId = viewingTaskIdRef.current;
      const nextViewingTaskId =
        currentViewingTaskId && currentTaskIds.includes(currentViewingTaskId)
          ? currentViewingTaskId
          : currentTaskIds[0] || "";
      if (viewingTaskIdRef.current !== nextViewingTaskId && nextViewingTaskId) {
        viewingTaskIdRef.current = nextViewingTaskId;
        setViewingTaskId(nextViewingTaskId);
      }
      if (!generationStartedAt) {
        setGenerationStartedAt(Date.now());
      }
      const seededProgresses = currentTaskIds.map((_, index) =>
        clampProgress(taskProgresses[index] ?? progress),
      );
      generationEpochRef.current += 1;
      generatingTaskIdsRef.current = [...currentTaskIds];
      generatingViewingTaskIdRef.current = nextViewingTaskId;
      generatingBatchChildIdsRef.current = [...batchChildTaskIds];
      generatingFeatureKeyRef.current = historyFeatureKey;
      generatingImagesRef.current = currentTaskIds.map((_, index) => generatedImages[index] || "");
      generatingProgressRef.current = seededProgresses;
      generatingFailedRef.current = [];
      generatingModelImgRef.current = "";
      generatingProductImgRef.current = "";
      startProgressSimulation(Math.max(5, progress, ...seededProgresses));
      setGenerationActive(true);
      setGenerationTrigger(prev => prev + 1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, currentTaskIds, batchParentTaskId]);

  // ====== AI智能商拍 任务状态轮询（支持多批次，独立于视图切换） ======
  // 关键设计：总是将完成结果累积到 ref 中，不受当前视图影响。
  // 当用户正在查看本次生成时，立即同步到 state 驱动 UI 更新。
  // 当用户切换到历史记录再切回时，从 ref 恢复已完成的结果。
  useEffect(() => {
    if (!generationActive) return;
    const taskIds = generatingTaskIdsRef.current;
    if (taskIds.length === 0) return;

    const epoch = generationEpochRef.current;
    const completedSet = new Set<number>();
    const failedSet = new Set<number>(taskIds.flatMap((id, index) => id ? [] : [index]));
    let stopped = false;
    const isCurrentGeneration = () => !stopped && epoch === generationEpochRef.current;
    const isViewingGeneration = () => isCurrentGeneration() && (
      viewingTaskIdRef.current === generatingViewingTaskIdRef.current ||
      taskIds.includes(viewingTaskIdRef.current));
    let pollController: EcomPollingLoopController | null = null;

    const poll = async () => {
      if (!generatingTaskIdsRef.current.length || !isCurrentGeneration()) {
        pollController?.stop();
        return;
      }
      try {
        const progressUpdates: Record<number, number> = {};

        // Requests share the API's four-request coordinator. A slow slot must not
        // prevent an already completed sibling from reaching its own result slot.
        await Promise.all(taskIds.map(async (taskId, i) => {
          if (completedSet.has(i) || failedSet.has(i)) return;
          try {
            const res = await getTaskStatus(taskId);
            // stop() only cancels future ticks, not a request already in flight.
            // Check ownership before touching shared refs as well as React state.
            if (!isCurrentGeneration() || !res.data) return;
            if (res.data.task_id && res.data.task_id !== taskId) return;

            const actual = res.data.progress || 0;
            progressUpdates[i] = actual;

            if (res.data.status === TASK_STATUS.FAILED || res.data.status === TASK_STATUS.CANCELLED) {
              failedSet.add(i);
              progressUpdates[i] = 100;
              generatingFailedRef.current = Array.from(failedSet).sort((a, b) => a - b);
            }

            if (res.data.status === TASK_STATUS.COMPLETED) {
              const outputImages = resolveTaskOutputImages(
                res.data.output_images,
                res.data.coze_raw_output,
              );
              const modelImg = res.data.model_image || '';
              const productImg = res.data.product_image || '';

              let imageUrl = '';
              if (outputImages && outputImages.length > 0) {
                imageUrl = outputImages[0];
              }
              // Status and result visibility may arrive in separate snapshots.
              // Keep polling a successful task until its image has been recovered.
              if (!imageUrl && !generatingImagesRef.current[i]) return;
              completedSet.add(i);
              progressUpdates[i] = 100;

              // Always accumulate to refs so results survive view switches
              if (imageUrl) {
                generatingImagesRef.current[i] = imageUrl;
              }
              if (i === 0) {
                generatingModelImgRef.current = modelImg;
                generatingProductImgRef.current = productImg;
              }

              // Immediately sync to display state — each slot updates independently
              const isViewingThisGeneration = isViewingGeneration();

              if (imageUrl && isViewingThisGeneration) {
                setGeneratedImages(prev => {
                  const next = [...prev];
                  while (next.length <= i) next.push("");
                  next[i] = imageUrl;
                  return next;
                });
              }

              if (i === 0 && isViewingThisGeneration) {
                setResultModelImage(modelImg);
                setResultProductImage(productImg);
              }
            }
          } catch {
            // individual task poll error, continue
          }
        }));
        if (!isCurrentGeneration()) return;

        // Always accumulate progress to ref
        for (const [idxStr, prog] of Object.entries(progressUpdates)) {
          const idx = Number(idxStr);
          generatingProgressRef.current[idx] = Math.max(generatingProgressRef.current[idx] || 0, prog);
        }

        const isViewingThisGeneration = isViewingGeneration();

        if (Object.keys(progressUpdates).length > 0 && isViewingThisGeneration) {
          setTaskProgresses(prev => {
            const next = [...prev];
            for (const [idxStr, prog] of Object.entries(progressUpdates)) {
              const idx = Number(idxStr);
              next[idx] = Math.max(next[idx] || 0, prog);
            }
            return next;
          });
          const aggregateProgress = generatingProgressRef.current.length > 0
            ? Math.round(
              generatingProgressRef.current.reduce((sum, value) => sum + clampProgress(value), 0) /
              generatingProgressRef.current.length,
            )
            : Math.max(...Object.values(progressUpdates).map((value) => clampProgress(value)));
          setProgress(prev => Math.max(prev, aggregateProgress));
        }

        if (failedSet.size > 0 && isViewingThisGeneration) {
          setFailedIndexes(Array.from(failedSet).sort((a, b) => a - b));
        }

        // All tasks resolved (completed or failed)
        const doneCount = completedSet.size + failedSet.size;
        if (doneCount === taskIds.length) {
          pollController?.stop();

          // Save batch child IDs for retry/edit
          if (taskIds.length > 1) {
            generatingBatchChildIdsRef.current = [...taskIds];
          }

          const hasAnyImage = completedSet.size > 0;
          const currentFailedArr = Array.from(failedSet);

          // Always restore accumulated images from ref when all tasks done
          stopProgressSimulation();
          if (isViewingThisGeneration) {
            setProgress(100);
            setTaskProgresses(prev => prev.map(() => 100));
            if (taskIds.length > 1) {
              setBatchChildTaskIds([...taskIds]);
            }
            // Restore all accumulated images from ref
            setGeneratedImages([...generatingImagesRef.current]);
            if (hasAnyImage) {
              setGenerationStartedAt(null);
              setStatus("completed");
              notifyPointsChanged();
              const firstImg = generatingImagesRef.current[0];
              if (firstImg) setGeneratedImage(firstImg);
              setResultModelImage(generatingModelImgRef.current);
              setResultProductImage(generatingProductImgRef.current);
              setErrorMessage("");
              setFailedIndexes(currentFailedArr);
            } else {
              setGenerationStartedAt(null);
              setStatus("failed");
              setErrorMessage("内容审核未通过，提示词或参考图可能包含违规内容，请修改后重试");
              setFailedIndexes(currentFailedArr.sort((a, b) => a - b));
            }
          } else {
            notifyPointsChanged();
          }

          setGenerationActive(false);
          generatingTaskIdsRef.current = [];
          setHistoryRefreshTrigger(prev => prev + 1);

          // Completion stops this polling effect. Metadata may still arrive for
          // the same visible result, but must not cross a new submit/tab switch.
          const metadataTabId = activeTabIdRef.current;
          const metadataRequestEpoch = generationRequestEpochRef.current;
          const metadataViewingTaskId = viewingTaskIdRef.current;
          const canApplyNextSteps = () => isViewingThisGeneration
            && epoch === generationEpochRef.current
            && viewingTaskIdRef.current === metadataViewingTaskId
            && isGenerationRequestCurrent(metadataTabId, metadataRequestEpoch);
          try {
            const currentKey = getFeatureKey(activeSubOption);
            const nsRes = await ecomImageApi.getWorkflowNextSteps(currentKey);
            if (canApplyNextSteps()) {
              setNextStepTargets(nsRes.data?.next_steps || []);
            }
          } catch {
            if (canApplyNextSteps()) setNextStepTargets([]);
          }
        }
      } catch (err) {
        console.error("Poll task status error:", err);
      }
    };
    pollController = startEcomPollingLoop({
      poll,
      baseDelayMs: 2500,
    });
    return () => {
      stopped = true;
      pollController?.stop();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [generationActive, generationTrigger]);

  // ====== 单图重新生成 任务状态轮询 ======
  useEffect(() => {
    if (activeRetryTrackerRef.current.size === 0) return;

    const pollController = startEcomPollingLoop({
      poll: async () => {
      for (const [actualRetryTaskId, tracker] of Array.from(activeRetryTrackerRef.current.entries())) {
        try {
          const res = await getTaskStatus(actualRetryTaskId);
          if (!res.data || res.data.task_id !== actualRetryTaskId) continue;
          const registeredTracker = activeRetryTrackerRef.current.get(actualRetryTaskId);
          if (!registeredTracker || !isSameActiveRetryIdentity(registeredTracker, tracker)) continue;

          if (res.data.status === TASK_STATUS.COMPLETED) {
            const outputImages = resolveTaskOutputImages(
              res.data.output_images,
              res.data.coze_raw_output,
            );
            const newImageUrl = outputImages[0] || "";
            if (!newImageUrl) continue;
            updateRetryOwnerWorkspace(tracker, "completed", newImageUrl);
            // Keep the tracker until history persistence succeeds. A temporary
            // write failure must not leave a visible image that disappears on reload.
            await patchTaskOutputImage(tracker.sourceTaskId, tracker.patchIndex, newImageUrl, tracker.preRetryImageUrl, recoveryAccount(ecomStorageScope || ""));
            setHistoryRefreshTrigger(prev => prev + 1);
            removeDurableRetry(recoveryAccount(ecomStorageScope || ""), actualRetryTaskId);
            activeRetryTrackerRef.current.delete(actualRetryTaskId);
            setRetryTrackerTrigger(prev => prev + 1);
          } else if (
            res.data.status === TASK_STATUS.FAILED
            || res.data.status === TASK_STATUS.CANCELLED
          ) {
            updateRetryOwnerWorkspace(tracker, "failed");
            removeDurableRetry(recoveryAccount(ecomStorageScope || ""), actualRetryTaskId);
            activeRetryTrackerRef.current.delete(actualRetryTaskId);
            setRetryTrackerTrigger(prev => prev + 1);
            setHistoryRefreshTrigger(prev => prev + 1);
          }
        } catch {
          // continue polling
        }
      }
      },
      baseDelayMs: 2500,
    });
    return () => pollController.stop();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [retryTrackerTrigger, updateRetryOwnerWorkspace]);

  const clearRefineStateForTab = useCallback((ownerTabId: string) => {
    let trackerChanged = false;
    for (const [trackerKey, tracker] of activeRefineTrackerRef.current.entries()) {
      if (tracker.ownerTabId !== ownerTabId) continue;
      activeRefineTrackerRef.current.delete(trackerKey);
      trackerChanged = true;
    }
    if (trackerChanged) setRefineTrackerTrigger((prev) => prev + 1);

    const history = loadEcomTabHistory(ecomStorageScope);
    const tab = history?.tabs[ownerTabId];
    if (history && tab && (tab.refinePreviews || []).length > 0) {
      saveEcomTabHistory({
        ...history,
        tabs: {
          ...history.tabs,
          [ownerTabId]: { ...tab, refinePreviews: [], updatedAt: Date.now() },
        },
        updatedAt: Date.now(),
      }, ecomStorageScope);
    }
    if (activeTabIdRef.current === ownerTabId) {
      setRefinePreviews([]);
      setRefineLifecycleEpoch((prev) => prev + 1);
    }
  }, [ecomStorageScope]);

  const clearActiveRefineState = useCallback(() => {
    clearRefineStateForTab(activeTabIdRef.current);
  }, [clearRefineStateForTab]);

  const clearForegroundGenerationState = useCallback((
    emptyWorkspaceFields: EcomForegroundGenerationWorkspaceFields = createEmptyForegroundGenerationWorkspaceFields(),
  ) => {
    // Clearing the preview must also retire any in-flight poll that still owns
    // the previous images, including the gap before new task IDs arrive.
    generationEpochRef.current += 1;
    generatingTaskIdsRef.current = [];
    setGenerationActive(false);
    stopProgressSimulation();
    setStatus(emptyWorkspaceFields.status);
    setProgress(emptyWorkspaceFields.progress);
    setGeneratedImage(emptyWorkspaceFields.generatedImage);
    setResultModelImage(emptyWorkspaceFields.resultModelImage);
    setResultProductImage(emptyWorkspaceFields.resultProductImage);
    setGeneratedImages(emptyWorkspaceFields.generatedImages);
    clearActiveRefineState();
    setErrorMessage(emptyWorkspaceFields.errorMessage);
    setCurrentTaskIds(emptyWorkspaceFields.currentTaskIds);
    setViewingTaskId(emptyWorkspaceFields.viewingTaskId);
    setCurrentPrompt(emptyWorkspaceFields.currentPrompt);
    setFailedIndexes(emptyWorkspaceFields.failedIndexes);
    setBatchChildTaskIds(emptyWorkspaceFields.batchChildTaskIds);
    setPreviewOutputCount(emptyWorkspaceFields.previewOutputCount);
    setTaskProgresses(emptyWorkspaceFields.taskProgresses);
    setBatchProcessMode(emptyWorkspaceFields.batchProcessMode);
    setBatchParamValues(emptyWorkspaceFields.batchParamValues);
    setBatchParentTaskId(emptyWorkspaceFields.batchParentTaskId);
    setBatchTaskStatus(emptyWorkspaceFields.batchTaskStatus);
    setManualStitchMode(emptyWorkspaceFields.manualStitchMode);
    setHistoryFeatureKey(emptyWorkspaceFields.historyFeatureKey);
    setRefImageUrl(emptyWorkspaceFields.refImageUrl);
    setPerBatchRatios(emptyWorkspaceFields.perBatchRatios);
    setRefinePreviews(emptyWorkspaceFields.refinePreviews);
    setGenerationStartedAt(emptyWorkspaceFields.generationStartedAt);
    applyInspireResults(emptyWorkspaceFields.inspireResults);
    applyMultiTemplateLabels(emptyWorkspaceFields.multiTemplateLabels);
    applyMultiTemplateWorkflows(emptyWorkspaceFields.multiTemplateWorkflows);
    inspireTaskIdsRef.current = [];
    viewingTaskIdRef.current = emptyWorkspaceFields.viewingTaskId;
  }, [
    applyInspireResults,
    applyMultiTemplateLabels,
    applyMultiTemplateWorkflows,
    clearActiveRefineState,
    stopProgressSimulation,
  ]);

  const updateRefinePreviewForTab = useCallback((
    ownerTabId: string,
    sourceTaskId: string,
    imageIndex: number,
    update: EcomRefinePreview | null,
  ) => {
    const history = loadEcomTabHistory(ecomStorageScope);
    const tab = history?.tabs[ownerTabId];
    if (!history || !tab || !history.openTabIds.includes(ownerTabId)) return false;
    const nextPreviews = (tab.refinePreviews || []).filter(
      (item) => !(item.sourceTaskId === sourceTaskId && item.imageIndex === imageIndex),
    );
    if (update) nextPreviews.push(update);
    saveEcomTabHistory({
      ...history,
      tabs: {
        ...history.tabs,
        [ownerTabId]: { ...tab, refinePreviews: nextPreviews, updatedAt: Date.now() },
      },
      updatedAt: Date.now(),
    }, ecomStorageScope);
    if (activeTabIdRef.current === ownerTabId) setRefinePreviews(nextPreviews);
    return true;
  }, [ecomStorageScope]);

  const syncRefineTabSpinner = useCallback((ownerTabId: string) => {
    const hasActiveRefine = Array.from(activeRefineTrackerRef.current.values()).some((tracker) => (
      tracker.ownerTabId === ownerTabId
    ));
    const ownerStatus = loadEcomTabHistory(ecomStorageScope)?.tabs[ownerTabId]?.status || "completed";
    const nextStatus: EcomGenerationStatus = hasActiveRefine ? "generating" : ownerStatus;
    setTabStatusMap((previous) => previous[ownerTabId] === nextStatus
      ? previous
      : { ...previous, [ownerTabId]: nextStatus });
  }, [ecomStorageScope]);

  // ====== 单图编辑(refine) 任务状态轮询 ======
  useEffect(() => {
    if (activeRefineTrackerRef.current.size === 0) return;

    const pollController = startEcomPollingLoop({
      poll: async () => {
      for (const [trackerKey, tracker] of Array.from(activeRefineTrackerRef.current.entries())) {
        // While the submission is still returning a task ID, there is no
        // server status to query. Keep its mask until submission succeeds/fails.
        if (tracker.refineTaskId.startsWith("__")) continue;
        try {
          const res = await getFunctionalResult(tracker.refineTaskId);
          // Closing/replacing a preview can register a new refine under the same
          // source slot while this request is in flight.
          if (activeRefineTrackerRef.current.get(trackerKey) !== tracker) continue;
          const data = (res as { data?: { status?: string; output_images?: string[] | string; coze_raw_output?: string } })?.data;
          if (!data) continue;

          const outputUrl = resolveTaskOutputImages(data.output_images, data.coze_raw_output)[0] || "";
          const outcome = getEcomRefinePollOutcome(data.status, outputUrl);
          if (outcome === "completed") {
            if (outputUrl) preloadGeneratedImages([outputUrl]);
            const kept = updateRefinePreviewForTab(
              tracker.ownerTabId,
              tracker.targetTaskId,
              tracker.batchIndex,
              outputUrl ? {
                sourceTaskId: tracker.targetTaskId,
                imageIndex: tracker.batchIndex,
                originalUrl: tracker.originalUrl,
                refineTaskId: tracker.refineTaskId,
                status: "completed",
                outputUrl,
                startedAt: tracker.startedAt,
              } : null,
            );
            activeRefineTrackerRef.current.delete(trackerKey);
            syncRefineTabSpinner(tracker.ownerTabId);
            setRefineTrackerTrigger(prev => prev + 1);
            setHistoryRefreshTrigger(prev => prev + 1);
            if (kept && outputUrl) {
              feedback.success("AI重绘完成，当前标签已临时替换；新图已保存到单图编辑历史");
            } else if (kept) {
              feedback.error({ code: "E5999", fallback: "operation" });
            }
          } else if (outcome === "failed") {
            updateRefinePreviewForTab(tracker.ownerTabId, tracker.targetTaskId, tracker.batchIndex, null);
            activeRefineTrackerRef.current.delete(trackerKey);
            syncRefineTabSpinner(tracker.ownerTabId);
            setRefineTrackerTrigger(prev => prev + 1);
            setHistoryRefreshTrigger(prev => prev + 1);
            feedback.error({ code: "E5999", fallback: "operation" });
          }
        } catch {
          // Transient failures do not expire restored work without a server status.
        }
      }
      },
      baseDelayMs: 2500,
    });
    return () => pollController.stop();
  }, [feedback, getFunctionalResult, refineTrackerTrigger, syncRefineTabSpinner, updateRefinePreviewForTab]);

  const applyHistorySelection = useCallback((result: EcomHistorySelection) => {
    setManualStitchMode(false);
    setHistoryViewOverlay(null);

    // If switching to the currently-generating batch, restore generation view
    // including any images that completed while viewing other history items.
    if (generationActive && result.taskId &&
      (result.taskId === generatingViewingTaskIdRef.current ||
        generatingTaskIdsRef.current.includes(result.taskId))) {
      viewingTaskIdRef.current = generatingViewingTaskIdRef.current;
      setStatus("generating");
      setCurrentTaskIds(generatingTaskIdsRef.current);
      setBatchChildTaskIds(generatingBatchChildIdsRef.current);
      setViewingTaskId(generatingViewingTaskIdRef.current);
      setHistoryFeatureKey(generatingFeatureKeyRef.current);
      setGeneratedImages([...generatingImagesRef.current]);
      setTaskProgresses([...generatingProgressRef.current]);
      setFailedIndexes([...generatingFailedRef.current]);
      if (generatingModelImgRef.current) setResultModelImage(generatingModelImgRef.current);
      if (generatingProductImgRef.current) setResultProductImage(generatingProductImgRef.current);
      return;
    }

    viewingTaskIdRef.current = result.taskId || "";

    stopProgressSimulation();
    setProgress(100);
    setTaskProgresses([]);
    setErrorMessage("");

    const processingIndexes: number[] = [];
    const newFailedIndexes: number[] = [];
    if (result.childStatuses && result.childTaskIds) {
      for (let i = 0; i < result.childStatuses.length; i++) {
        const s = result.childStatuses[i];
        if (s === TASK_STATUS.PENDING || s === TASK_STATUS.PROCESSING || s === TASK_STATUS.QUEUED) {
          processingIndexes.push(i);
        } else if (s === TASK_STATUS.FAILED) {
          newFailedIndexes.push(i);
        }
      }
    }

    const restoredRefineIndexes: number[] = [];
    for (const tracker of Array.from(activeRefineTrackerRef.current.values())) {
      let childIdx = -1;
      if (result.childTaskIds) {
        childIdx = result.childTaskIds.indexOf(tracker.targetTaskId);
      } else if (tracker.batchParentId === (result.taskId || "")) {
        childIdx = tracker.batchIndex;
      }
      if (childIdx >= 0) {
        restoredRefineIndexes.push(childIdx);
        const failIdx = newFailedIndexes.indexOf(childIdx);
        if (failIdx >= 0) newFailedIndexes.splice(failIdx, 1);
      }
    }
    if (result.taskId) {
      const tracker = Array.from(activeRefineTrackerRef.current.values())
        .find((item) => item.targetTaskId === result.taskId);
      if (tracker) restoredRefineIndexes.push(tracker.batchIndex);
    }

    setRegeneratingIndexes(processingIndexes);
    setFailedIndexes(newFailedIndexes);
    setStatus("completed");

    if (result.childTaskIds && result.childTaskIds.length > 0) {
      setCurrentTaskIds(result.childTaskIds);
      setBatchChildTaskIds(result.childTaskIds);
    } else if (result.taskId) {
      setCurrentTaskIds([result.taskId]);
      setBatchChildTaskIds([]);
    } else {
      setCurrentTaskIds([]);
      setBatchChildTaskIds([]);
    }
    setHistoryFeatureKey(result.featureKey || null);
    setViewingTaskId(result.taskId || "");
    setManualStitchMode(false);
    if (result.images.length === 1) {
      setGeneratedImage(result.images[0]);
      setGeneratedImages([]);
      setPreviewOutputCount(1);
    } else {
      setGeneratedImage(result.images[0]);
      setGeneratedImages(result.images);
      setPreviewOutputCount(result.images.length);
    }
    setResultModelImage(result.modelImage);
    setResultProductImage(result.productImage);

    setNextStepTargets([]);
    if (result.featureKey) {
      ecomImageApi.getWorkflowNextSteps(result.featureKey).then(nsRes => {
        setNextStepTargets(nsRes.data?.next_steps || []);
      }).catch(() => {
        setNextStepTargets([]);
      });
    }
  }, [generationActive, stopProgressSimulation]);

  const handleActiveHistoryTaskClick = useCallback((payload?: EcomActiveHistoryTaskPayload) => {
    if (!payload) {
      setActiveWorkspaceView("generate");
      return;
    }

    const targetWorkflow = getWorkflowTabForFeatureKey(payload.featureKey);
    const targetMainTab = targetWorkflow?.mainTab || activeMainTab;
    const targetSubOption = targetWorkflow?.subOption || activeSubOption;
    const targetTabId = createEcomTabId(targetMainTab, targetSubOption);
    const outputCount = Math.max(
      payload.images.length,
      payload.childTaskIds?.length || 0,
      payload.taskProgresses?.length || 0,
      1,
    );
    const images = Array.from({ length: outputCount }, (_, index) => payload.images[index] || "");
    const taskProgresses = Array.from(
      { length: outputCount },
      (_, index) => clampProgress(payload.taskProgresses?.[index] ?? payload.progress),
    );
    const childTaskIds = payload.childTaskIds || [];
    const activeTaskIds = childTaskIds.length > 0
      ? childTaskIds
      : payload.taskId
        ? [payload.taskId]
        : [];
    const failedIndexes = (payload.childStatuses || [])
      .map((taskStatus, index) => taskStatus === TASK_STATUS.FAILED ? index : -1)
      .filter((index) => index >= 0);
    const firstImage = images[0] || null;
    const viewingId = payload.taskId || activeTaskIds[0] || "";

    persistCurrentWorkspaceTab();
    stopProgressSimulation();

    const currentHistory = loadEcomTabHistory(ecomStorageScope);
    const matchedGeneratingTab = findEcomWorkspaceTabByTaskIds(currentHistory?.tabs, [
      ...activeTaskIds,
      payload.taskId || "",
    ]);
    const existingTab = matchedGeneratingTab?.tab || currentHistory?.tabs[targetTabId] || createEmptyEcomWorkspaceTab(targetMainTab, targetSubOption);
    const targetRestoreTabId = matchedGeneratingTab?.tabId || targetTabId;
    closedTabIdsRef.current.delete(targetRestoreTabId);
    activateReversePromptTab(targetRestoreTabId);
    const restoredGenerationStartedAt =
      matchedGeneratingTab?.tab.generationStartedAt ||
      existingTab.generationStartedAt ||
      parseApiTimestampMs(payload.createdAt) ||
      Date.now();
    const restoredTab: EcomWorkspaceTab = {
      ...existingTab,
      tabId: targetRestoreTabId,
      mainTab: targetMainTab,
      subOption: targetSubOption,
      title: targetSubOption,
      updatedAt: Date.now(),
      status: "generating",
      progress: clampProgress(payload.progress),
      generatedImage: firstImage,
      resultModelImage: payload.modelImage,
      resultProductImage: payload.productImage,
      generatedImages: images,
      errorMessage: "",
      currentTaskIds: activeTaskIds,
      viewingTaskId: viewingId,
      generationStartedAt: restoredGenerationStartedAt,
      failedIndexes,
      batchChildTaskIds: childTaskIds,
      previewOutputCount: outputCount,
      taskProgresses,
      batchParentTaskId: payload.isBatchParent ? payload.taskId || null : null,
      batchTaskStatus: null,
      manualStitchMode: false,
      historyFeatureKey: payload.featureKey || null,
    };
    const nextOpenTabIds = Array.from(new Set([...(currentHistory?.openTabIds || []), targetRestoreTabId]));

    saveEcomTabHistory({
      version: 1,
      activeTabId: targetRestoreTabId,
      activeWorkspaceView: "generate",
      openTabIds: nextOpenTabIds,
      tabs: {
        ...(currentHistory?.tabs || {}),
        [targetRestoreTabId]: restoredTab,
      },
      updatedAt: Date.now(),
    }, ecomStorageScope);

    setOpenedSubOptions((prev) => {
      const next = getUniqueOpenedOptions(prev.includes(targetRestoreTabId) ? prev : [...prev, targetRestoreTabId]);
      saveEcomOpenedTabs(next, ecomStorageScope);
      return next;
    });
    setActiveWorkspaceView("generate");
    applyWorkspaceTab(restoredTab, true);
    setViewingTaskId(viewingId);
    viewingTaskIdRef.current = viewingId;
    setHistoryFeatureKey(payload.featureKey || null);
    setNextStepTargets([]);
  }, [
    activeMainTab,
    activeSubOption,
    activateReversePromptTab,
    applyWorkspaceTab,
    ecomStorageScope,
    getWorkflowTabForFeatureKey,
    persistCurrentWorkspaceTab,
    stopProgressSimulation,
  ]);

  // 推荐下一步回调：切换工作流并预填充参数
  const handleNextStep = useCallback((target: NextStepTarget, selectedImages: string[]) => {
    const singleTypes = ['image_upload', 'reference_image', 'model_gallery', 'background_gallery'];
    const adapted = singleTypes.includes(target.target_component_type)
      ? selectedImages[0]
      : selectedImages;

    const targetConfig = findEnabledWorkflowByFeatureKey(target.target_workflow_key);
    const currentKey = getFeatureKey(activeSubOption);
    const isSameWorkflow = target.target_workflow_key === currentKey;

    if (!isSameWorkflow) {
      // 跨工作流：先清空 UI 配置，防止 prefillData 被旧工作流的 useEffect 提前消费
      setWorkflowUIConfig(null);
      setCozeParams({});
    }

    if (targetConfig) {
      const workflowTab = getWorkflowTabForFeatureKey(target.target_workflow_key);
      const targetTab: MainTab = workflowTab?.mainTab || activeMainTab;
      if (targetTab !== activeMainTab) {
        setActiveMainTab(targetTab);
      }
      ensureMainTabExpanded(targetTab);
      setActiveSubOption(targetConfig.name);
    }

    setPrefillData({ [target.target_coze_param_key]: adapted });

    setNextStepTargets([]);
    setStatus("empty");
    setGeneratedImage(null);
    setGeneratedImages([]);
    clearActiveRefineState();
    setHistoryFeatureKey(null);
  }, [activeMainTab, activeSubOption, clearActiveRefineState, ensureMainTabExpanded, findEnabledWorkflowByFeatureKey, getWorkflowTabForFeatureKey]);


  const [restoringAgentTaskId, setRestoringAgentTaskId] = useState<string | null>(null);
  const restoringAgentTaskRef = useRef(false);
  const handleOpenAgentHistory = useCallback(async (taskId: string) => {
    if (restoringAgentTaskRef.current) return;
    restoringAgentTaskRef.current = true;
    setRestoringAgentTaskId(taskId);
    persistCurrentWorkspaceTab();
    try {
      const tabId = await agentController.openTaskHistory(`agent:${crypto.randomUUID()}`, taskId);
      if (tabId) {
        setActiveWorkspaceView("generate");
        setIsHistoryDrawerOpen(false);
      }
    } catch {
      feedback.error({ code: "E1005", fallback: "operation" });
    } finally {
      restoringAgentTaskRef.current = false;
      setRestoringAgentTaskId(null);
    }
  }, [agentController, feedback, persistCurrentWorkspaceTab]);

  const handleOpenAgentConversation = useCallback(async (conversationId: string) => {
    persistCurrentWorkspaceTab();
    try {
      const tabId = await agentController.openConversationHistory(`agent:${crypto.randomUUID()}`, conversationId);
      if (tabId) {
        setActiveWorkspaceView("generate");
        setIsHistoryDrawerOpen(false);
      }
    } catch {
      feedback.error({ code: "E1005", fallback: "operation" });
    }
  }, [agentController, feedback, persistCurrentWorkspaceTab]);

  const handleRegenerateFromHistory = useCallback((payload: EcomHistoryRegeneratePayload) => {
    if (payload.featureKey === "agent" || payload.featureKey === "agent_workflow") {
      if (payload.taskId) void handleOpenAgentHistory(payload.taskId);
      else feedback.error({ code: "E1005", fallback: "operation" });
      return;
    }
    if (payload.mode === "refine" && payload.refine) {
      const refine = payload.refine;
      persistCurrentWorkspaceTab();
      const workflowTab = getWorkflowTabForFeatureKey(refine.featureKey);
      const targetMainTab = workflowTab?.mainTab || activeMainTab;
      const targetSubOption = workflowTab?.subOption || activeSubOption;
      const currentHistory = loadEcomTabHistory(ecomStorageScope);
      const matched = findEcomWorkspaceTabByTaskIds(currentHistory?.tabs, [refine.taskId]);
      const existingIds = [
        ...(currentHistory?.openTabIds || []),
        ...openedSubOptions.filter((id) => id !== HISTORY_TAB_LABEL),
        ...(currentHistory ? Object.keys(currentHistory.tabs) : []),
      ];
      const created = matched
        ? { tabId: matched.tabId, instance: parseEcomTabId(matched.tabId)?.instance || 1 }
        : createUniqueEcomTabId(targetMainTab, targetSubOption, existingIds);
      const baseTab = matched?.tab || createEmptyEcomWorkspaceTab(targetMainTab, targetSubOption, created);
      const sourceImages = normalizeEcomRefineSourceImages(
        refine.sourceImages,
        refine.imageIndex,
        refine.imageUrl,
      );
      const restoredSourceTaskIds = refine.sourceTaskIds?.length === sourceImages.length
        ? refine.sourceTaskIds
        : [];
      const restoredSourceTaskId = restoredSourceTaskIds[refine.imageIndex] || refine.taskId;
      const restoredReferences = normalizeDetailReplicaImages(
        refine.params[DETAIL_REPLICA_REFERENCE_IMAGES_KEY],
      );
      const restoredSlotCount = Math.max(
        sourceImages.length,
        restoredReferences.length,
        refine.imageIndex + 1,
        1,
      );
      const restoredGeneratedImages = Array.from(
        { length: restoredSlotCount },
        (_, index) => sourceImages[index] || "",
      );
      const restoredRefinePreviews = (baseTab.refinePreviews || []).filter(
        (preview) => !(preview.sourceTaskId === restoredSourceTaskId && preview.imageIndex === refine.imageIndex),
      );
      if (refine.refinedImageUrl) {
        restoredRefinePreviews.push({
          sourceTaskId: restoredSourceTaskId,
          imageIndex: refine.imageIndex,
          originalUrl: sourceImages[refine.imageIndex] || refine.imageUrl,
          refineTaskId: refine.refineTaskId || `history-refine-${Date.now()}`,
          status: "completed",
          outputUrl: refine.refinedImageUrl,
          startedAt: Date.now(),
        });
      }
      const restoredParams = { ...refine.params };
      if (isDetailPageReplicaWorkflow(refine.featureKey, targetSubOption)) {
        const referenceImages = normalizeDetailReplicaImages(
          restoredParams[DETAIL_REPLICA_REFERENCE_IMAGES_KEY],
        );
        if (referenceImages.length === 0 && refine.sourceInputImages.length > 0) {
          restoredParams[DETAIL_REPLICA_REFERENCE_IMAGES_KEY] = refine.sourceInputImages;
        }
      }
      const restoredTab: EcomWorkspaceTab = {
        ...baseTab,
        tabId: created.tabId,
        mainTab: targetMainTab,
        subOption: targetSubOption,
        title: created.instance > 1 ? `${targetSubOption} #${created.instance}` : targetSubOption,
        updatedAt: Date.now(),
        cozeParams: restoredParams,
        status: "completed",
        progress: 100,
        generatedImage: restoredGeneratedImages[0] || null,
        generatedImages: restoredSlotCount > 1 ? restoredGeneratedImages : [],
        currentTaskIds: restoredSourceTaskIds.length > 0 ? restoredSourceTaskIds : [refine.taskId],
        viewingTaskId: refine.taskId,
        currentPrompt: refine.prompt,
        batchChildTaskIds: restoredSourceTaskIds,
        previewOutputCount: restoredSlotCount,
        taskProgresses: restoredGeneratedImages.map((image) => image ? 100 : 0),
        historyFeatureKey: refine.featureKey,
        resultProductImage: refine.productImage || baseTab.resultProductImage,
        refinePreviews: restoredRefinePreviews,
      };
      restoredTab.generationInputs = captureEcomGenerationInputs(restoredTab);
      saveEcomTabHistory({
        version: 1,
        activeTabId: created.tabId,
        activeWorkspaceView: "generate",
        openTabIds: Array.from(new Set([...(currentHistory?.openTabIds || []), created.tabId])),
        tabs: { ...(currentHistory?.tabs || {}), [created.tabId]: restoredTab },
        updatedAt: Date.now(),
      }, ecomStorageScope);
      closedTabIdsRef.current.delete(created.tabId);
      activateReversePromptTab(created.tabId);
      setOpenedSubOptions((prev) => {
        const next = getUniqueOpenedOptions(prev.includes(created.tabId) ? prev : [...prev, created.tabId]);
        saveEcomOpenedTabs(next, ecomStorageScope);
        return next;
      });
      setTabStatusMap((prev) => ({ ...prev, [created.tabId]: "completed" }));
      setActiveWorkspaceView("generate");
      applyWorkspaceTab(restoredTab, true);
      setHistoryRefineEditor({
        ownerTabId: created.tabId,
        imageUrl: refine.editorImageUrl,
        taskId: refine.taskId,
        imageIndex: refine.imageIndex,
        productImage: refine.productImage,
      });
      return;
    }
    if (!shouldUseRestoreParamsForRetry("history")) return;

    const {
      featureKey,
      params,
      prompt,
      ratio,
      perBatchRatios: historyPerBatchRatios,
      cozeBatchCount: historyBatchCount,
      batchCount: historyBatchCountCompat,
      ...selection
    } = payload;
    const targetConfig = findEnabledWorkflowByFeatureKey(featureKey);
    const workflowTab = getWorkflowTabForFeatureKey(featureKey);
    if (!targetConfig || !workflowTab) {
      feedback.error({ code: "E5006", fallback: "operation" });
      return;
    }

    const targetMainTab: MainTab = workflowTab.mainTab;
    const targetSubOption = targetConfig.name;

    const restoreParams: Record<string, unknown> = stripDeprecatedDetailReplicaParams({ ...params });
    if (prompt && !restoreParams.prompt) restoreParams.prompt = prompt;
    if (selection.modelImage) {
      if (!restoreParams.ref_image) restoreParams.ref_image = selection.modelImage;
      if (!restoreParams.model_image) restoreParams.model_image = selection.modelImage;
    }
    if (selection.productImage) {
      if (!restoreParams.product_image) restoreParams.product_image = selection.productImage;
    }
    if (ratio) {
      for (const key of ["ratio", "aspect_ratio", "aspectRatio"]) {
        if (!(key in restoreParams)) {
          restoreParams[key] = ratio;
          break;
        }
      }
    }
    setPrefillData(restoreParams);

    persistCurrentWorkspaceTab();
    stopProgressSimulation();

    const currentHistory = loadEcomTabHistory(ecomStorageScope);
    const existingIds = Array.from(new Set([
      ...(currentHistory?.openTabIds || []),
      ...openedSubOptions.filter((id) => id !== HISTORY_TAB_LABEL),
      ...(currentHistory ? Object.keys(currentHistory.tabs) : []),
    ]));
    const isActiveSameName = activeMainTab === targetMainTab && activeSubOption === targetSubOption;
    const matchingTabId = isActiveSameName
      ? activeTabId
      : existingIds.find((tabId) => {
        const parsed = parseEcomTabId(tabId);
        return parsed?.mainTab === targetMainTab && parsed.subOption === targetSubOption;
      });
    const targetTab =
      matchingTabId
        ? { tabId: matchingTabId, instance: parseEcomTabId(matchingTabId)?.instance ?? 1 }
        : createUniqueEcomTabId(targetMainTab, targetSubOption, existingIds);
    const targetTabId = targetTab.tabId;
    closedTabIdsRef.current.delete(targetTabId);
    activateReversePromptTab(targetTabId);
    const targetInstance = targetTab.instance;
    const outputCount = Math.max(
      historyBatchCount || historyBatchCountCompat || 0,
      selection.images.length,
      selection.childTaskIds?.length || 0,
      selection.childStatuses?.length || 0,
      1,
    );
    const images = Array.from({ length: outputCount }, (_, index) => selection.images[index] || "");
    const restoredChildTaskIds = selection.childTaskIds || [];
    const childTaskIds = restoredChildTaskIds;
    const isActiveHistoryStatus = (taskStatus: number | undefined) =>
      taskStatus === TASK_STATUS.PENDING ||
      taskStatus === TASK_STATUS.PROCESSING ||
      taskStatus === TASK_STATUS.QUEUED;
    const hasActiveHistoryTasks = (selection.childStatuses || []).some(isActiveHistoryStatus);
    const failedIndexes = (selection.childStatuses || [])
      .map((taskStatus, index) => taskStatus === TASK_STATUS.FAILED ? index : -1)
      .filter((index) => index >= 0);
    const existingTab = currentHistory?.tabs[targetTabId];
    const newTitle = targetInstance > 1 ? `${targetSubOption} #${targetInstance}` : targetSubOption;
    const firstImage = images[0] || null;
    // 优先使用历史里推导出的批次数，否则按图片数兜底
    const restoredBatchCount = historyBatchCount && historyBatchCount > 0 ? historyBatchCount : outputCount;
    const restoredPerBatchRatios = normalizePerBatchRatios(
      historyPerBatchRatios as PerBatchRatio[] | undefined,
      restoredBatchCount,
    );
    const restoredTaskProgresses: number[] = hasActiveHistoryTasks
      ? Array.from({ length: outputCount }, (_, index) => {
        const taskStatus = selection.childStatuses?.[index];
        if (taskStatus === TASK_STATUS.COMPLETED || taskStatus === TASK_STATUS.FAILED || taskStatus === TASK_STATUS.CANCELLED) {
          return 100;
        }
        return isActiveHistoryStatus(taskStatus) ? 5 : 0;
      })
      : [];
    const restoredProgress = hasActiveHistoryTasks && restoredTaskProgresses.length > 0
      ? Math.max(5, Math.round(restoredTaskProgresses.reduce((sum, value) => sum + value, 0) / restoredTaskProgresses.length))
      : 100;
    const restoredTaskIds = childTaskIds.length > 0 ? childTaskIds : selection.taskId ? [selection.taskId] : [];
    const restoredViewingTaskId = selection.taskId || restoredTaskIds.find(Boolean) || "";
    const restoredInspireResults = (targetMainTab === INSPIRE_REVERSE_MODULE_KEY || targetConfig.execution_mode === "llm_vision_reverse")
      ? ((selection.inspireResults && selection.inspireResults.length > 0)
        ? (selection.inspireResults as EcomInspireTabResult[])
        : prompt
          ? [{
            taskId: selection.taskId || `history-inspire-${Date.now()}`,
            status: "completed",
            prompt,
            inputImages: selection.images.length > 0 ? selection.images : undefined,
            batchIndex: 1,
          } as EcomInspireTabResult]
          : [])
      : [];
    const hasActiveInspireResults = restoredInspireResults.some((result) =>
      result.status === "pending" || result.status === "processing"
    );
    const restoredStatus: EcomGenerationStatus = hasActiveHistoryTasks || hasActiveInspireResults ? "generating" : "completed";
    const restoredTab: EcomWorkspaceTab = {
      ...createEmptyEcomWorkspaceTab(targetMainTab, targetSubOption, {
        tabId: targetTabId,
        instance: targetInstance,
        title: existingTab?.title || newTitle,
      }),
      createdAt: existingTab?.createdAt || Date.now(),
      cozeParams: restoreParams,
      cozeBatchCount: restoredBatchCount,
      status: restoredStatus,
      progress: restoredProgress,
      generatedImage: firstImage,
      resultModelImage: selection.modelImage,
      resultProductImage: selection.productImage,
      generatedImages: images.length > 1 ? images : [],
      currentTaskIds: restoredTaskIds,
      viewingTaskId: restoredViewingTaskId,
      generationStartedAt: hasActiveHistoryTasks ? existingTab?.generationStartedAt || Date.now() : null,
      currentPrompt: prompt || JSON.stringify(restoreParams),
      failedIndexes,
      // A non-batch history task may expose several output images under one UUID.
      // Repeat that UUID per slot so AI refine can target any restored output.
      batchChildTaskIds: restoredChildTaskIds.length > 0
        ? restoredChildTaskIds
        : selection.taskId
          ? Array.from({ length: outputCount }, () => selection.taskId as string)
          : [],
      previewOutputCount: outputCount,
      taskProgresses: restoredTaskProgresses,
      historyFeatureKey: featureKey,
      perBatchRatios: restoredPerBatchRatios,
      inspireResults: restoredInspireResults,
    };
    restoredTab.generationInputs = captureEcomGenerationInputs(restoredTab);
    const nextOpenTabIds = Array.from(new Set([...(currentHistory?.openTabIds || []), targetTabId]));

    saveEcomTabHistory({
      version: 1,
      activeTabId: targetTabId,
      activeWorkspaceView: "generate",
      openTabIds: nextOpenTabIds,
      tabs: {
        ...(currentHistory?.tabs || {}),
        [targetTabId]: restoredTab,
      },
      updatedAt: Date.now(),
    }, ecomStorageScope);

    setOpenedSubOptions((prev) => {
      const next = getUniqueOpenedOptions(prev.includes(targetTabId) ? prev : [...prev, targetTabId]);
      saveEcomOpenedTabs(next, ecomStorageScope);
      return next;
    });
    setTabStatusMap((prev) => ({ ...prev, [targetTabId]: restoredStatus }));
    setActiveWorkspaceView("generate");
    applyWorkspaceTab(restoredTab, true);
    if ((targetMainTab === INSPIRE_REVERSE_MODULE_KEY || targetConfig.execution_mode === "llm_vision_reverse") && restoredInspireResults.length > 0) {
      applyInspireResults(restoredInspireResults as InspireBatchResult[]);
      setStatus(restoredStatus);
      setProgress(100);
      setErrorMessage("");
      window.setTimeout(() => {
        applyInspireResults(restoredInspireResults as InspireBatchResult[]);
        setStatus(restoredStatus);
        setProgress(100);
        setErrorMessage("");
      }, 0);
    }
    if (featureKey) {
      ecomImageApi.getWorkflowNextSteps(featureKey).then(nsRes => {
        setNextStepTargets(nsRes.data?.next_steps || []);
      }).catch(() => {
        setNextStepTargets([]);
      });
    }
    feedback.info(hasActiveHistoryTasks ? "已恢复生成中的历史任务，再次提交会创建新标签卡" : "参数已恢复，请点击「开始生图」重新生成");
  }, [
    activeMainTab,
    activeSubOption,
    activeTabId,
    activateReversePromptTab,
    applyInspireResults,
    applyWorkspaceTab,
    ecomStorageScope,
    findEnabledWorkflowByFeatureKey,
    getWorkflowTabForFeatureKey,
    handleOpenAgentHistory,
    openedSubOptions,
    persistCurrentWorkspaceTab,
    stopProgressSimulation,
  ]);

  /** Wait until all blob: URLs in cozeParams are replaced by real URLs (max 30s). */
  const waitForUploads = useCallback(async (): Promise<boolean> => {
    const MAX_WAIT = 30_000;
    const POLL = 400;
    const deadline = Date.now() + MAX_WAIT;
    while (hasPendingBlobUrls(cozeParamsRef.current)) {
      if (Date.now() > deadline) {
        feedback.error({ code: "E5003", fallback: "upload" });
        return false;
      }
      await new Promise(r => setTimeout(r, POLL));
    }
    return true;
  }, []);

  /** True when the current workspace tab still has an unfinished generation in flight. */
  const isCurrentTabGenerating = useCallback(() => {
    if (status === "generating") return true;
    if (Array.from(activeRefineTrackerRef.current.values()).some((tracker) => (
      tracker.ownerTabId === activeTabIdRef.current
    ))) return true;
    if (batchParentTaskId && batchTaskStatus) {
      const total = batchTaskStatus.batch_total ?? 0;
      const done = (batchTaskStatus.completed_count ?? 0) + (batchTaskStatus.failed_count ?? 0);
      if (total > 0 && done < total) return true;
    }
    if (inspireResultsRef.current.some((result) =>
      result.status === "pending" || result.status === "processing" || result.status === "queued"
    )) {
      return true;
    }
    return false;
  }, [batchParentTaskId, batchTaskStatus, status]);

  /** Cancel and dispose the background monitor (if any) associated with the given tab. */
  const cancelDetachedTabMonitor = useCallback((tabId: string) => {
    const cancel = detachedTabMonitorsRef.current.get(tabId);
    if (cancel) {
      detachedTabMonitorsRef.current.delete(tabId);
      try {
        cancel();
      } catch {
        // Ignore cleanup errors — the monitor entry has already been removed.
      }
    }
  }, []);

  /**
   * Start polling task progress in the background for a tab that was forked away while
   * still generating. The monitor only writes to the saved workspace history (no React
   * state for the active tab) so it can survive arbitrary tab switches; once the task
   * resolves it flips the stored status to `completed`/`failed`, updates `tabStatusMap`
   * so the spinner on the top tab strip stops, and removes itself.
   *
   * The same tab is never monitored twice concurrently.
   */
  const monitorDetachedWorkspaceTab = useCallback((initialTab: EcomWorkspaceTab) => {
    const tabId = initialTab.tabId;
    if (!generationPageMountedRef.current || !tabId) return;
    // A later receipt can add tasks or settle placeholders in this same tab.
    // Retire the old closure before monitoring the latest persisted snapshot.
    cancelDetachedTabMonitor(tabId);
    if (initialTab.status !== "generating") return;
    const reversePromptOperation = reversePromptClaimsRef.current.get(tabId) || null;
    if (reversePromptOperation && !isReversePromptOperationCurrent(reversePromptOperation)) return;
    const hasStrictRetryOwner = (tab: EcomWorkspaceTab) => Array.from(activeRetryTrackerRef.current.values()).some((tracker) => (
      tracker.ownerTabId === tabId
      && isRetryProjectionIdentityCurrent(tracker, toRetryWorkspaceContext(tab))
    ));

    const hasBatchParent = !!initialTab.batchParentTaskId;
    const trackedTaskIds = initialTab.currentTaskIds || [];
    const isInspireTab = (initialTab.inspireResults || []).some(
      (result) => result.taskId && !result.taskId.startsWith("pending-"),
    );
    if (!hasBatchParent && !trackedTaskIds.some(Boolean) && !isInspireTab) return;

    let cancelled = false;
    let pollController: EcomPollingLoopController | null = null;

    const readOwnedTab = (): EcomWorkspaceTab | null => {
      if (cancelled) return null;
      if (reversePromptOperation && !isReversePromptOperationCurrent(reversePromptOperation)) {
        finish();
        return null;
      }
      if (closedTabIdsRef.current.has(tabId)) {
        finish();
        return null;
      }
      const history = loadEcomTabHistory(ecomStorageScope);
      if (!history) return null;
      const existing = history.tabs[tabId];
      if (!existing) {
        // Tab was closed by the user — give up.
        finish();
        return null;
      }
      const sameTaskContext = hasBatchParent
        ? existing.batchParentTaskId === initialTab.batchParentTaskId
        : !existing.batchParentTaskId
          && JSON.stringify(existing.currentTaskIds) === JSON.stringify(trackedTaskIds);
      const sameInspireTasks = !isInspireTab || JSON.stringify(existing.inspireResults.map(result => result.taskId))
        === JSON.stringify(initialTab.inspireResults.map(result => result.taskId));
      if (!sameTaskContext || !sameInspireTasks || existing.recoveryGeneration !== initialTab.recoveryGeneration) {
        finish();
        return null;
      }
      // Retry ownership can change while a request is in flight. Recheck the
      // latest projection before reading results or committing any old response.
      return hasStrictRetryOwner(existing) ? null : existing;
    };

    const updateStoredTab = (patch: Partial<EcomWorkspaceTab>): boolean => {
      const existing = readOwnedTab();
      if (!existing) return false;
      const history = loadEcomTabHistory(ecomStorageScope);
      if (!history) return false;
      const hasMeaningfulChange = Object.entries(patch).some(([key, value]) => {
        const current = existing[key as keyof EcomWorkspaceTab];
        if (Object.is(current, value)) return false;
        if (
          (Array.isArray(current) && Array.isArray(value)) ||
          (current && value && typeof current === "object" && typeof value === "object")
        ) {
          return JSON.stringify(current) !== JSON.stringify(value);
        }
        return true;
      });
      if (!hasMeaningfulChange) return true;
      const merged: EcomWorkspaceTab = {
        ...existing,
        ...patch,
        updatedAt: Date.now(),
      };
      saveEcomTabHistory({
        ...history,
        tabs: { ...history.tabs, [tabId]: merged },
        updatedAt: Date.now(),
      }, ecomStorageScope);
      if ((reversePromptOperation || isInspireTab) && activeTabIdRef.current === tabId) {
        if (patch.inspireResults) {
          applyInspireResults(patch.inspireResults as InspireBatchResult[]);
        }
        if (patch.status) setStatus(patch.status);
        if (typeof patch.progress === "number") setProgress(patch.progress);
        if (patch.generationStartedAt !== undefined) setGenerationStartedAt(patch.generationStartedAt);
        if (typeof patch.errorMessage === "string") setErrorMessage(patch.errorMessage);
      }
      return true;
    };

    const finish = () => {
      cancelled = true;
      pollController?.stop();
      pollController = null;
      detachedTabMonitorsRef.current.delete(tabId);
    };

    const cleanupClosedDetachedWorkspaceTab = () => {
      if (cancelled) return true;
      const history = loadEcomTabHistory(ecomStorageScope);
      if (!history || !history.tabs[tabId]) {
        finish();
        return true;
      }
      // A tab that is no longer open belongs to a detached monitor. Once its
      // task reaches a terminal state, remove the hidden snapshot instead of
      // leaving it available for a later task-id based restore.
      const nextHistory = removeClosedDetachedWorkspaceTab(history, tabId);
      if (nextHistory === history) return false;
      saveEcomTabHistory(nextHistory, ecomStorageScope);
      finish();
      return true;
    };

    const setTerminalStatus = (
      nextStatus: EcomGenerationStatus,
      extraPatch: Partial<EcomWorkspaceTab> = {},
    ) => {
      if (cancelled) return;
      if (cleanupClosedDetachedWorkspaceTab()) return;
      if (!updateStoredTab({ status: nextStatus, progress: 100, ...extraPatch })) return;
      if (reversePromptOperation) {
        updateReversePromptOperation(
          reversePromptOperation,
          nextStatus === "completed" ? "completed" : "failed",
          extraPatch,
        );
        reversePromptClaimsRef.current.delete(tabId);
      }
      setTabStatusMap((prev) => {
        if (prev[tabId] === nextStatus) return prev;
        return { ...prev, [tabId]: nextStatus };
      });
      finish();
    };

    const POLL_INTERVAL_MS = 3000;
    const startDetachedPolling = (poll: () => Promise<void>) => {
      pollController = startEcomPollingLoop({
        // Pending retries exclusively own their projections. Keep this loop
        // dormant, rather than dropping it, so unfinished siblings resume once
        // the strict retry trackers have settled while the tab is in background.
        poll: async () => {
          if (!readOwnedTab()) return;
          await poll();
        },
        baseDelayMs: POLL_INTERVAL_MS,
      });
    };

    if (isInspireTab) {
      const results = [...(initialTab.inspireResults || [])];
      const pendingSet = new Set(
        results
          .filter((result) =>
            result.taskId &&
            !result.taskId.startsWith("pending-") &&
            (result.status === "pending" || result.status === "processing" || result.status === "queued")
          )
          .map((result) => result.taskId),
      );

      const tick = async () => {
        if (cancelled) return;
        try {
          const ids = Array.from(pendingSet);
          const responses = await Promise.allSettled(ids.map((id) => getFunctionalResult(id)));
          if (cancelled) return;
          responses.forEach((resp, i) => {
            if (resp.status !== "fulfilled") return;
            const data = resp.value.data;
            if (!data) return;
            const taskId = ids[i];
            const targetIdx = results.findIndex((result) => result.taskId === taskId);
            if (targetIdx < 0) return;
            const nextStatus = (data.status || "processing") as EcomInspireBatchStatus;
            if (nextStatus === "completed" || nextStatus === "failed" || nextStatus === "cancelled") {
              pendingSet.delete(taskId);
            }
            results[targetIdx] = {
              ...results[targetIdx],
              status: nextStatus,
              prompt: normalizeReversePromptToPlainText(data.inspire_prompt) || results[targetIdx].prompt || "",
              tags: data.inspire_tags || results[targetIdx].tags || [],
              presetLabel: data.preset_label || results[targetIdx].presetLabel,
              errorCode: nextStatus === "failed" ? toErrorCode(data.failure?.code || "E5302") : undefined,
            };
          });

          const total = results.length || 1;
          const pendingCount = results.filter(result =>
            result.status === "pending" || result.status === "processing" || result.status === "queued"
          ).length;
          const done = total - pendingCount;
          const nextProgress = pendingCount === 0
            ? 100
            : Math.max(10, Math.min(95, Math.round((done / total) * 90 + 10)));
          const hasFailureOnly = results.length > 0 && results.every((result) =>
            result.status === "failed" || result.status === "cancelled"
          );
          const basePatch: Partial<EcomWorkspaceTab> = {
            inspireResults: results as EcomInspireTabResult[],
            progress: nextProgress,
          };

          if (pendingCount === 0) {
            setTerminalStatus(hasFailureOnly ? "failed" : "completed", {
              ...basePatch,
              errorMessage: hasFailureOnly ? "E5302" : "",
            });
          } else {
            updateStoredTab({ ...basePatch, status: "generating" });
          }
        } catch {
          // Transient errors are ignored; the next tick will retry.
        }
      };

      startDetachedPolling(tick);
    } else if (hasBatchParent) {
      const parentId = initialTab.batchParentTaskId as string;
      const expectedTotal = Math.max(
        initialTab.previewOutputCount || 0,
        initialTab.batchTaskStatus?.batch_total || 0,
        1,
      );
      const tick = async () => {
        if (cancelled) return;
        try {
          const res = await getBatchTaskStatus(parentId);
          if (cancelled) return;
          const batch = res.data;
          if (!batch) return;
          if (batch.parent_task_id && batch.parent_task_id !== parentId) return;
          const saved = readOwnedTab();
          if (!saved) return;
          // The retry tracker may have settled since this monitor was created.
          // Preserve its result and task ID before merging the parent's old IDs.
          const results = mergeEcomBatchResults(saved, batch.children || [],
            Math.max(expectedTotal, batch.batch_total || 0), batch.status);
          const isDone = hasAllEcomBatchResults(results);
          const allFailed = results.failedIndexes.length === results.previewOutputCount;
          const inferredStatus: EcomGenerationStatus = isDone
            ? (allFailed ? "failed" : "completed")
            : "generating";

          const basePatch: Partial<EcomWorkspaceTab> = {
            ...results,
            generatedImage: results.generatedImages[0] || null,
            progress: isDone ? 100 : clampProgress(batch.progress),
            batchTaskStatus: batch,
            currentTaskIds: results.batchChildTaskIds,
          };

          if (isDone) {
            setTerminalStatus(inferredStatus, {
              ...basePatch,
              batchParentTaskId: null,
              generationStartedAt: null,
              errorMessage: allFailed
                ? "内容审核未通过，提示词或参考图可能包含违规内容，请修改后重试"
                : "",
            });
          } else {
            updateStoredTab({ ...basePatch, status: "generating" });
          }
        } catch {
          // Transient errors are ignored; the next tick will retry.
        }
      };

      // Kick off an immediate first poll so completed-while-page-was-closed tasks resolve quickly.
      startDetachedPolling(tick);
    } else {
      const taskIds = [...trackedTaskIds];
      const completedSet = new Set<number>();
      const failedSet = new Set<number>(taskIds.flatMap((id, index) => id ? [] : [index]));
      const images: string[] = new Array(taskIds.length).fill("");
      const progresses: number[] = new Array(taskIds.length).fill(0);
      let modelImage = initialTab.resultModelImage || "";
      let productImage = initialTab.resultProductImage || "";

      const syncStoredResults = () => {
        const saved = readOwnedTab();
        if (!saved) return false;
        taskIds.forEach((_, i) => {
          if (saved.generatedImages[i]) images[i] = saved.generatedImages[i];
          if (images[i]) {
            completedSet.add(i);
            failedSet.delete(i);
          } else if (saved.failedIndexes.includes(i)) {
            failedSet.add(i);
          }
          progresses[i] = completedSet.has(i) || failedSet.has(i)
            ? 100
            : Math.max(progresses[i], clampProgress(saved.taskProgresses[i] || 0));
        });
        modelImage = saved.resultModelImage || modelImage;
        productImage = saved.resultProductImage || productImage;
        return true;
      };

      const tick = async () => {
        if (!syncStoredResults()) return;
        try {
          for (let i = 0; i < taskIds.length; i++) {
            if (cancelled) return;
            if (completedSet.has(i) || failedSet.has(i)) continue;
            const taskId = taskIds[i];
            if (!taskId) continue;
            try {
              const res = await getTaskStatus(taskId);
              if (cancelled) return;
              const data = res.data;
              if (!data) continue;
              if (data.task_id && data.task_id !== taskId) continue;
              progresses[i] = Math.max(progresses[i], clampProgress(data.progress));
              if (data.status === TASK_STATUS.FAILED || data.status === TASK_STATUS.CANCELLED) {
                failedSet.add(i);
                progresses[i] = 100;
              } else if (data.status === TASK_STATUS.COMPLETED) {
                const outputImages = resolveTaskOutputImages(
                  data.output_images,
                  data.coze_raw_output,
                );
                if (outputImages[0]) {
                  images[i] = outputImages[0];
                }
                if (!images[i]) continue;
                completedSet.add(i);
                progresses[i] = 100;
                if (i === 0) {
                  modelImage = data.model_image || modelImage;
                  productImage = data.product_image || productImage;
                }
              }
            } catch {
              // Per-task transient error — try again on the next tick.
            }
          }

          if (!syncStoredResults()) return;
          const aggregateProgress = progresses.length === 0
            ? 0
            : Math.round(progresses.reduce((a, b) => a + b, 0) / progresses.length);
          const allDone = completedSet.size + failedSet.size === taskIds.length;
          const basePatch: Partial<EcomWorkspaceTab> = {
            generatedImages: [...images],
            generatedImage: images[0] || null,
            taskProgresses: [...progresses],
            failedIndexes: Array.from(failedSet).sort((a, b) => a - b),
            resultModelImage: modelImage,
            resultProductImage: productImage,
            batchChildTaskIds: taskIds.length > 1 ? [...taskIds] : (initialTab.batchChildTaskIds || []),
            progress: allDone ? 100 : aggregateProgress,
          };

          if (allDone) {
            const hasAny = completedSet.size > 0;
            setTerminalStatus(hasAny ? "completed" : "failed", {
              ...basePatch,
              errorMessage: hasAny
                ? ""
                : "内容审核未通过，提示词或参考图可能包含违规内容，请修改后重试",
            });
          } else {
            updateStoredTab({ ...basePatch, status: "generating" });
          }
        } catch {
          // Outer try/catch guard for unexpected runtime issues.
        }
      };

      startDetachedPolling(tick);
    }

    detachedTabMonitorsRef.current.set(tabId, () => {
      cancelled = true;
      pollController?.stop();
      pollController = null;
    });
  }, [applyInspireResults, cancelDetachedTabMonitor, ecomStorageScope]);

  const monitorReversePromptOwnerAfterSubmit = useCallback((
    operation: ReversePromptOperationClaim | null,
    ownerTabId: string,
  ) => {
    if (!isReversePromptOperationCurrent(operation)) return;
    const ownerTab = loadEcomTabHistory(ecomStorageScope)?.tabs[ownerTabId];
    if (!ownerTab || closedTabIdsRef.current.has(ownerTabId)) return;
    reversePromptClaimsRef.current.set(ownerTabId, operation);
    updateReversePromptOperation(operation, "polling", {
      currentTaskIds: ownerTab.currentTaskIds || [],
      inspireResults: ownerTab.inspireResults || [],
    });
    monitorDetachedWorkspaceTab(ownerTab);
  }, [ecomStorageScope, monitorDetachedWorkspaceTab]);

  useEffect(() => {
    if (!ecomStorageScope) return;
    const account = recoveryAccount(ecomStorageScope);
    const restoreRetries = () => {
      for (const saved of durableRetries(account)) {
        if (saved.scope !== ecomStorageScope) continue;
        if (!activeRetryTrackerRef.current.has(saved.tracker.retryTaskId)) {
          activeRetryTrackerRef.current.set(saved.tracker.retryTaskId, saved.tracker);
          updateRetryOwnerWorkspace(saved.tracker, "pending");
        }
      }
      setRetryTrackerTrigger(value => value + 1);
    };
    restoreRetries();
    const stopRecovery = startDurableRecovery(account, (entry) => {
      restoreRetries();
      setHistoryRefreshTrigger(value => value + 1);
      if (entry.owner.scope !== ecomStorageScope || entry.owner.kind === "retry") return;
      const tab = loadEcomTabHistory(ecomStorageScope)?.tabs[entry.owner.tabId];
      if (!tab || tab.recoveryGeneration !== entry.owner.generation || closedTabIdsRef.current.has(tab.tabId)) return;
      if (entry.owner.kind === "refine") {
        for (const preview of tab.refinePreviews) {
          if (preview.status === "completed" || preview.refineTaskId.startsWith("__")) continue;
          activeRefineTrackerRef.current.set(createRefineTrackerKey(tab.tabId, preview.sourceTaskId, preview.imageIndex), {
            refineTaskId: preview.refineTaskId, targetTaskId: preview.sourceTaskId,
            batchParentId: tab.viewingTaskId, batchIndex: preview.imageIndex, ownerTabId: tab.tabId,
            operationId: `restored:${preview.refineTaskId}`, originalUrl: preview.originalUrl, startedAt: preview.startedAt,
          });
        }
        setRefineTrackerTrigger(value => value + 1);
      }
      setTabStatusMap(previous => ({ ...previous, [tab.tabId]: entry.owner.kind === "refine" ? "generating" : tab.status }));
      if (activeTabIdRef.current === tab.tabId) applyWorkspaceTab(tab);
      if (activeTabIdRef.current !== tab.tabId || entry.owner.kind === "inspire") monitorDetachedWorkspaceTab(tab);
    });
    let stopped = false;
    const otherWindows = startEcomPollingLoop({
      baseDelayMs: 3000,
      poll: async () => {
        for (const saved of durableRetries(account)) {
          if (stopped || saved.scope === ecomStorageScope) continue;
          const tracker = saved.tracker;
          try {
            const res = await getTaskStatus(tracker.retryTaskId);
            if (stopped || res.data?.task_id !== tracker.retryTaskId) continue;
            if (res.data.status === TASK_STATUS.COMPLETED) {
              const image = resolveTaskOutputImages(res.data.output_images, res.data.coze_raw_output)[0];
              if (!image) continue;
              await patchTaskOutputImage(tracker.sourceTaskId, tracker.patchIndex, image, tracker.preRetryImageUrl, account);
            } else if (res.data.status !== TASK_STATUS.FAILED && res.data.status !== TASK_STATUS.CANCELLED) continue;
            removeDurableRetry(account, tracker.retryTaskId);
            if (!stopped) setHistoryRefreshTrigger(value => value + 1);
          } catch { /* Retain unacknowledged work across pages and network failures. */ }
        }
      },
    });
    return () => { stopped = true; otherWindows.stop(); stopRecovery(); };
    // The recovery worker follows account/window ownership, not active tab renders.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ecomStorageScope]);

  // Cancel all detached monitors when the page unmounts so we don't leak intervals.
  useEffect(() => {
    const monitors = detachedTabMonitorsRef.current;
    const submissions = pendingGenerationSubmissionsRef.current;
    generationPageMountedRef.current = true;
    return () => {
      generationPageMountedRef.current = false;
      submissions.clear();
      generationRequestEpochRef.current += 1;
      generationEpochRef.current += 1;
      monitors.forEach((cancel) => {
        try {
          cancel();
        } catch {
          // Ignore — we are shutting down anyway.
        }
      });
      monitors.clear();
    };
  }, []);

  // If the user re-activates a tab that was being background-monitored, stop the
  // detached monitor and let the regular foreground polling take over.
  useEffect(() => {
    if (!activeTabId) return;
    const reversePromptOperation = reversePromptClaimsRef.current.get(activeTabId);
    if (isReversePromptOperationCurrent(reversePromptOperation)) return;
    cancelDetachedTabMonitor(activeTabId);
  }, [activeTabId, cancelDetachedTabMonitor]);

  /**
   * When the user submits a new generation while the currently-active tab is still
   * generating, persist the in-flight state to the current tab and open a brand-new
   * tab (same workflow, fresh instance index) before the new task starts. This avoids
   * overwriting an in-progress task and lets the user keep both tabs visible.
   *
   * Returns the new tabId (or null if no fork happened).
   */
  const forkActiveTabIfGenerating = useCallback((): string | null => {
    if (!isCurrentTabGenerating()) return null;

    // Snapshot the currently-active tab as "still generating" so the user can return to it.
    const previousTabId = activeTabIdRef.current;
    // Build the snapshot BEFORE we reset React state so it captures the in-flight
    // currentTaskIds / batchParentTaskId for the background monitor.
    const detachedSnapshot = restoreEcomGenerationInputs(buildCurrentWorkspaceTab(previousTabId));
    cancelScheduledWorkspacePersistence();
    persistCurrentWorkspaceTab(undefined, detachedSnapshot);
    setTabStatusMap((prev) => ({ ...prev, [previousTabId]: "generating" }));

    const currentHistory = loadEcomTabHistory(ecomStorageScope);
    const existingIds: string[] = [
      ...(currentHistory?.openTabIds || []),
      ...openedSubOptions,
      ...(currentHistory ? Object.keys(currentHistory.tabs) : []),
    ];
    const { tabId: newTabId, instance: newInstance } = createUniqueEcomTabId(
      activeMainTab,
      activeSubOption,
      existingIds,
    );
    closedTabIdsRef.current.delete(newTabId);
    activateReversePromptTab(newTabId);
    const newTitle = newInstance > 1 ? `${activeSubOption} #${newInstance}` : activeSubOption;
    const newTab: EcomWorkspaceTab = {
      ...createEmptyEcomWorkspaceTab(activeMainTab, activeSubOption, {
        tabId: newTabId,
        instance: newInstance,
        title: newTitle,
      }),
      // Carry over user inputs so the new submission inherits the same parameters.
      cozeParams: { ...cozeParams },
      cozeBatchCount,
      perBatchRatios: normalizePerBatchRatios(perBatchRatiosRef.current, cozeBatchCount),
      batchProcessMode,
      batchParamValues: { ...batchParamValues },
      batchConcurrency,
      refImageUrl,
    };

    const nextOpenTabIds = Array.from(new Set([
      ...(currentHistory?.openTabIds || []),
      newTabId,
    ]));
    saveEcomTabHistory({
      version: 1,
      activeTabId: newTabId,
      activeWorkspaceView: "generate",
      openTabIds: nextOpenTabIds,
      tabs: {
        ...(currentHistory?.tabs || {}),
        [newTabId]: newTab,
      },
      updatedAt: Date.now(),
    }, ecomStorageScope);

    setOpenedSubOptions((prev) => {
      const next = getUniqueOpenedOptions(prev.includes(newTabId) ? prev : [...prev, newTabId]);
      saveEcomOpenedTabs(next, ecomStorageScope);
      return next;
    });
    setTabStatusMap((prev) => ({ ...prev, [newTabId]: "empty" }));

    // Stop foreground polling for the OLD tab; the new submission will take over the
    // shared generation refs. A detached monitor (below) keeps following the old task
    // and writes its progress/result back to the saved workspace tab so the spinner on
    // its top-strip entry eventually clears.
    stopProgressSimulation();
    setGenerationActive(false);
    generationEpochRef.current += 1;
    generatingTaskIdsRef.current = [];

    // The new workspace owns a new result lifecycle and must not inherit a
    // completed or in-flight refine projection from the detached source tab.
    setRefinePreviews([]);

    // Start background monitoring for the detached old tab. The monitor writes only to
    // localStorage + tabStatusMap; it doesn't touch React state for the active tab.
    monitorDetachedWorkspaceTab(detachedSnapshot);
    stopInspirePolling();

    // Reset visible generation state for the new tab.
    isApplyingTabHistoryRef.current = true;
    setActiveTabInstance(newInstance);
    setStatus("empty");
    setProgress(0);
    setGeneratedImage(null);
    setResultModelImage("");
    setResultProductImage("");
    setGeneratedImages([]);
    setErrorMessage("");
    setCurrentTaskIds([]);
    setCurrentPrompt("");
    setFailedIndexes([]);
    setBatchChildTaskIds([]);
    setPreviewOutputCount(1);
    setTaskProgresses([]);
    setBatchParentTaskId(null);
    setBatchTaskStatus(null);
    setManualStitchMode(false);
    setHistoryFeatureKey(null);
    setHistoryViewOverlay(null);
    setNextStepTargets([]);
    setRegeneratingIndexes([]);
    applyInspireResults([]);
    applyMultiTemplateLabels({});
    applyMultiTemplateWorkflows({});
    inspireTaskIdsRef.current = [];
    setViewingTaskId("");
    viewingTaskIdRef.current = "";
    activeTabIdRef.current = newTabId;
    setTimeout(() => {
      isApplyingTabHistoryRef.current = false;
    }, 50);

    return newTabId;
  }, [
    activeMainTab,
    activeSubOption,
    activateReversePromptTab,
    batchConcurrency,
    batchParamValues,
    batchProcessMode,
    buildCurrentWorkspaceTab,
    cancelScheduledWorkspacePersistence,
    cozeBatchCount,
    cozeParams,
    ecomStorageScope,
    applyInspireResults,
    applyMultiTemplateLabels,
    applyMultiTemplateWorkflows,
    isCurrentTabGenerating,
    monitorDetachedWorkspaceTab,
    openedSubOptions,
    persistCurrentWorkspaceTab,
    refImageUrl,
    stopInspirePolling,
    stopProgressSimulation,
  ]);

  const startGenerationSubmission = (
    requestTabId: string,
    requestEpoch: number,
    generationStartedAt: number,
    previewOutputCount: number,
    submittedCozeParams: Record<string, unknown> = cozeParams,
  ): boolean => {
    if (!isGenerationRequestCurrent(requestTabId, requestEpoch)) return false;
    pendingGenerationSubmissionsRef.current.set(requestTabId, requestEpoch);
    cancelScheduledWorkspacePersistence();
    const history = loadEcomTabHistory(ecomStorageScope);
    const tab: EcomWorkspaceTab = {
      ...buildCurrentWorkspaceTab(requestTabId),
      ...createEmptyForegroundGenerationWorkspaceFields(),
      // Uploads can settle after the submit handler's render. Freeze the same
      // form used to build the request, including its resolved reference URLs.
      cozeParams: stripDeprecatedDetailReplicaParams(stripBlobUrls(submittedCozeParams)),
      recoveryGeneration: createClientUUID(),
      status: "generating",
      generationStartedAt,
      previewOutputCount,
      batchProcessMode,
      batchParamValues,
      refImageUrl,
      perBatchRatios,
    };
    tab.generationInputs = captureEcomGenerationInputs(tab);
    // The first submit may beat the debounced save that creates its workspace.
    saveEcomTabHistory({
      version: 1,
      activeTabId: requestTabId,
      activeWorkspaceView: "generate",
      openTabIds: Array.from(new Set([...(history?.openTabIds || []), requestTabId])),
      tabs: { ...(history?.tabs || {}), [requestTabId]: tab },
      updatedAt: Date.now(),
    }, ecomStorageScope);
    if (clearTaskIdsTimerRef.current) {
      clearTimeout(clearTaskIdsTimerRef.current);
      clearTaskIdsTimerRef.current = null;
    }
    clearForegroundGenerationState(tab);
    setNextStepTargets([]);
    setHistoryViewOverlay(null);
    startProgressSimulation(0);
    return true;
  };

  const finishGenerationSubmission = (
    requestTabId: string,
    requestEpoch: number,
    patch: Partial<EcomWorkspaceTab>,
  ): boolean => {
    if (!generationPageMountedRef.current || pendingGenerationSubmissionsRef.current.get(requestTabId) !== requestEpoch) return false;
    pendingGenerationSubmissionsRef.current.delete(requestTabId);
    const history = loadEcomTabHistory(ecomStorageScope);
    const existing = history?.tabs[requestTabId];
    if (!history || !existing || !history.openTabIds.includes(requestTabId) || closedTabIdsRef.current.has(requestTabId)) return false;
    const tab = { ...existing, ...patch, updatedAt: Date.now() };
    saveEcomTabHistory({
      ...history,
      tabs: { ...history.tabs, [requestTabId]: tab },
      updatedAt: Date.now(),
    }, ecomStorageScope);
    setTabStatusMap((previous) => ({ ...previous, [requestTabId]: tab.status }));
    setHistoryRefreshTrigger((previous) => previous + 1);
    if (activeTabIdRef.current !== requestTabId) {
      monitorDetachedWorkspaceTab(tab);
      return false;
    }
    clearForegroundGenerationState(tab);
    if (tab.status === "generating") startProgressSimulation(tab.progress);
    return true;
  };

  // ========================== 灵感反推（llm_vision_reverse）核心流程 ==========================
  //
  // 单次提交一组「批次」，每个批次会调一次 /functional/execute（= 一次后台任务 = 扣 1 豌豆/批次的成本）。
  // - 单图模式 + image 作为批次关键参数 + 上传 10 张 → 调用方传 10 个批次，每批次的 params.image 是 1 张图
  // - 多图模式 + batch_size=3 + 上传 6 张 → 调用方传 2 个批次，每批次的 params.images 是 3 张图
  // - 不绑定批次 → 调用方传 1 个批次，复用全部 params
  //
  const handleInspireSubmit = useCallback(async (batches: Array<{ params: Record<string, unknown>; inputImages: string[]; presetLabel?: string; templateIds?: number[] }>) => {
    const callableKey = getCurrentCallableKey();
    if (!callableKey) {
      feedback.error({ code: "E5006", fallback: "operation" });
      return;
    }
    if (batches.length === 0) {
      feedback.warning("请至少上传一张图片");
      return;
    }
    const requestTabId = activeTabIdRef.current;
    const reversePromptOperation = claimReversePromptOperation(
      getReversePromptOwnerScope(requestTabId),
      "llm_vision_reverse",
      createClientUUID(),
      { batchCount: batches.length },
    );
    if (!reversePromptOperation) return;
    reversePromptClaimsRef.current.set(requestTabId, reversePromptOperation);

    stopInspirePolling();

    // 初始化 N 个批次的 pending 状态
    const initial: InspireBatchResult[] = batches.map((b, idx) => ({
      taskId: `pending-${idx}`,
      status: "pending" as InspireBatchStatus,
      presetLabel: b.presetLabel,
      inputImages: b.inputImages,
      templateId: b.templateIds?.length === 1 ? b.templateIds[0] : undefined,
      batchIndex: idx + 1,
    }));
    applyInspireResults(initial);
    setStatus("generating");
    setErrorMessage("");
    setCurrentTaskIds([]);
    setViewingTaskId("");
    viewingTaskIdRef.current = "";
    setProgress(5);
    setGenerationStartedAt(Date.now());
    commitReversePromptOwnerPatch(reversePromptOperation, requestTabId, {
      status: "generating",
      progress: 5,
      errorMessage: "",
      generationStartedAt: Date.now(),
      inspireResults: initial as EcomInspireTabResult[],
    });
    startProgressSimulation(5);

    const concurrency = Math.max(1, Math.min(batches.length, getCurrentBatchProcessConfig().concurrency || 5));
    const pendingTaskIds = batches.map((_, idx) => `pending-inspire-${Date.now()}-${idx}`);
    setCurrentTaskIds(pendingTaskIds);
    setBatchChildTaskIds(pendingTaskIds);
    setTaskProgresses(new Array(batches.length).fill(0));
    setGeneratedImages(new Array(batches.length).fill(""));
    setPreviewOutputCount(batches.length);

    let taskIds: string[] = [];
    try {
      if (batches.length > 1) {
        const res = await executeFunctionalBatch({
          callable_key: callableKey,
          concurrency,
          batches: batches.map((batch) => ({
            params: batch.params,
            input_images: batch.inputImages,
            preset_label: batch.presetLabel,
            ...(batch.templateIds && batch.templateIds.length === 1 ? { template_ids: batch.templateIds } : {}),
          })),
        }, captureDurableOwner(ecomStorageScope, requestTabId, "inspire"));
        taskIds = res.data?.task_ids || [];
      } else {
        const batch = batches[0];
        const res = await executeFunctional({
          callable_key: callableKey,
          params: batch.params,
          ...(batch.templateIds && batch.templateIds.length === 1 ? { template_ids: batch.templateIds } : {}),
        }, captureDurableOwner(ecomStorageScope, requestTabId, "inspire"));
        const taskId = res.data?.task_id || "";
        if (taskId) taskIds = [taskId];
      }
    } catch (err: unknown) {
      if (isDurableSubmissionPending(err)) return;
      if (!isReversePromptOperationCurrent(reversePromptOperation)) return;
      const raw = err instanceof Error ? err.message : "";
      const failedResults = readReversePromptOwnerResults(requestTabId, initial).map((item) => ({
        ...item,
        status: "failed" as InspireBatchStatus,
        errorCode: toErrorCode(raw || "E5302"),
      }));
      commitReversePromptOwnerPatch(reversePromptOperation, requestTabId, {
        status: "failed",
        progress: 100,
        generationStartedAt: null,
        errorMessage: toErrorCode(raw || "E5302"),
        failedIndexes: batches.map((_, idx) => idx),
        inspireResults: failedResults as EcomInspireTabResult[],
      });
      updateReversePromptOperation(reversePromptOperation, "failed", { errorCode: toErrorCode(raw || "E5302") });
      reversePromptClaimsRef.current.delete(requestTabId);
      if (!isReversePromptOwnerVisible(reversePromptOperation, requestTabId)) return;
      if (isInsufficientBalanceError(err)) {
        setShowBalanceModal(true);
      }
      stopProgressSimulation();
      setStatus("failed");
      setGenerationStartedAt(null);
      setErrorMessage(toErrorCode(raw || "E5302"));
      setFailedIndexes(batches.map((_, idx) => idx));
      applyInspireResults(failedResults);
      return;
    }

    if (!isReversePromptOperationCurrent(reversePromptOperation)) return;

    if (taskIds.length === 0) {
      const latestResults = readReversePromptOwnerResults(requestTabId, initial);
      commitReversePromptOwnerPatch(reversePromptOperation, requestTabId, {
        status: "failed",
        progress: 100,
        generationStartedAt: null,
        errorMessage: "E5302",
        inspireResults: latestResults as EcomInspireTabResult[],
      });
      updateReversePromptOperation(reversePromptOperation, "failed", { errorCode: "E5302" });
      reversePromptClaimsRef.current.delete(requestTabId);
      if (isReversePromptOwnerVisible(reversePromptOperation, requestTabId)) {
        stopProgressSimulation();
        setStatus("failed");
        setGenerationStartedAt(null);
        setErrorMessage("E5302");
      }
      return;
    }

    const submitted = taskIds.map((taskId, index) => ({
      taskId,
      index,
      presetLabel: batches[index]?.presetLabel,
      inputImages: batches[index]?.inputImages || [],
    }));
    const nextResults = readReversePromptOwnerResults(requestTabId, initial).map((item, idx) => ({
      ...item,
      taskId: taskIds[idx] || item.taskId,
      status: idx < concurrency ? "processing" as InspireBatchStatus : "queued" as InspireBatchStatus,
    }));

    commitReversePromptOwnerPatch(reversePromptOperation, requestTabId, {
      status: "generating",
      currentTaskIds: taskIds,
      batchChildTaskIds: taskIds,
      viewingTaskId: taskIds[0] || "",
      previewOutputCount: taskIds.length,
      taskProgresses: new Array(taskIds.length).fill(0),
      generatedImages: new Array(taskIds.length).fill(""),
      inspireResults: nextResults as EcomInspireTabResult[],
    });
    updateReversePromptOperation(reversePromptOperation, "polling", {
      currentTaskIds: taskIds,
      inspireResults: nextResults,
    });
    if (!isReversePromptOwnerVisible(reversePromptOperation, requestTabId)) {
      monitorReversePromptOwnerAfterSubmit(reversePromptOperation, requestTabId);
      return;
    }

    setCurrentTaskIds(taskIds);
    setBatchChildTaskIds(taskIds);
    setViewingTaskId(taskIds[0] || "");
    viewingTaskIdRef.current = taskIds[0] || "";
    setTaskProgresses(new Array(taskIds.length).fill(0));
    setGeneratedImages(new Array(taskIds.length).fill(""));
    setPreviewOutputCount(taskIds.length);
    setHistoryRefreshTrigger(prev => prev + 1);
    applyInspireResults(nextResults);
    inspireTaskIdsRef.current = taskIds;

    // 启动轮询：每 2 秒拉一次所有尚未完成的反推任务，queued 会留在队列态直到后端拿到执行槽位。
    const pendingSet = new Set(taskIds);
    inspirePollTimerRef.current = startEcomPollingLoop({
      poll: async () => {
        if (!isReversePromptOperationCurrent(reversePromptOperation)) {
          stopInspirePolling();
          return;
        }
        if (!isReversePromptOwnerVisible(reversePromptOperation, requestTabId)) {
          stopInspirePolling();
          monitorReversePromptOwnerAfterSubmit(reversePromptOperation, requestTabId);
          return;
        }
        const ids = Array.from(pendingSet);
        if (ids.length === 0) return;

        const responses = await Promise.allSettled(ids.map(id => getFunctionalResult(id)));
        if (!isReversePromptOperationCurrent(reversePromptOperation)) return;
        if (!isReversePromptOwnerVisible(reversePromptOperation, requestTabId)) {
          stopInspirePolling();
          monitorReversePromptOwnerAfterSubmit(reversePromptOperation, requestTabId);
          return;
        }
        let anyChanged = false;
        responses.forEach((resp, i) => {
          if (resp.status !== "fulfilled") return;
          const data = resp.value.data;
          if (!data) return;
          const id = ids[i];
          const targetIdx = submitted.findIndex(s => s.taskId === id);
          if (targetIdx < 0) return;
          const status = (data.status || "processing") as InspireBatchStatus;

          if (status === "completed" || status === "failed" || status === "cancelled") {
            pendingSet.delete(id);
          }
          anyChanged = true;
          const persistedResults = readReversePromptOwnerResults(requestTabId, nextResults);
          const updatedResults = ((prev: InspireBatchResult[]) => {
            const next = [...prev];
            const slot = next[targetIdx];
            if (!slot) return prev;
            next[targetIdx] = {
              ...slot,
              status,
              prompt: normalizeReversePromptToPlainText(data.inspire_prompt) || slot.prompt || "",
              tags: data.inspire_tags || slot.tags || [],
              presetLabel: data.preset_label || slot.presetLabel,
              errorCode: status === "failed" ? toErrorCode(data.failure?.code || "E5302") : undefined,
            };
            return next;
          })(persistedResults);
          applyInspireResults(updatedResults);
          setTaskProgresses(prev => {
            const next = [...prev];
            while (next.length <= targetIdx) next.push(0);
            next[targetIdx] = status === "completed" || status === "failed" || status === "cancelled"
              ? 100
              : status === "processing"
                ? Math.max(next[targetIdx] || 0, 20)
                : 0;
            return next;
          });
          if (status === "failed") {
            setFailedIndexes(prev => prev.includes(targetIdx) ? prev : [...prev, targetIdx]);
          }
          commitReversePromptOwnerPatch(reversePromptOperation, requestTabId, {
            inspireResults: updatedResults as EcomInspireTabResult[],
          });
        });

        const latestResults = readReversePromptOwnerResults(requestTabId, nextResults);
        if (pendingSet.size === 0) {
          stopInspirePolling();
          stopProgressSimulation();
          setProgress(100);
          setGenerationStartedAt(null);
          const hasAnySuccess = latestResults.some((item) => item.status === "completed");
          const nextStatus = hasAnySuccess ? "completed" : "failed";
          setStatus(nextStatus);
          commitReversePromptOwnerPatch(reversePromptOperation, requestTabId, {
            status: nextStatus,
            progress: 100,
            generationStartedAt: null,
            errorMessage: hasAnySuccess ? "" : "E5302",
            inspireResults: latestResults as EcomInspireTabResult[],
          });
          updateReversePromptOperation(
            reversePromptOperation,
            hasAnySuccess ? "completed" : "failed",
            { inspireResults: latestResults },
          );
          reversePromptClaimsRef.current.delete(requestTabId);
          setHistoryRefreshTrigger(prev => prev + 1);
          notifyPointsChanged();
        } else if (anyChanged) {
          const total = submitted.length;
          const done = total - pendingSet.size;
          const nextProgress = Math.max(10, Math.min(95, Math.round((done / total) * 90 + 10)));
          setProgress(nextProgress);
          commitReversePromptOwnerPatch(reversePromptOperation, requestTabId, {
            status: "generating",
            progress: nextProgress,
            inspireResults: latestResults as EcomInspireTabResult[],
          });
        }
      },
      baseDelayMs: 2000,
      startImmediately: false,
    });
  }, [
    applyInspireResults,
    commitReversePromptOwnerPatch,
    getCurrentBatchProcessConfig,
    getCurrentCallableKey,
    getReversePromptOwnerScope,
    readReversePromptOwnerResults,
    startProgressSimulation,
    isReversePromptOwnerVisible,
    monitorReversePromptOwnerAfterSubmit,
    stopProgressSimulation,
    stopInspirePolling,
  ]);

  // ========== Multi-Template Submit: uses template_ids param and group polling ==========
  const handleMultiTemplateSubmit = useCallback(async (
    params: Record<string, unknown>,
    inputImages: string[],
    templateIds: number[],
    presetLabel?: string,
  ) => {
    const callableKey = getCurrentCallableKey();
    if (!callableKey) {
      feedback.error({ code: "E5006", fallback: "operation" });
      return;
    }
    const requestTabId = activeTabIdRef.current;
    const reversePromptOperation = claimReversePromptOperation(
      getReversePromptOwnerScope(requestTabId),
      "llm_vision_reverse:multi_template",
      createClientUUID(),
      { templateIds },
    );
    if (!reversePromptOperation) return;
    reversePromptClaimsRef.current.set(requestTabId, reversePromptOperation);

    stopInspirePolling();

    // Build template labels from UI config
    const tplLabelsMap: Record<number, string> = {};
    const tplWorkflowsMap: Record<number, string[]> = {};
    const tplComp = workflowUIConfig?.components?.find(c => c.component_type === "template_multi_select");
    if (tplComp) {
      const templates = (tplComp.config?.templates as TemplateTargetConfig[]) || [];
      for (const t of templates) {
        tplLabelsMap[t.id] = t.label;
      }
      for (const t of templates) {
        const targetWorkflowKeys = getTemplateTargetWorkflowKeys(t);
        if (targetWorkflowKeys.length > 0) {
          tplWorkflowsMap[t.id] = targetWorkflowKeys;
        }
      }
      applyMultiTemplateWorkflows(tplWorkflowsMap);
    }
    applyMultiTemplateLabels(tplLabelsMap);

    // Initialize pending results for each template
    const initial: InspireBatchResult[] = templateIds.map((tplId, idx) => ({
      taskId: `pending-tpl-${tplId}-${idx}`,
      status: "pending" as InspireBatchStatus,
      presetLabel,
      inputImages,
      templateId: tplId,
      batchIndex: 1,
    }));
    applyInspireResults(initial);
    setStatus("generating");
    setErrorMessage("");
    setCurrentTaskIds([]);
    setViewingTaskId("");
    viewingTaskIdRef.current = "";
    setProgress(5);
    setGenerationStartedAt(Date.now());
    commitReversePromptOwnerPatch(reversePromptOperation, requestTabId, {
      status: "generating",
      progress: 5,
      errorMessage: "",
      generationStartedAt: Date.now(),
      inspireResults: initial as EcomInspireTabResult[],
      multiTemplateLabels: tplLabelsMap,
      multiTemplateWorkflows: tplWorkflowsMap,
    });
    startProgressSimulation(5);
    let ownerResults = initial;

    try {
      const res = await executeFunctional({
        callable_key: callableKey,
        params,
        template_ids: templateIds,
      }, captureDurableOwner(ecomStorageScope, requestTabId, "inspire"));
      if (!isReversePromptOperationCurrent(reversePromptOperation)) return;

      const data = res.data;
      if (!data) throw new Error("创建多模板任务失败");

      // Multi-template response: task_ids + template_group_id
      if (data.task_ids && data.template_group_id) {
        const groupId = data.template_group_id;
        const taskIds = data.task_ids;
        const initialTaskProgresses = new Array(taskIds.length).fill(20);
        const initialGeneratedImages = new Array(taskIds.length).fill("");

        // Update results with actual task IDs
        const nextResults = ((prev: InspireBatchResult[]) => {
          const next = [...prev];
          taskIds.forEach((tid, idx) => {
            if (next[idx]) {
              next[idx] = { ...next[idx], taskId: tid, status: "processing" };
            }
          });
          return next;
        })(ownerResults);
        ownerResults = nextResults;
        commitReversePromptOwnerPatch(reversePromptOperation, requestTabId, {
          status: "generating",
          currentTaskIds: taskIds,
          batchChildTaskIds: taskIds,
          viewingTaskId: taskIds[0] || "",
          previewOutputCount: taskIds.length,
          taskProgresses: initialTaskProgresses,
          generatedImages: initialGeneratedImages,
          inspireResults: nextResults as EcomInspireTabResult[],
          multiTemplateLabels: tplLabelsMap,
          multiTemplateWorkflows: tplWorkflowsMap,
        });
        updateReversePromptOperation(reversePromptOperation, "polling", {
          currentTaskIds: taskIds,
          inspireResults: nextResults,
        });
        if (!isReversePromptOwnerVisible(reversePromptOperation, requestTabId)) {
          monitorReversePromptOwnerAfterSubmit(reversePromptOperation, requestTabId);
          return;
        }

        applyInspireResults(nextResults);
        setCurrentTaskIds(taskIds);
        setBatchChildTaskIds(taskIds);
        setViewingTaskId(taskIds[0] || "");
        viewingTaskIdRef.current = taskIds[0] || "";
        setTaskProgresses(initialTaskProgresses);
        setGeneratedImages(initialGeneratedImages);
        setPreviewOutputCount(taskIds.length);
        setFailedIndexes([]);
        setHistoryRefreshTrigger(prev => prev + 1);
        inspireTaskIdsRef.current = taskIds;

        // Poll using group endpoint; fall back to single-task polling on group failure/empty
        // payload to avoid getting stuck in "分析中" forever when /result-group is unavailable.
        const pendingSet = new Set<string>(taskIds);

        const applyTaskUpdate = (
          taskId: string,
          payload: {
            status?: string;
            template_id?: number;
            inspire_prompt?: string;
            inspire_tags?: string[];
            preset_label?: string;
            failure?: EcomApiFailure;
          },
        ) => {
          const idx = taskIds.indexOf(taskId);
          if (idx < 0) return;
          const status = (payload.status || "processing") as InspireBatchStatus;
          if (status === "completed" || status === "failed" || status === "cancelled") {
            pendingSet.delete(taskId);
          }
          const nextResults = ((prev: InspireBatchResult[]) => {
            const next = [...prev];
            const slot = next[idx];
            if (!slot) return prev;
            next[idx] = {
              ...slot,
              taskId,
              status,
              prompt: normalizeReversePromptToPlainText(payload.inspire_prompt) || slot.prompt || "",
              tags: payload.inspire_tags || slot.tags || [],
              presetLabel: payload.preset_label || slot.presetLabel,
              templateId: payload.template_id ?? slot.templateId,
              errorCode: status === "failed" ? toErrorCode(payload.failure?.code || "E5302") : undefined,
            };
            return next;
          })(ownerResults);
          ownerResults = nextResults;
          applyInspireResults(nextResults);
          setTaskProgresses(prev => {
            const next = [...prev];
            while (next.length <= idx) next.push(0);
            next[idx] = status === "completed" || status === "failed" || status === "cancelled"
              ? 100
              : status === "processing"
                ? Math.max(next[idx] || 0, 20)
                : 0;
            return next;
          });
          if (status === "failed") {
            setFailedIndexes(prev => prev.includes(idx) ? prev : [...prev, idx]);
          }
          commitReversePromptOwnerPatch(reversePromptOperation, requestTabId, {
            inspireResults: nextResults as EcomInspireTabResult[],
          });
        };

        const finishIfAllDone = () => {
          if (pendingSet.size === 0) {
            stopInspirePolling();
            stopProgressSimulation();
            setProgress(100);
            setGenerationStartedAt(null);
            setStatus("completed");
            commitReversePromptOwnerPatch(reversePromptOperation, requestTabId, {
              status: "completed",
              progress: 100,
              generationStartedAt: null,
              errorMessage: "",
              inspireResults: ownerResults as EcomInspireTabResult[],
            });
            updateReversePromptOperation(reversePromptOperation, "completed", { inspireResults: ownerResults });
            reversePromptClaimsRef.current.delete(requestTabId);
            setHistoryRefreshTrigger(prev => prev + 1);
            notifyPointsChanged();
            return true;
          }
          const total = taskIds.length;
          const done = total - pendingSet.size;
          const nextProgress = Math.max(10, Math.min(95, Math.round((done / total) * 90 + 10)));
          setProgress(nextProgress);
          commitReversePromptOwnerPatch(reversePromptOperation, requestTabId, {
            status: "generating",
            progress: nextProgress,
            inspireResults: ownerResults as EcomInspireTabResult[],
          });
          return false;
        };

        inspirePollTimerRef.current = startEcomPollingLoop({
          poll: async () => {
          if (!isReversePromptOperationCurrent(reversePromptOperation)) {
            stopInspirePolling();
            return;
          }
          if (!isReversePromptOwnerVisible(reversePromptOperation, requestTabId)) {
            stopInspirePolling();
            monitorReversePromptOwnerAfterSubmit(reversePromptOperation, requestTabId);
            return;
          }
          if (pendingSet.size === 0) {
            stopInspirePolling();
            return;
          }

          let groupOk = false;
          try {
            const groupRes = await getResultGroup(groupId);
            if (!isReversePromptOperationCurrent(reversePromptOperation)) return;
            if (!isReversePromptOwnerVisible(reversePromptOperation, requestTabId)) {
              stopInspirePolling();
              monitorReversePromptOwnerAfterSubmit(reversePromptOperation, requestTabId);
              return;
            }
            const results = groupRes?.data?.results;
            if (Array.isArray(results) && results.length > 0) {
              groupOk = true;
              for (const result of results) {
                applyTaskUpdate(result.task_id, result);
              }
            }
          } catch {
            // Group endpoint failed; fall back to per-task query below.
          }

          if (!groupOk) {
            const ids = Array.from(pendingSet);
            const responses = await Promise.allSettled(ids.map(id => getFunctionalResult(id)));
            if (!isReversePromptOperationCurrent(reversePromptOperation)) return;
            if (!isReversePromptOwnerVisible(reversePromptOperation, requestTabId)) {
              stopInspirePolling();
              monitorReversePromptOwnerAfterSubmit(reversePromptOperation, requestTabId);
              return;
            }
            responses.forEach((resp, i) => {
              if (resp.status !== "fulfilled") return;
              const rData = resp.value?.data;
              if (!rData) return;
              applyTaskUpdate(ids[i], {
                status: rData.status,
                inspire_prompt: rData.inspire_prompt,
                inspire_tags: rData.inspire_tags,
                preset_label: rData.preset_label,
                failure: rData.failure,
              });
            });
          }

            finishIfAllDone();
          },
          baseDelayMs: 2000,
          startImmediately: false,
        });
      } else if (data.task_id) {
        // Single template fallback (backward compat)
        const singleTemplateResult: InspireBatchResult[] = [{
          taskId: data.task_id,
          status: "processing",
          presetLabel,
          inputImages,
          templateId: templateIds[0],
          batchIndex: 1,
        }];
        ownerResults = singleTemplateResult;
        commitReversePromptOwnerPatch(reversePromptOperation, requestTabId, {
          status: "generating",
          currentTaskIds: [data.task_id],
          batchChildTaskIds: [data.task_id],
          viewingTaskId: data.task_id,
          previewOutputCount: 1,
          taskProgresses: [20],
          generatedImages: [""],
          inspireResults: singleTemplateResult as EcomInspireTabResult[],
          multiTemplateLabels: tplLabelsMap,
          multiTemplateWorkflows: tplWorkflowsMap,
        });
        updateReversePromptOperation(reversePromptOperation, "polling", {
          currentTaskIds: [data.task_id],
          inspireResults: singleTemplateResult,
        });
        if (!isReversePromptOwnerVisible(reversePromptOperation, requestTabId)) {
          monitorReversePromptOwnerAfterSubmit(reversePromptOperation, requestTabId);
          return;
        }
        inspireTaskIdsRef.current = [data.task_id];
        applyInspireResults(singleTemplateResult);
        setCurrentTaskIds([data.task_id]);
        setBatchChildTaskIds([data.task_id]);
        setViewingTaskId(data.task_id);
        viewingTaskIdRef.current = data.task_id;

        // Poll single task
        const pendingSet = new Set([data.task_id]);
        inspirePollTimerRef.current = startEcomPollingLoop({
          poll: async () => {
            if (!isReversePromptOperationCurrent(reversePromptOperation)) {
              stopInspirePolling();
              return;
            }
            if (!isReversePromptOwnerVisible(reversePromptOperation, requestTabId)) {
              stopInspirePolling();
              monitorReversePromptOwnerAfterSubmit(reversePromptOperation, requestTabId);
              return;
            }
            const ids = Array.from(pendingSet);
            if (ids.length === 0) return;
            const responses = await Promise.allSettled(ids.map(id => getFunctionalResult(id)));
            if (!isReversePromptOperationCurrent(reversePromptOperation)) return;
            if (!isReversePromptOwnerVisible(reversePromptOperation, requestTabId)) {
              stopInspirePolling();
              monitorReversePromptOwnerAfterSubmit(reversePromptOperation, requestTabId);
              return;
            }
            responses.forEach((resp, i) => {
              if (resp.status !== "fulfilled") return;
              const rData = resp.value.data;
              if (!rData) return;
              const st = (rData.status || "processing") as InspireBatchStatus;
              if (st === "completed" || st === "failed" || st === "cancelled") {
                pendingSet.delete(ids[i]);
                const nextResults = ((prev: InspireBatchResult[]) => {
                  const next = [...prev];
                  next[0] = {
                    ...next[0],
                    status: st,
                    prompt: normalizeReversePromptToPlainText(rData.inspire_prompt) || "",
                    tags: rData.inspire_tags || [],
                    presetLabel: rData.preset_label || next[0]?.presetLabel,
                    errorCode: st === "failed" ? toErrorCode(rData.failure?.code || "E5302") : undefined,
                  };
                  return next;
                })(ownerResults);
                ownerResults = nextResults;
                applyInspireResults(nextResults);
                commitReversePromptOwnerPatch(reversePromptOperation, requestTabId, {
                  inspireResults: nextResults as EcomInspireTabResult[],
                });
              }
            });
            if (pendingSet.size === 0) {
              stopInspirePolling();
              stopProgressSimulation();
              setProgress(100);
              setGenerationStartedAt(null);
              setStatus("completed");
              commitReversePromptOwnerPatch(reversePromptOperation, requestTabId, {
                status: "completed",
                progress: 100,
                generationStartedAt: null,
                errorMessage: "",
                inspireResults: ownerResults as EcomInspireTabResult[],
              });
              updateReversePromptOperation(reversePromptOperation, "completed", { inspireResults: ownerResults });
              reversePromptClaimsRef.current.delete(requestTabId);
              setHistoryRefreshTrigger(prev => prev + 1);
              notifyPointsChanged();
            }
          },
          baseDelayMs: 2000,
          startImmediately: false,
        });
      } else {
        throw new Error("未知的响应格式");
      }
    } catch (err: unknown) {
      if (isDurableSubmissionPending(err)) return;
      if (!isReversePromptOperationCurrent(reversePromptOperation)) return;
      const raw = err instanceof Error ? err.message : "";
      const failedResults = ownerResults.map(r => ({
        ...r,
        status: "failed" as InspireBatchStatus,
        errorCode: toErrorCode(raw),
      }));
      ownerResults = failedResults;
      commitReversePromptOwnerPatch(reversePromptOperation, requestTabId, {
        status: "failed",
        progress: 100,
        generationStartedAt: null,
        errorMessage: toErrorCode(raw),
        inspireResults: failedResults as EcomInspireTabResult[],
      });
      updateReversePromptOperation(reversePromptOperation, "failed", { errorCode: toErrorCode(raw) });
      reversePromptClaimsRef.current.delete(requestTabId);
      if (!isReversePromptOwnerVisible(reversePromptOperation, requestTabId)) return;
      if (isInsufficientBalanceError(err)) {
        setShowBalanceModal(true);
      }
      stopProgressSimulation();
      setStatus("failed");
      setGenerationStartedAt(null);
      setErrorMessage(toErrorCode(raw));
      applyInspireResults(failedResults);
    }
  }, [
    applyInspireResults,
    applyMultiTemplateLabels,
    applyMultiTemplateWorkflows,
    commitReversePromptOwnerPatch,
    getCurrentCallableKey,
    getReversePromptOwnerScope,
    isReversePromptOwnerVisible,
    monitorReversePromptOwnerAfterSubmit,
    workflowUIConfig,
    startProgressSimulation,
    stopProgressSimulation,
    stopInspirePolling,
  ]);

  const handleBatchedMultiTemplateSubmit = useCallback(async (
    batches: Array<{ params: Record<string, unknown>; inputImages: string[]; presetLabel?: string; templateIds?: number[] }>,
  ) => {
    const callableKey = getCurrentCallableKey();
    const templateIds = batches[0]?.templateIds || [];
    if (!callableKey) {
      feedback.error({ code: "E5006", fallback: "operation" });
      return;
    }
    if (batches.length === 0 || templateIds.length <= 1) {
      await handleInspireSubmit(batches);
      return;
    }
    const requestTabId = activeTabIdRef.current;
    const reversePromptOperation = claimReversePromptOperation(
      getReversePromptOwnerScope(requestTabId),
      "llm_vision_reverse:batched_multi_template",
      createClientUUID(),
      { batchCount: batches.length, templateIds },
    );
    if (!reversePromptOperation) return;
    reversePromptClaimsRef.current.set(requestTabId, reversePromptOperation);

    stopInspirePolling();

    const tplLabelsMap: Record<number, string> = {};
    const tplWorkflowsMap: Record<number, string[]> = {};
    const tplComp = workflowUIConfig?.components?.find(c => c.component_type === "template_multi_select");
    if (tplComp) {
      const templates = (tplComp.config?.templates as TemplateTargetConfig[]) || [];
      for (const t of templates) {
        tplLabelsMap[t.id] = t.label;
        const targetWorkflowKeys = getTemplateTargetWorkflowKeys(t);
        if (targetWorkflowKeys.length > 0) {
          tplWorkflowsMap[t.id] = targetWorkflowKeys;
        }
      }
    }
    applyMultiTemplateLabels(tplLabelsMap);
    applyMultiTemplateWorkflows(tplWorkflowsMap);

    const initial: InspireBatchResult[] = batches.flatMap((batch, batchIdx) =>
      templateIds.map((tplId, tplIdx) => ({
        taskId: `pending-b${batchIdx}-tpl-${tplId}-${tplIdx}`,
        status: "pending" as InspireBatchStatus,
        presetLabel: batch.presetLabel,
        inputImages: batch.inputImages,
        templateId: tplId,
        batchIndex: batchIdx + 1,
      })),
    );

    applyInspireResults(initial);
    setStatus("generating");
    setErrorMessage("");
    setCurrentTaskIds([]);
    setViewingTaskId("");
    viewingTaskIdRef.current = "";
    setProgress(5);
    setGenerationStartedAt(Date.now());
    commitReversePromptOwnerPatch(reversePromptOperation, requestTabId, {
      status: "generating",
      progress: 5,
      errorMessage: "",
      generationStartedAt: Date.now(),
      inspireResults: initial as EcomInspireTabResult[],
      multiTemplateLabels: tplLabelsMap,
      multiTemplateWorkflows: tplWorkflowsMap,
    });
    startProgressSimulation(5);
    let ownerResults = initial;

    const pendingSet = new Set<string>();
    const taskSlotMap = new Map<string, number>();
    const concurrency = Math.max(1, Math.min(batches.length, getCurrentBatchProcessConfig().concurrency || 5));

    const failBatchSlots = (batchIdx: number, raw: string) => {
      const errorCode = toErrorCode(raw);
      const nextResults = ((prev: InspireBatchResult[]) => {
        const next = [...prev];
        for (let tplIdx = 0; tplIdx < templateIds.length; tplIdx++) {
          const slotIdx = batchIdx * templateIds.length + tplIdx;
          if (next[slotIdx]) {
            next[slotIdx] = { ...next[slotIdx], status: "failed", errorCode };
          }
        }
        return next;
      })(ownerResults);
      ownerResults = nextResults;
      commitReversePromptOwnerPatch(reversePromptOperation, requestTabId, {
        inspireResults: nextResults as EcomInspireTabResult[],
      });
      if (isReversePromptOwnerVisible(reversePromptOperation, requestTabId)) {
        applyInspireResults(nextResults);
      }
    };

    const submitOne = async (batchIdx: number) => {
      const batch = batches[batchIdx];
      try {
        const res = await executeFunctional({
          callable_key: callableKey,
          params: batch.params,
          template_ids: templateIds,
        }, captureDurableOwner(ecomStorageScope, requestTabId, "inspire", batchIdx * templateIds.length));
        if (!isReversePromptOperationCurrent(reversePromptOperation)) return;
        const data = res.data;
        if (!data?.task_ids?.length) {
          throw new Error("创建多模板任务失败");
        }

        const nextResults = ((prev: InspireBatchResult[]) => {
          const next = [...prev];
          data.task_ids?.forEach((taskId, tplIdx) => {
            const slotIdx = batchIdx * templateIds.length + tplIdx;
            if (!next[slotIdx]) return;
            pendingSet.add(taskId);
            taskSlotMap.set(taskId, slotIdx);
            next[slotIdx] = { ...next[slotIdx], taskId, status: "processing" };
          });
          return next;
        })(ownerResults);
        ownerResults = nextResults;
        commitReversePromptOwnerPatch(reversePromptOperation, requestTabId, {
          inspireResults: nextResults as EcomInspireTabResult[],
        });
        if (isReversePromptOwnerVisible(reversePromptOperation, requestTabId)) {
          applyInspireResults(nextResults);
        }
      } catch (err: unknown) {
        if (isDurableSubmissionPending(err)) return;
        if (!isReversePromptOperationCurrent(reversePromptOperation)) return;
        const raw = err instanceof Error ? err.message : "";
        if (isInsufficientBalanceError(err) && isReversePromptOwnerVisible(reversePromptOperation, requestTabId)) {
          setShowBalanceModal(true);
        }
        failBatchSlots(batchIdx, raw);
      }
    };

    const queue: number[] = batches.map((_, i) => i);
    const workers = Array.from({ length: concurrency }, async () => {
      while (queue.length > 0) {
        if (!isReversePromptOperationCurrent(reversePromptOperation)) return;
        const idx = queue.shift();
        if (idx == null) break;
        await submitOne(idx);
      }
    });
    await Promise.all(workers);
    if (!isReversePromptOperationCurrent(reversePromptOperation)) return;

    const submittedTaskIds = Array.from(pendingSet);
    commitReversePromptOwnerPatch(reversePromptOperation, requestTabId, {
      status: "generating",
      currentTaskIds: submittedTaskIds,
      batchChildTaskIds: submittedTaskIds,
      viewingTaskId: submittedTaskIds[0] || "",
      previewOutputCount: ownerResults.length,
      taskProgresses: ownerResults.map((result) => result.status === "failed" ? 100 : 20),
      generatedImages: new Array(ownerResults.length).fill(""),
      inspireResults: ownerResults as EcomInspireTabResult[],
    });
    if (pendingSet.size === 0) {
      commitReversePromptOwnerPatch(reversePromptOperation, requestTabId, {
        status: "failed",
        progress: 100,
        generationStartedAt: null,
        errorMessage: "E5302",
        inspireResults: ownerResults as EcomInspireTabResult[],
      });
      updateReversePromptOperation(reversePromptOperation, "failed", { errorCode: "E5302" });
      reversePromptClaimsRef.current.delete(requestTabId);
      if (isReversePromptOwnerVisible(reversePromptOperation, requestTabId)) {
        stopProgressSimulation();
        setGenerationStartedAt(null);
        setStatus("failed");
        setErrorMessage("E5302");
      }
      return;
    }
    updateReversePromptOperation(reversePromptOperation, "polling", {
      currentTaskIds: submittedTaskIds,
      inspireResults: ownerResults,
    });
    if (!isReversePromptOwnerVisible(reversePromptOperation, requestTabId)) {
      monitorReversePromptOwnerAfterSubmit(reversePromptOperation, requestTabId);
      return;
    }
    inspireTaskIdsRef.current = submittedTaskIds;
    applyInspireResults(ownerResults);
    setCurrentTaskIds(submittedTaskIds);
    setBatchChildTaskIds(submittedTaskIds);
    setViewingTaskId(submittedTaskIds[0] || "");
    viewingTaskIdRef.current = submittedTaskIds[0] || "";

    inspirePollTimerRef.current = startEcomPollingLoop({
      poll: async () => {
        if (!isReversePromptOperationCurrent(reversePromptOperation)) {
          stopInspirePolling();
          return;
        }
        if (!isReversePromptOwnerVisible(reversePromptOperation, requestTabId)) {
          stopInspirePolling();
          monitorReversePromptOwnerAfterSubmit(reversePromptOperation, requestTabId);
          return;
        }
        const ids = Array.from(pendingSet);
        if (ids.length === 0) {
          stopInspirePolling();
          return;
        }

        const responses = await Promise.allSettled(ids.map(id => getFunctionalResult(id)));
        if (!isReversePromptOperationCurrent(reversePromptOperation)) return;
        if (!isReversePromptOwnerVisible(reversePromptOperation, requestTabId)) {
          stopInspirePolling();
          monitorReversePromptOwnerAfterSubmit(reversePromptOperation, requestTabId);
          return;
        }
        responses.forEach((resp, i) => {
          if (resp.status !== "fulfilled") return;
          const data = resp.value.data;
          if (!data) return;
          const taskId = ids[i];
          const slotIdx = taskSlotMap.get(taskId);
          if (slotIdx == null) return;
          const status = (data.status || "processing") as InspireBatchStatus;
          if (status === "completed" || status === "failed" || status === "cancelled") {
            pendingSet.delete(taskId);
          }
          const nextResults = ((prev: InspireBatchResult[]) => {
            const next = [...prev];
            const slot = next[slotIdx];
            if (!slot) return prev;
            next[slotIdx] = {
              ...slot,
              status,
              prompt: normalizeReversePromptToPlainText(data.inspire_prompt) || slot.prompt || "",
              tags: data.inspire_tags || slot.tags || [],
              presetLabel: data.preset_label || slot.presetLabel,
              errorCode: status === "failed" ? toErrorCode(data.failure?.code || "E5302") : undefined,
            };
            return next;
          })(ownerResults);
          ownerResults = nextResults;
          applyInspireResults(nextResults);
          commitReversePromptOwnerPatch(reversePromptOperation, requestTabId, {
            inspireResults: nextResults as EcomInspireTabResult[],
          });
        });

        if (pendingSet.size === 0) {
          stopInspirePolling();
          stopProgressSimulation();
          setProgress(100);
          setGenerationStartedAt(null);
          setStatus("completed");
          commitReversePromptOwnerPatch(reversePromptOperation, requestTabId, {
            status: "completed",
            progress: 100,
            generationStartedAt: null,
            errorMessage: "",
            inspireResults: ownerResults as EcomInspireTabResult[],
          });
          updateReversePromptOperation(reversePromptOperation, "completed", { inspireResults: ownerResults });
          reversePromptClaimsRef.current.delete(requestTabId);
          setHistoryRefreshTrigger(prev => prev + 1);
          notifyPointsChanged();
        } else {
          const total = taskSlotMap.size;
          const done = total - pendingSet.size;
          const nextProgress = Math.max(10, Math.min(95, Math.round((done / total) * 90 + 10)));
          setProgress(nextProgress);
          commitReversePromptOwnerPatch(reversePromptOperation, requestTabId, {
            status: "generating",
            progress: nextProgress,
            inspireResults: ownerResults as EcomInspireTabResult[],
          });
        }
      },
      baseDelayMs: 2000,
      startImmediately: false,
    });
  }, [
    applyInspireResults,
    applyMultiTemplateLabels,
    applyMultiTemplateWorkflows,
    commitReversePromptOwnerPatch,
    getCurrentBatchProcessConfig,
    getCurrentCallableKey,
    getReversePromptOwnerScope,
    handleInspireSubmit,
    isReversePromptOwnerVisible,
    monitorReversePromptOwnerAfterSubmit,
    workflowUIConfig,
    startProgressSimulation,
    stopProgressSimulation,
    stopInspirePolling,
  ]);

  const handleGenerate = async (overrideBatchCount?: number) => {
    // Agent inputs must never be submitted through the ordinary generation API.
    if (!configsLoaded || agentSelected || agentActive) return;
    // 防止 React 合成事件对象被误当 overrideBatchCount 传入（onClick 直接绑定时）。
    // Math.max(1, SyntheticEvent) 会得到 NaN，使 Array.from({length: NaN}) 退化为空数组，
    // 进而不发请求直接抛"创建任务失败"。
    const safeOverride = typeof overrideBatchCount === 'number' && Number.isFinite(overrideBatchCount)
      ? overrideBatchCount
      : undefined;
    const currentFeatureKey = getFeatureKey(activeSubOption);
    const requestedBatchCount =
      safeOverride !== undefined
        ? safeOverride
        : cozeBatchCount;
    const effectiveBatchCount = Number.isFinite(requestedBatchCount)
      ? Math.max(1, requestedBatchCount)
      : 1;

    // 生成入口冷却检查
    const now = Date.now();
    if (now - lastRequestTimeRef.current < GENERATE_BUTTON_COOLDOWN_MS) {
      feedback.warning("请求太频繁，请稍后再试");
      return;
    }
    lastRequestTimeRef.current = now;

    // Keep history-restored active tasks from being overwritten, including inspire workflows
    // that return before the normal generation path below.
    forkActiveTabIfGenerating();
    clearActiveRefineState();
    const { requestTabId, requestEpoch } = beginGenerationRequest();

    // 灵感反推工作流：直接走 inspire 路径，按左侧批次数提交多个反推批次。
    if (isCurrentInspireWorkflow()) {
      const latest = cozeParamsRef.current;
      const params: Record<string, unknown> = {};
      const inputImages: string[] = [];
      const imageReversePromptKeys = workflowUIConfig?.components
        ?.filter((c) => c.component_type === "image_reverse_prompt")
        .map((c) => c.coze_param_key);
      let multiTemplateIds: number[] | undefined;
      const tplComp = workflowUIConfig?.components?.find(c => c.component_type === "template_multi_select");
      for (const [key, val] of Object.entries(stripEcomDraftOnlyParams(latest))) {
        if (val == null) continue;
        // Detect template_multi_select component value
        if (tplComp && tplComp.coze_param_key === key) {
          if (Array.isArray(val) && val.length > 0) {
            multiTemplateIds = val as number[];
          }
          continue; // Don't pass raw array as a coze param
        }
        if (val && typeof val === "object" && "coze_value" in (val as Record<string, unknown>)) {
          params[key] = (val as { coze_value: string }).coze_value;
        } else {
          params[key] = val;
        }
        if (typeof val === "string" && looksLikeImageURL(val)) {
          inputImages.push(val);
        } else if (Array.isArray(val)) {
          for (const item of val) {
            if (typeof item === "string" && looksLikeImageURL(item)) inputImages.push(item);
          }
        }
      }
      const draftImageUrls = collectImageReversePromptDraftUrls(latest, imageReversePromptKeys);
      draftImageUrls.forEach((imageUrl, index) => {
        if (!inputImages.includes(imageUrl)) inputImages.push(imageUrl);
        params[`__image_reverse_prompt_image_${index + 1}`] = imageUrl;
      });
      if (inputImages.length === 0) {
        feedback.warning("请至少上传一张图片");
        return;
      }
      if (!multiTemplateIds || multiTemplateIds.length === 0) {
        const templates = (tplComp?.config?.templates as Array<{ id: number }> | undefined) || [];
        if (templates[0]?.id != null) {
          multiTemplateIds = [templates[0].id];
        }
      }
      const presetLabel = typeof params.preset_label === "string" ? (params.preset_label as string) : undefined;
      const inspireBatches = Array.from({ length: effectiveBatchCount }, () => ({
        params: { ...params },
        inputImages: [...inputImages],
        presetLabel,
        templateIds: multiTemplateIds,
      }));

      // Multi-template mode: pass template_ids and use group polling
      if (multiTemplateIds && multiTemplateIds.length > 1) {
        if (effectiveBatchCount > 1) {
          await handleBatchedMultiTemplateSubmit(inspireBatches);
        } else {
          await handleMultiTemplateSubmit(params, inputImages, multiTemplateIds, presetLabel);
        }
        return;
      }

      await handleInspireSubmit(inspireBatches);
      return;
    }

    // If images are still uploading, wait for them to finish before submitting
    if (hasPendingBlobUrls(cozeParamsRef.current)) {
      feedback.info("图片上传中，完成后自动开始生成...");
      const ok = await waitForUploads();
      if (!ok) return;
      if (!isGenerationRequestCurrent(requestTabId, requestEpoch)) return;
    }

    const generationStartTime = Date.now();

    try {
      // Use ref to get the latest params (may have been updated by async uploads)
      const latestCozeParams = cozeParamsRef.current;
      const rawParams: Record<string, unknown> = {};
      for (const [key, val] of Object.entries(latestCozeParams)) {
        if (val && typeof val === 'object' && 'coze_value' in (val as Record<string, unknown>)) {
          rawParams[key] = (val as ModelAsset | BackgroundAsset).coze_value;
        } else if (val !== null && val !== undefined) {
          rawParams[key] = val;
        }
      }
      // Safety net: strip any residual blob: URLs that couldn't be uploaded
      const params = stripDeprecatedDetailReplicaParams(stripEcomDraftOnlyParams(stripBlobUrls(rawParams)));
      delete params[DETAIL_REPLICA_RATIO_CONFIG_SIGNATURE_KEY];
      delete params[DETAIL_REPLICA_CROP_SIZES_KEY];
      normalizeLogoPresetSubmission(params, workflowUIConfig);

      // 详情页复刻要等到每个子请求都已替换为当前屏参考图后才解析智能比例。
      // 提前按整批参数解析会误用后台候选或其它屏的锚点，并显示错误的提示。
      if (!isDetailReplicaWorkflow) {
        await applyAutoRatioParams(params, workflowUIConfig);
      }
      applyRatioSelectorParams(params, workflowUIConfig);

      if (flowXPromptReferenceText && !getPromptTextFromParams(params)) {
        params.prompt = flowXPromptReferenceText;
      }

      if (referenceResult) {
        params[referenceResult.targetParamKey] = referenceResult.resultUrl;
        params[referenceResult.targetParamKey + "_weight"] = referenceResult.weight;
      }

      // 为本次批量生成创建统一的 group_id
      const groupId = createClientUUID();

      // Collect batch_prompt component keys so each batch gets its own prompt
      const batchPromptKeys: string[] = [];
      const batchMultiImageKeys: string[] = [];
      if (workflowUIConfig?.components) {
        for (const comp of workflowUIConfig.components) {
          if (comp.component_type === "batch_prompt") {
            batchPromptKeys.push(comp.coze_param_key);
          }
          if (comp.component_type === "batch_multi_image") {
            batchMultiImageKeys.push(comp.coze_param_key);
          }
        }
      }
      // Remove batch_prompt arrays from base params; they'll be set per-batch
      for (const key of batchPromptKeys) {
        delete params[key];
      }
      // Remove batch_multi_image arrays from base params; they'll be set per-batch
      for (const key of batchMultiImageKeys) {
        delete params[key];
      }

      const cozeTemplateSetComp = workflowUIConfig?.components?.find(
        (c) => c.is_visible && c.component_type === "coze_template_set",
      );
      if (cozeTemplateSetComp) {
        const templateSetValue = normalizeCozeTemplateSetValue(latestCozeParams[cozeTemplateSetComp.coze_param_key]);
        const validation = validateCozeTemplateSetValue(templateSetValue);
        if (!validation.ok) {
          feedback.warning(getCozeTemplateSetValidationMessage(validation));
          return;
        }
        if (!templateSetValue) return;
        if (
          isFallbackCozeTemplateSetId(templateSetValue.template_set_id) &&
          templateSetValue.template_set_id !== COZE_TEMPLATE_REFERENCE_DRAFT_ID
        ) {
          feedback.warning("请先在后台配置真实模板库后再生成");
          return;
        }

        const templateBaseParams = { ...params };
        delete templateBaseParams[cozeTemplateSetComp.coze_param_key];
        const templateRequest = buildCozeTemplateSetGenerateRequest({
          value: templateSetValue,
          workflowKey: currentFeatureKey,
          baseParams: templateBaseParams,
          mapping: {
            template_image_param: typeof cozeTemplateSetComp.config?.template_image_param === "string" ? cozeTemplateSetComp.config.template_image_param : undefined,
            upload_images_param: typeof cozeTemplateSetComp.config?.upload_images_param === "string" ? cozeTemplateSetComp.config.upload_images_param : undefined,
            prompt_param: typeof cozeTemplateSetComp.config?.prompt_param === "string" ? cozeTemplateSetComp.config.prompt_param : undefined,
          },
        });
        const templateTaskCount = templateRequest.selected_slots.length;
        if (!startGenerationSubmission(requestTabId, requestEpoch, generationStartTime, templateTaskCount, latestCozeParams)) return;
        const res = templateRequest.template_set_id === COZE_TEMPLATE_REFERENCE_DRAFT_ID
          ? await ecomImageApi.generateCozeTemplateReferenceDraft(
            templateRequest.template_set_id,
            templateRequest, captureDurableOwner(ecomStorageScope, requestTabId),
          )
          : await ecomImageApi.generateCozeTemplateSet(templateRequest.template_set_id, templateRequest, captureDurableOwner(ecomStorageScope, requestTabId));
        const taskIds = (
          res.data?.task_ids ||
          res.data?.tasks?.map((task) => task.task_id || task.uuid || "").filter(Boolean) ||
          []
        );
        if (taskIds.length === 0) {
          throw new Error("创建任务失败");
        }

        finishGenerationSubmission(requestTabId, requestEpoch, {
          status: "generating",
          progress: 5,
          generatedImages: new Array(taskIds.length).fill(""),
          taskProgresses: new Array(taskIds.length).fill(0),
          currentTaskIds: taskIds,
          viewingTaskId: taskIds[0] || "",
          batchChildTaskIds: taskIds.length > 1 ? taskIds : [],
          currentPrompt: JSON.stringify(templateRequest),
          previewOutputCount: Math.max(templateTaskCount, taskIds.length),
        });
        return;
      }

      let taskIds: string[];
      const detailReplicaResolvedRatios: AutoRatioResolveResult[] = [];

      // 收集 ratio_selector 的 param key 与每批次覆盖快照，便于后续按 batchIdx 注入。
      const ratioSelectorComp = workflowUIConfig?.components?.find(
        (c) => c.component_type === "ratio_selector",
      );
      const ratioParamKey = ratioSelectorComp?.coze_param_key || null;
      const perBatchRatiosSnapshot = perBatchRatiosRef.current;
      const detailReplicaKeys = resolveDetailReplicaComponentKeys(workflowUIConfig);
      const hasPerBatchOverride =
        cozeBatchCount > 1 && !!ratioParamKey && hasAnyPerBatchOverride(perBatchRatiosSnapshot);
      const ratioSelectorOptions = resolveRatioSelectorOptions(ratioSelectorComp?.config);

      const applyPerBatchRatioOverride = (target: Record<string, unknown>, batchIdx: number) => {
        if (!hasPerBatchOverride || !ratioParamKey) return;
        const override = perBatchRatiosSnapshot[batchIdx];
        const normalizedOverride = normalizeRatioSelectorSelection(
          ratioSelectorOptions,
          override?.ratio,
          override?.cropSize,
        );
        if (!normalizedOverride.ratio) return;
        target[ratioParamKey] = normalizedOverride.ratio;
        if (normalizedOverride.cropSize) {
          target[OUTPUT_CROP_SIZE_PARAM] = normalizedOverride.cropSize;
          delete target[OUTPUT_CROP_RATIO_PARAM];
        } else {
          delete target[OUTPUT_CROP_SIZE_PARAM];
          applyOutputCropRatioParam(target, normalizedOverride.ratio);
        }
      };

      // llm_inference 模式下若存在每批次覆盖，则降级为 N 次并行 coze_direct 调用（保证每个子任务能携带独立 ratio）。
      const llmInferenceWithOverrides =
        currentExecutionMode === 'llm_inference' && (hasPerBatchOverride || isDetailReplicaWorkflow);
      if (llmInferenceWithOverrides) {
        feedback.info(`已拆分为 ${cozeBatchCount} 次并行请求以支持各${getCurrentBatchUnit()}次独立比例`);
      }

      if (!startGenerationSubmission(requestTabId, requestEpoch, generationStartTime, effectiveBatchCount, latestCozeParams)) return;
      if (currentExecutionMode === 'llm_inference' && !llmInferenceWithOverrides) {
        // For llm_inference mode, pass pose URLs as __pose_urls__ so the backend
        // can assign one per inferred task (batch index → pose index).
        if (mergedPoses.length > 0) {
          const poseUrls = mergedPoses.map(p => p.image_url).filter(Boolean);
          if (poseUrls.length > 0) {
            params.__pose_urls__ = poseUrls;
          }
        }

        setCurrentPrompt(JSON.stringify(params));

        const res = await ecomImageApi.generateCoze({
          feature: currentFeatureKey,
          batch_count: effectiveBatchCount,
          group_id: groupId,
          params,
        }, captureDurableOwner(ecomStorageScope, requestTabId));
        if (res.data?.mode === 'llm_inference' && res.data?.task_ids && res.data.task_ids.length > 0) {
          taskIds = res.data.task_ids;
        } else if (res.data?.task_id) {
          taskIds = [res.data.task_id];
        } else {
          throw new Error("创建任务失败");
        }

        finishGenerationSubmission(requestTabId, requestEpoch, {
          status: "generating",
          progress: 5,
          taskProgresses: new Array(taskIds.length).fill(0),
          currentTaskIds: taskIds,
          viewingTaskId: taskIds[0] || "",
          previewOutputCount: taskIds.length,
          currentPrompt: JSON.stringify(params),
        });
      } else {
        // coze_direct / smart_reasoning（或 llm_inference 被降级）：每批次独立请求，传入 batch_index 锁定提交顺序
        const responses = await Promise.allSettled(
          Array.from({ length: effectiveBatchCount }, async (_, batchIdx) => {
            const batchParams = { ...params };
            if (mergedPoses.length > 0 && batchIdx < mergedPoses.length) {
              const pose = mergedPoses[batchIdx];
              if (pose && pose.image_url) {
                batchParams.ref_image_openpose = pose.image_url;
                if (!isPublicStorageUrl(pose.image_url)) {
                  markParamUrlOnly(batchParams, "ref_image_openpose");
                }
              }
            }
            for (const key of batchPromptKeys) {
              const prompts = cozeParams[key];
              if (Array.isArray(prompts) && batchIdx < prompts.length) {
                batchParams[key] = prompts[batchIdx];
              }
            }
            for (const key of batchMultiImageKeys) {
              const images = cozeParams[key];
              if (Array.isArray(images)) {
                if (images.length > 0 && Array.isArray(images[0])) {
                  const batchImages = (images as string[][])[batchIdx] || [];
                  const validImages = batchImages.filter(url => typeof url === "string" && url !== "");
                  if (validImages.length > 0) {
                    batchParams[key] = validImages;
                  }
                } else {
                  const validImages = images.filter((url: unknown) => typeof url === "string" && url !== "");
                  if (validImages.length > 0) {
                    batchParams[key] = validImages[batchIdx % validImages.length];
                  }
                }
              }
            }
            if (isDetailReplicaWorkflow) {
              const referenceImage = detailReplicaState.references[batchIdx];
              if (referenceImage) {
                batchParams[detailReplicaKeys.reference] = referenceImage;
              }
              const screenRatio = detailReplicaState.ratios[batchIdx];
              if (screenRatio) {
                batchParams[detailReplicaKeys.ratio] = screenRatio;
              }
              const screenCropSize = detailReplicaState.cropSizes[batchIdx];
              if (screenCropSize) {
                batchParams[OUTPUT_CROP_SIZE_PARAM] = screenCropSize;
              } else {
                delete batchParams[OUTPUT_CROP_SIZE_PARAM];
              }
              const resolvedRatios = await resolveDetailReplicaAutoRatioParams(
                batchParams,
                workflowUIConfig,
                detailReplicaKeys.reference,
              );
              detailReplicaResolvedRatios.push(...resolvedRatios);
              applyRatioSelectorParams(batchParams, workflowUIConfig);
            }
            applyPerBatchRatioOverride(batchParams, batchIdx);
            return ecomImageApi.generateCoze({ feature: currentFeatureKey, batch_count: 1, group_id: groupId, batch_index: batchIdx + 1, params: batchParams }, captureDurableOwner(ecomStorageScope, requestTabId, "generation", batchIdx));
          })
        );

        // 关键修复：保留下标空位（不 filter 压缩），确保 currentTaskIds[i] 与
        // generatedImages[i] 下标对齐——任一槽位失败时点击「重试」拿到的是
        // 该槽位真实 task_id 而非相邻槽位 id（避免参数错位）。
        taskIds = responses.map((response) => response.status === "fulfilled"
          ? response.value.data?.task_id || ""
          : "");
        if (taskIds.every(id => !id)) {
          const rejected = responses.find((response) => response.status === "rejected");
          throw rejected?.reason || new Error("创建任务失败");
        }

        const visible = finishGenerationSubmission(requestTabId, requestEpoch, {
          status: "generating",
          progress: 5,
          taskProgresses: new Array(taskIds.length).fill(0),
          currentTaskIds: taskIds,
          viewingTaskId: taskIds.find(Boolean) || "",
          failedIndexes: taskIds.map((id, i) => (id ? -1 : i)).filter(i => i >= 0),
          currentPrompt: JSON.stringify(params),
          previewOutputCount: taskIds.length,
        });
        if (visible && isDetailReplicaWorkflow && detailReplicaResolvedRatios.length > 0) {
          feedback.info(
            `已根据每屏参考图自动选择比例 ${detailReplicaResolvedRatios.map((item) => item.ratio).join("、")}`,
          );
        }

      }
    } catch (error: unknown) {
      if (isDurableSubmissionPending(error)) return;
      console.error('Generate failed:', error);
      const raw = error instanceof Error ? error.message : "";
      const failedPatch: Partial<EcomWorkspaceTab> = {
        status: "failed", progress: 0, errorMessage: toErrorCode(raw),
        generationStartedAt: null, currentTaskIds: [],
      };
      const visible = pendingGenerationSubmissionsRef.current.get(requestTabId) === requestEpoch
        ? finishGenerationSubmission(requestTabId, requestEpoch, failedPatch)
        : isGenerationRequestCurrent(requestTabId, requestEpoch);
      if (!visible) return;
      stopProgressSimulation();
      setErrorMessage(toErrorCode(raw));
      setGenerationStartedAt(null);
      setStatus("failed");
      setCurrentTaskIds([]);
      if (isInsufficientBalanceError(error)) setShowBalanceModal(true);
    }
  };

  /** 已完成任务点击「重新生成」：仅保留左侧参数与当前预览，不提交 API，需用户手动点「开始生图」。 */
  const prepareRegenerateWithoutSubmit = useCallback(() => {
    stopProgressSimulation();
    clearActiveRefineState();
    setCurrentTaskIds([]);
    setBatchChildTaskIds([]);
    setViewingTaskId("");
    viewingTaskIdRef.current = "";
    setFailedIndexes([]);
    setRegeneratingIndexes([]);
    setHistoryFeatureKey(null);
    setErrorMessage("");
    feedback.info("参数已保留，请点击「开始生图」重新生成");
  }, [clearActiveRefineState, stopProgressSimulation]);

  const openRetryConfirmModal = useCallback((taskIds: string[], singleIndex: number | null = null) => {
    setRetryModalTaskIds(taskIds);
    setRetryModalSingleIndex(singleIndex);
    setRetryModalOpen(true);
  }, []);

  const handleRegenerate = async (source?: unknown) => {
    const retrySource = normalizeEcomRetrySource(source, "preview");
    const retryableIds = (currentTaskIds.length > 0 ? currentTaskIds : batchChildTaskIds).filter(Boolean);
    const candidateTaskIds = retryableIds.length > 0 ? retryableIds : [viewingTaskId];
    const decision = decideRetryEntryAction(retrySource, candidateTaskIds);

    if (shouldOpenRetryModalForDecision(decision)) {
      openRetryConfirmModal(decision.taskIds);
      return;
    }

    if (decision.action === "restore-params") {
      prepareRegenerateWithoutSubmit();
      return;
    }

    feedback.warning("未找到可重试的任务，请从历史记录恢复参数后重新生成");
  };

  const executeRetrySingle = async (index: number, retryTaskId: string, modifiedParams: Record<string, unknown> | null) => {
    clearActiveRefineState();
    persistCurrentWorkspaceTab();

    const ownerTabId = activeTabIdRef.current;
    const ownerHistory = loadEcomTabHistory(ecomStorageScope);
    const ownerTab = ownerHistory?.tabs[ownerTabId];
    if (
      !ownerTab
      || !ownerHistory.openTabIds.includes(ownerTabId)
      || closedTabIdsRef.current.has(ownerTabId)
    ) return;

    const patchIndex = retryTaskId === ownerTab.viewingTaskId ? index : 0;
    const sourceIdentity = captureRetrySourceIdentity({
      ...toRetryWorkspaceContext(ownerTab),
      sourceTaskId: retryTaskId,
      slotIndex: index,
      patchIndex,
    });
    if (!sourceIdentity) return;

    lastRequestTimeRef.current = Date.now();

    try {
      const res = await ecomImageApi.retryTask(retryTaskId, modifiedParams ?? undefined, captureDurableOwner(ecomStorageScope, ownerTabId, "retry", index, sourceIdentity));
      if (!res.data?.task_id) throw new Error("重试任务失败");
      const tracker = bindActualRetryTaskId(sourceIdentity, res.data.task_id);
      if (!tracker || activeRetryTrackerRef.current.has(tracker.retryTaskId)) return;

      activeRetryTrackerRef.current.set(tracker.retryTaskId, tracker);
      updateRetryOwnerWorkspace(tracker, "pending");
      setRetryTrackerTrigger(prev => prev + 1);
      setHistoryRefreshTrigger(prev => prev + 1);
    } catch (error: unknown) {
      if (isDurableSubmissionPending(error)) return;
      console.error('Single retry failed:', error);
      const latestHistory = loadEcomTabHistory(ecomStorageScope);
      const latestOwnerTab = latestHistory?.tabs[ownerTabId];
      const mayNotifyOwner = activeTabIdRef.current === ownerTabId
        && !closedTabIdsRef.current.has(ownerTabId)
        && !!latestOwnerTab
        && !!latestHistory?.openTabIds.includes(ownerTabId)
        && isRetrySourceIdentityCurrent(sourceIdentity, toRetryWorkspaceContext(latestOwnerTab))
        && isRetrySourceIdentityCurrent(sourceIdentity, activeRetryWorkspaceContextRef.current);
      if (!mayNotifyOwner) return;
      const raw = error instanceof Error ? error.message : "";
      if (isInsufficientBalanceError(error)) {
        setShowBalanceModal(true);
        return;
      }
      feedback.error({ code: mapLegacyEcomFailureCode(raw), fallback: "operation" });
    }
  };

  const handleRegenerateSingle = async (index: number, source?: unknown) => {
    const retrySource = normalizeEcomRetrySource(source, "failed-card");

    const originalTaskId = resolveSingleRetryTaskId({
      index,
      batchChildTaskIds,
      currentTaskIds,
      children: batchTaskStatus?.children,
    });
    const decision = decideRetryEntryAction(retrySource, originalTaskId ? [originalTaskId] : []);

    if (shouldOpenRetryModalForDecision(decision)) {
      openRetryConfirmModal(decision.taskIds, index);
      return;
    }

    if (decision.action === "restore-params") {
      prepareRegenerateWithoutSubmit();
      return;
    }

    feedback.warning("未找到可重试的任务，请从历史记录恢复参数后重新生成");
  };

  const handleRetryConfirm = async (
    taskIds: string[],
    modifiedParams: Record<string, unknown> | null,
    batchParams?: Record<string, Record<string, unknown> | null>,
  ) => {
    if (taskIds.length === 0) {
      return;
    }
    setRetryModalOpen(false);
    if (retryModalSingleIndex !== null) {
      await executeRetrySingle(retryModalSingleIndex, taskIds[0], modifiedParams);
      return;
    }

    // Resolve slots before any response replaces the source IDs. The modal's
    // filtered task list can be sparse or reordered relative to the preview.
    const retryTargets = Array.from(new Set(taskIds.filter(Boolean))).map((taskId) => {
      const childIndex = batchChildTaskIds.indexOf(taskId);
      const currentIndex = currentTaskIds.indexOf(taskId);
      const index = childIndex >= 0 ? childIndex : currentIndex >= 0 ? currentIndex : taskId === viewingTaskId ? 0 : -1;
      return { taskId, index };
    }).filter(({ index }) => index >= 0);

    // Each single retry retains its own durable owner, tracker and safe error
    // handling, so a failed submission does not stop the remaining tasks.
    await Promise.all(retryTargets.map(({ taskId, index }) => executeRetrySingle(
      index, taskId, batchParams ? batchParams[taskId] ?? null : modifiedParams,
    )));
  };

  // Track a refine operation in the tab that launched it. The tracker and tab
  // preview survive tab switches and reloads; closing that tab discards both.
  const handleRefineTrack = useCallback((info: EcomRefineTrackInfo): boolean => {
    const existingEntry = Array.from(activeRefineTrackerRef.current.entries())
      .find(([, tracker]) => tracker.operationId === info.operationId);
    if (info.refineTaskId !== "__pending__" && !existingEntry) return false;

    const existing = existingEntry?.[1];
    const ownerTabId = existing?.ownerTabId || info.ownerTabId;
    if (!ownerTabId || closedTabIdsRef.current.has(ownerTabId)) return false;
    const history = loadEcomTabHistory(ecomStorageScope);
    if (!history?.tabs[ownerTabId] || !history.openTabIds.includes(ownerTabId)) return false;
    const targetTaskId = existing?.targetTaskId || info.targetTaskId;
    const imageIndex = existing?.batchIndex ?? info.imageIndex;
    const trackerKey = existingEntry?.[0] || createRefineTrackerKey(ownerTabId, targetTaskId, imageIndex);
    if (
      info.refineTaskId === "__pending__"
      && !existingEntry
      && activeRefineTrackerRef.current.has(trackerKey)
    ) return false;

    if (info.refineTaskId === "__cancel__") {
      activeRefineTrackerRef.current.delete(trackerKey);
      updateRefinePreviewForTab(ownerTabId, targetTaskId, imageIndex, null);
    } else {
      const currentImages = generatedImages.length > 0 ? generatedImages : generatedImage ? [generatedImage] : [];
      const tracker: ActiveRefineTracker = {
        refineTaskId: info.refineTaskId,
        targetTaskId,
        batchParentId: existing?.batchParentId || viewingTaskIdRef.current || "",
        batchIndex: imageIndex,
        ownerTabId,
        operationId: info.operationId,
        originalUrl: existing?.originalUrl || info.originalUrl || currentImages[imageIndex] || "",
        startedAt: existing?.startedAt || Date.now(),
      };
      activeRefineTrackerRef.current.set(trackerKey, tracker);
      updateRefinePreviewForTab(ownerTabId, targetTaskId, imageIndex, {
        sourceTaskId: targetTaskId,
        imageIndex,
        originalUrl: tracker.originalUrl,
        refineTaskId: info.refineTaskId,
        status: info.refineTaskId === "__pending__" ? "pending" : "processing",
        startedAt: tracker.startedAt,
      });
      if (info.refineTaskId === "__pending__") {
        try {
          registerDurableRefineOwner(info.operationId, ecomStorageScope, ownerTabId, {
            sourceTaskId: targetTaskId, imageIndex, originalUrl: tracker.originalUrl,
            refineTaskId: info.refineTaskId, status: "pending", startedAt: tracker.startedAt,
          });
        } catch {
          activeRefineTrackerRef.current.delete(trackerKey);
          updateRefinePreviewForTab(ownerTabId, targetTaskId, imageIndex, null);
          return false;
        }
      }
    }
    setRefineTrackerTrigger(prev => prev + 1);
    setTabStatusMap((previous) => previous[ownerTabId] === "generating"
      ? previous
      : { ...previous, [ownerTabId]: "generating" });
    return true;
  }, [ecomStorageScope, generatedImage, generatedImages, updateRefinePreviewForTab]);

  // ====== 批处理生成 ======
  const handleBatchGenerate = async () => {
    const currentFeatureKey = getFeatureKey(activeSubOption);
    const bpConfig = getCurrentBatchProcessConfig();

    const now = Date.now();
    if (now - lastRequestTimeRef.current < GENERATE_BUTTON_COOLDOWN_MS) {
      feedback.warning("请求太频繁，请稍后再试");
      return;
    }
    lastRequestTimeRef.current = now;

    // Apply the same tab-forking rule before inspire batch generation can return early.
    forkActiveTabIfGenerating();
    clearActiveRefineState();
    const { requestTabId, requestEpoch } = beginGenerationRequest();

    // 灵感反推工作流：走 inspire 批处理路径
    if (isCurrentInspireWorkflow()) {
      await handleInspireBatchGenerate();
      return;
    }
    // Wait for any ongoing image uploads
    if (hasPendingBlobUrls(cozeParamsRef.current)) {
      feedback.info("图片上传中，完成后自动开始生成...");
      const ok = await waitForUploads();
      if (!ok) return;
      if (!isGenerationRequestCurrent(requestTabId, requestEpoch)) return;
    }

    const generationStartTime = Date.now();

    // Validate batch params have values
    const batchParams: Record<string, unknown[]> = {};
    let totalItems = 0;
    for (const paramKey of bpConfig.params) {
      const values = batchParamValues[paramKey] || [];
      if (values.length > 0) {
        batchParams[paramKey] = values;
        if (values.length > totalItems) totalItems = values.length;
      }
    }
    if (totalItems === 0) {
      feedback.warning("请至少上传一个批处理文件");
      return;
    }

    // Build base params (non-batch params)
    let params: Record<string, unknown> = {};
    for (const [key, val] of Object.entries(cozeParams)) {
      if (bpConfig.params.includes(key)) continue;
      if (val && typeof val === 'object' && 'coze_value' in (val as Record<string, unknown>)) {
        params[key] = (val as { coze_value: string }).coze_value;
      } else if (val !== null && val !== undefined) {
        params[key] = val;
      }
    }
    params = stripEcomDraftOnlyParams(params);
    normalizeLogoPresetSubmission(params, workflowUIConfig);
    await applyAutoRatioParams(params, workflowUIConfig);
    if (!isGenerationRequestCurrent(requestTabId, requestEpoch)) return;
    applyRatioSelectorParams(params, workflowUIConfig);

    try {
      if (!startGenerationSubmission(requestTabId, requestEpoch, generationStartTime, totalItems)) return;

      const res = await ecomImageApi.batchGenerate({
        feature: currentFeatureKey,
        params,
        batch_params: batchParams,
        concurrency: batchConcurrency,
      }, captureDurableOwner(ecomStorageScope, requestTabId));

      if (!res.data?.parent_task_id) throw new Error("创建批处理任务失败");

      const batchTotal = res.data.batch_total || totalItems;
      finishGenerationSubmission(requestTabId, requestEpoch, {
        status: "generating",
        progress: 5,
        batchParentTaskId: res.data.parent_task_id,
        viewingTaskId: res.data.parent_task_id,
        previewOutputCount: batchTotal,
        taskProgresses: new Array(batchTotal).fill(-1),
        generatedImages: new Array(batchTotal).fill(""),
        currentPrompt: JSON.stringify(params),
      });
    } catch (error: unknown) {
      if (isDurableSubmissionPending(error)) return;
      console.error('Batch generate failed:', error);
      const raw = error instanceof Error ? error.message : "";
      const visible = finishGenerationSubmission(requestTabId, requestEpoch, {
        status: "failed", progress: 0, errorMessage: toErrorCode(raw),
        generationStartedAt: null, batchParentTaskId: null,
      });
      if (visible && isInsufficientBalanceError(error)) setShowBalanceModal(true);
    }
  };

  // ========== 灵感反推批处理：在前端按照「批次关键参数」拆分成 N 个批次，每批一次 executeFunctional ==========
  //
  // 拆批规则：
  //   - 单图组件 image_upload：每张图作为 1 批
  //   - 多图组件 multi_image：组件 config.batch_size 决定每批 N 张图（2~5），上传数必须为 N 的整数倍
  //   - 未绑定为批次关键参数的字段：所有批次复用同一值
  //   - 多个批次关键参数共存时：每个参数独立拆批，最终批次数 = 所有参数批次数中的最大值
  //
  const handleInspireBatchGenerate = useCallback(async () => {
    const bpConfig = getCurrentBatchProcessConfig();
    if (!bpConfig.enabled || bpConfig.params.length === 0) {
      feedback.warning("当前工作流未启用批处理模式或未配置批次关键参数");
      return;
    }

    if (hasPendingBlobUrls(cozeParamsRef.current)) {
      feedback.info("图片上传中，完成后自动开始反推...");
      const ok = await waitForUploads();
      if (!ok) return;
    }

    // 收集每个批次关键参数的"按批切片"
    type BatchSlice = { paramKey: string; isMultiImage: boolean; chunks: unknown[] };
    const slices: BatchSlice[] = [];

    for (const paramKey of bpConfig.params) {
      const values = batchParamValues[paramKey] || [];
      if (values.length === 0) continue;

      const comp = workflowUIConfig?.components.find(c => c.coze_param_key === paramKey);
      const compType = comp?.component_type || "image_upload";
      const isMultiImage = compType === "multi_image" || compType === "batch_multi_image";

      if (!isMultiImage) {
        // 单图（包括 image_upload / 文本字段）：1 个值 = 1 批
        const chunks = values.map(v => v);
        slices.push({ paramKey, isMultiImage: false, chunks });
      } else {
        // 多图：必须配置 batch_size，且 values.length 必须是 batch_size 整数倍
        const rawBatchSize = (comp?.config as Record<string, unknown> | undefined)?.batch_size;
        const batchSize = typeof rawBatchSize === "number" ? rawBatchSize : parseInt(String(rawBatchSize || ""), 10);
        if (!Number.isFinite(batchSize) || batchSize < 2 || batchSize > 5) {
          feedback.error({ code: "E5304", fallback: "operation" });
          return;
        }
        if (values.length % batchSize !== 0) {
          feedback.error({ code: "E5304", fallback: "operation" });
          return;
        }
        const chunks: unknown[][] = [];
        for (let i = 0; i < values.length; i += batchSize) {
          chunks.push(values.slice(i, i + batchSize));
        }
        slices.push({ paramKey, isMultiImage: true, chunks });
      }
    }

    if (slices.length === 0) {
      feedback.warning("请至少上传一个批处理文件");
      return;
    }

    // 总批次数 = 各 slice 批次数的最大值；不足的 slice 用其最后一组循环复用
    const totalBatches = Math.max(...slices.map(s => s.chunks.length));

    // 构建 base params（非批次关键参数）
    const baseParams: Record<string, unknown> = {};
    const imageReversePromptKeys = workflowUIConfig?.components
      .filter((c) => c.component_type === "image_reverse_prompt")
      .map((c) => c.coze_param_key);
    const baseInputImages = collectImageReversePromptDraftUrls(cozeParams, imageReversePromptKeys);
    for (const [key, val] of Object.entries(stripEcomDraftOnlyParams(cozeParams))) {
      if (bpConfig.params.includes(key)) continue;
      if (val == null) continue;
      if (val && typeof val === "object" && "coze_value" in (val as Record<string, unknown>)) {
        baseParams[key] = (val as { coze_value: string }).coze_value;
      } else {
        baseParams[key] = val;
      }
    }
    baseInputImages.forEach((imageUrl, index) => {
      baseParams[`__image_reverse_prompt_image_${index + 1}`] = imageUrl;
    });
    const presetLabel = typeof baseParams.preset_label === "string" ? (baseParams.preset_label as string) : undefined;

    // 拼装每批次的 params
    let multiTemplateIds: number[] | undefined;
    const tplComp = workflowUIConfig?.components.find(c => c.component_type === "template_multi_select");
    if (tplComp) {
      const rawTemplateIds = cozeParams[tplComp.coze_param_key];
      if (Array.isArray(rawTemplateIds) && rawTemplateIds.length > 0) {
        multiTemplateIds = rawTemplateIds.filter((id): id is number => typeof id === "number");
      }
      if (!multiTemplateIds || multiTemplateIds.length === 0) {
        const templates = (tplComp.config?.templates as Array<{ id: number }> | undefined) || [];
        if (templates[0]?.id != null) {
          multiTemplateIds = [templates[0].id];
        }
      }
    }

    const batches: Array<{ params: Record<string, unknown>; inputImages: string[]; presetLabel?: string; templateIds?: number[] }> = [];
    for (let i = 0; i < totalBatches; i++) {
      const params: Record<string, unknown> = { ...baseParams };
      const inputImages: string[] = [...baseInputImages];
      for (const slice of slices) {
        const chunk = slice.chunks[Math.min(i, slice.chunks.length - 1)];
        params[slice.paramKey] = chunk;
        if (slice.isMultiImage && Array.isArray(chunk)) {
          for (const c of chunk) {
            if (typeof c === "string" && looksLikeImageURL(c)) inputImages.push(c);
          }
        } else if (typeof chunk === "string" && looksLikeImageURL(chunk)) {
          inputImages.push(chunk);
        }
      }
      batches.push({ params, inputImages, presetLabel, templateIds: multiTemplateIds });
    }

    if (multiTemplateIds && multiTemplateIds.length > 1) {
      await handleBatchedMultiTemplateSubmit(batches);
      return;
    }

    await handleInspireSubmit(batches);
  }, [
    batchParamValues,
    cozeParams,
    workflowUIConfig,
    getCurrentBatchProcessConfig,
    handleInspireSubmit,
    handleBatchedMultiTemplateSubmit,
    waitForUploads,
  ]);

  // SSE real-time batch task updates
  const [sseFailed, setSseFailed] = useState(false);
  const sseActiveRef = useRef(false);
  const batchResultsRef = useRef<EcomBatchResults>({
    batchChildTaskIds, generatedImages, taskProgresses, failedIndexes, previewOutputCount,
  });
  const batchRequestEpoch = generationRequestEpochRef.current;
  // Each foreground activation is a fresh subscription, including A -> B -> A.
  // A stale "active" flag must never disable polling for a new connection.
  useEffect(() => {
    sseActiveRef.current = false;
    batchResultsRef.current = {
      batchChildTaskIds, generatedImages, taskProgresses, failedIndexes, previewOutputCount,
    };
    setSseFailed(false);
    return () => { sseActiveRef.current = false; };
    // The accumulator is reset only when its owner changes, never by an older render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTabId, batchParentTaskId, batchRequestEpoch]);

  useBatchSSE({
    sessionKey: batchRequestEpoch,
    parentTaskId: batchParentTaskId,
    enabled: !!batchParentTaskId && status === "generating" && !sseFailed,
    onChildUpdate: (update: BatchChildUpdate) => {
      if (!isGenerationRequestCurrent(activeTabId, batchRequestEpoch)) return;
      if (update.parent_task_id !== batchParentTaskId) return;
      const idx = update.batch_index - 1;
      const previous = batchResultsRef.current;
      if (!Number.isSafeInteger(idx) || idx < 0 || idx >= previous.previewOutputCount || !update.child_uuid) return;
      if (previous.batchChildTaskIds[idx] && previous.batchChildTaskIds[idx] !== update.child_uuid) return;
      sseActiveRef.current = true;
      const results = mergeEcomBatchResults(previous, [{
        uuid: update.child_uuid, batch_index: update.batch_index, status: update.status,
        progress: update.progress, output_images: [update.output_image],
      }]);
      batchResultsRef.current = results;
      setBatchChildTaskIds(results.batchChildTaskIds);
      setGeneratedImages(results.generatedImages);
      setTaskProgresses(results.taskProgresses);
      setFailedIndexes(results.failedIndexes);
      setProgress(prev => Math.max(prev, update.progress || 0));
    },
    onDone: () => {
      if (!isGenerationRequestCurrent(activeTabId, batchRequestEpoch)) return;
      // Reconnecting to a terminal parent emits only "done", without replaying
      // children. Keep its identity until polling reconciles the full result.
      sseActiveRef.current = false;
      setSseFailed(true);
    },
    onError: () => {
      if (!isGenerationRequestCurrent(activeTabId, batchRequestEpoch)) return;
      sseActiveRef.current = false;
      setSseFailed(true);
    },
  });

  // SSE supplies fast updates; snapshots also recover missed events on quiet streams.
  useEffect(() => {
    if (!batchParentTaskId || status !== "generating") return;
    let stopped = false;
    let lastSnapshotAt = 0;
    const isCurrentBatch = () => !stopped && isGenerationRequestCurrent(activeTabId, batchRequestEpoch);
    const poll = async () => {
      if (!isCurrentBatch()) return;
      if (sseActiveRef.current && !sseFailed && Date.now() - lastSnapshotAt < 10_000) return;
      lastSnapshotAt = Date.now();
      try {
        const res = await getBatchTaskStatus(batchParentTaskId);
        if (!res.data || !isCurrentBatch()) return;
        if (res.data.parent_task_id && res.data.parent_task_id !== batchParentTaskId) return;

        setBatchTaskStatus(res.data);
        const pct = res.data.progress || 0;
        setProgress(prev => Math.max(prev, pct));

        const results = mergeEcomBatchResults(batchResultsRef.current, res.data.children || [], res.data.batch_total, res.data.status);
        batchResultsRef.current = results;
        setPreviewOutputCount(results.previewOutputCount);
        setBatchChildTaskIds(results.batchChildTaskIds);
        setGeneratedImages(results.generatedImages);
        setTaskProgresses(results.taskProgresses);
        setFailedIndexes(results.failedIndexes);

        if (hasAllEcomBatchResults(results)) {
          stopped = true;
          stopProgressSimulation();
          setProgress(100);
          setGenerationStartedAt(null);
          setStatus(results.generatedImages.some(Boolean) ? "completed" : "failed");
          if (results.failedIndexes.length > 0) {
            setErrorMessage("部分批处理子任务失败");
          }
          setHistoryRefreshTrigger(prev => prev + 1);
          setBatchParentTaskId(null);
          setHistoryViewOverlay(null);
          return;
        }
      } catch {
        // poll error, continue
      }
    };

    const pollController = startEcomPollingLoop({
      poll,
      baseDelayMs: 2000,
    });

    return () => {
      stopped = true;
      pollController.stop();
    };
  }, [activeTabId, batchParentTaskId, batchRequestEpoch, isGenerationRequestCurrent, status, sseFailed]);

  const handleDownload = async (selectedUrls?: string[]) => {
    if (selectedUrls && selectedUrls.length > 0) {
      await downloadFiles(selectedUrls, "flowx");
      return;
    }
    const validImages = generatedImages.filter(Boolean);
    if (validImages.length > 0) {
      await downloadFiles(validImages, "flowx");
    } else if (generatedImage) {
      await downloadFile(generatedImage, `flowx_${Date.now()}.png`);
    }
  };

  const handleFavorite = async (imageUrl: string) => {
    if (!imageUrl) return;
    try {
      if (previewFavoriteTaskUUID) {
        const result = await togglePreviewFavorite();
        if (typeof result === "boolean") feedback.success(result ? "已添加到我的收藏" : "已取消收藏");
        return;
      }
      const inspirationModule = await import("../lib/inspiration-api");
      if (previewFallbackFavorite?.url === imageUrl) {
        const res = await inspirationModule.default.favoritePost(previewFallbackFavorite.uuid);
        if (typeof res.data?.is_favorited !== "boolean") throw new Error("missing favorite state");
        setPreviewFallbackFavorite({ ...previewFallbackFavorite, favorited: res.data.is_favorited });
        feedback.success(res.data.is_favorited ? "已添加到我的收藏" : "已取消收藏");
        return;
      }
      const meta = await inspirationModule.getImageMeta(imageUrl);
      const res = await inspirationModule.default.createPost({
        title: `${activeMainTab} - ${activeSubOption}`,
        description: "来自电商生图",
        media_url: imageUrl,
        media_type: "image",
        category: "其他",
        tags: [],
        prompt: currentPrompt || undefined,
        visibility: "private",
        width: meta.width,
        height: meta.height,
      });
      if (!res.data?.uuid) throw new Error("missing post uuid");
      const favorite = await inspirationModule.default.favoritePost(res.data.uuid);
      setPreviewFallbackFavorite({ url: imageUrl, uuid: res.data.uuid, favorited: !!favorite.data?.is_favorited });
      feedback.success("已添加到我的收藏");
    } catch {
      feedback.error({ code: "E5007", fallback: "operation" });
    }
  };

  // 主功能 Tab 选项（顺序由后端 module-names.sort_order 决定，可在后台「工作流配置」调整）
  const mainTabs: MainTab[] = moduleConfigs.map((item) => item.module_key);

  // 已登录用户：严格按后端返回的可见工作流（团队权限过滤后的结果）
  // 未登录用户：AI 商拍加载失败或结果为空时，使用内置默认选项作为 fallback。
  const isLoggedIn = !!user;
  const aiShangpaiOptions = useMemo(() => {
    if (!configsLoaded) return [];
    const configuredOptions = getModuleOptions(ECOM_IMAGE_MODULE_KEY);
    if (configuredOptions.length > 0) return configuredOptions;
    if (isLoggedIn) return [];
    return Object.entries(DEFAULT_FEATURE_NAMES).map(([feature_key, name]) => ({ feature_key, name }));
  }, [configsLoaded, getModuleOptions, isLoggedIn]);

  const targetGenerateWorkflowOptions = Array.from(
    new Map(
      allWorkflowConfigs
        .filter(wf => wf.is_enabled && wf.execution_mode !== "llm_vision_reverse")
        .map(wf => [wf.feature_key, { feature_key: wf.feature_key, name: wf.name }] as const),
    ).values(),
  );

  const restoredInspirePromptForDisplay = useMemo(() => {
    const candidates = [
      normalizeReversePromptToPlainText(extractPromptValue(
        typeof cozeParams.inspire_prompt === "string" ? cozeParams.inspire_prompt : "",
      )),
      cozeParams.prompt,
      cozeParams.image_reverse_prompt,
      currentPrompt,
    ];
    for (const candidate of candidates) {
      if (typeof candidate === "string") {
        const value = extractPromptValue(candidate);
        if (value) return value;
      }
      if (Array.isArray(candidate)) {
        const value = candidate
          .map((item) => typeof item === "string" ? extractPromptValue(item) : "")
          .find(Boolean);
        if (value) return value;
      }
    }
    return "";
  }, [cozeParams, currentPrompt]);

  const restoredInspireInputImagesForDisplay = useMemo(() => (
    collectImageURLsFromParams(cozeParams)
  ), [cozeParams]);

  const displayedInspireResults = useMemo<InspireBatchResult[]>(() => {
    if (inspireResults.length > 0) return inspireResults;
    if (activeMainTab !== INSPIRE_REVERSE_MODULE_KEY || !restoredInspirePromptForDisplay) {
      return inspireResults;
    }
    return [{
      // 数据隔离：恢复展示用的反推结果不允许借用真实任务 id，
      // 否则 AssetPanel 会为该 id 合成幽灵「反推」历史卡。
      taskId: "restored-inspire-result",
      status: "completed",
      prompt: restoredInspirePromptForDisplay,
      inputImages: restoredInspireInputImagesForDisplay,
      batchIndex: 1,
    }];
  }, [
    activeMainTab,
    inspireResults,
    restoredInspireInputImagesForDisplay,
    restoredInspirePromptForDisplay,
  ]);

  const activeWorkflowKey = getFeatureKey(activeSubOption);
  const currentInspireTemplateBindings = useMemo(() => {
    const currentWorkflowUIConfig = workflowUIConfig?.workflow_key === activeWorkflowKey
      ? workflowUIConfig
      : null;
    const templateComponent = currentWorkflowUIConfig?.components?.find(
      (component) => component.component_type === "template_multi_select",
    );
    const templates = currentWorkflowUIConfig
      ? (templateComponent?.config?.templates as InspireTemplateTargetConfig[] | undefined) || []
      : undefined;
    return resolveInspireWorkflowBindings({
      templates,
      selectedTemplateIds: templateComponent ? cozeParams[templateComponent.coze_param_key] : undefined,
      resultTemplateIds: displayedInspireResults.map((result) => result.templateId),
    });
  }, [activeWorkflowKey, cozeParams, displayedInspireResults, workflowUIConfig]);

  const applicableRefinePreviews = useMemo(
    () => refinePreviews.filter((preview) => {
      if (preview.imageIndex < 0 || preview.imageIndex >= Math.max(previewOutputCount, 1)) return false;
      const sourceTaskMatchesTab = preview.sourceTaskId === viewingTaskId
        || currentTaskIds.includes(preview.sourceTaskId)
        || batchChildTaskIds.includes(preview.sourceTaskId);
      if (!sourceTaskMatchesTab) return false;
      const slotTaskId = batchChildTaskIds[preview.imageIndex];
      return !slotTaskId || slotTaskId === preview.sourceTaskId || preview.sourceTaskId === viewingTaskId;
    }),
    [batchChildTaskIds, currentTaskIds, previewOutputCount, refinePreviews, viewingTaskId],
  );
  const activeRefinesForCurrentTab = useMemo(() => {
    return resolveActiveRefinesForTab(activeRefineTrackerRef.current.values(), {
      activeTabId,
      batchChildTaskIds,
      currentTaskIds,
      viewingTaskId,
    });
  }, [activeTabId, batchChildTaskIds, currentTaskIds, refineTrackerTrigger, viewingTaskId]);
  const displayedRefineOutput = useMemo(
    () => applyEcomRefinePreviews(generatedImage, generatedImages, applicableRefinePreviews),
    [applicableRefinePreviews, generatedImage, generatedImages],
  );
  const displayGeneratedImage = displayedRefineOutput.generatedImage;
  const displayGeneratedImages = displayedRefineOutput.generatedImages.length > 0
    ? displayedRefineOutput.generatedImages
    : displayGeneratedImage
      ? [displayGeneratedImage]
      : [];

  const currentHistoryGeneratedImages = useMemo(() => {
    if (currentTaskIds.length === 0) return generatedImages;
    return currentTaskIds.map((_, index) => {
      if (generatedImages[index]) return generatedImages[index];
      // generatedImage can be the first *available* image after history restore,
      // which may belong to a later batch. Never fill an empty batch slot with it.
      return currentTaskIds.length === 1 && generatedImages.length === 0
        ? generatedImage || ""
        : "";
    });
  }, [currentTaskIds, generatedImage, generatedImages]);

  // 不同主功能下的次级功能选项（下拉框）
  const subOptionsMap = useMemo<Record<MainTab, { feature_key: string; name: string }[]>>(
    () => Object.fromEntries(
      moduleConfigs.map((moduleConfig) => [
        moduleConfig.module_key,
        moduleConfig.module_key === ECOM_IMAGE_MODULE_KEY ? aiShangpaiOptions : getModuleOptions(moduleConfig.module_key),
      ]),
    ),
    [aiShangpaiOptions, getModuleOptions, moduleConfigs],
  );
  const activeModuleOptions = useMemo(
    () => subOptionsMap[activeMainTab] || [],
    [activeMainTab, subOptionsMap],
  );

  const requiredFieldsFilled = useMemo(
    () => checkRequiredFieldsFilled(workflowUIConfig, cozeParams),
    [workflowUIConfig, cozeParams],
  );
  const activeWorkflowFeatureKey = getFeatureKey(activeSubOption);
  const isDetailReplicaWorkflow = isDetailPageReplicaWorkflow(activeWorkflowFeatureKey, activeSubOption);
  const detailReplicaState = useMemo(
    () => resolveDetailReplicaState(workflowUIConfig, cozeParams),
    [cozeParams, workflowUIConfig],
  );
  const detailReplicaGate = useMemo(
    () => resolveDetailReplicaGate(detailReplicaState),
    [detailReplicaState],
  );
  const insertDetailReplicaResultReferences = useCallback(async (files: FileList, insertAfterIndex: number) => {
    const allImageFiles = Array.from(files).filter(isImageFile);
    if (allImageFiles.length === 0) return;
    // 与工作区上传入口同款 30 屏上限，超限部分按顺序截断，避免反推/生图超过上限直接失败。
    const remainingSlots = Math.max(0, DETAIL_REPLICA_MAX_SCREENS - detailReplicaState.references.length);
    const imageFiles = allImageFiles.slice(0, remainingSlots);
    if (imageFiles.length === 0) {
      feedback.warning(`详情页复刻最多支持 ${DETAIL_REPLICA_MAX_SCREENS} 张参考图，请先删除部分图片`);
      return;
    }
    if (imageFiles.length < allImageFiles.length) {
      feedback.warning(`详情页复刻最多支持 ${DETAIL_REPLICA_MAX_SCREENS} 张参考图，已按顺序保留前 ${imageFiles.length} 张`);
    }

    try {
      const urls = (await Promise.all(imageFiles.map(async (file) => {
        const response = await ecomImageApi.uploadProductImage(file, { maxBytes: MAX_REVERSE_PROMPT_BYTES });
        return resolveDetailReplicaUploadURL(response.data);
      }))).filter(Boolean);
      if (urls.length === 0) throw new Error("E5003");

      const insertAt = Math.max(0, Math.min(insertAfterIndex + 1, detailReplicaState.references.length));
      const insertValues = <T,>(values: T[], inserts: T[]) => [
        ...values.slice(0, insertAt),
        ...inserts,
        ...values.slice(insertAt),
      ];
      const count = urls.length;
      const keys = resolveDetailReplicaComponentKeys(workflowUIConfig);
      const references = insertValues(detailReplicaState.references, urls);
      ownedCozeParamChange(keys.reference, references);
      if (keys.reference !== DETAIL_REPLICA_REFERENCE_IMAGES_KEY) {
        ownedCozeParamChange(DETAIL_REPLICA_REFERENCE_IMAGES_KEY, references);
      }
      ownedCozeParamChange(keys.product, insertValues(detailReplicaState.products, Array.from({ length: count }, () => [])));
      ownedCozeParamChange(keys.model, insertValues(detailReplicaState.models, Array.from({ length: count }, () => [])));
      ownedCozeParamChange(keys.prompt, insertValues(detailReplicaState.prompts, Array.from({ length: count }, () => "")));
      ownedCozeParamChange(DETAIL_REPLICA_BLANK_FLAGS_KEY, insertValues(detailReplicaState.blankFlags, Array.from({ length: count }, () => false)));
      ownedCozeParamChange(DETAIL_REPLICA_RATIOS_KEY, insertValues(detailReplicaState.ratios, Array.from({ length: count }, () => "")));
      ownedCozeParamChange(DETAIL_REPLICA_CROP_SIZES_KEY, insertValues(detailReplicaState.cropSizes, Array.from({ length: count }, () => "")));
      // 同步在效果图数组的对应位置插入空占位并补足输出数量，使结果面板在插入位显示
      // 「等待结果」占位而非生图卡片；批次子任务 id 列表同步插入空槽保持下标对齐。
      // 先把效果图/子任务数组补齐到与参考图同长（完成态下可能短于参考图），
      // 再在 insertAt 处插入，避免拼接越过数组末尾、占位落到最后一张。
      const referenceCount = detailReplicaState.references.length;
      const padToReferenceCount = <T,>(values: T[], fill: T) => {
        if (values.length >= referenceCount) return values;
        return [...values, ...Array.from({ length: referenceCount - values.length }, () => fill)];
      };
      setGeneratedImages((prev) => insertValues(padToReferenceCount(prev, ""), Array.from({ length: count }, () => "")));
      setBatchChildTaskIds((prev) => insertValues(padToReferenceCount(prev, ""), Array.from({ length: count }, () => "")));
      setPreviewOutputCount((prev) => Math.max(prev, referenceCount) + count);
      // 插入后定位到新插入的首张，让结果面板/配置面板同步聚焦到该屏。
      focusDetailReplicaIndex(insertAt);
      feedback.success("图片已插入");
    } catch {
      feedback.error({ code: "E5003", fallback: "operation" });
    }
  }, [detailReplicaState, focusDetailReplicaIndex, ownedCozeParamChange, workflowUIConfig]);
  // 结果态删除参考图的撤回历史；与工作区同款 Ctrl/Meta+Z 回退。
  const detailReplicaResultDeletionHistoryRef = useRef<DetailReplicaDeletedScreen[]>([]);
  useEffect(() => {
    detailReplicaResultDeletionHistoryRef.current = [];
  }, [detailReplicaState.references]);
  const deleteDetailReplicaResultReference = useCallback((index: number) => {
    const keys = resolveDetailReplicaComponentKeys(workflowUIConfig);
    const result = removeDetailReplicaScreen(detailReplicaState, index);
    if (!result) return;
    detailReplicaResultDeletionHistoryRef.current = [
      ...detailReplicaResultDeletionHistoryRef.current,
      result.deleted,
    ].slice(-50);
    ownedCozeParamChange(keys.reference, result.state.references);
    if (keys.reference !== DETAIL_REPLICA_REFERENCE_IMAGES_KEY) {
      ownedCozeParamChange(DETAIL_REPLICA_REFERENCE_IMAGES_KEY, result.state.references);
    }
    ownedCozeParamChange(keys.product, result.state.products);
    ownedCozeParamChange(keys.model, result.state.models);
    ownedCozeParamChange(keys.prompt, result.state.prompts);
    ownedCozeParamChange(DETAIL_REPLICA_BLANK_FLAGS_KEY, result.state.blankFlags);
    ownedCozeParamChange(DETAIL_REPLICA_RATIOS_KEY, result.state.ratios);
    ownedCozeParamChange(DETAIL_REPLICA_CROP_SIZES_KEY, result.state.cropSizes);
    // 同步删除效果图/子任务槽位并补正输出数量，保持下标与参考图对齐。
    const removeAt = <T,>(values: T[]) => values.filter((_, itemIndex) => itemIndex !== index);
    setGeneratedImages((prev) => removeAt(prev));
    setBatchChildTaskIds((prev) => removeAt(prev));
    setPreviewOutputCount((prev) => Math.max(1, prev - 1));
    // 删除后选中索引收敛到合法范围，静默更新即可，无需额外 focusRequest。
    setDetailReplicaActiveIndex(Math.max(0, Math.min(index, result.state.references.length - 1)));
  }, [detailReplicaState, ownedCozeParamChange, setDetailReplicaActiveIndex, workflowUIConfig]);
  const undoDetailReplicaResultDeletion = useCallback(() => {
    const keys = resolveDetailReplicaComponentKeys(workflowUIConfig);
    const deleted = detailReplicaResultDeletionHistoryRef.current.at(-1);
    if (!deleted) return;
    detailReplicaResultDeletionHistoryRef.current = detailReplicaResultDeletionHistoryRef.current.slice(0, -1);
    const restored = restoreDetailReplicaScreen(detailReplicaState, deleted);
    ownedCozeParamChange(keys.reference, restored.references);
    if (keys.reference !== DETAIL_REPLICA_REFERENCE_IMAGES_KEY) {
      ownedCozeParamChange(DETAIL_REPLICA_REFERENCE_IMAGES_KEY, restored.references);
    }
    ownedCozeParamChange(keys.product, restored.products);
    ownedCozeParamChange(keys.model, restored.models);
    ownedCozeParamChange(keys.prompt, restored.prompts);
    ownedCozeParamChange(DETAIL_REPLICA_BLANK_FLAGS_KEY, restored.blankFlags);
    ownedCozeParamChange(DETAIL_REPLICA_RATIOS_KEY, restored.ratios);
    ownedCozeParamChange(DETAIL_REPLICA_CROP_SIZES_KEY, restored.cropSizes);
    const insertAt = <T,>(values: T[], value: T) => [
      ...values.slice(0, deleted.index),
      value,
      ...values.slice(deleted.index),
    ];
    setGeneratedImages((prev) => insertAt(prev, ""));
    setBatchChildTaskIds((prev) => insertAt(prev, ""));
    setPreviewOutputCount((prev) => prev + 1);
    setDetailReplicaActiveIndex(Math.max(0, Math.min(deleted.index, restored.references.length - 1)));
  }, [detailReplicaState, ownedCozeParamChange, setDetailReplicaActiveIndex, workflowUIConfig]);
  const isDetailReplicaOnboarding = isDetailReplicaWorkflow
    && status === "empty"
    && !detailReplicaGate.hasReferences;
  const isFocusedOnboarding = !agentWorkspaceMode && (isTemplateOnboarding || isDetailReplicaOnboarding);
  const isFocusedWorkspace = !agentWorkspaceMode && (isTemplateOnboarding || isDetailReplicaWorkflow);
  // 详情页复刻生成中/结果态也保留右侧操作台，仅在 onboarding（尚无参考图）时隐藏
  const shouldHideGeneratePanel = isFocusedOnboarding || agentWorkspaceMode;
  const useSplitDetailReplicaLayout = !agentWorkspaceMode && isDetailReplicaWorkflow
    && activeWorkspaceView === "generate";
  const extendDetailReplicaTabStrip = useSplitDetailReplicaLayout
    && shouldHideGeneratePanel;

  const cozeTemplateSetGenerateGate = resolveCozeTemplateSetGenerateGate(
    workflowUIConfig?.components,
    cozeParams,
  );
  const cozeTemplateSetGenerateDisabled = cozeTemplateSetGenerateGate.disabled;
  const cozeTemplateSetGenerateValidation = cozeTemplateSetGenerateGate.validation;

  const noGenerateOptions = activeModuleOptions.length === 0;

  const batchGenerateTotal = batchProcessMode
    ? Math.max(...Object.values(batchParamValues).map((values) => values.length), 0)
    : 0;

  const isBatchGenerateDisabled = batchProcessMode && batchGenerateTotal === 0;
  const isGenerateSubmitDisabled =
    !configsLoaded || agentWorkspaceMode ||
    noGenerateOptions ||
    cozeTemplateSetGenerateDisabled ||
    (isDetailReplicaWorkflow && !detailReplicaGate.canGenerate) ||
    (!isDetailReplicaWorkflow && !batchProcessMode && !requiredFieldsFilled) ||
    isBatchGenerateDisabled ||
    promptGenerating;
  const isGenerateButtonDisabled = isGenerateSubmitDisabled || generateButtonCoolingDown;

  const handleGenerateSubmit = (overrideBatchCount?: number) => {
    // 防止 onClick 直接绑定时把 React 合成事件对象当作 overrideBatchCount 传入
    // （event 是 truthy 对象，会导致 Math.max(1, event) = NaN，从而走"创建任务失败"分支）。
    const safeOverride = typeof overrideBatchCount === 'number' ? overrideBatchCount : undefined;
    // 详情页复刻 30 屏上限：生图按每屏一个子任务提交，超限前全局报错拦截，
    // 与反推入口（runReverseAll）保持同款提示。
    if (isDetailReplicaWorkflow && detailReplicaState.references.length > DETAIL_REPLICA_MAX_SCREENS) {
      feedback.error({ code: "E5304", fallback: "operation" });
      return;
    }
    if (isGenerateSubmitDisabled || promptGeneratingRef.current || generateButtonCooldownRef.current) return;
    startGenerateButtonCooldown();
    if (batchProcessMode) {
      void handleBatchGenerate();
      return;
    }
    void handleGenerate(safeOverride);
  };

  const renderWorkflowConfigLoading = () => (
    <div className="flex items-center justify-center py-8">
      <div className="w-5 h-5 border-2 border-white/30 border-t-white/80 rounded-full animate-spin" />
      <span className="ml-2 text-sm text-white/50">加载配置中...</span>
    </div>
  );

  const renderWorkflowConfigError = () => (
    <div className="flex flex-col items-center justify-center py-10 text-center">
      <svg className="w-8 h-8 text-white/20 mb-2" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
        <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
        <path d="M12 8v4M12 16h.01" strokeLinecap="round" />
      </svg>
      <span className="text-sm text-white/40">配置加载失败，请重试</span>
      <button
        type="button"
        onClick={() => setWorkflowConfigReloadKey((prev) => prev + 1)}
        className="mt-3 h-8 rounded-sm bg-[#2CE9E0] px-4 text-xs font-medium text-[#021315] transition-all hover:brightness-110"
      >
        重试
      </button>
    </div>
  );

  const renderWorkflowConfigEmpty = () => (
    <div className="flex flex-col items-center justify-center py-10 text-center">
      <svg className="w-8 h-8 text-white/20 mb-2" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
        <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
        <path d="M12 8v4M12 16h.01" strokeLinecap="round" />
      </svg>
      <span className="text-sm text-white/40">该工作流尚未配置 UI 组件</span>
      <span className="text-xs text-white/25 mt-1">请在管理端 (4001) 配置组件后刷新</span>
    </div>
  );

  const handleGenerateShortcutKeyDown = (e: ReactKeyboardEvent<HTMLDivElement>) => {
    if (!e.ctrlKey || e.key !== "Enter" || e.repeat || e.nativeEvent.isComposing) return;
    e.preventDefault();
    e.stopPropagation();
    if (isGenerateButtonDisabled) {
      if (generateButtonCoolingDown) return;
      if (promptGeneratingRef.current) {
        feedback.info("提示词生成中，请稍候");
        return;
      }
      if (noGenerateOptions) {
        feedback.warning("当前暂无可用工作流");
      } else if (cozeTemplateSetGenerateDisabled) {
        feedback.warning(getCozeTemplateSetValidationMessage(cozeTemplateSetGenerateValidation));
      } else if (isDetailReplicaWorkflow) {
        feedback.warning(detailReplicaGate.promptsReady ? "请完成详情页复刻配置" : "请先反推全部提示词");
      } else {
        feedback.warning(batchProcessMode ? "请至少上传一个批处理文件" : "请填写所有带 * 的必填项");
      }
      return;
    }
    handleGenerateSubmit();
  };

  const batchRenderedInline = hasBatchSelectorInConfig(workflowUIConfig);

  /** 当前工作流的 ratio_selector 组件（若有） */
  const currentRatioComp = useMemo(
    () =>
      workflowUIConfig?.components?.find((c) => c.component_type === "ratio_selector") || null,
    [workflowUIConfig],
  );

  const perBatchRatioBindingConfig = useMemo(
    () => resolvePerBatchRatioBindingConfig(workflowUIConfig),
    [workflowUIConfig],
  );

  const handlePerBatchRatioChange = useCallback((index: number, next: PerBatchRatio) => {
    setPerBatchRatios((prev) => {
      const base = normalizePerBatchRatios(prev, Math.max(prev.length, index + 1));
      base[index] = next;
      return base;
    });
  }, []);

  const handlePerBatchRatioResetAll = useCallback(() => {
    setPerBatchRatios((prev) => (prev.length === 0 ? prev : prev.map(() => ({ ratio: null, cropSize: null }))));
  }, []);

  const perBatchRatioGlobal = useMemo(() => {
    const ratioComp = perBatchRatioBindingConfig?.ratioComp || currentRatioComp;
    if (!ratioComp) return null;
    const ratioKey = ratioComp.coze_param_key;
    const rawGlobal = cozeParams[ratioKey];
    const globalRatio =
      typeof rawGlobal === "string" && rawGlobal
        ? rawGlobal
        : (ratioComp.config?.default_value as string | undefined) || "auto";
    const globalCropSize = typeof cozeParams[OUTPUT_CROP_SIZE_PARAM] === "string"
      ? (cozeParams[OUTPUT_CROP_SIZE_PARAM] as string)
      : null;
    return { globalRatio, globalCropSize };
  }, [currentRatioComp, perBatchRatioBindingConfig, cozeParams]);

  const perBatchRatioBinding = useMemo(() => {
    if (cozeBatchCount <= 1 || !perBatchRatioBindingConfig || !perBatchRatioGlobal) {
      return null;
    }
    return {
      boundParamKey: perBatchRatioBindingConfig.targetParamKey,
      ratioComp: perBatchRatioBindingConfig.ratioComp,
      values: perBatchRatios,
      globalRatio: perBatchRatioGlobal.globalRatio,
      globalCropSize: perBatchRatioGlobal.globalCropSize,
      onChange: handlePerBatchRatioChange,
      onResetAll: handlePerBatchRatioResetAll,
      count: cozeBatchCount,
      batchUnit: getCurrentBatchUnit(),
      executionMode: currentExecutionMode,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    perBatchRatioBindingConfig,
    perBatchRatioGlobal,
    cozeBatchCount,
    perBatchRatios,
    handlePerBatchRatioChange,
	    handlePerBatchRatioResetAll,
	    currentExecutionMode,
	    activeSubOption,
	    allWorkflowConfigs,
	  ]);

  /** 渲染每批次比例面板；仅在 N>1 且工作流存在 ratio_selector 时返回内容。 */
  const renderPerBatchRatioPanel = useCallback(() => {
    if (!currentRatioComp) return null;
    if (cozeBatchCount <= 1) return null;
    if (perBatchRatioBinding) return null;
    if (!perBatchRatioGlobal) return null;
    return (
      <PerBatchRatioPanel
        count={cozeBatchCount}
        ratioComp={currentRatioComp}
        globalRatio={perBatchRatioGlobal.globalRatio}
        globalCropSize={perBatchRatioGlobal.globalCropSize}
        values={perBatchRatios}
        onChange={handlePerBatchRatioChange}
        onResetAll={handlePerBatchRatioResetAll}
        batchUnit={getCurrentBatchUnit()}
        executionMode={currentExecutionMode}
      />
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    currentRatioComp,
    cozeBatchCount,
    perBatchRatioBinding,
    perBatchRatioGlobal,
    perBatchRatios,
    handlePerBatchRatioChange,
	    handlePerBatchRatioResetAll,
	    currentExecutionMode,
	    activeSubOption,
	    allWorkflowConfigs,
	  ]);

  const renderBatchSelectorInline = useCallback((headerAction?: React.ReactNode) => {
    const batchConfig = getCurrentBatchConfig();
    if (batchConfig.options.length <= 1) return null;
    const batchUnit = getCurrentBatchUnit();
    return (
      <div className="mb-3">
        <div className="mb-2 flex min-h-7 items-center justify-between gap-2">
          <div className="inline-flex min-w-0 items-center gap-1.5 text-[15px] font-medium text-white">
            <span className="truncate">生成{batchUnit}次</span>
            {headerAction}
          </div>
        </div>
        <BatchTabsSelector
          options={batchConfig.options}
          value={cozeBatchCount}
          unit={batchUnit}
          onChange={setCozeBatchCount}
        />
        {!perBatchRatioBinding && renderPerBatchRatioPanel()}
      </div>
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
	  }, [cozeBatchCount, allWorkflowConfigs, activeSubOption, renderPerBatchRatioPanel, perBatchRatioBinding]);

  // 切换工作流时，重置批次数和批处理状态
  useEffect(() => {
    if (!configsLoaded) return;

    const { options } = getCurrentBatchConfig();
    const nextBatchCount = resolveDetailReplicaBatchCount({
      isDetailReplicaWorkflow,
      referenceCount: detailReplicaState.references.length,
      currentBatchCount: cozeBatchCount,
      configuredOptions: options,
    });
    if (nextBatchCount !== cozeBatchCount) {
      setCozeBatchCount(nextBatchCount);
    }
    setBatchProcessMode(false);
    setBatchParamValues({});
    const bpCfg = getCurrentBatchProcessConfig();
    setBatchConcurrency(bpCfg.concurrency);
    // 切换工作流时清空 per-batch 比例覆盖（每个工作流的 ratio_selector 配置可能不同）
    setPerBatchRatios([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSubOption, activeMainTab, allWorkflowConfigs, configsLoaded]);

  // 详情页复刻是一张参考图对应一个子任务，不受通用批次数选项约束。
  // 独立同步，避免参考图增删时误触上面的工作流切换清理逻辑。
  useEffect(() => {
    if (!isDetailReplicaWorkflow || detailReplicaState.references.length === 0) return;
    setCozeBatchCount((current) => resolveDetailReplicaBatchCount({
      isDetailReplicaWorkflow: true,
      referenceCount: detailReplicaState.references.length,
      currentBatchCount: current,
      configuredOptions: getCurrentBatchConfig().options,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detailReplicaState.references.length, isDetailReplicaWorkflow]);

  // 同步 perBatchRatios 长度到 cozeBatchCount；若工作流已加载且确认无 ratio_selector 则清空。
  // 注意：workflowUIConfig 为 null 表示尚未加载完成，此时不要清空（保留可能刚从历史恢复的 per-batch 状态）。
  useEffect(() => {
    if (!workflowUIConfig) return;
    const hasRatioSelector = !!workflowUIConfig.components?.some(
      (c) => c.component_type === "ratio_selector",
    );
    if (!hasRatioSelector) {
      setPerBatchRatios((prev) => (prev.length === 0 ? prev : []));
      return;
    }
    const ratioSelector = workflowUIConfig.components?.find(
      (component) => component.component_type === "ratio_selector",
    );
    const ratioOptions = resolveRatioSelectorOptions(ratioSelector?.config);
    setPerBatchRatios((prev) => {
      const next = normalizePerBatchRatios(prev, cozeBatchCount).map((item) =>
        normalizeRatioSelectorSelection(ratioOptions, item.ratio, item.cropSize),
      );
      if (
        prev.length === next.length &&
        prev.every((item, idx) => item?.ratio === next[idx]?.ratio && item?.cropSize === next[idx]?.cropSize)
      ) {
        return prev;
      }
      return next;
    });
  }, [cozeBatchCount, workflowUIConfig]);

  // 工作流配置加载后，如果当前选中的子选项已被禁用，自动切换到第一个可用选项。
  // 新浏览器页会话默认落在 FLOW智创，等待配置加载后选中第一个工作流。
  useEffect(() => {
    if (!configsLoaded) return;
    if (activeModuleOptions.some(o => o.name === activeSubOption)) return;
    // A restored Agent selection follows the backend-configured module.
    if (selectedWorkflow?.is_enabled && selectedWorkflow.execution_mode === 'agent') {
      const destination = getWorkflowTabForFeatureKey(selectedWorkflow.feature_key);
      if (destination) {
        setActiveMainTab(destination.mainTab);
        ensureMainTabExpanded(destination.mainTab);
        return;
      }
    }
    if (activeModuleOptions.length === 0) {
      const fallback = moduleConfigs.find(module => getModuleOptions(module.module_key).length > 0);
      if (fallback) setActiveMainTab(fallback.module_key);
      return;
    }
    setActiveSubOption(activeModuleOptions[0].name);
    setActiveTabInstance(1);
    ensureMainTabExpanded(activeMainTab);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeMainTab, activeSubOption, activeModuleOptions, configsLoaded, ensureMainTabExpanded, getModuleOptions, getWorkflowTabForFeatureKey, moduleConfigs, selectedWorkflow]);

  // Ordinary workflows reuse their base tab; Agent navigation starts a fresh
  // conversation. Existing Agent tasks are selected from the top tab strip.
  const handleSubOptionSelect = useCallback((
    option: string,
    mainTabOverride?: MainTab,
    sourceWorkspaceOverrides?: Partial<EcomWorkspaceTab>,
    initialAgentPrompt?: string,
  ) => {
    const targetMainTab = mainTabOverride || activeMainTab;
    const workflow = (workflowConfigsByModule[targetMainTab] || []).find(item => item.name === option && item.is_enabled);
    if (workflow?.execution_mode === 'agent') {
      const detachedSnapshot = !agentSelectionRef.current && isCurrentTabGenerating()
        ? { ...buildCurrentWorkspaceTab(activeTabIdRef.current), ...sourceWorkspaceOverrides }
        : null;
      cancelScheduledWorkspacePersistence();
      persistCurrentWorkspaceTab(undefined, sourceWorkspaceOverrides);
      generationEpochRef.current += 1;
      if (detachedSnapshot) {
        monitorDetachedWorkspaceTab(detachedSnapshot);
        setGenerationActive(false);
        generatingTaskIdsRef.current = [];
        stopInspirePolling();
      }
      stopProgressSimulation();
      const navigationEpoch = tabSwitchEpochRef.current;
      const selectionKey = `${ecomStorageScope}:${workflow.feature_key}`;
      setEcomAgentEntryDismissed(selectionKey, false);
      setActiveWorkspaceView('generate');
      applyWorkspaceTab(createEmptyEcomWorkspaceTab(targetMainTab, option));
      setAgentLaunchFailed(false);
      void (async () => {
        let connection = agentController.getSnapshot();
        if ((connection.discovery === 'error' || connection.discovery === 'unavailable') && user?.id && user.current_team_id) {
          // Discovery can fail on a transient gateway error. A later explicit
          // click retries the read-only discovery before creating one new tab.
          feedback.info(`${workflow.name}配置加载中，请稍后`);
          await agentController.connect({ userId: user.id, teamId: user.current_team_id });
          connection = agentController.getSnapshot();
        }
        if (navigationEpoch !== tabSwitchEpochRef.current) return;
        if (connection.discovery !== 'ready' || !connection.entry || !connection.entryForm) {
          // The selected entry waits for discovery; it never falls back to Coze.
          if (connection.discovery !== 'loading' && connection.discovery !== 'idle') {
            setAgentLaunchFailed(true);
            feedback.error({ code: 'E5999', fallback: 'operation' });
          }
          return;
        }
        handledAgentSelectionsRef.current.add(selectionKey);
        const tabInstanceId = `agent:${crypto.randomUUID()}`;
        const conversation = await agentController.openNew(tabInstanceId, option, undefined, workflow.feature_key);
        const instance = agentStore.getSnapshot().instances[tabInstanceId];
        if (conversation && initialAgentPrompt && instance?.viewOpen && !instance.form.user_prompt && !instance.submissionPending) {
          agentStore.patchForm(tabInstanceId, { user_prompt: initialAgentPrompt });
        }
      })().catch(() => {
        if (navigationEpoch !== tabSwitchEpochRef.current) return;
        setAgentLaunchFailed(true);
        feedback.error({ code: 'E5999', fallback: 'operation' });
      });
      return;
    }
    agentStore.activate(null);
    const targetTabId = createEcomTabId(targetMainTab, option);
    closedTabIdsRef.current.delete(targetTabId);
    activateReversePromptTab(targetTabId);
    const isSameWorkflow =
      targetMainTab === activeMainTab && option === activeSubOption && activeTabInstance === 1;
    const detachedSnapshot = !agentSelectionRef.current && isCurrentTabGenerating()
      ? {
        ...buildCurrentWorkspaceTab(activeTabIdRef.current),
        ...sourceWorkspaceOverrides,
      }
      : null;
    cancelScheduledWorkspacePersistence();
    persistCurrentWorkspaceTab(undefined, sourceWorkspaceOverrides);
    generationEpochRef.current += 1;
    if (detachedSnapshot && detachedSnapshot.tabId !== targetTabId) {
      monitorDetachedWorkspaceTab(detachedSnapshot);
      setGenerationActive(false);
      generatingTaskIdsRef.current = [];
      stopInspirePolling();
    }
    setActiveWorkspaceView("generate");
    const currentHistory = loadEcomTabHistory(ecomStorageScope);
    const existingTab = currentHistory?.tabs[targetTabId];
    const nextTab = normalizeStaleGeneratingTab(existingTab, pendingGenerationSubmissionsRef.current.has(existingTab?.tabId || "") || pendingDurableTab(ecomStorageScope, existingTab?.tabId || "")) || createEmptyEcomWorkspaceTab(targetMainTab, option);
    const nextOpenTabIds = Array.from(new Set([...(currentHistory?.openTabIds || []), targetTabId]));
    saveEcomTabHistory({
      version: 1,
      activeTabId: targetTabId,
      activeWorkspaceView: "generate",
      openTabIds: nextOpenTabIds,
      tabs: {
        ...(currentHistory?.tabs || {}),
        [targetTabId]: nextTab,
      },
      updatedAt: Date.now(),
    }, ecomStorageScope);
    if (isSameWorkflow) {
      if (!workflowUIConfig) {
        setWorkflowConfigReloadKey((prev) => prev + 1);
      }
    } else {
      setWorkflowUIConfig(null);
      setExpandConfigs([]);
    }
    setOpenedSubOptions(prev => {
      const next = getUniqueOpenedOptions(prev.includes(targetTabId) ? prev : [...prev, targetTabId]);
      saveEcomOpenedTabs(next, ecomStorageScope);
      return next;
    });
    setTabStatusMap((prev) => ({ ...prev, [targetTabId]: nextTab.status }));
    stopProgressSimulation();
    applyWorkspaceTab(nextTab);
  }, [agentController, agentStore, workflowConfigsByModule, activeMainTab, activeSubOption, activeTabInstance, activateReversePromptTab, applyWorkspaceTab, buildCurrentWorkspaceTab, cancelScheduledWorkspacePersistence, ecomStorageScope, feedback, isCurrentTabGenerating, monitorDetachedWorkspaceTab, persistCurrentWorkspaceTab, stopInspirePolling, stopProgressSimulation, user?.id, user?.current_team_id, workflowUIConfig]);

  useEffect(() => {
    if (!configsLoaded || activeWorkspaceView !== 'generate' || !agentSelected || !selectedWorkflow?.is_enabled) return;
    if (agentActive) {
      handledAgentSelectionsRef.current.add(agentSelectionKey);
      return;
    }
    if (agentConnection.discovery !== 'ready' || handledAgentSelectionsRef.current.has(agentSelectionKey)
      || isEcomAgentEntryDismissed(agentSelectionKey)) return;
    const destination = getWorkflowTabForFeatureKey(selectedWorkflow.feature_key);
    if (!destination) return;
    handledAgentSelectionsRef.current.add(agentSelectionKey);
    // Restored sessions have priority over creating a new default conversation.
    const restored = agentWorkspace.hostTabs.find(tab => tab.kind === 'agent'
      && agentWorkspace.instances[tab.tabInstanceId]?.moduleKey === selectedWorkflow.feature_key);
    if (restored) agentStore.activate(restored.tabInstanceId);
    else handleSubOptionSelect(selectedWorkflow.name, destination.mainTab, undefined,
      urlWorkflow === selectedWorkflow.feature_key ? urlPrefillPrompt ?? undefined : undefined);
  }, [activeWorkspaceView, agentActive, agentConnection, agentSelected, agentSelectionKey, agentStore, agentWorkspace,
    configsLoaded, getWorkflowTabForFeatureKey, handleSubOptionSelect, selectedWorkflow, urlPrefillPrompt, urlWorkflow]);

  // Switch directly to a specific tab by its tabId (used by the top tab strip; supports
  // duplicate instances of the same sub-option).
  const handleSwitchToTab = useCallback((tabId: string) => {
    if (tabId === activeTabId && activeWorkspaceView === "generate") return;
    const parsed = parseEcomTabId(tabId);
    if (!parsed) return;
    const workflow = allWorkflowConfigs.find(item => item.name === parsed.subOption && item.is_enabled);
    if (workflow?.execution_mode === 'agent') {
      const destination = getWorkflowTabForFeatureKey(workflow.feature_key);
      if (destination) handleSubOptionSelect(workflow.name, destination.mainTab);
      return;
    }
    closedTabIdsRef.current.delete(tabId);
    activateReversePromptTab(tabId);
    const detachedSnapshot = !agentSelectionRef.current && isCurrentTabGenerating()
      ? buildCurrentWorkspaceTab(activeTabIdRef.current)
      : null;
    cancelScheduledWorkspacePersistence();
    persistCurrentWorkspaceTab();
    generationEpochRef.current += 1;
    if (detachedSnapshot && detachedSnapshot.tabId !== tabId) {
      monitorDetachedWorkspaceTab(detachedSnapshot);
      setGenerationActive(false);
      generatingTaskIdsRef.current = [];
      stopInspirePolling();
    }
    setActiveWorkspaceView("generate");
    const currentHistory = loadEcomTabHistory(ecomStorageScope);
    const existingTab = currentHistory?.tabs[tabId];
    const nextTab = normalizeStaleGeneratingTab(existingTab, pendingGenerationSubmissionsRef.current.has(existingTab?.tabId || "") || pendingDurableTab(ecomStorageScope, existingTab?.tabId || ""))
      || createEmptyEcomWorkspaceTab(parsed.mainTab, parsed.subOption, {
        tabId,
        instance: parsed.instance,
      });
    const nextOpenTabIds = Array.from(new Set([...(currentHistory?.openTabIds || []), tabId]));
    saveEcomTabHistory({
      version: 1,
      activeTabId: tabId,
      activeWorkspaceView: "generate",
      openTabIds: nextOpenTabIds,
      tabs: {
        ...(currentHistory?.tabs || {}),
        [tabId]: nextTab,
      },
      updatedAt: Date.now(),
    }, ecomStorageScope);
    const isSameWorkflow =
      parsed.mainTab === activeMainTab && parsed.subOption === activeSubOption;
    if (isSameWorkflow) {
      if (!workflowUIConfig) {
        setWorkflowConfigReloadKey((prev) => prev + 1);
      }
    } else {
      setWorkflowUIConfig(null);
      setExpandConfigs([]);
    }
    setOpenedSubOptions((prev) => {
      const next = getUniqueOpenedOptions(prev.includes(tabId) ? prev : [...prev, tabId]);
      saveEcomOpenedTabs(next, ecomStorageScope);
      return next;
    });
    setTabStatusMap((prev) => ({ ...prev, [tabId]: nextTab.status }));
    stopProgressSimulation();
    applyWorkspaceTab(nextTab);
  }, [
    activeMainTab,
    activeSubOption,
    activeTabId,
    activeWorkspaceView,
    activateReversePromptTab,
    allWorkflowConfigs,
    applyWorkspaceTab,
    buildCurrentWorkspaceTab,
    cancelScheduledWorkspacePersistence,
    ecomStorageScope,
    getWorkflowTabForFeatureKey,
    handleSubOptionSelect,
    isCurrentTabGenerating,
    monitorDetachedWorkspaceTab,
    persistCurrentWorkspaceTab,
    stopInspirePolling,
    stopProgressSimulation,
    workflowUIConfig,
  ]);

  const handleCloseAgentTab = useCallback((tabInstanceId: string) => {
    const instance = agentStore.getSnapshot().instances[tabInstanceId];
    const wasActive = agentStore.getSnapshot().activeTabInstanceId === tabInstanceId;
    agentController.close(tabInstanceId);
    const next = agentStore.getSnapshot();
    if (instance && !next.hostTabs.some(tab => tab.kind === 'agent' && next.instances[tab.tabInstanceId]?.moduleKey === instance.moduleKey)) {
      const key = `${ecomStorageScope}:${instance.moduleKey}`;
      handledAgentSelectionsRef.current.add(key);
      setEcomAgentEntryDismissed(key, true);
    }
    if (wasActive && next.activeTabInstanceId?.startsWith('coze:')) {
      handleSwitchToTab(next.activeTabInstanceId.slice('coze:'.length));
    } else if (wasActive && !next.activeTabInstanceId) {
      const firstWorkflow = getModuleOptions('flow_creative')[0];
      if (firstWorkflow) handleSubOptionSelect(firstWorkflow.name, 'flow_creative');
    }
  }, [agentController, agentStore, ecomStorageScope, getModuleOptions, handleSubOptionSelect, handleSwitchToTab]);

  // Open a clean, independent workspace beside any existing tabs of a workflow.
  // The new tab keeps only the module/workflow identity; all inputs, uploads,
  // results, and task state start from the initial empty snapshot.
  const openFreshWorkflowTab = useCallback((source: { mainTab: MainTab; subOption: string }) => {
    const currentHistory = loadEcomTabHistory(ecomStorageScope);
    const existingIds = [
      ...(currentHistory?.openTabIds || []),
      ...openedSubOptions,
      ...(currentHistory ? Object.keys(currentHistory.tabs) : []),
    ];
    const { tabId: newTabId, instance: newInstance } = createUniqueEcomTabId(
      source.mainTab,
      source.subOption,
      existingIds,
    );
    const newTab = createFreshEcomWorkspaceTabCopy(source, {
      tabId: newTabId,
      instance: newInstance,
    });
    closedTabIdsRef.current.delete(newTabId);
    activateReversePromptTab(newTabId);

    saveEcomTabHistory({
      version: 1,
      activeTabId: newTabId,
      activeWorkspaceView: "generate",
      openTabIds: Array.from(new Set([...(currentHistory?.openTabIds || []), newTabId])),
      tabs: {
        ...(currentHistory?.tabs || {}),
        [newTabId]: newTab,
      },
      updatedAt: Date.now(),
    }, ecomStorageScope);
    setOpenedSubOptions((prev) => {
      const next = getUniqueOpenedOptions(prev.includes(newTabId) ? prev : [...prev, newTabId]);
      saveEcomOpenedTabs(next, ecomStorageScope);
      return next;
    });
    setTabStatusMap((prev) => ({ ...prev, [newTabId]: "empty" }));
    handleSwitchToTab(newTabId);
  }, [activateReversePromptTab, ecomStorageScope, handleSwitchToTab, openedSubOptions]);

  const handleCopyCurrentWorkflow = useCallback(() => {
    const parsed = tabContextMenu && tabContextMenu.tabId !== HISTORY_TAB_LABEL
      ? parseEcomTabId(tabContextMenu.tabId)
      : null;
    setTabContextMenu(null);
    if (parsed) openFreshWorkflowTab(parsed);
  }, [openFreshWorkflowTab, tabContextMenu]);

  const resetGenerateWorkspace = useCallback(() => {
    isApplyingTabHistoryRef.current = true;
    stopProgressSimulation();
    setActiveWorkspaceView("generate");
    setCozeParams({});
    setCozeBatchCount(1);
    setStatus("empty");
    setGenerationStartedAt(null);
    setProgress(0);
    setGeneratedImage(null);
    setResultModelImage("");
    setResultProductImage("");
    setGeneratedImages([]);
    clearActiveRefineState();
    setErrorMessage("");
    setCurrentTaskIds([]);
    setCurrentPrompt("");
    setFailedIndexes([]);
    setBatchChildTaskIds([]);
    setPreviewOutputCount(1);
    setTaskProgresses([]);
    setBatchProcessMode(false);
    setBatchParamValues({});
    setBatchParentTaskId(null);
    setBatchTaskStatus(null);
    setManualStitchMode(false);
    setHistoryFeatureKey(null);
    setRefImageUrl("");
    applyInspireResults([]);
    applyMultiTemplateLabels({});
    applyMultiTemplateWorkflows({});
    inspireTaskIdsRef.current = [];
    setTimeout(() => {
      isApplyingTabHistoryRef.current = false;
    }, 50);
  }, [applyInspireResults, applyMultiTemplateLabels, applyMultiTemplateWorkflows, clearActiveRefineState, stopProgressSimulation]);

  const handleCloseOpenedSubOption = useCallback((tabIdToClose: string) => {
    setTabContextMenu(null);
    closedTabIdsRef.current.add(tabIdToClose);
    pendingGenerationSubmissionsRef.current.delete(tabIdToClose);
    const isClosingActive = tabIdToClose === activeTabId;
    if (isClosingActive) {
      invalidateGenerationRequests();
    }
    if (isClosingActive && persistTimerRef.current) {
      clearTimeout(persistTimerRef.current);
      persistTimerRef.current = null;
    }
    cancelDetachedTabMonitor(tabIdToClose);
    clearRefineStateForTab(tabIdToClose);
    setOpenedSubOptions((prev) => {
      const next = getUniqueOpenedOptions(prev.filter(item => item !== tabIdToClose));
      saveEcomOpenedTabs(next, ecomStorageScope);
      return next;
    });
    setTabStatusMap((prev) => {
      if (!(tabIdToClose in prev)) return prev;
      const next = { ...prev };
      delete next[tabIdToClose];
      return next;
    });
    if (tabIdToClose === HISTORY_TAB_LABEL) {
      if (activeWorkspaceView === "history") {
        setActiveWorkspaceView("generate");
      }
      return;
    }
    const reverseHistoryScope = getReversePromptOwnerScope(tabIdToClose);
    cancelReversePromptScope(reverseHistoryScope);
    reversePromptClaimsRef.current.delete(tabIdToClose);
    clearCozeReversePromptHistoryScope(reverseHistoryScope);
    clearDetailReplicaReverseHistoryScope(reverseHistoryScope);

    const currentHistory = loadEcomTabHistory(ecomStorageScope);
    const hasFallbackOpenTab = !!currentHistory?.openTabIds.some(
      (tabId) => tabId !== tabIdToClose && !!currentHistory.tabs[tabId],
    );
    const wasActiveGenerating = isClosingActive && isCurrentTabGenerating();
    // Closing is an explicit ownership release. Keep the backend task running,
    // but remove this tab's local snapshot and stop all foreground/background
    // writers so a later render cannot resurrect it.
    if (isClosingActive) {
      if (!hasFallbackOpenTab) {
        closedTabIdsRef.current.add(createEcomTabId(activeMainTab, activeSubOption));
      }
      generationEpochRef.current += 1;
      if (wasActiveGenerating) {
        setGenerationActive(false);
        generatingTaskIdsRef.current = [];
        stopInspirePolling();
      }
    }
    if (currentHistory) {
      const nextTabs = { ...currentHistory.tabs };
      delete nextTabs[tabIdToClose];
      const nextOpenTabIds = currentHistory.openTabIds.filter((id) => id !== tabIdToClose && nextTabs[id]);
      const fallbackTabId = isClosingActive
        ? nextOpenTabIds[nextOpenTabIds.length - 1] || null
        : currentHistory.activeTabId;
      saveEcomTabHistory({
        version: 1,
        activeTabId: fallbackTabId && nextTabs[fallbackTabId] ? fallbackTabId : null,
        activeWorkspaceView: "generate",
        openTabIds: nextOpenTabIds,
        tabs: nextTabs,
        updatedAt: Date.now(),
      }, ecomStorageScope);
      if (isClosingActive) {
        const fallbackTab = fallbackTabId ? nextTabs[fallbackTabId] : null;
        if (fallbackTab) {
          setActiveWorkspaceView("generate");
          applyWorkspaceTab(fallbackTab);
          return;
        }
      }
    }

    if (isClosingActive) {
      resetGenerateWorkspace();
      setActiveTabInstance(1);
    }
  }, [
    activeMainTab,
    activeSubOption,
    activeTabId,
    activeWorkspaceView,
    applyWorkspaceTab,
    cancelDetachedTabMonitor,
    clearRefineStateForTab,
    ecomStorageScope,
    getReversePromptOwnerScope,
    isCurrentTabGenerating,
    invalidateGenerationRequests,
    resetGenerateWorkspace,
    stopInspirePolling,
  ]);

  const handleCloseAllOpenedTabs = useCallback(() => {
    setTabContextMenu(null);
    pendingGenerationSubmissionsRef.current.clear();
    invalidateGenerationRequests();
    openedSubOptions.forEach((tabId) => closedTabIdsRef.current.add(tabId));
    closedTabIdsRef.current.add(createEcomTabId(activeMainTab, activeSubOption));
    if (persistTimerRef.current) {
      clearTimeout(persistTimerRef.current);
      persistTimerRef.current = null;
    }
    generationEpochRef.current += 1;
    setGenerationActive(false);
    generatingTaskIdsRef.current = [];
    generatingBatchChildIdsRef.current = [];
    generatingImagesRef.current = [];
    stopInspirePolling();
    // Cancel every background monitor; none of the detached tabs need updating any more.
    const monitors = detachedTabMonitorsRef.current;
    monitors.forEach((cancel) => {
      try {
        cancel();
      } catch {
        // Ignore — we are wiping state regardless.
      }
    });
    monitors.clear();
    activeRefineTrackerRef.current.clear();
    setRefineTrackerTrigger((prev) => prev + 1);
    Array.from(new Set([
      ...openedSubOptions,
      createEcomTabId(activeMainTab, activeSubOption),
    ]))
      .filter((tabId) => tabId !== HISTORY_TAB_LABEL)
      .forEach((tabId) => {
        const reverseHistoryScope = getReversePromptOwnerScope(tabId);
        cancelReversePromptScope(reverseHistoryScope);
        reversePromptClaimsRef.current.delete(tabId);
        clearCozeReversePromptHistoryScope(reverseHistoryScope);
        clearDetailReplicaReverseHistoryScope(reverseHistoryScope);
      });
    setOpenedSubOptions([]);
    saveEcomOpenedTabs([], ecomStorageScope);
    saveEcomTabHistory({
      version: 1,
      activeTabId: null,
      activeWorkspaceView: "generate",
      openTabIds: [],
      tabs: {},
      updatedAt: Date.now(),
    }, ecomStorageScope);
    setTabStatusMap({});
    setActiveTabInstance(1);
    resetGenerateWorkspace();
  }, [activeMainTab, activeSubOption, ecomStorageScope, getReversePromptOwnerScope, invalidateGenerationRequests, openedSubOptions, resetGenerateWorkspace, stopInspirePolling]);

  const handleCloseTabsToRight = useCallback(() => {
    if (!tabContextMenu) return;
    const targetIndex = openedSubOptions.indexOf(tabContextMenu.tabId);
    const tabsToClose = targetIndex >= 0 ? openedSubOptions.slice(targetIndex + 1) : [];
    if (tabsToClose.length === 0) {
      setTabContextMenu(null);
      return;
    }
    tabsToClose.forEach((tabId) => handleCloseOpenedSubOption(tabId));
  }, [handleCloseOpenedSubOption, openedSubOptions, tabContextMenu]);

  const handleOpenTabContextMenu = useCallback((e: React.MouseEvent, tabId: string) => {
    e.preventDefault();
    e.stopPropagation();
    setTabContextMenu({ tabId, x: e.clientX, y: e.clientY });
  }, []);

  useEffect(() => {
    if (!tabContextMenu) return;
    const close = () => setTabContextMenu(null);
    const closeOnEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") close();
    };
    window.addEventListener("click", close);
    window.addEventListener("contextmenu", close);
    window.addEventListener("keydown", closeOnEscape);
    return () => {
      window.removeEventListener("click", close);
      window.removeEventListener("contextmenu", close);
      window.removeEventListener("keydown", closeOnEscape);
    };
  }, [tabContextMenu]);

  useEffect(() => {
    if (activeWorkspaceView === "generate") {
      setShouldRenderHistoryDrawer(true);
    } else {
      setIsHistoryDrawerOpen(false);
    }
  }, [activeWorkspaceView]);

  const handleHistoryNavClick = useCallback(() => {
    cancelScheduledWorkspacePersistence();
    agentStore.activate(null);
    persistCurrentWorkspaceTab({ activeWorkspaceView: "history" });
    setActiveWorkspaceView("history");
    setExpandedMainTabs([]);
    setOpenedSubOptions(prev => {
      const next = getUniqueOpenedOptions(prev.includes(HISTORY_TAB_LABEL) ? prev : [...prev, HISTORY_TAB_LABEL]);
      saveEcomOpenedTabs(next, ecomStorageScope);
      return next;
    });
  }, [agentStore, cancelScheduledWorkspacePersistence, ecomStorageScope, persistCurrentWorkspaceTab]);

  const handleHistoryDrawerExpand = useCallback(() => {
    setIsHistoryDrawerOpen(false);
    handleHistoryNavClick();
  }, [handleHistoryNavClick]);

  const handleHistoryPageCollapse = useCallback(() => {
    persistCurrentWorkspaceTab({ activeWorkspaceView: "generate" });
    setActiveWorkspaceView("generate");
    setShouldRenderHistoryDrawer(true);
    setIsHistoryDrawerOpen(true);
  }, [persistCurrentWorkspaceTab]);

  const toggleLeftNavCollapsed = useCallback(() => {
    setIsLeftNavCollapsed((prev) => {
      const next = !prev;
      if (!next) {
        leftNavShellRef.current?.classList.remove("is-popup-hidden");
      }
      saveLeftNavCollapsed(next);
      return next;
    });
  }, []);

  const hideCollapsedNavPopup = useCallback(() => {
    leftNavShellRef.current?.classList.add("is-popup-hidden");
  }, []);

  const restoreCollapsedNavPopup = useCallback(() => {
    leftNavShellRef.current?.classList.remove("is-popup-hidden");
  }, []);

  const handleMakeSame = useCallback((workflowKey: string, prompt: string, promptParamKey = "prompt") => {
    const workflowTab = getWorkflowTabForFeatureKey(workflowKey);
    const workflow = findEnabledWorkflowByFeatureKey(workflowKey);
    if (!workflowTab || !workflow) return;
    if (workflow.execution_mode === "agent") {
      handleSubOptionSelect(workflow.name, workflowTab.mainTab, undefined, prompt);
      return;
    }
    // 做同款始终单开标签，已有标签的提示词与结果保持原样。
    agentStore.activate(null);
    openFreshWorkflowTab({ mainTab: workflowTab.mainTab, subOption: workflow.name });
    if (prompt) setInspirationPrefill({ workflowKey, prompt, paramKey: promptParamKey.trim() || "prompt", scope: ecomStorageScope });
  }, [agentStore, ecomStorageScope, findEnabledWorkflowByFeatureKey, getWorkflowTabForFeatureKey, handleSubOptionSelect, openFreshWorkflowTab]);

  // Consume only after the selected form is ready; a delayed config cannot fill another workflow.
  useEffect(() => {
    if (!inspirationPrefill) return;
    if (inspirationPrefill.scope !== ecomStorageScope || inspirationPrefill.workflowKey !== selectedWorkflow?.feature_key) {
      setInspirationPrefill(null);
      return;
    }
    if (workflowUIConfigLoading || workflowUIConfig?.workflow_key !== inspirationPrefill.workflowKey) return;
    setPrefillData({ [inspirationPrefill.paramKey]: inspirationPrefill.prompt });
    setInspirationPrefill(null);
  }, [ecomStorageScope, inspirationPrefill, selectedWorkflow?.feature_key, workflowUIConfig, workflowUIConfigLoading]);

  // 灵感中心"一键做同款"跳转：自动切换工作流 + 填充提示词
  const lastAppliedRef = useRef<string>("");
  useEffect(() => {
    if (!urlWorkflow) return;
    if (!configsLoaded) return;

    const key = JSON.stringify([urlWorkflow, urlPrefillPrompt, urlPrefillPromptKey]);
    if (lastAppliedRef.current === key) return;

    const workflowTab = getWorkflowTabForFeatureKey(urlWorkflow);
    const workflow = findEnabledWorkflowByFeatureKey(urlWorkflow);
    if (workflowTab && workflow) {
      if (workflow.execution_mode === 'agent') {
        setActiveMainTab(workflowTab.mainTab);
        ensureMainTabExpanded(workflowTab.mainTab);
        setActiveSubOption(workflow.name);
      } else {
        handleMakeSame(urlWorkflow, urlPrefillPrompt ?? "", urlPrefillPromptKey);
        // 跳转参数只消费一次，刷新页面不能再开一个标签。
        const url = new URL(window.location.href);
        ["workflow", "prefill_prompt", "prefill_prompt_key", "from"].forEach((param) => url.searchParams.delete(param));
        window.history.replaceState(window.history.state, "", url);
      }
      lastAppliedRef.current = key;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlWorkflow, urlPrefillPrompt, urlPrefillPromptKey, ecomStorageScope, configsLoaded, findEnabledWorkflowByFeatureKey, getWorkflowTabForFeatureKey, ensureMainTabExpanded]);

  return (
    <div
      data-ecom-workspace-root
      className="relative flex min-w-0 h-[calc(100vh-70px)] overflow-clip"
    >
      <EcomAgentHostLifecycle tabs={agentHostTabs} activeTabInstanceId={activeAgentHostTabId} preserveControllerAgentTabs />
      <EcomAgentHostConnection
        identity={user?.id && user.current_team_id
          ? { userId: user.id, teamId: user.current_team_id }
          : null}
      />
      {/* ===== 左侧参数设置面板 - 列表式功能选择 ===== */}
      <div
        ref={leftNavShellRef}
        className={`group/shell relative shrink-0 ${
          useSplitDetailReplicaLayout
            ? "contents"
            : activeWorkspaceView === "history" || agentWorkspaceMode
              ? "w-[260px] [&.is-collapsed]:w-[72px]"
              : "w-fit"
        } ${isLeftNavCollapsed || isFocusedWorkspace ? "is-collapsed z-50" : ""} ${isFocusedOnboarding ? "!w-[72px]" : ""}`}
      >
        {/* 主容器 - 毛玻璃效果 */}
        <div className={useSplitDetailReplicaLayout
          ? "contents"
          : `h-full bg-white/10 rounded-[14px] shadow-[inset_0_0_7px_0_rgba(255,255,255,0.35)] backdrop-blur-[25px] flex overflow-visible ${
            activeWorkspaceView === "history" || agentWorkspaceMode
              ? "w-[260px] group-[.is-collapsed]/shell:w-[72px]"
              : "w-fit"
          } ${isFocusedOnboarding ? "!w-[72px]" : ""}`
        }>
          {/* ===== 左列：功能列表（一级模块 + 二级功能） ===== */}
          <div
            data-ecom-left-navigation
            className="relative z-40 flex min-h-0 w-[260px] shrink-0 flex-col overflow-hidden bg-[#1C1C1C] px-2 py-4 [&_button:not(:disabled)]:cursor-pointer group-[.is-collapsed]/shell:w-[72px] group-[.is-collapsed]/shell:overflow-visible"
          >
            <div className="min-h-0 flex-1 overflow-y-auto [scrollbar-width:none] group-[.is-collapsed]/shell:overflow-visible [&::-webkit-scrollbar]:hidden">
              <div className="flex flex-col gap-2 pb-4">
              {mainTabs.map((tab) => {
                const opts = subOptionsMap[tab] || [];
                const activeAgentDestination = agentActive ? getWorkflowTabForFeatureKey(
                  agentWorkspace.instances[agentWorkspace.activeTabInstanceId!]?.moduleKey) : null;
                const isActiveGroup = activeWorkspaceView === "generate" && (activeAgentDestination?.mainTab ?? activeMainTab) === tab;
                const isExpandedGroup = expandedMainTabs.includes(tab);
                const loading = !configsLoaded;
                const emptyText = tab === ECOM_IMAGE_MODULE_KEY ? "暂无可用功能" : "暂无已配置工作流";
                return (
                  <div key={tab} className="group/nav relative select-none" onMouseLeave={restoreCollapsedNavPopup}>
                    <button
                      type="button"
                      onClick={() => {
                        setExpandedMainTabs((prev) =>
                          prev.includes(tab)
                            ? prev.filter((item) => item !== tab)
                            : [...prev, tab],
                        );
                      }}
                      className={`flex h-10 w-full items-center gap-1.5 rounded-[8px] px-2 pl-[12px] text-[14px] transition-colors duration-150 group-[.is-collapsed]/shell:h-[58px] group-[.is-collapsed]/shell:flex-col group-[.is-collapsed]/shell:justify-center group-[.is-collapsed]/shell:gap-1 group-[.is-collapsed]/shell:bg-transparent group-[.is-collapsed]/shell:px-1 group-[.is-collapsed]/shell:text-[10px]
                        ${isActiveGroup ? "bg-[#202325] text-[#F6F6F6]" : "text-white/58 hover:bg-[#202325] hover:text-white/80"}`}
                      title={getTabLabel(tab)}
                    >
                      <ModuleNavIcon iconKey={getTabIconKey(tab)} iconSvg={getTabIconSvg(tab)} />
                      <span className="min-w-0 flex-1 truncate text-left transition-opacity duration-200 group-[.is-collapsed]/shell:max-w-full group-[.is-collapsed]/shell:flex-none group-[.is-collapsed]/shell:text-center group-[.is-collapsed]/shell:leading-none">{getTabLabel(tab)}</span>
                      <svg
                        className={`h-3 w-3 shrink-0 text-white/45 origin-center transform-gpu will-change-transform transition-transform duration-200 ease-out motion-reduce:transition-none group-[.is-collapsed]/shell:hidden ${isExpandedGroup ? "rotate-180" : ""}`}
                        viewBox="0 0 12 12"
                        fill="none"
                        aria-hidden="true"
                      >
                        <path d="M3 4.5L6 7.5L9 4.5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </button>

                    {isExpandedGroup && (
                      <div className="mt-4 flex flex-col gap-2 ml-[32px] group-[.is-collapsed]/shell:hidden">
                        {loading ? (
                          <div className="flex items-center gap-1.5 px-3 py-1.5 text-[14px] text-white/35">
                            <span className="h-2.5 w-2.5 rounded-full border border-white/20 border-t-white/60 animate-spin" />
                            <span>加载中...</span>
                          </div>
                        ) : opts.length === 0 ? (
                          <div className="px-3 py-1.5 text-[14px] text-white/35">{emptyText}</div>
                        ) : (
                          opts.map((opt) => {
                            const isCurrent = isActiveGroup && (activeAgentDestination?.subOption ?? activeSubOption) === opt.name;
                            return (
                              <button
                                key={opt.feature_key}
                                type="button"
                                onClick={() => {
                                  if (!isActiveGroup) {
                                    setActiveMainTab(tab);
                                  }
                                  handleSubOptionSelect(opt.name, tab);
                                }}
                                className={`group flex h-8 w-full items-center gap-2 rounded-[8px] px-3 text-left text-[14px] transition-colors
                                    ${isCurrent ? "bg-[#202325] text-white" : "text-white/45 hover:bg-[#1B1E1F] hover:text-white/70"}`}
                                title={opt.name}
                              >
                                <span className={`h-0.5 w-0.5 shrink-0 rounded-full ${isCurrent ? "bg-white/80" : "bg-white/32 group-hover:bg-white/55"}`} />
                                <span className="min-w-0 flex-1 truncate">{opt.name}</span>
                              </button>
                            );
                          })
                        )}
                      </div>
                    )}

                    <div
                      data-ecom-collapsed-workflow-popup
                      className="ecom-collapsed-workflow-popup pointer-events-none invisible absolute left-[58px] top-0 z-[60] w-[190px] translate-x-0 rounded-[10px] bg-[#242526] p-2 opacity-0 shadow-[0_16px_40px_rgba(0,0,0,0.45)] transition-[opacity,transform,visibility] duration-150 ease-out before:absolute before:-left-3 before:top-0 before:h-full before:w-4 before:content-[''] group-[.is-collapsed]/shell:group-hover/nav:visible group-[.is-collapsed]/shell:group-hover/nav:pointer-events-auto group-[.is-collapsed]/shell:group-hover/nav:opacity-100 group-[.is-popup-hidden]/shell:!invisible group-[.is-popup-hidden]/shell:!pointer-events-none group-[.is-popup-hidden]/shell:!opacity-0"
                    >
                      <div
                        data-ecom-collapsed-workflow-scroll
                        className="ecom-collapsed-workflow-scroll"
                      >
                        {loading ? (
                          <div className="flex items-center gap-1.5 px-2 py-2 text-[12px] text-white/45">
                            <span className="h-2.5 w-2.5 rounded-full border border-white/20 border-t-white/60 animate-spin" />
                            <span>加载中...</span>
                          </div>
                        ) : opts.length === 0 ? (
                          <div className="px-2 py-2 text-[12px] text-white/45">{emptyText}</div>
                        ) : (
                          <div className="flex flex-col gap-1">
                            {opts.map((opt) => {
                              const isCurrent = isActiveGroup && (activeAgentDestination?.subOption ?? activeSubOption) === opt.name;
                              return (
                                <button
                                  key={opt.feature_key}
                                  type="button"
                                  onClick={() => {
                                    hideCollapsedNavPopup();
                                    if (!isActiveGroup) {
                                      setActiveMainTab(tab);
                                    }
                                    ensureMainTabExpanded(tab);
                                    handleSubOptionSelect(opt.name, tab);
                                  }}
                                  className={`w-full rounded-[7px] px-3 py-2 text-left text-[14px] transition-colors
                                      ${isCurrent ? "bg-[#303234] text-white" : "text-white/65 hover:bg-white/8 hover:text-white"}`}
                                  title={opt.name}
                                >
                                  <span className="block truncate">{opt.name}</span>
                                </button>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
              </div>
            </div>

            <div className="relative z-40 mt-auto flex shrink-0 flex-col gap-2 bg-[#1C1C1C] pt-4 text-[14px] text-white/62">
              <button
                type="button"
                onClick={() => setShowInspirationCommunity(true)}
                className="flex items-center gap-2 px-2 py-1 text-left transition-colors hover:text-white group-[.is-collapsed]/shell:flex-col group-[.is-collapsed]/shell:justify-center group-[.is-collapsed]/shell:gap-1 group-[.is-collapsed]/shell:px-0 group-[.is-collapsed]/shell:text-[10px]"
                title="灵感中心"
              >
                <svg width="20" height="20" viewBox="0 0 1024 1024" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                  <path d="M610.304 943.616h-199.68c-20.992 1.536-37.376 19.456-37.376 40.448 0 22.016 17.92 39.936 39.936 39.936v-1.024h1.024v1.024h199.168c20.992-1.536 37.376-19.456 37.376-39.936 0-10.752-4.096-20.992-11.776-28.672-7.68-7.68-17.92-11.776-28.672-11.776zM512 0C284.16 0 98.816 185.344 98.816 413.184c0 159.744 81.92 297.984 216.064 367.616v39.936c2.048 42.496 36.864 75.776 79.36 75.776h239.616c42.496-2.048 75.776-36.864 75.776-79.36v-35.328c134.144-69.632 216.064-207.872 216.064-367.616C925.184 185.344 739.84 0 512 0zm140.8 718.336c-10.752 5.12-18.432 14.336-22.016 25.088-1.536 4.096-2.56 8.704-2.56 13.824v57.856H395.264v-60.928c-0.512-4.096-1.024-8.192-2.56-11.776 0-0.512 0-1.024-0.512-1.024-3.584-10.24-11.264-17.92-20.992-22.528-118.272-52.224-192-168.96-192-305.664 0-183.296 149.504-332.8 332.8-332.8s332.8 149.504 332.8 332.8c0 136.704-73.728 253.44-192 305.152z" fill="currentColor" />
                  <path d="M275.456 393.728v3.072c1.536 20.992 18.944 37.376 39.936 37.376 10.752 0 20.992-4.096 28.672-11.776 7.68-7.68 11.776-17.92 11.776-28.672V389.12c2.56-73.728 62.464-131.584 136.704-131.584h1.024V176.64h-6.656c-116.736 3.072-211.456 100.352-211.456 217.088z" fill="currentColor" opacity="0.55" />
                </svg>
                <span>灵感中心</span>
              </button>
              <button
                type="button"
                onClick={handleHistoryNavClick}
                className={`flex items-center gap-2 px-2 py-1 text-left transition-colors hover:text-white group-[.is-collapsed]/shell:flex-col group-[.is-collapsed]/shell:justify-center group-[.is-collapsed]/shell:gap-1 group-[.is-collapsed]/shell:px-0 group-[.is-collapsed]/shell:text-[10px] ${
                  activeWorkspaceView === "history" ? "text-[#F6F6F6]" : ""
                }`}
                title="历史记录"
              >
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12.019 7.84192C14.7986 7.84199 17.0579 10.1015 17.0581 12.881C17.0581 13.8721 16.7752 14.8202 16.2339 15.6417L18.2817 17.673C18.4755 17.8668 18.5825 18.1255 18.5825 18.4054C18.5825 18.6826 18.4754 18.9412 18.2817 19.1378L18.2729 19.1466C18.0737 19.335 17.8149 19.4396 17.5483 19.4396C17.2819 19.4395 17.0237 19.3349 16.8218 19.1466L14.7827 17.1075C13.9666 17.6462 13.0156 17.9317 12.019 17.9318C9.23936 17.9318 6.979 15.6714 6.979 12.8917C6.97909 11.5505 7.50509 10.2851 8.4585 9.32629C9.41199 8.3701 10.6777 7.84192 12.019 7.84192ZM16.2983 4.52063V9.71887L15.7603 9.17786C15.7307 9.14829 15.7034 9.11881 15.6792 9.09192C15.6715 9.08288 15.6642 9.07365 15.6567 9.06555C15.6487 9.05677 15.6403 9.04856 15.6333 9.04016L14.6938 8.31067V6.08801H12.3228L12.1694 6.03723L12.1538 6.02942C12.1516 6.0283 12.1482 6.02682 12.145 6.02551C12.1407 6.02372 12.1354 6.02168 12.1294 6.01868L12.1255 6.01672C11.9493 5.94241 11.426 5.72172 11.0649 5.30188C10.6288 4.79028 10.5943 4.07395 10.5942 3.86633V2.18567H3.4917C3.42979 2.2072 3.31352 2.26334 3.16553 2.41125C3.12243 2.45435 3.06066 2.55488 3.02295 2.77844V16.5743C3.023 16.7385 3.06268 16.9403 3.09229 17.005C3.14884 17.0669 3.25149 17.0939 3.4292 17.0939H8.36084L9.021 17.6085C9.3415 17.797 9.7054 17.9558 10.104 18.0851L12.0054 18.7023H3.42627C3.30764 18.7023 3.06503 18.7014 2.77979 18.6261C2.39752 18.5264 2.0553 18.298 1.81299 17.9884C1.45759 17.528 1.41751 16.8386 1.41748 16.5636V2.71301C1.41758 2.48372 1.52782 1.77822 2.02588 1.27747C2.6696 0.633784 3.26002 0.58313 3.42432 0.58313H11.8306L16.2983 4.52063ZM12.0269 9.24231C10.0257 9.24239 8.396 10.872 8.396 12.8732C8.39601 14.8744 10.0257 16.504 12.0269 16.504C14.0281 16.504 15.6577 14.8744 15.6577 12.8732C15.6577 10.8719 14.0281 9.24231 12.0269 9.24231ZM12.1909 3.89563C12.1909 4.02761 12.2348 4.21359 12.2671 4.26477C12.3371 4.34278 12.482 4.43188 12.603 4.4884H13.8208L12.1909 3.04993V3.89563Z" fill="currentColor" />
                </svg>
                <span>历史记录</span>
              </button>
              <button
                type="button"
                onClick={toggleLeftNavCollapsed}
                className="flex items-center gap-2 px-2 py-1 text-left transition-colors hover:text-white group-[.is-collapsed]/shell:justify-center group-[.is-collapsed]/shell:gap-0 group-[.is-collapsed]/shell:px-0 "
                title="收起/展开"
              >
                <svg className="rotate-180 group-[.is-collapsed]/shell:rotate-0" width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M4 12L8 8L4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  <rect x="15.25" y="15.25" width="14.5" height="14.5" rx="3.25" transform="rotate(-180 15.25 15.25)" stroke="currentColor" strokeWidth="1.5" />
                  <line x1="11.25" y1="15" x2="11.25" y2="1" stroke="currentColor" strokeWidth="1.5" />
                </svg>
                <span className="group-[.is-collapsed]/shell:hidden">收起</span>
              </button>
            </div>
          </div>

          {/* ===== 右列：选中功能的参数与生图按钮 ===== */}
          {activeWorkspaceView === "generate" && configsLoaded && !agentWorkspaceMode && (
            <div
              data-ecom-upload-preview-boundary
              style={{ width: WORKBAR_WIDTH }}
              className={`relative z-10 flex min-h-0 shrink-0 flex-col overflow-hidden bg-[#0D0E11] ${useSplitDetailReplicaLayout ? "order-1" : ""} ${shouldHideGeneratePanel ? "!hidden" : ""}`}
              onKeyDown={handleGenerateShortcutKeyDown}
            >
              <div
                ref={attachGenerateScroll}
                data-ecom-generate-scroll
                onScroll={syncGenerateScrollFade}
                className="min-h-0 flex-1 overflow-y-auto px-4 py-6 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
              >
            {/* 当前一级板块 - 全动态渲染 */}
            {activeModuleOptions.length > 0 && (
              <div className="mb-2">
                {workflowUIConfigLoading ? (
                  renderWorkflowConfigLoading()
                ) : workflowUIConfigError ? (
                  renderWorkflowConfigError()
                ) : workflowUIConfig && workflowUIConfig.components.length > 0 ? (
                  <DynamicComponentPanel
                    key={activeTabId}
                    workflowKey={getFeatureKey(activeSubOption)}
                    workflowName={activeSubOption}
                    reversePromptHistoryScope={getReversePromptOwnerScope(activeTabId)}
                    uiConfig={workflowUIConfig}
                    params={cozeParams}
                    onParamChange={ownedCozeParamChange}
                    onOpenRefSetting={() => handleOpenRefSetting()}
                    refImageUrl={refImageUrl}
                    onRefImageChange={setRefImageUrl}
                    referenceResult={referenceResult}
                    onClearReferenceResult={() => setReferenceResult(null)}
                    prefillData={prefillData}
                    onPrefillConsumed={() => setPrefillData(null)}
                    expandConfigs={expandConfigs}
                    batchCount={cozeBatchCount}
                    batchUnit={getCurrentBatchUnit()}
                    renderBatchSelector={renderBatchSelectorInline}
                    hiddenParamKeys={batchProcessMode ? getCurrentBatchProcessConfig().params : undefined}
                    onUploadingChange={setImageUploading}
                    onPromptGeneratingChange={handlePromptGeneratingChange}
                    onImageHoverPreviewChange={setUploadImageHoverPreview}
                    leftNavCollapsed={isLeftNavCollapsed || isTemplateOnboarding}
                    onTemplateOnboardingChange={setIsTemplateOnboarding}
                    perBatchRatioBinding={perBatchRatioBinding}
                    callableKey={getCurrentCallableKey()}
                    billingCosts={billingCosts}
                    myCosts={myCosts}
                    promptReferenceText={flowXPromptReferenceText}
                    detailReplicaActiveIndex={detailReplicaActiveIndex}
                    onDetailReplicaActiveIndexChange={(index) => {
                      // 配置格切换需要联动参考图/效果图滚动到对应屏，走 focusIndex 排队 focusRequest。
                      focusDetailReplicaIndex(index);
                    }}
                    onDetailReplicaGenerate={handleGenerateSubmit}
                    onReversePromptCompleted={() => {
                      notifyPointsChanged();
                      setHistoryRefreshTrigger(prev => prev + 1);
                    }}
                  />
                ) : workflowUIConfig && workflowUIConfig.components.length === 0 ? (
                  renderWorkflowConfigEmpty()
                ) : (
                  renderWorkflowConfigLoading()
                )}
              </div>
            )}

            {/* 当前一级板块无工作流时的空状态 */}
            {configsLoaded && activeModuleOptions.length === 0 && (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <svg className="w-10 h-10 text-white/15 mb-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                  <path d="M12 8v4M12 16h.01" strokeLinecap="round" />
                </svg>
                {activeMainTab === ECOM_IMAGE_MODULE_KEY ? (
                  <>
                    <span className="text-sm text-white/40">当前团队暂无可用的 AI 商拍功能</span>
                    <span className="text-xs text-white/25 mt-1">请联系管理员开通团队权限</span>
                  </>
                ) : (
                  <>
                    <span className="text-sm text-white/40">当前板块暂无已配置的工作流</span>
                    <span className="text-xs text-white/25 mt-1">请在管理端 (4001) 创建工作流并绑定到此板块</span>
                  </>
                )}
              </div>
            )}

            {/* 生成批次（仅在未被组件列表内联渲染时显示） */}
            {!batchRenderedInline && activeModuleOptions.length > 0 && (() => {
              const batchConfig = getCurrentBatchConfig();
              if (batchConfig.options.length <= 1) return null;
              const batchUnit = getCurrentBatchUnit();
              return (
                <div className="mb-3">
                  <div className="text-[15px] font-medium text-white mb-2">生成{batchUnit}次</div>
                  <BatchTabsSelector
                    options={batchConfig.options}
                    value={cozeBatchCount}
                    unit={batchUnit}
                    onChange={setCozeBatchCount}
                  />
                  {renderPerBatchRatioPanel()}
                </div>
              );
            })()}

            {/* 姿势选择 */}
            {(() => {
              const currentWorkflow = allWorkflowConfigs.find(wf => wf.name === activeSubOption && wf.is_enabled);
              return currentWorkflow?.support_pose;
            })() && (
                <div className="mb-3">
                  <button
                    onClick={() => { setActivePoseCategoryId(POSE_ALL_CATEGORY_ID); fetchPoseAssets(); setShowPoseModal(true); }}
                    className="w-full h-10 bg-[#353535] hover:bg-white/10 text-white text-sm font-medium rounded-sm transition-all flex items-center justify-center gap-2"
                  >
                    <span>姿势预设</span>
                    {mergedPoses.length > 0 && (
                      <span className="min-w-5 rounded-lg border border-[#00FAE5]/40 bg-[#00FAE5]/15 px-1.5 py-0.5 text-center text-xs font-medium text-[#00FAE5]">
                        {mergedPoses.length}
                      </span>
                    )}
                  </button>
                </div>
              )}

            {/* 批处理模式开关 */}
            {getCurrentBatchProcessConfig().enabled && (
              <div className="mb-3 p-3 rounded-xl bg-[#1A1A1A] border border-white/10">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <svg className="w-4 h-4 text-[#F6F6F6]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <rect x="2" y="2" width="8" height="8" rx="1" /><rect x="14" y="2" width="8" height="8" rx="1" />
                      <rect x="2" y="14" width="8" height="8" rx="1" /><rect x="14" y="14" width="8" height="8" rx="1" />
                    </svg>
                    <span className="text-sm font-medium text-[#F6F6F6]">批处理模式</span>
                  </div>
                  <EcomSwitch
                    checked={batchProcessMode}
                    onCheckedChange={(checked) => {
                      setBatchProcessMode(checked);
                      if (!checked) setBatchParamValues({});
                    }}
                    aria-label="切换批处理模式"
                    variant="flowx"
                    size="flowx"
                  />
                </div>

                {batchProcessMode && (
                  <div className="mt-3 space-y-3">
                    {getCurrentBatchProcessConfig().params
                      .filter(paramKey => workflowUIConfig?.components.some(c => c.coze_param_key === paramKey))
                      .map(paramKey => {
                        const comp = workflowUIConfig?.components.find(c => c.coze_param_key === paramKey);
                        const uploadedFiles = batchParamValues[paramKey] || [];
                        const uploadProgress = batchUploading[paramKey];
                        const isDragOver = batchDragOver === paramKey;
                        return (
                          <div key={paramKey}>
                            <div className="flex items-center justify-between mb-1.5">
                              <label className="text-xs text-white/60">
                                {comp?.label || paramKey}
                                <span className="text-white/45 ml-1">({uploadedFiles.length} 个)</span>
                              </label>
                              {uploadedFiles.length > 0 && (
                                <button
                                  onClick={() => setBatchParamValues(prev => ({ ...prev, [paramKey]: [] }))}
                                  className="text-[14px] text-red-400/60 hover:text-red-400 transition-colors"
                                >清空</button>
                              )}
                            </div>
                            <div
                              onDragOver={e => { e.preventDefault(); e.stopPropagation(); setBatchDragOver(paramKey); }}
                              onDragLeave={e => { e.preventDefault(); e.stopPropagation(); setBatchDragOver(null); }}
                              onDrop={async e => {
                                e.preventDefault(); e.stopPropagation(); setBatchDragOver(null);
                                const files = await collectImageFiles(e.dataTransfer);
                                if (files.length > 0) handleBatchUploadFiles(paramKey, files);
                              }}
                              className={`border border-dashed rounded-lg p-3 transition-all ${isDragOver
                                ? "border-white/70 bg-white/10"
                                : "border-white/15 hover:border-white/30"
                                }`}
                            >
                              {uploadedFiles.length > 0 && (
                                <div className="flex flex-wrap gap-1.5 mb-2">
                                  {uploadedFiles.map((url, idx) => (
                                    <div key={idx} className="relative w-12 h-12 rounded-lg overflow-hidden group/thumb border border-white/10">
                                      <img src={url} alt={`${idx + 1}`} className="w-full h-full object-cover" />
                                      <button
                                        onClick={() => setBatchParamValues(prev => ({
                                          ...prev,
                                          [paramKey]: prev[paramKey]?.filter((_, i) => i !== idx) || [],
                                        }))}
                                        className="absolute inset-0 bg-black/60 opacity-0 group-hover/thumb:opacity-100 flex items-center justify-center transition-opacity"
                                      >
                                        <svg className="w-4 h-4 text-red-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                          <path d="M18 6L6 18M6 6l12 12" strokeLinecap="round" />
                                        </svg>
                                      </button>
                                      <span className="absolute bottom-0 left-0 right-0 text-center text-[8px] text-white/70 bg-black/50 py-0.5">{idx + 1}</span>
                                    </div>
                                  ))}
                                </div>
                              )}

                              {uploadProgress ? (
                                <div className="py-2 text-center">
                                  <div className="w-full h-1 bg-[#353535] rounded-full overflow-hidden mb-1.5">
                                    <div
                                      className="h-full bg-white/80 rounded-full transition-all"
                                      style={{ width: `${(uploadProgress.done / uploadProgress.total) * 100}%` }}
                                    />
                                  </div>
                                  <span className="text-[14px] text-white/50">
                                    上传中 {uploadProgress.done}/{uploadProgress.total}
                                  </span>
                                </div>
                              ) : (
                                <EcomUploadEmptyState
                                  dragOver={isDragOver}
                                  multiple
                                  className="min-h-[104px] border-0 bg-transparent hover:bg-transparent"
                                >
                                  {!isDragOver && (
                                    <div className="flex flex-wrap items-center justify-center gap-2">
                                      <div className="flex items-center gap-2">
                                        <label className="cursor-pointer text-[11px] text-white/55 hover:text-white/75 transition-colors px-2 py-1 rounded bg-white/8 hover:bg-white/12">
                                          选择图片
                                          <input
                                            type="file"
                                            accept={IMAGE_ACCEPT}
                                            multiple
                                            className="absolute w-0 h-0 opacity-0 overflow-hidden pointer-events-none"
                                            tabIndex={-1}
                                            aria-hidden="true"
                                            onChange={e => {
                                              const files = Array.from(e.target.files || []);
                                              if (files.length > 0) handleBatchUploadFiles(paramKey, files);
                                              e.target.value = '';
                                            }}
                                          />
                                        </label>
                                        <label className="cursor-pointer text-[11px] text-white/55 hover:text-white/75 transition-colors px-2 py-1 rounded bg-white/8 hover:bg-white/12">
                                          选择文件夹
                                          <input
                                            type="file"
                                            className="absolute w-0 h-0 opacity-0 overflow-hidden pointer-events-none"
                                            tabIndex={-1}
                                            aria-hidden="true"
                                            ref={el => { if (el) el.setAttribute("webkitdirectory", ""); }}
                                            onChange={e => {
                                              const files = Array.from(e.target.files || []);
                                              if (files.length > 0) handleBatchUploadFiles(paramKey, files);
                                              e.target.value = '';
                                            }}
                                          />
                                        </label>
                                      </div>
                                    </div>
                                  )}
                                </EcomUploadEmptyState>
                              )}
                            </div>
                          </div>
                        );
                      })}

                    {/* Batch total info */}
                    {(() => {
                      const totalItems = Math.max(...Object.values(batchParamValues).map(v => v.length), 0);
                      return totalItems > 0 ? (
                        <div className="text-xs text-white/50 flex items-center gap-2 pt-1">
                          <span>共 <span className="text-white/55 font-medium">{totalItems}</span> 个子任务</span>
                        </div>
                      ) : null;
                    })()}
                  </div>
                )}
              </div>
            )}

              </div>

              {/* 豌豆消耗提示 + 开始生图按钮；详情页复刻由专属反推面板接管 */}
              {!isDetailReplicaWorkflow && <div
                data-ecom-generate-footer
                className="relative z-10 shrink-0 bg-[#0D0E11] px-4 py-6"
              >
                {generateScrollHasBottomOverflow && (
                  <div
                    aria-hidden="true"
                    data-ecom-generate-bottom-fade
                    className="pointer-events-none absolute inset-x-0 bottom-full h-16 bg-[linear-gradient(to_bottom,rgba(13,14,17,0)_0%,rgba(13,14,17,.45)_55%,#0D0E11_100%)]"
                  />
                )}
                {(() => {
                  const costInfo = getCurrentEstimatedCost();
                  return (
                    <>
                      {costInfo && (
                        <div className="flex items-center justify-between px-1 mt-0 mb-1.5">
                          <div className="flex items-center gap-1.5">
                            <span className="text-[12px] font-normal text-white/45">预估消耗</span>
                            {costInfo.ruleLabel && (
                              <span className="text-[14px] text-white/40 bg-white/5 px-1.5 py-0.5 rounded">{costInfo.ruleLabel}</span>
                            )}
                          </div>
                          <div className="flex items-center gap-1">
                            {costInfo.mode === "fixed" ? (
                              <>
                                {costInfo.hasDiscount && (
                                  <span className="text-[12px] text-white/35 line-through">{costInfo.originalCost}</span>
                                )}
                                <span className="text-[18px] font-medium leading-none text-[#FCB453]">{costInfo.cost}</span>
                                <span className="text-[12px] text-white/55">豌豆</span>
                              </>
                            ) : (
                              <span className="text-xs text-white/50">按时长计费</span>
                            )}
                          </div>
                        </div>
                      )}
                      {(() => {
                        return (
                          <div className="relative group/btn">
                            <EcomButton
                              size="large"
                              type="button"
                              onClick={() => handleGenerateSubmit()}
                              disabled={isGenerateButtonDisabled}
                              aria-keyshortcuts="Control+Enter"
                              title="Ctrl+Enter 快速提交"
                              className={`relative overflow-hidden transition-colors duration-300 ${costInfo ? 'mt-0' : 'mt-3'}`}
                            >
                              <span className="relative z-10 flex items-center justify-center gap-2">
                                {batchProcessMode ? (
                                <>
                                  开始批处理 ({batchGenerateTotal} 张)
                                </>
                              ) : (
                                <>
                                  {currentExecutionMode === 'llm_vision_reverse'
                                    ? '开始反推'
                                    : currentExecutionMode === 'llm_inference'
                                      ? '推理生图'
                                      : '开始生图'}
                                </>
                              )}
                              </span>
                            </EcomButton>
                            {isGenerateSubmitDisabled && !noGenerateOptions && (
                              <div className="absolute -top-9 left-1/2 -translate-x-1/2 whitespace-nowrap
                                            px-3 py-1.5 bg-black/90 rounded-lg text-[11px] text-white/80
                                            opacity-0 group-hover/btn:opacity-100 transition-opacity pointer-events-none
                                            border border-white/10">
                                {promptGenerating
                                  ? "提示词生成中，请稍候"
                                  : cozeTemplateSetGenerateDisabled
                                    ? getCozeTemplateSetValidationMessage(cozeTemplateSetGenerateValidation)
                                    : batchProcessMode
                                      ? "请至少上传一个批处理文件"
                                      : "请填写所有带 * 的必填项"}
                              </div>
                            )}
                          </div>
                        );
                      })()}
                    </>
                  );
                })()}
              </div>}
          </div>
          )}
        </div>
      </div>

      <EcomAgentParameterSurface workflowConfigs={allWorkflowConfigs} workflowName={getEcomWorkflowName(agentWorkspace.instances[agentWorkspace.activeTabInstanceId ?? ""]?.moduleKey, allWorkflowConfigs)} onImageHoverPreviewChange={setUploadImageHoverPreview} />
      {agentWorkspaceMode && !agentActive && <AgentEntryPlaceholder
        workflowName={selectedWorkflow?.name || activeSubOption || "工作流"}
        loading={!configsLoaded || agentConnection.discovery === 'loading' || agentConnection.discovery === 'idle'}
        unavailable={agentLaunchFailed || agentConnection.discovery === 'error' || agentConnection.discovery === 'unavailable'}
        onOpen={() => {
          const destination = getWorkflowTabForFeatureKey(selectedWorkflow?.feature_key);
          if (destination) handleSubOptionSelect(destination.subOption, destination.mainTab);
        }}
      />}
      {/* ===== 中间预览区域 ===== */}
      <div className="flex-1 min-w-0 min-h-0 relative flex flex-col">
        {(renderableOpenedSubOptions.length > 0 || agentWorkspace.hostTabs.some(tab => tab.kind === "agent")) && (
          <DndContext
            sensors={openedTabSensors}
            collisionDetection={closestCenter}
            onDragEnd={handleOpenedTabDragEnd}
          >
            <SortableContext items={renderableOpenedSubOptions} strategy={rectSortingStrategy}>
              <div
                data-ecom-workspace-tab-strip
                data-ecom-workspace-tab-strip-full={extendDetailReplicaTabStrip ? "true" : undefined}
                className={`relative z-30 flex min-h-[48px] shrink-0 flex-wrap items-center gap-1 border border-[#353537] bg-[#171717] px-2 pb-2.5 ${
                  extendDetailReplicaTabStrip ? "mr-[-58px] w-[calc(100%+58px)] border-r-0" : ""
                }`}
              >
                {renderableOpenedSubOptions.map((tabId) => {
            const isHistoryTab = tabId === HISTORY_TAB_LABEL;
            if (isHistoryTab) {
              const isActive = !agentActive && activeWorkspaceView === "history";
              const activateTab = () => { agentStore.activate(null); handleHistoryNavClick(); };
              return (
                <SortableWorkspaceTab
                  key={tabId}
                  tabId={tabId}
                  title={HISTORY_TAB_LABEL}
                  isActive={isActive}
                  onActivate={activateTab}
                  onContextMenu={(e) => handleOpenTabContextMenu(e, tabId)}
                  className={`group relative h-[30px] mt-2 min-w-0 max-w-[calc(50%-2px)] shrink-0 overflow-hidden rounded-md border text-[12px] transition-none sm:max-w-[calc(33.333%-3px)] lg:max-w-[148px] ${
                    isActive
                      ? "ecom-workspace-tab-glass text-[#00FAE5]"
                      : "border-white/10 bg-black/30 text-white/60 hover:border-white/20 hover:text-white"
                  }`}
                >
                  <button
                    type="button"
                    data-ecom-workspace-tab-select
                    onClick={activateTab}
                    className="relative z-10 flex h-full w-full min-w-0 items-center rounded-md px-2 pr-7 text-left"
                    title={HISTORY_TAB_LABEL}
                  >
                    <span className="min-w-0 flex-1 truncate">{HISTORY_TAB_LABEL}</span>
                  </button>
                  <button
                    type="button"
                    onPointerDown={(e) => e.stopPropagation()}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleCloseOpenedSubOption(tabId);
                    }}
                    className="absolute right-2 top-1/2 z-20 flex h-[17px] w-[17px] -translate-y-1/2 items-center justify-center rounded p-0 transition-colors hover:bg-white/10"
                    aria-label={`关闭 ${HISTORY_TAB_LABEL}`}
                  >
                    <WorkspaceTabCloseIcon />
                  </button>
                </SortableWorkspaceTab>
              );
            }
            const parsed = parseEcomTabId(tabId);
            if (!parsed) return null;
            const { subOption: tabSubOption, instance: tabInstance } = parsed;
            const tabTitle = tabInstance > 1 ? `${tabSubOption} #${tabInstance}` : tabSubOption;
            const isActive = !agentActive && activeWorkspaceView === "generate" && tabId === activeTabId;
            const activateTab = () => { agentStore.activate(createEcomCozeHostTabId(tabId)); handleSwitchToTab(tabId); };
            const isTabRefining = Array.from(activeRefineTrackerRef.current.values()).some((tracker) => (
              tracker.ownerTabId === tabId
            ));
            const isTabGenerating = isTabRefining || (isActive
              ? isCurrentTabGenerating()
              : tabStatusMap[tabId] === "generating");
            return (
              <SortableWorkspaceTab
                key={tabId}
                tabId={tabId}
                title={tabTitle}
                isActive={isActive}
                onActivate={activateTab}
                onContextMenu={(e) => handleOpenTabContextMenu(e, tabId)}
                className={`group relative h-[30px] mt-2 min-w-0 max-w-[calc(50%-2px)] shrink-0 overflow-hidden rounded-md border text-[12px] transition-none sm:max-w-[calc(33.333%-3px)] lg:max-w-[148px] ${
                  isActive
                    ? "ecom-workspace-tab-glass text-[#00FAE5]"
                    : "border-white/10 bg-black/30 text-white/60 hover:border-white/20 hover:text-white"
                }`}
              >
                <button
                  type="button"
                  data-ecom-workspace-tab-select
                  onClick={activateTab}
                  className="relative z-10 flex h-full w-full min-w-0 items-center gap-1.5 rounded-md px-2 pr-7 text-left"
                  title={tabTitle}
                >
                  {isTabGenerating && (
                    <span
                      aria-hidden="true"
                      className="inline-block h-2.5 w-2.5 shrink-0 rounded-full border border-current border-t-transparent animate-spin"
                    />
                  )}
                  <span className="min-w-0 flex-1 truncate">{tabTitle}</span>
                </button>
                <button
                  type="button"
                  onPointerDown={(e) => e.stopPropagation()}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleCloseOpenedSubOption(tabId);
                  }}
                  className="absolute right-2 top-1/2 z-20 flex h-[17px] w-[17px] -translate-y-1/2 items-center justify-center rounded p-0 transition-colors hover:bg-white/10"
                  aria-label={`关闭 ${tabTitle}`}
                >
                  <WorkspaceTabCloseIcon />
                </button>
              </SortableWorkspaceTab>
            );
          })}
                {agentWorkspace.hostTabs.filter(tab => tab.kind === 'agent').map((tab, index) => {
                  const selected = agentWorkspace.activeTabInstanceId === tab.tabInstanceId;
                  const workflowKey = agentWorkspace.instances[tab.tabInstanceId]?.moduleKey;
                  const workflowName = getEcomWorkflowName(workflowKey, allWorkflowConfigs);
                  const ordinal = agentWorkspace.hostTabs.filter(item => item.kind === 'agent').slice(0, index + 1)
                    .filter(item => agentWorkspace.instances[item.tabInstanceId]?.moduleKey === workflowKey).length;
                  const title = ordinal === 1 ? workflowName : `${workflowName} #${ordinal}`;
                  const notification = !selected ? agentWorkspace.instances[tab.tabInstanceId]?.notification : null;
                  const instance = agentWorkspace.instances[tab.tabInstanceId];
                  const activateTab = () => { cancelScheduledWorkspacePersistence(); setActiveWorkspaceView('generate'); agentStore.activate(tab.tabInstanceId); };
                  return <SortableWorkspaceTab key={tab.tabInstanceId} tabId={tab.tabInstanceId} title={title} onContextMenu={event => event.preventDefault()}
                    isActive={selected} onActivate={activateTab}
                    className={`group relative mt-2 h-[30px] min-w-0 max-w-[148px] shrink-0 overflow-hidden rounded-md border text-[12px] ${selected ? 'ecom-workspace-tab-glass text-[#00FAE5]' : 'border-white/10 bg-black/30 text-white/60 hover:text-white'}`}>
                    <button type="button" data-ecom-workspace-tab-select className="relative flex h-full w-full min-w-0 items-center gap-1.5 rounded-md px-2 pr-7 text-left" onClick={activateTab}>
                      {notification && <span data-agent-tab-notification={notification} role="img"
                        aria-label={notification === 'attention' ? '等待处理' : notification === 'completed' ? '任务已完成' : '任务故障'}
                        className={`h-1.5 w-1.5 shrink-0 rounded-full ${notification === 'attention' ? 'bg-[#FCB453]' : notification === 'completed' ? 'bg-[#0066FF]' : 'bg-[#FF4717]'}`} />}
                      <AgentWorkspaceTabTitle title={title} instance={instance} />
                    </button>
                    <button type="button" aria-label={`关闭 ${title}`} className="absolute right-2 top-1/2 flex h-[17px] w-[17px] -translate-y-1/2 items-center justify-center rounded p-0 hover:bg-white/10" onPointerDown={event => event.stopPropagation()} onClick={() => handleCloseAgentTab(tab.tabInstanceId)}><WorkspaceTabCloseIcon /></button>
                  </SortableWorkspaceTab>;
                })}
              </div>
            </SortableContext>
          </DndContext>
        )}
        {tabContextMenu && (() => {
          const contextTabIndex = openedSubOptions.indexOf(tabContextMenu.tabId);
          const hasTabsToRight = contextTabIndex >= 0 && contextTabIndex < openedSubOptions.length - 1;
          return (
            <div
              className="fixed z-9999 w-44 overflow-hidden rounded-lg border border-white/10 bg-[#151515]/95 py-1 text-[12px] text-white/80 shadow-2xl shadow-black/50 backdrop-blur-md"
              style={{ left: tabContextMenu.x, top: tabContextMenu.y }}
              onClick={(e) => e.stopPropagation()}
              onContextMenu={(e) => e.preventDefault()}
            >
              {tabContextMenu.tabId !== HISTORY_TAB_LABEL && (
                <button
                  type="button"
                  onClick={handleCopyCurrentWorkflow}
                  className="flex w-full items-center px-3 py-2 text-left transition-colors hover:bg-white/10 hover:text-white"
                >
                  复制当前工作流
                </button>
              )}
              <button
                type="button"
                onClick={() => handleCloseOpenedSubOption(tabContextMenu.tabId)}
                className="flex w-full items-center px-3 py-2 text-left transition-colors hover:bg-white/10 hover:text-white"
              >
                关闭当前标签
              </button>
              <button
                type="button"
                disabled={!hasTabsToRight}
                onClick={handleCloseTabsToRight}
                className={`flex w-full items-center px-3 py-2 text-left transition-colors ${
                  hasTabsToRight
                    ? "hover:bg-white/10 hover:text-white"
                    : "cursor-not-allowed text-white/30"
                }`}
              >
                关闭右侧其他标签
              </button>
              <button
                type="button"
                onClick={handleCloseAllOpenedTabs}
                className="flex w-full items-center px-3 py-2 text-left text-red-300 transition-colors hover:bg-red-500/15 hover:text-red-200"
              >
                关闭全部标签
              </button>
            </div>
          );
        })()}
        <EcomAgentHostSurface uploadHoverPreview={uploadImageHoverPreview} onImageHoverPreviewChange={setUploadImageHoverPreview} />
        {/* Unmount inactive workflow portals; CSS hiding does not hide body portals. */}
        {!agentWorkspaceMode && <div
          data-ecom-preview-surface
          aria-hidden={isTemplateOnboarding || undefined}
          className={`min-h-0 flex-1 overflow-hidden ${isTemplateOnboarding ? "invisible pointer-events-none" : ""}`}
        >
        {activeWorkspaceView === "generate" && isDetailReplicaWorkflow && status === "empty" ? (
          <DetailPageReplicaWorkspace
            uiConfig={workflowUIConfig}
            params={cozeParams}
            onParamChange={ownedCozeParamChange}
            activeIndex={detailReplicaActiveIndex}
            onActiveIndexChange={setDetailReplicaActiveIndex}
            focusRequest={detailReplicaFocusRequest}
            onReferenceCountChange={(count) => setCozeBatchCount(Math.max(1, count))}
            onUploadingChange={setImageUploading}
            uploadHoverPreview={uploadImageHoverPreview}
          />
        ) : activeWorkspaceView === "history" ? (
          <HistoryPanel
            variant="page"
            className="h-full rounded-none border-0 bg-transparent"
            onImageSelect={(imageUrl) => {
              applyHistorySelection({
                images: [imageUrl],
                modelImage: "",
                productImage: "",
              });
            }}
            onGroupSelect={(result) => {
              applyHistorySelection(result);
            }}
            onRegenerateFromHistory={handleRegenerateFromHistory}
            onOpenAgentHistory={handleOpenAgentHistory}
            agentController={agentController}
            onOpenAgentConversation={handleOpenAgentConversation}
            agentFeatureKeys={allWorkflowConfigs.filter((workflow) => workflow.execution_mode === 'agent').map((workflow) => workflow.feature_key)}
            restoringAgentTaskId={restoringAgentTaskId}
            onCollapse={handleHistoryPageCollapse}
            filterKey={historyFilterKey}
            onViewImageLayer={(task, imageUrl) => setHistoryImageLayer({ task, imageUrl })}
            onFilterChange={setHistoryFilterKey}
            workflowKey={currentPreviewFeatureKey}
            onTaskCompleted={(result) => {
              if (status === "generating" && currentTaskIds.length > 0) {
                setHistoryRefreshTrigger(prev => prev + 1);
                return;
              }
              if (isRefining || activeRefineTrackerRef.current.size > 0) {
                setHistoryRefreshTrigger(prev => prev + 1);
                return;
              }
              if (!isCurrentPreviewTask(result.taskId, result.featureKey)) {
                setHistoryRefreshTrigger(prev => prev + 1);
                return;
              }
              setGeneratedImage(result.effectImage);
              setResultModelImage(result.modelImage);
              setResultProductImage(result.productImage);
              setStatus("completed");
              setProgress(100);
              setTaskProgresses([]);
              setErrorMessage("");
              notifyPointsChanged();
            }}
            refreshTrigger={historyRefreshTrigger}
            teamId={user?.current_team_id}
            workflowOptions={enabledWorkflowOptions}
            currentTaskIds={currentTaskIds}
            currentProgress={progress}
            taskProgresses={taskProgresses}
            currentGeneratedImages={currentHistoryGeneratedImages}
            currentFailedIndexes={failedIndexes}
            currentStatus={status}
            currentInspireResults={displayedInspireResults}
            onActiveTaskClick={handleActiveHistoryTaskClick}
            regeneratingTaskUuids={[
              ...regeneratingIndexes.map(i => currentTaskIds[i] || batchChildTaskIds[i] || "").filter(Boolean),
              ...Array.from(activeRetryTrackerRef.current.values()).map((tracker) => tracker.retryTaskId),
            ]}
            refiningTaskUuids={Array.from(activeRefineTrackerRef.current.values()).map((tracker) => tracker.targetTaskId)}
          />
        ) : (
        <div className="w-full h-full">
          {activeMainTab === INSPIRE_REVERSE_MODULE_KEY || isCurrentInspireWorkflow() || inspireResults.length > 0 ? (
            <InspireResultPanel
              results={displayedInspireResults}
              isEmpty={status === "empty" && displayedInspireResults.length === 0}
              isAllPending={status === "generating"}
              errorMessage={status === "failed" && errorMessage ? errorMessage : undefined}
              templateLabels={Object.keys(multiTemplateLabels).length > 0 ? multiTemplateLabels : undefined}
              templateWorkflows={currentInspireTemplateBindings.templateWorkflows}
              fallbackWorkflowKeys={currentInspireTemplateBindings.fallbackWorkflowKeys}
              availableWorkflows={targetGenerateWorkflowOptions}
              onNavigateToWorkflow={(workflowKey, prompt, tags, inputImages) => {
                // Switch to target workflow tab and prefill prompt (no auto-submit)
                const targetWf = findEnabledWorkflowByFeatureKey(workflowKey);
                const workflowTab = getWorkflowTabForFeatureKey(workflowKey);
                if (targetWf && workflowTab) {
                  // Navigation only prepares the target workflow. Clear every
                  // generation identity from the reverse-prompt tab first so
                  // history cannot project a phantom task before submission.
                  const sourceTabId = activeTabIdRef.current;
                  const sourceWorkspaceFields = createReverseNavigateSourceWorkspaceFields(
                    createEmptyForegroundGenerationWorkspaceFields(),
                    buildCurrentWorkspaceTab(sourceTabId),
                  );
                  persistCurrentWorkspaceTab(undefined, sourceWorkspaceFields);
                  cancelScheduledWorkspacePersistence();
                  invalidateGenerationRequests();
                  stopInspirePolling();
                  clearForegroundGenerationState(sourceWorkspaceFields);
                  setGenerationActive(false);
                  generatingTaskIdsRef.current = [];
                  generatingViewingTaskIdRef.current = "";
                  generatingBatchChildIdsRef.current = [];
                  generatingFeatureKeyRef.current = null;
                  generatingImagesRef.current = [];
                  generatingProgressRef.current = [];
                  generatingFailedRef.current = [];
                  setRegeneratingIndexes([]);
                  const targetTab = workflowTab.mainTab;
                  handleSubOptionSelect(targetWf.name, targetTab, sourceWorkspaceFields);
                  // Prefill prompt and the source image used for this reverse prompt — no auto-submit.
                  const sourceImages = inputImages && inputImages.length > 0
                    ? inputImages
                    : restoredInspireInputImagesForDisplay;
                  setPrefillData({
                    prompt,
                    tags,
                    ...(sourceImages.length > 0 ? { [ECOM_PREFILL_SOURCE_IMAGES_KEY]: sourceImages } : {}),
                  });
                }
              }}
              onSendToGenerate={(prompt) => {
                if (typeof window !== "undefined") {
                  try {
                    sessionStorage.setItem("inspire_prompt_to_use", prompt);
                  } catch { /* ignore */ }
                }
                feedback.success("反推提示词已暂存，请切换到生图工作流后粘贴或自动填充");
              }}
            />
          ) : isDetailReplicaWorkflow && (status === "generating" || status === "completed" || status === "failed") ? (
            <DetailPageReplicaResultPanel
              references={detailReplicaState.references}
              images={displayGeneratedImages}
              ratios={detailReplicaState.ratios}
              status={status}
              taskProgresses={taskProgresses}
              outputCount={previewOutputCount}
              failedIndexes={failedIndexes}
              regeneratingIndexes={regeneratingIndexes}
              generationStartedAt={generationStartedAt}
              taskId={viewingTaskId}
              batchChildTaskIds={batchChildTaskIds}
              productImage={resultProductImage || undefined}
              onPreview={(url, index) => openPreview(url, index)}
              onPublish={(url) => openInspirationUpload(url)}
              onFavorite={handleFavorite}
              onRegenerateSingle={handleRegenerateSingle}
              onImagesUpdated={(imgs, targetTaskId) => {
                if (!isCurrentPreviewTask(targetTaskId)) {
                  setHistoryRefreshTrigger((prev) => prev + 1);
                  return;
                }
                setGeneratedImages(imgs);
                if (imgs[0]) setGeneratedImage(imgs[0]);
              }}
              onHistoryRefresh={() => setHistoryRefreshTrigger((prev) => prev + 1)}
              refineOwnerTabId={activeTabId}
              onRefineTrack={handleRefineTrack}
              activeRefines={activeRefinesForCurrentTab}
              refineStateVersion={refineLifecycleEpoch}
              onInsertReferenceFiles={insertDetailReplicaResultReferences}
              onDeleteReference={deleteDetailReplicaResultReference}
              onUndoReferenceDeletion={undoDetailReplicaResultDeletion}
              activeIndex={detailReplicaActiveIndex}
              onActiveIndexChange={setDetailReplicaActiveIndex}
              focusRequest={detailReplicaFocusRequest}
            />
          ) : (stitchMode || manualStitchMode) && (status === "generating" || status === "completed" || status === "failed") ? (
            <ImageStitchPanel
              images={displayGeneratedImages}
              status={status}
              progress={Math.min(Math.round(progress), 100)}
              taskProgresses={taskProgresses}
              outputCount={previewOutputCount}
              onPreview={(url: string, index?: number) => openPreview(url, index)}
              onPublish={(url: string) => openInspirationUpload(url)}
              onFavorite={handleFavorite}
              onRegenerateSingle={handleRegenerateSingle}
              regeneratingIndexes={regeneratingIndexes}
              failedIndexes={failedIndexes}
              statusHint={inferenceStatusHint}
              taskId={viewingTaskId}
              batchChildTaskIds={batchChildTaskIds}
              productImage={resultProductImage || undefined}
              onImagesUpdated={(imgs, targetTaskId) => {
                if (!isCurrentPreviewTask(targetTaskId)) {
                  setHistoryRefreshTrigger(prev => prev + 1);
                  return;
                }
                setGeneratedImages(imgs);
                if (imgs[0]) setGeneratedImage(imgs[0]);
              }}
              onHistoryRefresh={() => setHistoryRefreshTrigger(prev => prev + 1)}
              onExitStitch={manualStitchMode ? () => setManualStitchMode(false) : undefined}
              refineOwnerTabId={activeTabId}
              onRefineTrack={handleRefineTrack}
              activeRefines={activeRefinesForCurrentTab}
              refineStateVersion={refineLifecycleEpoch}
            />
          ) : (
            <div className="relative w-full h-full">
              {/* Batch progress status bar */}
              {batchProcessMode && batchTaskStatus && (status === "generating" || status === "completed" || status === "failed") && (
                <div className="absolute top-3 left-1/2 -translate-x-1/2 z-20
                                flex items-center gap-3 px-4 py-2 rounded-full
                                bg-bg-card/90 backdrop-blur-md border border-white/10
                                shadow-[0_4px_20px_rgba(0,0,0,0.4)]">
                  <svg className="w-4 h-4 text-purple-400 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="2" y="2" width="8" height="8" rx="1" /><rect x="14" y="2" width="8" height="8" rx="1" />
                    <rect x="2" y="14" width="8" height="8" rx="1" /><rect x="14" y="14" width="8" height="8" rx="1" />
                  </svg>
                  <span className="text-xs text-white/80 font-medium">
                    批处理 {batchTaskStatus.completed_count}/{batchTaskStatus.batch_total}
                  </span>
                  {status === "generating" && (
                    <>
                      {batchTaskStatus.processing_count > 0 && (
                        <span className="text-[11px] text-blue-400 flex items-center gap-1">
                          <span className="w-1.5 h-1.5 rounded-full bg-blue-400 animate-pulse" />
                          {batchTaskStatus.processing_count} 生成中
                        </span>
                      )}
                      {batchTaskStatus.queued_count > 0 && (
                        <span className="text-[11px] text-white/40">{batchTaskStatus.queued_count} 排队</span>
                      )}
                    </>
                  )}
                  {batchTaskStatus.failed_count > 0 && (
                    <span className="text-[11px] text-red-400">{batchTaskStatus.failed_count} 失败</span>
                  )}
                  <div className="w-20 h-1.5 bg-white/10 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-linear-to-r from-[#FCA164] via-[#D24DDA] to-[#652DF5] transition-all duration-500"
                      style={{ width: `${batchTaskStatus.progress}%` }}
                    />
                  </div>
                  <span className="text-[11px] text-white/50">AI Generating</span>
                </div>
              )}
              <PreviewArea
                status={status}
                progress={Math.min(Math.round(progress), 100)}
                generationStartedAt={generationStartedAt}
                imageUrl={displayGeneratedImage || undefined}
                modelImage={batchProcessMode && batchTaskStatus ? null : (resultModelImage || null)}
                productImage={batchProcessMode && batchTaskStatus ? null : (resultProductImage || null)}
                backgroundImage={null}
                subOption={activeSubOption}
                onDownload={handleDownload}
                historyImageLayer={historyImageLayer}
                onRegenerate={handleRegenerate}
                onRegenerateSingle={handleRegenerateSingle}
                regeneratingIndexes={regeneratingIndexes}
                failedIndexes={failedIndexes}
                onFavorite={handleFavorite}
                onPublish={(imageUrl: string) => openInspirationUpload(imageUrl)}
                onPreview={(imageUrl: string, index?: number) => openPreview(imageUrl, index)}
                generatedImages={displayGeneratedImages}
                outputCount={previewOutputCount}
                errorMessage={errorMessage}
                taskProgresses={taskProgresses}
                nextStepTargets={nextStepTargets}
                onNextStep={handleNextStep}
                aspectRatio={(() => {
                  const ratioComp = workflowUIConfig?.components?.find(c => c.component_type === "ratio_selector");
                  if (!ratioComp) return null;
                  const v = (cozeParams[ratioComp.coze_param_key] as string | undefined) ?? null;
                  if (!v) return null;
                  // Pass through "auto" or any "W:H" semantic ratio.
                  if (v === "auto" || /^\d+:\d+$/.test(v)) return v;
                  // Backward compat: legacy drafts may keep a binding resolution
                  // (e.g. "1440x1920") in the ratio key. Map it back to the
                  // workflow's configured semantic ratio so the preview frame
                  // reflects the intended shape rather than falling through.
                  const binding = findBoundRatioForSize(ratioComp.config?.ratio_resolutions, v);
                  if (binding) {
                    return binding.ratio;
                  }
                  return v;
                })()}
                outputCropSize={typeof cozeParams.__output_crop_size === "string" ? cozeParams.__output_crop_size : null}
                aspectRatios={cozeBatchCount > 1 && perBatchRatios.length > 0 ? perBatchRatios.map((p) => p?.ratio ?? null) : undefined}
                outputCropSizes={cozeBatchCount > 1 && perBatchRatios.length > 0 ? perBatchRatios.map((p) => p?.cropSize ?? null) : undefined}
                statusHint={inferenceStatusHint}
                uploadHoverPreview={uploadImageHoverPreview}
                taskId={viewingTaskId}
                batchChildTaskIds={batchChildTaskIds}
                onImagesUpdated={(images, targetTaskId) => {
                  if (!isCurrentPreviewTask(targetTaskId)) {
                    setHistoryRefreshTrigger(prev => prev + 1);
                    return;
                  }
                  setGeneratedImages(images);
                  if (images[0]) setGeneratedImage(images[0]);
                }}
                onHistoryRefresh={() => setHistoryRefreshTrigger(prev => prev + 1)}
                onAutoStitch={() => setManualStitchMode(true)}
                onRefiningChange={setIsRefining}
                refineOwnerTabId={activeTabId}
                onRefineTrack={handleRefineTrack}
                activeRefines={activeRefinesForCurrentTab}
                refineStateVersion={refineLifecycleEpoch}
                onBatchDownload={handleDownload}
              />
            </div>
          )}
        </div>
        )}
        </div>}
      </div>

      {useSplitDetailReplicaLayout && (
        <div
          data-detail-replica-history-rail
          className="order-2 w-[58px] shrink-0 bg-black"
          aria-hidden="true"
        />
      )}

      <div data-ecom-history-drawer-host className="contents">
        {/* ===== 最右侧吸附式历史记录抽屉 ===== */}
        {activeWorkspaceView === "generate" && (
          <>
            {/* 收起态：右侧竖向吸附按钮 */}
            {!isHistoryDrawerOpen && (
              <button
                type="button"
                onClick={() => {
                  setShouldRenderHistoryDrawer(true);
                  setIsHistoryDrawerOpen(true);
                }}
                aria-label="展开历史记录"
                className={`absolute right-0 top-1/2 z-40 -translate-y-1/2 ${historyDrawerToggleBtnClass}`}
              >
                <HistoryDrawerToggleIcon collapsed />
                <span
                  className="whitespace-nowrap leading-tight tracking-[0.2em]"
                  style={{ writingMode: "vertical-rl" }}
                >
                  {HISTORY_TAB_LABEL}
                </span>
              </button>
            )}

            {/* 常驻抽屉面板：预先挂载 HistoryPanel，展开时只切换 transform，避免首次点击卡顿。 */}
            {shouldRenderHistoryDrawer && (
              <div
                data-ecom-history-drawer-overlay
                aria-hidden={!isHistoryDrawerOpen}
                className={`pointer-events-none absolute right-0 top-0 bottom-0 z-40 flex transform-gpu transition-[transform,opacity] duration-200 ease-out will-change-transform ${
                  isHistoryDrawerOpen
                    ? "translate-x-0 opacity-100"
                    : "translate-x-full opacity-0"
                }`}
              >
                {/* 左侧收起把手 */}
                <button
                  type="button"
                  data-ecom-history-drawer-handle="true"
                  onClick={() => setIsHistoryDrawerOpen(false)}
                  aria-label="收起历史记录"
                  className={`pointer-events-auto self-center -mr-px ${historyDrawerToggleBtnClass}`}
                >
                  <HistoryDrawerToggleIcon collapsed={false} />
                  <span
                    className="whitespace-nowrap leading-tight tracking-[0.2em]"
                    style={{ writingMode: "vertical-rl" }}
                  >
                    {HISTORY_TAB_LABEL}
                  </span>
                </button>
                <div
                  data-ecom-history-drawer-panel="true"
                  className="pointer-events-auto h-full w-70 border-l border-white/10 bg-[#0D0E11] shadow-[-6px_0_24px_rgba(0,0,0,0.45)]"
                >
                  <HistoryPanel
                    variant="panel"
                    includeFailed
                    className="h-full"
                    onExpand={handleHistoryDrawerExpand}
                    filterKey={historyFilterKey}
                    onFilterChange={setHistoryFilterKey}
                    onViewImageLayer={(task, imageUrl) => setHistoryImageLayer({ task, imageUrl })}
                    workflowKey={currentPreviewFeatureKey}
                    onImageSelect={(imageUrl) => {
                      applyHistorySelection({
                        images: [imageUrl],
                        modelImage: "",
                        productImage: "",
                      });
                      setIsHistoryDrawerOpen(false);
                    }}
                    onGroupSelect={(result) => {
                      applyHistorySelection(result);
                      setIsHistoryDrawerOpen(false);
                    }}
                    onRegenerateFromHistory={(payload) => {
                      handleRegenerateFromHistory(payload);
                      setIsHistoryDrawerOpen(false);
                    }}
                    onOpenAgentHistory={handleOpenAgentHistory}
                    agentController={agentController}
                    onOpenAgentConversation={handleOpenAgentConversation}
                    agentFeatureKeys={allWorkflowConfigs.filter((workflow) => workflow.execution_mode === 'agent').map((workflow) => workflow.feature_key)}
                    restoringAgentTaskId={restoringAgentTaskId}
                    onTaskCompleted={(result) => {
                      if (status === "generating" && currentTaskIds.length > 0) {
                        setHistoryRefreshTrigger(prev => prev + 1);
                        return;
                      }
                      if (isRefining || activeRefineTrackerRef.current.size > 0) {
                        setHistoryRefreshTrigger(prev => prev + 1);
                        return;
                      }
                      if (!isCurrentPreviewTask(result.taskId, result.featureKey)) {
                        setHistoryRefreshTrigger(prev => prev + 1);
                        return;
                      }
                      setGeneratedImage(result.effectImage);
                      setResultModelImage(result.modelImage);
                      setResultProductImage(result.productImage);
                      setStatus("completed");
                      setProgress(100);
                      setTaskProgresses([]);
                      setErrorMessage("");
                      notifyPointsChanged();
                    }}
                    refreshTrigger={historyRefreshTrigger}
                    teamId={user?.current_team_id}
                    workflowOptions={[
                      ...enabledWorkflowOptions,
                    ]}
                    currentTaskIds={currentTaskIds}
                    currentProgress={progress}
                    taskProgresses={taskProgresses}
                    currentGeneratedImages={currentHistoryGeneratedImages}
                    currentFailedIndexes={failedIndexes}
                    currentStatus={status}
                    currentInspireResults={displayedInspireResults}
                    onActiveTaskClick={(payload) => {
                      handleActiveHistoryTaskClick(payload);
                      setIsHistoryDrawerOpen(false);
                    }}
                    regeneratingTaskUuids={[
                      ...regeneratingIndexes.map(i => currentTaskIds[i] || batchChildTaskIds[i] || "").filter(Boolean),
                      ...Array.from(activeRetryTrackerRef.current.values()).map((tracker) => tracker.retryTaskId),
                    ]}
                    refiningTaskUuids={Array.from(activeRefineTrackerRef.current.keys())}
                  />

                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* 参考图设置弹窗 */}
      <ReferenceSettingModal
        isOpen={showRefSettingModal}
        onClose={() => setShowRefSettingModal(false)}
        imageUrl={refImageUrl || ""}
        onConfirm={handleRefSettingConfirm}
        workflowKey={getFeatureKey(activeSubOption)}
      />

      {/* 姿势预设弹窗 */}
      {showPoseModal && (
        <>
          <div
            className="fixed inset-0 z-9998 bg-black/70 backdrop-blur-[6px]"
            onClick={() => setShowPoseModal(false)}
          />

          <div
            className="fixed left-1/2 top-1/2 z-9999 flex h-[min(600px,calc(100vh-48px))] w-[min(956px,calc(100vw-40px))]
                       -translate-x-1/2 -translate-y-1/2 flex-col overflow-hidden rounded-[14px]
                       border border-white/6 bg-[#1b1c22] px-[28px] py-[30px]
                       shadow-[0_30px_90px_rgba(0,0,0,0.62),0_0_0_1px_rgba(255,255,255,0.03)]"
            role="dialog"
            aria-modal="true"
            aria-label="姿势预设"
          >
            <div className="mb-5 flex items-start justify-between">
              <div className="flex min-w-0 items-center gap-4">
                <div className="flex h-[42px] w-[132px] rounded-[8px] bg-[#0f1014] p-0.5">
                  <div className="flex h-full flex-1 items-center justify-center rounded-[7px] bg-[#4a4a4a] text-sm font-medium text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.06)]">
                    姿势预设
                  </div>
                </div>
                <p className="hidden truncate text-sm text-white/50 sm:block">
                  当前 {filteredMergedPoses.length} / 共 {mergedPoses.length} 个姿势 · 拖拽调整顺序
                  {mergedPoses.some(p => p.is_custom) && (
                    <span className="ml-2 text-[#00FAE5]">· 含个人自定义</span>
                  )}
                </p>
              </div>
              <button
                onClick={() => setShowPoseModal(false)}
                className="-mr-1 -mt-1 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-white/80 transition-all hover:bg-white/8 hover:text-white"
                aria-label="关闭"
              >
                <X className="h-6 w-6" strokeWidth={2.2} />
              </button>
            </div>

            <div className="flex min-h-0 flex-1 flex-col overflow-hidden sm:flex-row">
              <div className="w-full shrink-0 overflow-x-auto border-b border-white/10 pb-3 sm:w-[196px] sm:overflow-y-auto sm:border-b-0 sm:pb-0 sm:pr-3
                              [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                <div className="flex gap-2 sm:block">
                  {poseCategoryOptions.map((category) => {
                    const isActive = activePoseCategoryId === category.id;
                    return (
                      <button
                        key={category.id}
                        onClick={() => setActivePoseCategoryId(category.id)}
                        className={`
                          mb-0 h-[38px] min-w-[112px] rounded-[6px] px-3 text-left text-sm transition-all sm:mb-1 sm:w-full sm:min-w-0 sm:px-[26px]
                          ${isActive
                            ? "bg-[#363636] text-white"
                            : "text-white/58 hover:bg-white/6 hover:text-white/82"
                          }
                        `}
                      >
                        <span className="block w-full truncate">{category.name || "未命名分类"}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="hidden w-px shrink-0 bg-white/10 sm:block" />

              <div className="min-h-0 flex-1 overflow-y-auto pt-4 sm:pl-6 sm:pr-3 sm:pt-0 lg:pl-[34px] lg:pr-[14px]
                              [&::-webkit-scrollbar]:w-3 [&::-webkit-scrollbar-thumb]:rounded-full
                              [&::-webkit-scrollbar-thumb]:border-x-[3px] [&::-webkit-scrollbar-thumb]:border-y-0
                              [&::-webkit-scrollbar-thumb]:border-solid [&::-webkit-scrollbar-thumb]:border-transparent
                              [&::-webkit-scrollbar-thumb]:bg-white/14 [&::-webkit-scrollbar-thumb]:bg-clip-content
                              [&::-webkit-scrollbar-track]:bg-transparent">
                <SortablePoseGrid
                  poses={filteredMergedPoses}
                  onReorder={handlePoseReorderNew}
                  onImageReplace={handlePoseImageReplace}
                  onReset={handleResetPose}
                  gridClassName="grid grid-cols-2 gap-[10px] sm:grid-cols-3 lg:grid-cols-5"
                  emptyMessage="当前分类暂无姿势"
                  getPoseIndex={(pose) => poseDisplayIndexById.get(pose.id) ?? 0}
                />
              </div>
            </div>

            <div className="mt-5 flex flex-col gap-3 border-t border-white/10 pt-4 sm:flex-row sm:items-center sm:justify-between">
              <span className="min-w-0 text-xs text-white/48">
                生图时按序号依次分配：{getCurrentBatchUnit()}次1→姿势1，{getCurrentBatchUnit()}次2→姿势2，以此类推
              </span>
              <div className="flex shrink-0 flex-wrap justify-end gap-3">
                <button
                  onClick={async () => {
                    if (!confirm('确定要重置所有姿势预设为团队默认设置吗？')) return;
                    await handleResetAllPoses();
                  }}
                  disabled={poseSaving}
                  className="inline-flex h-9 items-center gap-2 rounded-[8px] border border-white/14 bg-white/8 px-4 text-sm font-medium text-white/78 transition-all hover:bg-white/12 hover:text-white disabled:opacity-50"
                >
                  <RotateCcw className="h-4 w-4" />
                  重置
                </button>
                <button
                  onClick={() => setShowPoseModal(false)}
                  className="inline-flex h-9 items-center justify-center rounded-[8px] border border-white/18 bg-white px-5 text-sm font-medium text-[#181818] transition-all hover:bg-white/90"
                >
                  关闭
                </button>
                <button
                  onClick={async () => {
                    await handleSavePoseOverrides();
                    setShowPoseModal(false);
                  }}
                  disabled={poseSaving}
                  className="inline-flex h-9 items-center gap-2 rounded-[8px] border border-white/18 bg-white px-5 text-sm font-medium text-[#181818] transition-all hover:bg-white/90 disabled:opacity-50"
                >
                  <Save className="h-4 w-4" />
                  {poseSaving ? '保存中...' : '保存'}
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      {/* 豌豆余额不足弹窗 */}
      {showBalanceModal && (
        <div className="fixed inset-0 z-9999 flex items-center justify-center bg-black/60 backdrop-blur-xs">
          <div
            className="relative w-[400px] rounded-2xl border border-white/10 p-8 text-center"
            style={{ background: "linear-gradient(135deg, #2D1B2E 0%, #1B1025 50%, #1A0A1E 100%)" }}
          >
            <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-[#FCA164]/10">
              <svg className="h-7 w-7 text-[#FCA164]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <circle cx="12" cy="12" r="10" />
                <path d="M12 8v4M12 16h.01" strokeLinecap="round" />
              </svg>
            </div>
            <h3 className="mb-2 text-lg font-semibold text-white">豌豆余额不足</h3>
            <p className="mb-6 text-sm text-white/60">当前豌豆余额不足，请联系管理员补充</p>
            <button
              onClick={() => setShowBalanceModal(false)}
              className="w-full rounded-xl bg-linear-to-r from-[#FCA164] via-[#D24DDA] to-[#652DF5] py-2.5 text-sm font-medium text-white transition-all hover:opacity-90"
            >
              我知道了
            </button>
          </div>
        </div>
      )}

      {/* 重试确认弹窗 */}
      <RetryConfirmModal
        open={retryModalOpen}
        taskIds={retryModalTaskIds}
        fallbackParams={cozeParams}
        fallbackFeatureKey={getFeatureKey(activeSubOption)}
        onClose={() => setRetryModalOpen(false)}
        onConfirm={handleRetryConfirm}
      />

      <InspirationCommunityModal
        preloadEnabled={!!user}
        isOpen={showInspirationCommunity}
        onClose={() => setShowInspirationCommunity(false)}
        onMakeSame={handleMakeSame}
      />

      {historyRefineEditor && (
        <ImageRefineEditor
          imageUrl={historyRefineEditor.imageUrl}
          taskId={historyRefineEditor.taskId}
          imageIndex={historyRefineEditor.imageIndex}
          productImage={historyRefineEditor.productImage}
          onClose={() => setHistoryRefineEditor(null)}
          onRefineStart={(refineTaskId, operationId) => {
            if (refineTaskId.startsWith("__failed__:")) {
              const accepted = handleRefineTrack({
                ownerTabId: historyRefineEditor.ownerTabId,
                operationId,
                refineTaskId: "__cancel__",
                originalUrl: historyRefineEditor.imageUrl,
                imageIndex: historyRefineEditor.imageIndex,
                targetTaskId: historyRefineEditor.taskId,
              });
              feedback.error({ code: "E5999", fallback: "operation" });
              return accepted;
            }
            const accepted = handleRefineTrack({
              ownerTabId: historyRefineEditor.ownerTabId,
              operationId,
              refineTaskId,
              originalUrl: historyRefineEditor.imageUrl,
              imageIndex: historyRefineEditor.imageIndex,
              targetTaskId: historyRefineEditor.taskId,
            });
            if (accepted) setHistoryRefreshTrigger((prev) => prev + 1);
            return accepted;
          }}
        />
      )}

      <InspirationUploadModal
        open={uploadModalOpen}
        initialImageUrl={uploadPayload?.imageUrl}
        initialPrompt={uploadPayload?.prompt}
        onClose={closeInspirationUpload}
        onAfterClose={finishInspirationUploadClose}
        onSuccess={() => feedback.success("发布成功！")}
      />

      {previewImageUrl && (
        <div className="fixed inset-0 z-9999 bg-black/85 backdrop-blur-md flex" onClick={() => setPreviewImageUrl(null)}>
          {/* Image area with zoom & drag */}
          <div
            ref={previewContainerRef}
            className="flex-1 flex items-center justify-center overflow-hidden select-none outline-hidden"
            style={{ cursor: previewDragging ? "grabbing" : previewScale > 1 ? "grab" : "zoom-in" }}
            onClick={e => e.stopPropagation()}
            onWheel={handlePreviewWheel}
            onMouseDown={handlePreviewMouseDown}
            onMouseMove={handlePreviewMouseMove}
            onMouseUp={handlePreviewMouseUp}
            onMouseLeave={handlePreviewMouseUp}
          >
            <img
              src={previewImageUrl}
              alt="预览"
              draggable={false}
              decoding="async"
              className="max-w-none rounded-lg outline-hidden border-0"
              style={{
                transform: `translate(${previewPos.x}px, ${previewPos.y}px) scale(${previewScale})`,
                maxHeight: "88vh",
                maxWidth: "70vw",
                objectFit: "contain",
              }}
            />
          </div>

          {/* Zoom toolbar */}
          <div
            className="absolute bottom-6 left-1/2 -translate-x-1/2 z-20 flex items-center gap-1
                        bg-black/70 backdrop-blur-lg rounded-full px-2 py-1.5 border border-white/10"
            onClick={e => e.stopPropagation()}
          >
            <button
              onClick={() => setPreviewScale(s => Math.max(s - 0.25, 0.1))}
              className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-white/10 text-white/70 hover:text-white transition-colors"
              title="缩小"
            >
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M5 12h14" strokeLinecap="round" /></svg>
            </button>
            <button
              onClick={resetPreviewView}
              className="min-w-[52px] h-8 px-2 flex items-center justify-center rounded-full hover:bg-white/10 text-white/80 text-xs font-medium transition-colors"
              title="重置"
            >
              {Math.round(previewScale * 100)}%
            </button>
            <button
              onClick={() => setPreviewScale(s => Math.min(s + 0.25, 10))}
              className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-white/10 text-white/70 hover:text-white transition-colors"
              title="放大"
            >
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 5v14M5 12h14" strokeLinecap="round" /></svg>
            </button>
            <div className="w-px h-5 bg-white/15 mx-0.5" />
            <button
              onClick={resetPreviewView}
              className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-white/10 text-white/70 hover:text-white transition-colors"
              title="适应窗口"
            >
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
          </div>

          {/* Right info panel */}
          <div
            className="w-75 shrink-0 bg-[#141414]/95 border-l border-white/8 flex flex-col overflow-hidden"
            onClick={e => e.stopPropagation()}
          >
            <div className="px-4 pt-4 pb-3 border-b border-white/6">
              <button
                onClick={() => setPreviewImageUrl(null)}
                className="w-full h-9 mb-3 flex items-center justify-center gap-2 rounded-lg
                           bg-white/8 hover:bg-white/15 border border-white/12
                           text-white/80 hover:text-white text-sm transition-all active:scale-[0.98]"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M19 12H5M12 19l-7-7 7-7" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                返回生图页面
              </button>
              <h3 className="text-sm font-semibold text-white mb-1">图片详情</h3>
              <div className="text-xs text-white/40">
                {previewDetails?.featureName || activeSubOption}
              </div>
            </div>

            <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4 [&::-webkit-scrollbar]:w-1 [&::-webkit-scrollbar-thumb]:bg-white/15 [&::-webkit-scrollbar-thumb]:rounded-full">
              <div>
                <div className="text-[11px] text-white/35 uppercase tracking-wider mb-1.5">尺寸</div>
                <div className="text-sm text-white/80">
                  {previewImageDimensions ? `${previewImageDimensions.w} × ${previewImageDimensions.h} px` : "加载中..."}
                </div>
              </div>

              {previewImageFileSize && (
                <div>
                  <div className="text-[11px] text-white/35 uppercase tracking-wider mb-1.5">文件大小</div>
                  <div className="text-sm text-white/80">{previewImageFileSize}</div>
                </div>
              )}

              <div>
                <div className="text-[11px] text-white/35 uppercase tracking-wider mb-1.5">生成时间</div>
                <div className="text-sm text-white/80">{formatPreviewFullTime(previewDetails?.createdAt || "")}</div>
              </div>

              {previewDetails?.completedAt && (
                <div>
                  <div className="text-[11px] text-white/35 uppercase tracking-wider mb-1.5">完成时间</div>
                  <div className="text-sm text-white/80">{formatPreviewFullTime(previewDetails.completedAt)}</div>
                </div>
              )}

              {(previewDetails?.imageCount || 0) > 1 && (
                <div>
                  <div className="text-[11px] text-white/35 uppercase tracking-wider mb-1.5">图片数量</div>
                  <div className="text-sm text-white/80">
                    第 {(previewDetails?.imageIndex || 0) + 1} 张 / 共 {previewDetails?.imageCount} 张
                  </div>
                </div>
              )}

              {(previewDetails?.pointsCost || 0) > 0 && (
                <div>
                  <div className="text-[11px] text-white/35 uppercase tracking-wider mb-1.5">消耗</div>
                  <div className="text-sm font-medium">
                    <DouziAmount amount={previewDetails?.pointsCost || 0} />
                  </div>
                </div>
              )}

              {previewDetails?.prompt && (
                <div>
                  <div className="text-[11px] text-white/35 uppercase tracking-wider mb-1.5">提示词</div>
                  <div className="text-sm text-white/70 leading-relaxed bg-white/3 rounded-lg p-3 border border-white/6 break-all">
                    {previewDetails.prompt}
                  </div>
                </div>
              )}

              <div>
                <div className="text-[11px] text-white/35 uppercase tracking-wider mb-1.5">任务ID</div>
                <div className="text-xs text-white/40 font-mono break-all">{previewDetails?.taskId || "—"}</div>
              </div>
            </div>

            <div className="px-5 py-4 border-t border-white/6 space-y-2.5">
              <EcomButton
                variant="default"
                size="large"
                onClick={async () => {
                  await downloadFile(previewImageUrl, `flowx_${Date.now()}.png`);
                }}
              >
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                下载图片
              </EcomButton>
              <div className="grid grid-cols-2 gap-2">
                <EcomButton
                  variant="outline"
                  size="small"
                  onClick={() => handleFavorite(previewImageUrl)}
                  disabled={!!previewFavoriteTaskUUID && (previewFavoriteSaving || previewFavoriteChecking)}
                  loading={previewFavoriteSaving}
                  aria-pressed={!!previewFavoriteActive}
                >
                  {!previewFavoriteSaving && <Heart size={14} className="text-white" fill={previewFavoriteActive ? "currentColor" : "none"} />}
                  {previewFavoriteSaving ? "收藏中..." : previewFavoriteTaskUUID && previewFavoriteChecking ? "查询收藏中..." : previewFavoriteFailed ? "重试收藏状态" : previewFavoriteActive ? "已收藏" : "收藏"}
                </EcomButton>
                <EcomButton
                  variant="outline"
                  size="small"
                  onClick={() => {
                    openInspirationUpload(previewImageUrl);
                    setPreviewImageUrl(null);
                  }}
                >
                  <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M4 12v8a2 2 0 002 2h12a2 2 0 002-2v-8M16 6l-4-4-4 4M12 2v13" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  发布同款
                </EcomButton>
              </div>
            </div>
          </div>
        </div>
      )}

      {activeWorkspaceView === "history" && historyImageLayer && (
        <ImageLayerRecognitionModal
          imageUrl={historyImageLayer.imageUrl}
          initialTask={historyImageLayer.task}
          onClose={() => setHistoryImageLayer(null)}
        />
      )}

    </div>
  );
}
